import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.dashboard');
import { HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Service } from "src/libs/portal/season/service";
import { OwnitAuthSession, OwnitAuthUser, createAuthRepository } from './auth';
import { Calendar as OwnitCalendar, CalendarPermissionAction, CalendarSyncConflictResolution, CalendarSyncDirection, CalendarSyncStatus, Decoration as DecorationState, Event as OwnitEvent, ExternalCalendarDeletionPolicy, ExternalCalendarProviderType, ExternalCalendarWritePermissionStatus, ExternalEventMapping, Group as OwnitGroup, GroupMember as OwnitGroupMember, GroupMemberStatus, GroupRole, Holiday as OwnitHoliday, ReminderPreset, ReminderRule, ReminderSetting, RoutineRepeatType, StickerAsset as OwnitStickerAsset, StickerPlacement, StickerPlacementTarget, Theme, Todo as OwnitTodo, TodoCategory as OwnitTodoCategory, TodoCompletionRecord as OwnitTodoCompletionRecord, TodoDailyCategoryStat as OwnitTodoDailyCategoryStat, TodoDailyRecord as OwnitTodoDailyRecord, TodoGoal as OwnitTodoGoal, TodoReaction as OwnitTodoReaction, TodoRoutine as OwnitTodoRoutine, TodoTimerSession as OwnitTodoTimerSession, TodoTimerStatus, Visibility } from './models';
import { HolidayService } from './holidays';
import {
    AppPermissionAction,
    AppTabId,
    canAccessTab as canAccessTabForUser,
    canManageDecoration as canManageDecorationForUser,
    canManageEvent as canManageEventForUser,
    canManageGroup as canManageGroupForUser,
    canManageSettings as canManageSettingsForUser,
    canManageTodo as canManageTodoForUser,
    canSaveDailyReceipt,
    canViewCalendar as canViewCalendarForUser,
    canViewEvent as canViewEventForUser,
    hasPermission,
    isAdminUser,
    userRoleLabel
} from './permissions';
import { CloudRepositoryStatus, OwnitRepositoryAdapter, TodoCategoryDeleteResolution, createOwnitRepositoryAdapter, repositoryErrorMessage } from './repository';
import { AiSuggestionProvider, TodoAiSuggestionContext, TodoAiSuggestionResult, createAiSuggestionProvider, hasTodoAiSuggestionInput } from './ai-suggestions';
import {
    ExternalCalendarProvider,
    ExternalCalendarProviderSummary,
    ExternalCalendarSnapshot,
    ExternalOAuthConnection,
    ExternalOAuthConnectionStatus,
    ExternalOAuthProviderConfig,
    ExternalOAuthProviderId,
    buildExternalOAuthStartUrl,
    collectExternalCalendarSnapshot,
    createExternalOAuthConnection,
    createMockExternalCalendarProviders,
    externalProviderHasWriteScope,
    externalSourceLabel,
    getExternalOAuthProviderConfig,
    isExternalCalendarProviderId,
    isExternalOAuthProviderId,
    normalizeExternalDeletionPolicy,
    normalizeExternalSyncDirection,
    normalizeExternalSyncStatus,
    normalizeExternalWritePermission
} from './external-calendars';
import {
    OwnitWidgetSize,
    OwnitWidgetSummary,
    createOwnitWidgetSummary,
    createOwnitWidgetSummaryCache,
    widgetSummaryCacheKey
} from './widget-summary';
import {
    OWNIT_CLOUD_DANCER_THEME_ID,
    OWNIT_COSMIC_LATTE_THEME_ID,
    OWNIT_DEFAULT_THEME_ID,
    OWNIT_EMPTY_TEXTURE,
    OWNIT_NEUTRAL_COLORS,
    OWNIT_WHITE_BACKGROUND,
    createDefaultThemes,
    createEmptyDecoration,
    createEmptyGroup,
    createEmptyUser,
    createMonthKey
} from './sample-data';
import { OWNIT_DEFAULT_FONT_ID, OWNIT_FONT_OPTIONS, normalizeOwnitFontId, ownitFontStack } from './fonts';
import {
    TodoDayMarkerCompletedItem,
    TodoDayMarkerSlot,
    buildTodoDayMarkerSlots as buildTodoDayMarkerSlotData
} from './todo-day-marker-slots';

declare const WizRoute: any;

type AppTab = AppTabId;
type AuthMode = 'signin' | 'signup' | 'reset' | 'profile';
type OwnitUser = OwnitAuthUser;
type WeekendTone = 'weekday' | 'saturday' | 'sunday';
type CalendarRangeCancelSource = 'control' | 'history' | 'escape';
type TodoSocialView = 'me' | 'friends';
type SharedCompletionFilter = 'all' | 'open' | 'completed';
type SharedGuardDialogMode = 'rename-group' | 'remove-member' | 'leave-group';

interface ExternalOAuthPendingState {
    provider: ExternalOAuthProviderId;
    state: string;
    redirectUri: string;
    scope: string;
    createdAt: string;
}

interface ExternalCalendarSyncPolicy {
    calendarId: string;
    externalCalendarId: string;
    source: ExternalCalendarProviderType;
    provider: ExternalCalendarProviderType;
    direction: CalendarSyncDirection;
    deletionPolicy: ExternalCalendarDeletionPolicy;
    writePermission: ExternalCalendarWritePermissionStatus;
    writeScope: string;
    writeRequestedAt: string | null;
    lastSyncedAt: string | null;
    syncStatus: CalendarSyncStatus;
    syncError: string | null;
    updatedAt: string;
}

interface ExternalSyncConflictView {
    mapping: ExternalEventMapping;
    event: OwnitEvent;
    calendar: OwnitCalendar | null;
    reason: string;
}

interface CalendarDay {
    date: string;
    day: number;
    weekday: number;
    weekendTone: WeekendTone;
    holidays: OwnitHoliday[];
    holidayLabel: string;
    isCurrentMonth: boolean;
    isToday: boolean;
    isSunday: boolean;
    isSaturday: boolean;
    isHoliday: boolean;
    isPublicHoliday: boolean;
    eventCount: number;
    eventOverflowCount: number;
    todoCount: number;
    todoRecord?: MainCalendarTodoRecord | null;
}

interface CalendarDateRange {
    startDate: string;
    endDate: string;
}

interface CalendarEventSegment {
    event: OwnitEvent;
    label: string;
    focusDate: string;
    segmentStart: string;
    segmentEnd: string;
    weekRowIndex: number;
    startColumn: number;
    spanColumns: number;
    isStart: boolean;
    isEnd: boolean;
    gridColumn: string;
    gridRow: string;
    continuesBefore: boolean;
    continuesAfter: boolean;
    isShared: boolean;
    isExternal: boolean;
    isSingleDay: boolean;
    lane: number;
}

interface CalendarWeek {
    key: string;
    days: CalendarDay[];
    segments: CalendarEventSegment[];
    hiddenSegmentCount: number;
}

interface CalendarEventSegmentBuildResult {
    segmentsByWeekRowIndex: Record<number, CalendarEventSegment[]>;
    hiddenSegmentCountByWeekRowIndex: Record<number, number>;
    hiddenByDate: Record<string, number>;
}

interface MonthEventLayoutItem {
    event: OwnitEvent;
    visibleStart: string;
    visibleEnd: string;
    startIndex: number;
    endIndex: number;
}

interface EventFormState {
    title: string;
    description: string;
    scope: OwnitCalendar['type'];
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    allDay: boolean;
    color: string;
    calendarId: string;
    assigneeId: string;
    completed: boolean;
    reminderPreset: ReminderPreset;
    reminderCustomDate: string;
    reminderCustomTime: string;
}

interface CalendarFormState {
    type: OwnitCalendar['type'];
    groupId: string;
    groupName: string;
    name: string;
    description: string;
    color: string;
    visibility: Visibility;
    isVisible: boolean;
}

interface CalendarPayload extends Pick<OwnitCalendar, 'name' | 'type' | 'color' | 'description' | 'isVisible' | 'visibility' | 'updatedAt'> {
    groupId: string;
    groupName: string;
}

interface TodoFormState {
    title: string;
    date: string;
    memo: string;
    goalId: string;
    category: string;
    color: string;
    scope: OwnitTodo['scope'];
    reminderPreset: ReminderPreset;
    reminderDate: string;
    reminderTime: string;
}

interface InlineTodoDraftState {
    sectionId: string;
    goalId: string | null;
    category: string;
    color: string;
    title: string;
}

type TodoCreateMode = 'todo' | 'category' | 'routine';

interface TodoCreateFlowContext {
    date: string;
    goalId: string | null;
    sectionId: string;
    category: string;
    color: string;
}

type TodoActionMode = 'menu' | 'edit' | 'memo' | 'reminder' | 'photo' | 'repeat' | 'date' | 'routine' | 'delete';
type TodoActionCommand = 'edit' | 'delete' | 'memo' | 'reminder' | 'photo' | 'tomorrow' | 'repeat' | 'date' | 'routine';
type TodoCalendarDisplayOptionKey = 'enabled' | 'showTodoCompleted' | 'showRoutineCompleted' | 'showCompletionRate';
type TodoCategoryDeleteMode = TodoCategoryDeleteResolution;

interface TodoActionDraftState {
    memo: string;
    reminderAt: string;
    reminderOption: ReminderPreset;
    reminderDate: string;
    reminderTime: string;
    photoProofUrl: string;
    repeatDate: string;
    changeDate: string;
}

interface TodoActionSheetItem {
    id: TodoActionCommand;
    label: string;
    description: string;
    iconClass: string;
    tone: string;
    disabled: boolean;
}

type TodoGoalPaletteId = 'default' | 'ss-2024';

interface TodoGoalPaletteColor {
    label: string;
    value: string;
}

interface TodoGoalPalette {
    id: TodoGoalPaletteId;
    label: string;
    colors: TodoGoalPaletteColor[];
}

type TodoGoalMarkerId = 'dot' | 'ring' | 'square' | 'check' | 'star';

interface TodoGoalMarkerOption {
    value: TodoGoalMarkerId;
    label: string;
    iconClass: string;
}

interface TodoCalendarDisplayOptions {
    enabled: boolean;
    showTodoCompleted: boolean;
    showRoutineCompleted: boolean;
    showCompletionRate: boolean;
}

interface CalendarTodoMarker {
    color: string;
    label: string;
    marker: TodoGoalMarkerId;
}

interface MainCalendarTodoRecord {
    totalCount: number;
    completedCount: number;
    completedTodoCount: number;
    completedRoutineCount: number;
    completionRate: number;
    showCompletionRate: boolean;
    markers: CalendarTodoMarker[];
    markerOverflowCount: number;
    label: string;
}

interface TodoGoalFormState {
    name: string;
    color: string;
    marker: TodoGoalMarkerId;
    visibility: OwnitTodoGoal['visibility'];
    order: number;
    collapsed: boolean;
    setAsDefault: boolean;
    setAsRecent: boolean;
}

interface RoutineFormState {
    title: string;
    goalId: string;
    category: string;
    color: string;
    repeatType: RoutineRepeatType;
    repeatDays: number[];
    repeatDates: number[];
    startDate: string;
    endDate: string;
    reminderEnabled: boolean;
    reminderOption: ReminderPreset;
    reminderTime: string;
    hideToday: boolean;
}

interface ReminderPresetOption {
    value: ReminderPreset;
    label: string;
    description: string;
    iconClass: string;
}

interface RoutineRepeatTypeOption {
    value: RoutineRepeatType;
    label: string;
}

interface RoutineWeekdayOption {
    value: number;
    label: string;
}

interface SharedSummaryCard {
    id: 'day' | 'week' | 'month';
    label: string;
    rangeLabel: string;
    events: OwnitEvent[];
}

interface PermissionRow {
    role: GroupRole;
    label: string;
    description: string;
    actions: CalendarPermissionAction[];
}

interface MemberInviteDraftState {
    name: string;
    email: string;
    role: GroupRole;
}

interface DecorationBackgroundOption {
    id: string;
    label: string;
    color: string;
    texture: string;
}

interface BasicStickerAsset {
    id: string;
    label: string;
    emoji: string;
    categoryId: string;
    iconClass: string;
    color: string;
    background: string;
}

interface StickerDisplayAsset extends BasicStickerAsset {
    dataUrl: string;
    sourceType: 'basic' | 'upload' | 'emoji';
    renderOptions?: OwnitStickerAsset['renderOptions'];
}

interface EmojiStickerCategory {
    id: string;
    label: string;
    description: string;
    stickers: BasicStickerAsset[];
}

type StickerBackgroundRemovalStatus = 'idle' | 'processing' | 'done' | 'failed';

interface StickerUploadDraftState {
    name: string;
    dataUrl: string;
    originalDataUrl: string;
    processedDataUrl: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    originalFileSize: number;
    width: number;
    height: number;
    processedWidth: number;
    processedHeight: number;
    backgroundRemovalStatus: StickerBackgroundRemovalStatus;
    backgroundRemovalError: string;
    borderEnabled: boolean;
    shadowEnabled: boolean;
    error: string;
    notice: string;
}

interface StickerPreparedImage {
    dataUrl: string;
    fileType: string;
    fileSize: number;
    width: number;
    height: number;
    notice: string;
}

interface StickerEdgeBackground {
    r: number;
    g: number;
    b: number;
    threshold: number;
    transparentRatio: number;
}

interface StickerBackgroundRemovalResult {
    dataUrl: string;
    fileSize: number;
    width: number;
    height: number;
    removedRatio: number;
    alreadyTransparent: boolean;
}

interface StickerDragState {
    id: string;
    pointerId: number;
    container: HTMLElement;
    moved: boolean;
}

interface CalendarEventBarPointerState {
    pointerId: number;
    startX: number;
    startY: number;
    moved: boolean;
}

interface ReceiptDraft {
    emoji: string;
    mood: string;
    note: string;
}

type DailyReceiptDateFormat = 'iso' | 'korean-full' | 'korean-short' | 'dot';
type DailyReceiptSectionId = 'personalSchedules' | 'sharedSchedules' | 'completedTodos' | 'unfinishedTodoCount' | 'routines' | 'dailyMemo' | 'completionRate';
type DailyReceiptSectionKind = 'lines' | 'memo' | 'metrics';

interface DailyReceiptDateFormatOption {
    value: DailyReceiptDateFormat;
    label: string;
}

interface DailyReceiptTemplateStyle {
    id: string;
    label: string;
    paperClass: string;
    cssVars: Record<string, string>;
}

interface DailyReceiptTemplateSectionState {
    id: DailyReceiptSectionId;
    label: string;
    enabled: boolean;
    order: number;
}

interface DailyReceiptTemplateState {
    title: string;
    footerText: string;
    dateFormat: DailyReceiptDateFormat;
    includeSharedSchedules: boolean;
    styleId: string;
    sections: DailyReceiptTemplateSectionState[];
}

interface DailyRecordDraft {
    representativeEmoji: string;
    diary: string;
}

type TodoAiSuggestionPanelTab = 'diary' | 'tomorrow';

interface TodoAiSuggestionDraftState {
    open: boolean;
    loading: boolean;
    activeTab: TodoAiSuggestionPanelTab;
    error: string;
    notice: string;
    providerLabel: string;
    generatedAt: string;
    diaryDraft: string;
    tomorrowTodos: string[];
}

interface MateProfile {
    id: string;
    name: string;
    handle: string;
    avatar: string;
    bio: string;
    color: string;
    streakDays: number;
    publicTodoCount: number;
    adminOnly?: boolean;
}

interface MateReactionOption {
    emoji: string;
    label: string;
    message: string;
}

interface MateFeedItem {
    id: string;
    mateId: string;
    targetType: OwnitTodoReaction['targetType'];
    targetId: string;
    title: string;
    category: string;
    color: string;
    date: string;
    completedAt: string;
    kind: 'todo' | 'routine';
    visibility: OwnitTodo['visibility'];
    memo: string;
    baseHeartCount: number;
    baseEmojiCounts: Record<string, number>;
}

interface TodoMateHeader {
    title: string;
    monthLabel: string;
    selectedDateLabel: string;
    todayLabel: string;
    progress: number;
}

interface TodoMateCalendarDay extends CalendarDay {
    totalCount: number;
    completedCount: number;
    openCount: number;
    progress: number;
    hasBaseMarker: boolean;
    slots: TodoDayMarkerSlot[];
}

interface TodoMateCalendarComponent {
    monthLabel: string;
    weekdays: string[];
    weeks: TodoMateCalendarDay[][];
}

interface TodoItemRow {
    id: string;
    kind: 'todo' | 'routine';
    todo: OwnitTodo;
    routine: OwnitTodoRoutine | null;
    title: string;
    memo: string;
    category: string;
    color: string;
    completed: boolean;
    scopeLabel: string;
    repeatLabel: string;
    reminderLabel: string;
    completedAtLabel: string;
    timerSeconds: number;
    timerLabel: string;
    timerStatus: TodoTimerStatus;
    canUseTimer: boolean;
    canEdit: boolean;
}

interface TodoGoalSection {
    id: string;
    goalId: string | null;
    label: string;
    color: string;
    marker: TodoGoalMarkerId;
    collapsed: boolean;
    locked: boolean;
    totalCount: number;
    completedCount: number;
    openCount: number;
    progress: number;
    items: TodoItemRow[];
}

interface TodoRoutineSection {
    id: string;
    label: string;
    collapsed: boolean;
    totalCount: number;
    completedCount: number;
    openCount: number;
    progress: number;
    items: TodoItemRow[];
}

interface TodoActionSheet {
    title: string;
    dateLabel: string;
    canCreate: boolean;
    canMoveOpen: boolean;
    openCount: number;
}

interface TodoDaySummary {
    dateLabel: string;
    totalCount: number;
    completedCount: number;
    openCount: number;
    progress: number;
    categoryStats: OwnitTodoDailyCategoryStat[];
    totalTimerSeconds: number;
    timerLabel: string;
    representativeEmoji: string;
    diary: string;
}

interface WidgetShapeRow {
    size: OwnitWidgetSize;
    label: string;
    fields: string;
}

interface DailyReceiptLineItem {
    id: string;
    title: string;
    meta: string;
    amount: string;
    accentColor: string;
    tone: 'default' | 'done' | 'open';
}

interface DailyReceiptScheduleSummary {
    label: string;
    totalCount: number;
    personalCount: number;
    sharedCount: number;
    completedCount: number;
    items: DailyReceiptLineItem[];
}

interface DailyReceiptMetricItem {
    id: string;
    label: string;
    value: string;
    important?: boolean;
}

interface DailyReceiptSection {
    id: DailyReceiptSectionId;
    kind: DailyReceiptSectionKind;
    label: string;
    countLabel: string;
    emptyLabel: string;
    items: DailyReceiptLineItem[];
    metrics: DailyReceiptMetricItem[];
    stats: DailyReceiptMetricItem[];
    memo: string;
}

interface DailyReceiptDataViewModel {
    date: string;
    dateLabel: string;
    dateFullLabel: string;
    formattedDate: string;
    ownerLabel: string;
    savedAtIso: string;
    savedAtLabel: string;
    generatedAtLabel: string;
    moodEmoji: string;
    moodLabel: string;
    completedTodoCount: number;
    todoTotalCount: number;
    completedTodos: DailyReceiptLineItem[];
    unfinishedTodoCount: number;
    routineCompletedCount: number;
    routineTotalCount: number;
    routineRecords: DailyReceiptLineItem[];
    routineOpenCount: number;
    personalScheduleSummary: DailyReceiptScheduleSummary;
    sharedScheduleSummary: DailyReceiptScheduleSummary;
    includeSharedScheduleSummary: boolean;
    dailyMemo: string;
    completionRate: number;
    completionLabel: string;
    balanceLabel: string;
    timerLabel: string;
    scheduleSummaryLabel: string;
    todoSummaryLabel: string;
    routineSummaryLabel: string;
    hasData: boolean;
}

interface DailyReceiptViewModel extends DailyReceiptDataViewModel {
    data: DailyReceiptDataViewModel;
    template: DailyReceiptTemplateState;
    style: DailyReceiptTemplateStyle;
    sections: DailyReceiptSection[];
    scheduleSummary: DailyReceiptScheduleSummary;
    openTodos: DailyReceiptLineItem[];
    openTodoCount: number;
    routines: DailyReceiptLineItem[];
    representativeEmoji: string;
    mood: string;
    memo: string;
}

interface ActiveTodoTimerState {
    todoId: string;
    status: TodoTimerStatus;
    startedAt: string;
    startedDate: string;
    lastResumedAt: string;
    accumulatedSeconds: number;
}

type NotificationPermissionState = NotificationPermission | 'unsupported';

interface DueReminderNotice {
    id: string;
    userId: string;
    targetId: string;
    targetType: 'event' | 'todo' | 'routine' | 'shared';
    title: string;
    body: string;
    scheduledAt: string;
    date: string;
    color: string;
    createdAt: string;
}

interface ReminderPayload {
    reminderAt: string;
    reminderRule: ReminderRule;
    reminder: ReminderSetting;
}

type EventPayload = Pick<OwnitEvent, 'title' | 'description' | 'startDate' | 'endDate' | 'startTime' | 'endTime' | 'allDay' | 'color' | 'calendarId' | 'assigneeId' | 'category' | 'visibility' | 'reminders' | 'completed' | 'updatedAt'>;
type TodoPayload = Pick<OwnitTodo, 'title' | 'date' | 'memo' | 'goalId' | 'categoryId' | 'category' | 'color' | 'scope' | 'visibility' | 'updatedAt'> & Partial<Pick<OwnitTodo, 'reminders' | 'reminderAt' | 'reminderRule'>>;

interface TodoMatePage {
    header: TodoMateHeader;
    calendar: TodoMateCalendarComponent;
    dailySummary: TodoDaySummary;
    goalSections: TodoGoalSection[];
    routineSection: TodoRoutineSection;
    actionSheet: TodoActionSheet;
}

@Component({
    selector: 'wiz-page-dashboard',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.dashboard/view.scss */
@charset "UTF-8";
:host {
  display: block;
  min-height: 100%;
  background: #ffffff;
  color: var(--ownit-color-text, #2F2A25);
  font-family: var(--ownit-font-sans, OwnitNanumGothic, "Apple SD Gothic Neo", "Malgun Gothic", SUIT, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
}

* {
  box-sizing: border-box;
}

.ownit-page {
  --surface: var(--ownit-color-surface, #ffffff);
  --surface-soft: var(--ownit-color-surface-soft, #f8f8f8);
  --surface-tint: var(--ownit-color-surface-tint, #f7f7f6);
  --calendar-bg: var(--ownit-color-calendar-bg, #ffffff);
  --cloud-dancer: var(--ownit-color-cloud-dancer, #F0EEE9);
  --line: var(--ownit-color-line, #E6E4E1);
  --line-strong: var(--ownit-color-line-strong, #DAD7D2);
  --text: var(--ownit-color-text, #2F2A25);
  --muted: var(--ownit-color-muted, #77736D);
  --soft-text: var(--ownit-color-soft-text, #8E8982);
  --ink: var(--ownit-color-ink, #2F2A25);
  --green: var(--ownit-color-green, #6B6259);
  --blue: var(--ownit-color-blue, #8A7880);
  --coral: var(--ownit-color-coral, #A65A4F);
  --yellow: var(--ownit-color-yellow, #B59A5C);
  --weekend-saturday: var(--ownit-weekend-saturday, #5f748c);
  --weekend-saturday-muted: var(--ownit-weekend-saturday-muted, #9aa8b6);
  --weekend-sunday: var(--ownit-weekend-sunday, #9a625a);
  --weekend-sunday-muted: var(--ownit-weekend-sunday-muted, #be9f9a);
  --calendar-texture: var(--ownit-calendar-texture, linear-gradient(transparent, transparent));
  --calendar-texture-size: var(--ownit-calendar-texture-size, auto);
  --radius-sm: var(--ownit-radius-sm, 6px);
  --radius-md: var(--ownit-radius-md, 8px);
  --radius-lg: var(--ownit-radius-lg, 8px);
  --shadow-panel: var(--ownit-shadow-panel, 0 18px 50px rgba(36, 48, 43, 0.08));
  --shadow-card: var(--ownit-shadow-card, 0 8px 24px rgba(36, 48, 43, 0.06));
  --font-card-title: 18px;
  --font-card-title-large: 22px;
  --font-card-action: 16px;
  --font-card-metric: 20px;
  --font-card-metric-large: 22px;
  --font-card-title-weight: 600;
  --font-card-action-weight: 600;
  --font-card-metric-weight: 600;
  width: 100%;
  min-height: 100dvh;
  background: var(--ownit-color-app-bg, #ffffff);
  font-family: var(--ownit-font-active, var(--ownit-font-sans, OwnitNanumGothic, "Apple SD Gothic Neo", "Malgun Gothic", SUIT, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif));
}

.ownit-page.theme-cloud-dancer {
  --ownit-color-app-bg: var(--cloud-dancer);
  --ownit-color-calendar-bg: var(--cloud-dancer);
  --surface: var(--ownit-color-surface, #fbfaf7);
  --surface-soft: #fbfaf7;
  --surface-tint: #f6f2eb;
}

.ownit-page.theme-cosmic-latte {
  --surface: var(--ownit-color-surface, #f6f1e8);
  --surface-soft: #f6f1e8;
  --surface-tint: #fff8e7;
}

button,
input,
select,
textarea {
  font: inherit;
}

button {
  border: 0;
  cursor: pointer;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.auth-screen,
.app-shell {
  width: 100%;
  min-height: 100dvh;
  margin: 0 auto;
  background: var(--ownit-color-app-bg, #ffffff);
}

.auth-screen {
  display: grid;
  align-content: center;
  gap: 16px;
  padding: 24px;
}

.auth-panel,
.auth-note,
.panel,
.calendar-panel,
.day-panel,
.detail-section {
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  background: var(--surface);
}

.auth-panel,
.auth-note,
.panel,
.calendar-panel,
.day-panel {
  box-shadow: var(--shadow-card);
}

.auth-panel {
  display: grid;
  gap: 18px;
  padding: 24px;
}

.auth-heading {
  display: grid;
  gap: 8px;
}

.brand-lockup,
.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.brand-mark {
  display: grid;
  place-items: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  color: var(--ink);
  background: var(--surface);
  font-family: var(--ownit-font-brand, var(--ownit-font-typewriter, OwnitTypewriter, OwnitNanumGothic, "Courier New", monospace));
  font-size: 17px;
  font-weight: 600;
  line-height: 1;
}

.brand-name,
.brand-copy {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brand-name {
  color: var(--text);
  font-family: var(--ownit-font-brand, var(--ownit-font-typewriter, OwnitTypewriter, OwnitNanumGothic, "Courier New", monospace));
  font-size: 16px;
  font-weight: 600;
}

.brand-copy {
  margin-top: 2px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.auth-panel h1,
.shell-header h1,
.panel-header h2,
.detail-section h2 {
  margin: 0;
  color: var(--text);
  font-weight: 600;
  letter-spacing: 0;
}

.auth-panel h1 {
  font-size: 28px;
  line-height: 1.18;
}

.auth-copy,
.auth-note span,
.card-body p,
.todo-main p,
.calendar-row p {
  margin: 0;
  color: var(--muted);
  line-height: 1.5;
}

.auth-form,
.quick-form {
  display: grid;
  gap: 12px;
}

.auth-mode-row,
.two-field-grid {
  display: grid;
  gap: 8px;
}

.auth-mode-row {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 4px;
  background: var(--surface-soft);
}

.auth-mode-button {
  min-width: 0;
  min-height: 38px;
  border-radius: var(--radius-sm);
  padding: 0 8px;
  overflow: hidden;
  color: var(--muted);
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.auth-mode-button.active {
  color: #fff;
  background: var(--ink);
}

.auth-message {
  margin: 0;
  border-radius: var(--radius-md);
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
}

.auth-message.success {
  color: var(--green);
  background: var(--surface-tint);
}

.auth-message.error {
  color: #b84536;
  background: #fff0ee;
}

.field {
  display: grid;
  gap: 6px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 500;
}

.ui-input,
.auth-form input {
  width: 100%;
  height: 46px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 0 14px;
  color: var(--ink);
  background: #ffffff;
  outline: none;
}

select.ui-input {
  appearance: none;
}

.ui-input:focus {
  border-color: var(--green);
  box-shadow: 0 0 0 3px rgba(111, 98, 87, 0.14);
}

.primary-action,
.secondary-action,
.danger-action,
.today-button,
.add-event-button,
.add-todo-button,
.receipt-open-button,
.todo-receipt-entry,
.text-action,
.mini-action,
.side-nav-button,
.segmented-control button {
  min-height: 42px;
  border-radius: var(--radius-md);
  padding: 0 14px;
  font-weight: 600;
}

.primary-action {
  color: #fff;
  background: var(--green);
}

.secondary-action {
  color: var(--ink);
  background: var(--surface-tint);
}

.secondary-action.danger {
  color: #9a4035;
  background: #fff0ee;
}

.danger-action {
  width: 100%;
  margin-top: 12px;
  color: #b84536;
  background: #fff0ee;
}

.today-button,
.add-event-button,
.add-todo-button,
.receipt-open-button,
.todo-receipt-entry,
.text-action {
  color: var(--ink);
  background: var(--surface-tint);
}

.receipt-open-button,
.todo-receipt-entry {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #5f574e;
  background: var(--surface-tint);
}

.add-todo-button {
  color: var(--ink);
  background: var(--surface-tint);
}

.auth-note {
  display: grid;
  align-items: center;
  padding: 16px;
  background: var(--surface-soft);
}

.profile-setup-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 12px;
  background: var(--surface-soft);
}

.profile-setup-preview strong,
.profile-setup-preview span {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-setup-preview strong {
  color: var(--ink);
  font-weight: 600;
}

.profile-setup-preview span {
  margin-top: 4px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
}

.avatar.large {
  flex-basis: 52px;
  width: 52px;
  height: 52px;
  font-size: 20px;
}

.preview-phone {
  display: grid;
  gap: 12px;
  width: 100%;
  min-height: 280px;
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  padding: 14px;
  background: #fff;
}

.preview-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.preview-top span {
  display: block;
  width: 42%;
  height: 10px;
  border-radius: 999px;
  background: var(--line);
}

.preview-month {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
}

.preview-month > span {
  display: grid;
  place-items: center;
  min-height: 26px;
  border-radius: 5px;
  color: var(--muted);
  background: var(--surface-tint);
  font-size: 9px;
  font-weight: 600;
}

.preview-month .preview-cell {
  background: #fff;
  border: 1px solid var(--line);
}

.preview-month .preview-cell.active {
  border-color: var(--green);
  background: var(--surface-tint);
}

.preview-todo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 12px;
}

.preview-todo strong,
.preview-todo span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-todo strong {
  color: var(--ink);
  font-size: 14px;
}

.app-shell {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.shell-sidebar,
.shell-detail {
  display: none;
}

.shell-main {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.shell-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 18px 12px;
}

.profile-block {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.avatar {
  display: grid;
  place-items: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  color: #fff;
  background: var(--ink);
  font-weight: 600;
}

.header-title {
  min-width: 0;
}

.eyebrow {
  margin: 0 0 4px;
  color: #5f574e;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: uppercase;
}

.shell-header h1 {
  overflow: hidden;
  font-size: 20px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  min-height: 24px;
  margin-top: 6px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 0 8px;
  overflow: hidden;
  color: var(--ink);
  background: var(--surface-tint);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role-badge.admin {
  border-color: #d8cec4;
  color: #5f574e;
  background: var(--surface-tint);
}

.role-badge.inline {
  margin-top: 0;
}

.status-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 0 18px 12px;
}

.status-item {
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 10px;
  background: var(--surface-soft);
}

.status-label {
  display: block;
  margin-bottom: 4px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}

.status-item strong {
  display: block;
  overflow: hidden;
  color: var(--ink);
  font-size: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.route-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 18px 12px;
}

.route-pill,
.count-badge,
.category-chip,
.theme-token,
.event-scope-badge,
.status-chip,
.summary-kind,
.permission-action {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  min-height: 30px;
  border-radius: var(--radius-md);
  padding: 0 10px;
  overflow: hidden;
  color: var(--ink);
  background: var(--surface-tint);
  font-size: 12px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-scope-badge {
  flex: 0 0 auto;
  min-height: 24px;
  color: var(--green);
  background: var(--surface-tint);
  font-size: 11px;
}

.event-scope-badge.shared,
.status-chip {
  color: #6a5860;
  background: #f4eff1;
}

.event-scope-badge.external {
  color: #4f4a46;
  background: #efeeeb;
}

.status-chip.done {
  color: var(--green);
  background: var(--surface-tint);
}

.status-chip.warn {
  color: #7a5a2f;
  background: #fbf2df;
}

.status-chip.error {
  color: #934840;
  background: #fff1ed;
}

.shell-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 0 18px 92px;
}

.calendar-layout,
.content-grid {
  display: grid;
  gap: 12px;
}

.calendar-panel,
.day-panel,
.panel {
  min-width: 0;
  padding: 14px;
}

.calendar-panel {
  position: relative;
  overflow: hidden;
  background: var(--calendar-texture), var(--calendar-bg);
  background-size: var(--calendar-texture-size), auto;
}

.calendar-panel > .panel-header,
.calendar-panel > .week-grid,
.calendar-panel > .month-grid {
  position: relative;
  z-index: 2;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.panel-header.tight {
  margin-bottom: 10px;
}

.header-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.panel-header h2,
.detail-section h2 {
  font-size: 18px;
  line-height: 1.2;
}

.month-actions {
  display: flex;
  gap: 6px;
}

.icon-button {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  color: var(--ink);
  background: #fff;
  font-size: 18px;
  font-weight: 600;
}

.week-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.week-grid {
  gap: 4px;
  margin-bottom: 6px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  text-align: center;
}

.month-grid {
  display: grid;
  gap: 5px;
}

.calendar-week {
  position: relative;
  min-height: 94px;
}

.week-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
  height: 100%;
}

.calendar-day {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 94px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 6px 4px 54px;
  overflow: hidden;
  color: var(--text);
  background: var(--calendar-texture), var(--calendar-bg);
  background-size: var(--calendar-texture-size), auto;
  text-align: left;
  transition: background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
}

.calendar-day.outside {
  color: var(--soft-text);
  background: var(--surface-soft);
}

.calendar-day.today {
  border-color: var(--green);
}

.calendar-day.selected {
  border-color: var(--green);
  background: var(--calendar-selected, var(--surface-tint));
  box-shadow: inset 0 0 0 2px rgba(111, 98, 87, 0.38);
}

.calendar-day.today.selected {
  border-color: var(--green);
  background: var(--calendar-selected, var(--surface-tint));
  box-shadow: inset 0 0 0 2px var(--green), inset 0 0 0 4px #ffffff;
}

.calendar-day.has-items:not(.selected) {
  background: #ffffff;
}

.day-top {
  display: flex;
  position: relative;
  z-index: 2;
  align-items: flex-start;
  justify-content: space-between;
  gap: 3px;
  min-width: 0;
}

.day-number {
  display: grid;
  place-items: center;
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.calendar-day.today .day-number {
  color: #fff;
  background: var(--green);
}

.calendar-day.selected .day-number {
  color: #fff;
  background: var(--green);
}

.calendar-day.today.selected .day-number {
  background: var(--green);
}

.day-token {
  flex: 0 1 auto;
  max-width: 36px;
  min-height: 17px;
  border-radius: 999px;
  padding: 2px 5px;
  overflow: hidden;
  font-size: 9px;
  font-weight: 600;
  line-height: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.today-token {
  color: var(--green);
  background: var(--surface-tint);
}

.selected-token {
  color: #6a5860;
  background: #f4eff1;
}

.day-counts {
  display: flex;
  position: relative;
  z-index: 2;
  gap: 3px;
  margin-top: 4px;
  min-width: 0;
}

.day-count {
  display: grid;
  place-items: center;
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  overflow: hidden;
  color: #fff;
  font-size: 9px;
  font-weight: 600;
  line-height: 1;
}

.event-count {
  background: var(--green);
}

.todo-count {
  background: var(--yellow);
}

.day-overflow {
  position: absolute;
  z-index: 2;
  right: 7px;
  bottom: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 18px;
  border-radius: 999px;
  padding: 0 6px;
  color: var(--ink);
  background: rgba(255, 255, 255, 0.92);
  font-size: 10px;
  font-weight: 600;
  line-height: 18px;
  box-shadow: 0 2px 6px rgba(36, 48, 43, 0.12);
}

.day-sticker-layer {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  opacity: 0.42;
}

.day-sticker {
  position: absolute;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  box-shadow: none;
}

.calendar-sticker-layer {
  position: absolute;
  inset: 76px 14px 14px;
  z-index: 4;
  pointer-events: none;
}

.calendar-sticker-layer.view-mode {
  z-index: 1;
  opacity: 0.26;
  pointer-events: none;
}

.placed-sticker {
  position: absolute;
  display: grid;
  place-items: center;
  border: 1px solid rgba(34, 37, 41, 0.12);
  border-radius: var(--radius-md);
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
  pointer-events: auto;
  touch-action: none;
  user-select: none;
  box-shadow: 0 8px 18px rgba(36, 48, 43, 0.14);
}

.placed-sticker.view-only {
  border: 0;
  pointer-events: none;
  box-shadow: none;
}

.placed-sticker.selected {
  outline: 2px solid var(--ink);
  outline-offset: 2px;
}

.sticker-emoji,
.sticker-list-emoji {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
  line-height: 1;
}

.sticker-list-emoji {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  font-size: 18px;
}

.sticker-image {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: contain;
  pointer-events: none;
}

.week-events {
  position: absolute;
  right: 4px;
  bottom: 6px;
  left: 4px;
  display: grid;
  grid-auto-rows: 15px;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 2px 4px;
  pointer-events: none;
}

.event-bar {
  display: flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
  min-height: 15px;
  border-radius: 5px;
  padding: 0 5px;
  overflow: hidden;
  color: #fff;
  font-size: 9px;
  font-weight: 600;
  line-height: 15px;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: auto;
  box-shadow: 0 3px 8px rgba(36, 48, 43, 0.12);
}

.event-bar-scope {
  display: grid;
  place-items: center;
  flex: 0 0 14px;
  width: 14px;
  height: 12px;
  border-radius: 3px;
  color: var(--ink);
  background: rgba(255, 255, 255, 0.9);
  font-size: 8px;
  line-height: 1;
}

.event-bar-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-bar.single-day {
  border-radius: 999px;
  padding-left: 6px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.32), 0 2px 6px rgba(36, 48, 43, 0.12);
}

.event-bar.multi-day {
  border-radius: 4px;
}

.event-bar.shared {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35), 0 3px 8px rgba(36, 48, 43, 0.12);
}

.event-bar.external {
  border: 1px solid rgba(255, 255, 255, 0.52);
  box-shadow: inset 0 0 0 1px rgba(47, 42, 37, 0.16), 0 3px 8px rgba(36, 48, 43, 0.12);
}

.event-bar.shared .event-bar-scope {
  color: #6a5860;
}

.event-bar.personal .event-bar-scope {
  color: var(--green);
}

.event-bar-source {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  min-width: 12px;
  height: 11px;
  border-radius: 3px;
  padding: 0 2px;
  color: var(--ink);
  background: rgba(255, 255, 255, 0.86);
  font-size: 7px;
  font-weight: 700;
  line-height: 1;
}

.event-bar.continues-before {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.event-bar.continues-after {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.more-count {
  position: absolute;
  right: 7px;
  top: 7px;
  border-radius: 999px;
  padding: 2px 6px;
  color: var(--muted);
  background: rgba(255, 255, 255, 0.86);
  font-size: 10px;
  font-weight: 600;
}

.bottom-sheet {
  position: relative;
}

.bottom-sheet::before {
  content: "";
  display: block;
  width: 38px;
  height: 4px;
  margin: 0 auto 10px;
  border-radius: 999px;
  background: var(--line-strong);
}

.empty-state {
  display: grid;
  gap: 10px;
  border: 1px dashed var(--line);
  border-radius: var(--radius-md);
  padding: 18px;
  color: var(--muted);
  background: var(--surface-soft);
  line-height: 1.5;
}

.empty-state p {
  margin: 0;
}

.empty-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.event-card,
.todo-card,
.todo-row,
.calendar-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 12px;
  background: #fff;
}

.event-card.shared-event-card,
.calendar-row.shared-calendar {
  border-color: #e2d7dc;
  background: #ffffff;
}

.event-card.personal-event-card,
.calendar-row.personal-calendar {
  border-color: #ddd4c8;
  background: #ffffff;
}

.event-card.external-event-card,
.calendar-row.external-calendar {
  border-color: #d9d8d4;
  background: #ffffff;
}

.event-card.focused {
  border-color: var(--ink);
  box-shadow: 0 0 0 2px rgba(35, 31, 27, 0.08);
}

.event-card + .event-card,
.todo-card + .todo-card,
.event-card + .todo-card,
.todo-card + .event-card,
.todo-row + .todo-row,
.calendar-row + .calendar-row {
  margin-top: 8px;
}

.card-color,
.calendar-dot {
  flex: 0 0 10px;
  width: 10px;
  height: 38px;
  border-radius: 999px;
}

.calendar-dot {
  height: 32px;
}

.card-body,
.todo-main,
.calendar-row div {
  flex: 1 1 auto;
  min-width: 0;
}

.card-title-row,
.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.card-title-row h3 {
  margin-bottom: 0;
}

.card-body h3,
.todo-main h3,
.calendar-row strong {
  flex: 1 1 auto;
  margin: 0 0 4px;
  overflow: hidden;
  color: var(--text);
  font-size: 14px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}

.people-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.people-meta span {
  min-height: 26px;
  border-radius: var(--radius-sm);
  padding: 5px 8px;
  color: #6a5860;
  background: #f4eff1;
  font-size: 11px;
  font-weight: 600;
}

.multi-day-card {
  background: linear-gradient(90deg, #fff 0%, var(--surface-soft) 100%);
}

.focused-event-detail {
  display: grid;
  gap: 10px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  padding: 12px;
  background: var(--surface-soft);
}

.focused-event-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.focused-event-detail h3,
.focused-event-detail p {
  margin: 0;
}

.focused-event-detail h3 {
  color: var(--text);
  font-size: 15px;
  line-height: 1.35;
}

.focused-event-detail p {
  color: var(--muted);
  font-size: 12px;
  line-height: 1.5;
}

.readonly-event-notice {
  width: 100%;
  margin: 8px 0 0;
  border: 1px solid #dedbd5;
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  color: #5f574e;
  background: #f7f6f3;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.45;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.mini-action {
  min-height: 30px;
  padding: 0 10px;
  color: var(--green);
  background: var(--surface-tint);
  font-size: 11px;
}

.mini-action.danger {
  color: #b84536;
  background: #fff0ee;
}

.check-button {
  display: grid;
  place-items: center;
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  color: #fff;
  background: #fff;
  font-weight: 600;
}

.check-button.done {
  border-color: var(--green);
  background: var(--green);
}

.progress-label {
  color: var(--green);
  font-size: 18px;
  font-weight: 600;
}

.progress-track {
  width: 100%;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: #ecece7;
  margin-bottom: 12px;
}

.progress-bar {
  height: 100%;
  border-radius: 999px;
  background: var(--green);
}

.todo-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 10px 12px;
  background: var(--surface-soft);
  margin-bottom: 10px;
}

.todo-summary strong,
.todo-summary span {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-summary strong {
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
}

.todo-summary span {
  margin-top: 3px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}

.mini-progress-track {
  flex: 0 0 88px;
  width: 88px;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: #ecece7;
}

.mini-progress-bar {
  height: 100%;
  border-radius: 999px;
  background: var(--green);
}

.todo-row {
  align-items: center;
}

.todo-card.completed,
.todo-row.completed {
  border-color: var(--line);
  background: var(--surface-soft);
}

.todo-card.completed h3,
.todo-row.completed h3 {
  color: var(--soft-text);
  text-decoration: line-through;
}

.todo-card.completed p,
.todo-row.completed p {
  color: var(--soft-text);
}

.todo-color {
  flex: 0 0 6px;
  width: 6px;
  min-height: 28px;
  border-radius: 999px;
}

.todo-main {
  flex: 1 1 auto;
}

.todo-row-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  max-width: 180px;
}

.todo-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.category-chip {
  flex: 0 0 auto;
  border: 1px solid currentColor;
  background: #fff;
}

.shared-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 10px;
}

.shared-filter-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  align-items: end;
  gap: 10px;
  margin-bottom: 10px;
}

.shared-filter-actions {
  display: flex;
  justify-content: flex-end;
  min-width: 0;
}

.shared-filter-actions .secondary-action {
  min-height: 46px;
}

.shared-summary-card {
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 10px;
  background: var(--surface-soft);
}

.shared-summary-card strong,
.shared-summary-card p,
.section-title-row h3,
.section-title-row span {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shared-summary-card strong {
  margin-top: 8px;
  color: var(--ink);
  font-size: 20px;
  font-weight: 600;
}

.shared-summary-card p {
  margin: 3px 0 0;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}

.summary-kind {
  justify-content: center;
  width: fit-content;
  min-height: 24px;
  color: #6a5860;
  background: #f4eff1;
  font-size: 11px;
}

.shared-events-section {
  display: grid;
  gap: 8px;
  border-top: 1px solid var(--line);
  padding-top: 12px;
  margin-top: 12px;
}

.cloud-sync-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cloud-sync-actions .primary-action,
.cloud-sync-actions .secondary-action {
  flex: 1 1 150px;
}

.external-provider-list {
  display: grid;
  gap: 8px;
}

.external-provider-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 10px;
  background: #fff;
}

.external-provider-row strong,
.external-provider-row p {
  display: block;
  margin: 0;
}

.external-provider-row strong {
  color: var(--text);
  font-size: 13px;
  font-weight: 600;
}

.external-provider-row p {
  margin-top: 3px;
  color: var(--muted);
  font-size: 11px;
  line-height: 1.35;
}

.oauth-provider-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
}

.oauth-provider-copy {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.oauth-provider-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.oauth-provider-heading strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oauth-provider-meta {
  display: grid;
  gap: 6px;
  margin: 0;
}

.oauth-provider-meta div {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  gap: 8px;
}

.oauth-provider-meta dt,
.oauth-provider-meta dd {
  min-width: 0;
  margin: 0;
  font-size: 11px;
  line-height: 1.35;
}

.oauth-provider-meta dt {
  color: var(--muted);
  font-weight: 600;
}

.oauth-provider-meta dd {
  overflow-wrap: anywhere;
  color: var(--ink);
  font-weight: 500;
}

.oauth-provider-actions {
  display: grid;
  align-content: start;
  gap: 6px;
  min-width: 132px;
}

.oauth-provider-actions .primary-action,
.oauth-provider-actions .secondary-action {
  min-height: 34px;
  padding: 0 10px;
  font-size: 12px;
}

.external-conflict-list {
  display: grid;
  gap: 8px;
}

.external-conflict-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  border: 1px solid rgba(166, 90, 79, 0.32);
  border-radius: var(--radius-md);
  padding: 10px;
  background: #fff7f4;
}

.external-conflict-row strong,
.external-conflict-row p {
  display: block;
  min-width: 0;
  margin: 0;
}

.external-conflict-row strong {
  overflow: hidden;
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.external-conflict-row p {
  margin-top: 3px;
  color: var(--muted);
  font-size: 11px;
  line-height: 1.35;
}

.conflict-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  max-width: 260px;
}

.external-sync-card-actions {
  display: grid;
  flex: 0 1 310px;
  grid-template-columns: minmax(118px, 1fr) minmax(118px, 1fr);
  gap: 6px;
  justify-items: stretch;
  margin-top: 0;
  min-width: min(100%, 290px);
}

.external-sync-card-actions > .status-chip,
.external-sync-card-actions > .mini-action,
.external-sync-card-actions > .external-visible-toggle {
  justify-self: start;
}

.external-sync-select {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.external-sync-select span {
  color: var(--muted);
  font-size: 10px;
  font-weight: 700;
}

.external-sync-select .ui-input {
  min-height: 32px;
  padding: 0 8px;
  font-size: 11px;
}

.connected-provider-list {
  display: grid;
  gap: 8px;
}

.connected-provider-chip,
.empty-external-state {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 10px;
  background: var(--surface-soft);
}

.connected-provider-chip strong,
.connected-provider-chip p,
.empty-external-state p {
  display: block;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.connected-provider-chip strong {
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
}

.connected-provider-chip p,
.empty-external-state {
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
}

.empty-external-state i {
  color: var(--muted);
}

@media (max-width: 720px) {
  .oauth-provider-row {
    grid-template-columns: minmax(0, 1fr);
  }
  .oauth-provider-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    min-width: 0;
  }
}
.widget-preview-settings {
  gap: 10px;
}

.widget-shape-list {
  display: grid;
  gap: 6px;
}

.widget-shape-row {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 9px 10px;
  background: var(--surface-soft);
}

.widget-shape-row strong,
.widget-shape-row span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.widget-shape-row strong {
  color: var(--ink);
  font-size: 12px;
  font-weight: 600;
}

.widget-shape-row span {
  overflow: visible;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
  text-overflow: clip;
  white-space: normal;
}

.widget-preview-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.25fr) minmax(0, 1.45fr);
  align-items: start;
  gap: 10px;
}

.ownit-widget-card {
  display: grid;
  min-width: 0;
  gap: 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 12px;
  color: var(--ink);
  background: #fff;
}

.widget-small {
  aspect-ratio: 1/1;
  align-content: space-between;
  border-width: 2px;
}

.widget-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.widget-card-header span,
.widget-card-header strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.widget-card-header span {
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
}

.widget-card-header strong {
  color: var(--ink);
  font-size: 12px;
  font-weight: 700;
}

.widget-small-meter {
  display: grid;
  gap: 2px;
}

.widget-small-meter strong {
  color: var(--ink);
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.widget-small-meter span,
.widget-sub-line,
.widget-main-line,
.widget-next-row span,
.widget-next-row small {
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}

.widget-main-line,
.widget-sub-line {
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.widget-main-line {
  color: var(--ink);
  font-size: 13px;
  font-weight: 700;
}

.widget-split,
.widget-large-lists {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.widget-split h4 {
  margin: 0 0 6px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
}

.widget-list {
  display: grid;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.widget-list li {
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) auto;
  align-items: center;
  gap: 6px;
  min-height: 24px;
  color: var(--ink);
}

.widget-list li.done {
  color: var(--muted);
}

.widget-list li strong,
.widget-list li small {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.widget-list li strong {
  font-size: 12px;
  font-weight: 600;
}

.widget-list li small {
  color: var(--muted);
  font-size: 10px;
  font-weight: 500;
}

.widget-dot,
.widget-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  flex: 0 0 auto;
}

.widget-check {
  border: 1px solid var(--line-strong);
  color: var(--ink);
  font-size: 8px;
  font-weight: 700;
  background: var(--surface-soft);
}

.empty-widget-line {
  grid-template-columns: minmax(0, 1fr) !important;
  color: var(--muted) !important;
  font-size: 11px;
  font-weight: 500;
}

.widget-metric-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.widget-metric-grid div {
  display: grid;
  gap: 2px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 8px;
  background: var(--surface-soft);
}

.widget-metric-grid strong,
.widget-metric-grid span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.widget-metric-grid strong {
  color: var(--ink);
  font-size: 16px;
  font-weight: 700;
}

.widget-metric-grid span {
  color: var(--muted);
  font-size: 10px;
  font-weight: 600;
}

.widget-next-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 8px;
  background: var(--surface-soft);
}

.widget-next-row strong {
  min-width: 0;
  overflow: hidden;
  color: var(--ink);
  font-size: 12px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 980px) {
  .widget-preview-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .widget-small {
    aspect-ratio: auto;
  }
}
@media (max-width: 520px) {
  .widget-shape-row,
  .widget-split,
  .widget-large-lists {
    grid-template-columns: minmax(0, 1fr);
  }
}
.external-source-token,
.external-visible-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  min-height: 28px;
  border-radius: var(--radius-md);
  padding: 0 8px;
  color: var(--ink);
  background: var(--surface-tint);
  font-size: 11px;
  font-weight: 600;
}

.external-visible-toggle input {
  width: 14px;
  height: 14px;
  margin: 0;
}

.share-selector-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.calendar-row.selected {
  border-color: var(--ink);
  box-shadow: inset 0 0 0 1px var(--ink);
}

.shared-calendar-select {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr);
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
  color: inherit;
  background: transparent;
  text-align: left;
}

.shared-calendar-select strong,
.shared-calendar-select p {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 520px) {
  .share-selector-grid,
  .shared-filter-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .shared-filter-actions {
    justify-content: stretch;
  }
  .shared-filter-actions .secondary-action {
    width: 100%;
  }
}
.section-title-row h3 {
  margin: 0;
  color: var(--ink);
  font-size: 15px;
  font-weight: 600;
}

.section-title-row span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
}

.member-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.member-chip {
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 10px;
  background: var(--surface-soft);
}

.member-chip.inactive {
  opacity: 0.66;
}

.member-chip strong,
.member-chip span,
.member-chip small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-chip span {
  margin-top: 4px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}

.member-chip small {
  margin-top: 3px;
  color: var(--muted);
  font-size: 10px;
  font-weight: 500;
}

.permission-table {
  display: grid;
  gap: 10px;
}

.permission-row {
  display: grid;
  gap: 8px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 12px;
  background: #fff;
}

.permission-row strong,
.permission-row p {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.permission-row strong {
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
}

.permission-row p {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
}

.permission-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.permission-action {
  min-height: 24px;
  color: var(--ink);
  background: var(--surface-tint);
  font-size: 11px;
}

.data-list,
.settings-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.data-list li {
  border-top: 1px solid var(--line);
  padding: 11px 0;
  color: var(--ink);
  font-weight: 600;
}

.data-list li:first-child {
  border-top: 0;
  padding-top: 0;
}

.theme-preview {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 220px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background-image: var(--calendar-texture);
  background-size: var(--calendar-texture-size);
  overflow: hidden;
}

.preview-calendar {
  display: grid;
  gap: 8px;
  width: 150px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 14px;
  background: #fff;
  font-family: var(--ownit-font-decoration, var(--ownit-font-active));
}

.preview-date {
  color: var(--ink);
  font-size: 18px;
  font-weight: 600;
}

.preview-bar {
  display: block;
  height: 14px;
  border-radius: 6px;
}

.preview-bar.green,
.swatch.green {
  background: var(--green);
}

.preview-bar.blue,
.swatch.blue {
  background: var(--blue);
}

.preview-bar.coral,
.swatch.coral {
  background: var(--coral);
}

.swatch.yellow {
  background: var(--yellow);
}

.swatch.white {
  background: #fff;
}

.swatch.cloud {
  background: var(--cloud-dancer);
}

.sticker-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.sticker,
.preview-placed-sticker {
  position: absolute;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  color: var(--coral);
  background: #fff;
  font-size: 22px;
  font-weight: 600;
}

.preview-placed-sticker {
  border-color: rgba(34, 37, 41, 0.12);
  pointer-events: auto;
  touch-action: none;
  user-select: none;
  box-shadow: 0 6px 16px rgba(36, 48, 43, 0.1);
}

.preview-placed-sticker.selected {
  outline: 2px solid var(--ink);
  outline-offset: 2px;
}

.sticker.one {
  top: 24px;
  left: 28px;
  transform: rotate(-8deg);
}

.sticker.two {
  right: 34px;
  bottom: 34px;
  color: var(--blue);
  transform: rotate(6deg);
}

.swatch-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.swatch {
  width: 34px;
  height: 34px;
  border: 1px solid rgba(34, 37, 41, 0.12);
  border-radius: var(--radius-md);
}

.decoration-control-section,
.sticker-editor {
  display: grid;
  gap: 10px;
  border-top: 1px solid var(--line);
  padding-top: 12px;
  margin-top: 12px;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.theme-option-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.theme-option,
.background-option {
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 7px;
  min-width: 0;
  min-height: 78px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 10px 8px;
  color: var(--ink);
  background: #fff;
  font-size: 12px;
  font-weight: 600;
}

.theme-option.active,
.background-option.active {
  border-color: var(--green);
  box-shadow: inset 0 0 0 2px var(--green);
}

.theme-option-swatch,
.background-swatch {
  display: block;
  width: 36px;
  height: 28px;
  border: 1px solid rgba(34, 37, 41, 0.12);
  border-radius: var(--radius-sm);
}

.sticker-palette {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.emoji-custom-box {
  display: grid;
  gap: 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 12px;
  background: var(--surface-soft);
}

.emoji-custom-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
}

.emoji-sticker-category {
  display: grid;
  gap: 8px;
}

.emoji-sticker-category + .emoji-sticker-category {
  margin-top: 12px;
}

.emoji-category-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  color: var(--muted);
  font-size: 11px;
}

.emoji-category-title strong {
  color: var(--ink);
  font-size: 12px;
}

.emoji-palette {
  grid-template-columns: repeat(6, minmax(0, 1fr));
  margin-top: 0;
}

.placed-sticker-list {
  display: grid;
  gap: 6px;
  margin-top: 10px;
}

.sticker-list-button {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 36px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 0 10px;
  color: var(--ink);
  background: #fff;
  font-size: 12px;
  font-weight: 600;
}

.sticker-list-button.active {
  border-color: var(--green);
  background: var(--surface-tint);
}

.sticker-list-button span:last-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sticker-palette-button {
  display: grid;
  place-items: center;
  min-width: 0;
  height: 46px;
  border: 1px solid rgba(34, 37, 41, 0.1);
  border-radius: var(--radius-md);
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
}

.emoji-sticker-button {
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
}

.sticker-upload-box {
  display: grid;
  gap: 10px;
  border: 1px dashed var(--line-strong);
  border-radius: var(--radius-md);
  padding: 12px;
  background: var(--surface-soft);
}

.sticker-upload-picker {
  position: relative;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: 58px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 10px;
  color: var(--ink);
  background: var(--surface);
  cursor: pointer;
}

.sticker-upload-picker.disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.sticker-upload-picker input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: inherit;
}

.upload-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: #ffffff;
}

.upload-copy {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.upload-copy strong,
.sticker-asset-info strong {
  overflow: hidden;
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-copy small,
.upload-meta,
.sticker-asset-info span {
  overflow: hidden;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.form-notice {
  margin: 0;
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  color: var(--green);
  background: var(--surface-tint);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}

.sticker-upload-preview {
  display: grid;
  gap: 12px;
}

.sticker-compare-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.sticker-compare-card {
  display: grid;
  gap: 6px;
  min-width: 0;
  margin: 0;
}

.sticker-compare-card > span {
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
}

.sticker-compare-card img,
.sticker-processing-state {
  width: 100%;
  aspect-ratio: 1/1;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background-color: #ffffff;
  background-image: linear-gradient(45deg, rgba(34, 37, 41, 0.06) 25%, transparent 25%), linear-gradient(-45deg, rgba(34, 37, 41, 0.06) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(34, 37, 41, 0.06) 75%), linear-gradient(-45deg, transparent 75%, rgba(34, 37, 41, 0.06) 75%);
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
  background-size: 16px 16px;
  object-fit: contain;
}

.sticker-processing-state {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 4px;
  padding: 10px;
  color: var(--muted);
  text-align: center;
}

.sticker-processing-state i {
  color: var(--green);
  font-size: 18px;
}

.sticker-compare-card.processing .sticker-processing-state i {
  animation: ownit-spin 1s linear infinite;
}

.sticker-compare-card.failed .sticker-processing-state i {
  color: var(--coral);
}

.sticker-processing-state strong {
  color: var(--ink);
  font-size: 12px;
  font-weight: 700;
}

.sticker-processing-state small {
  color: var(--muted);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
}

@keyframes ownit-spin {
  to {
    transform: rotate(360deg);
  }
}
.sticker-upload-fields {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.sticker-removal-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.sticker-removal-actions .secondary-action:only-child {
  grid-column: 1/-1;
}

.sticker-style-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.sticker-asset-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.sticker-asset-card {
  display: grid;
  gap: 8px;
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 8px;
  background: #ffffff;
}

.sticker-asset-preview {
  display: grid;
  place-items: center;
  width: 100%;
  aspect-ratio: 1/1;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface-soft);
}

.sticker-asset-preview img {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: contain;
}

.sticker-asset-info,
.sticker-asset-rename {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.sticker-asset-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.sticker-asset-actions .mini-action {
  min-width: 0;
  min-height: 32px;
  padding: 0 6px;
}

.sticker-list-image {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  background: #ffffff;
  object-fit: contain;
}

.range-field {
  display: grid;
  gap: 6px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.range-field input {
  width: 100%;
  accent-color: var(--green);
}

.upload-placeholder {
  display: grid;
  gap: 8px;
  border: 1px dashed var(--line);
  border-radius: var(--radius-md);
  padding: 12px;
  color: var(--muted);
  background: var(--surface-soft);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
}

.upload-placeholder input {
  width: 100%;
}

.settings-list div {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 12px;
  border-top: 1px solid var(--line);
  padding: 12px 0;
}

.settings-list div:first-child {
  border-top: 0;
  padding-top: 0;
}

.settings-list dt {
  color: var(--muted);
  font-weight: 500;
}

.settings-list dd {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--ink);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.receipt-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(34, 37, 41, 0.36);
  backdrop-filter: blur(10px);
}

.receipt-modal {
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: 16px;
  width: min(1080px, 100%);
  max-height: calc(100dvh - 32px);
  border: 1px solid rgba(234, 229, 220, 0.94);
  border-radius: 10px;
  padding: 16px;
  overflow: auto;
  background: #f7f5f0;
  box-shadow: 0 26px 70px rgba(31, 41, 55, 0.24);
}

.receipt-controls,
.receipt-preview-shell {
  min-width: 0;
  border: 1px solid rgba(215, 210, 201, 0.92);
  border-radius: 8px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.92);
}

.receipt-controls {
  display: grid;
  align-content: start;
  gap: 12px;
}

.receipt-control-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(88px, 1fr));
  gap: 6px;
}

.receipt-control-summary span,
.emoji-choice {
  display: grid;
  place-items: center;
  min-width: 0;
  min-height: 38px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface-soft);
  color: var(--ink);
  font-size: 12px;
  font-weight: 600;
}

.receipt-empty-copy {
  margin: 0;
  border: 1px dashed rgba(111, 103, 89, 0.24);
  border-radius: 8px;
  padding: 10px 12px;
  color: #7f7669;
  background: #fffdf8;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
}

.receipt-template-panel {
  display: grid;
  gap: 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 10px;
  background: var(--surface-soft);
}

.receipt-template-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
}

.receipt-template-grid .field:nth-child(3) {
  grid-column: 1/-1;
}

.receipt-toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: var(--ink);
  font-size: 12px;
  font-weight: 700;
}

.receipt-toggle-row input {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  accent-color: var(--ink);
}

.receipt-toggle-row span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.receipt-section-control-list {
  display: grid;
  gap: 6px;
}

.receipt-section-control {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 8px;
  background: #fff;
}

.receipt-order-actions {
  display: inline-flex;
  gap: 4px;
}

.icon-button.tiny {
  width: 30px;
  height: 30px;
  min-height: 30px;
  border-radius: 8px;
  font-size: 12px;
}

.receipt-form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 88px;
  gap: 10px;
}

.receipt-emoji-input {
  text-align: center;
  font-size: 22px;
}

.emoji-choice-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 6px;
}

.emoji-choice {
  padding: 0;
  font-size: 20px;
}

.emoji-choice.active {
  border-color: #d8cec4;
  background: var(--surface-tint);
}

.receipt-note-input {
  min-height: 116px;
  padding-top: 12px;
  line-height: 1.5;
  resize: vertical;
}

.receipt-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
}

.receipt-save-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 48px;
  font-size: 14px;
}

.receipt-preview-shell {
  display: grid;
  gap: 10px;
}

.receipt-preview-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
}

.receipt-preview-save-button {
  display: none;
}

.receipt-preview-frame {
  display: grid;
  justify-items: center;
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 18px;
  overflow: auto;
  background: #f2efe8;
}

.receipt-preview-frame wiz-component-daily-receipt-preview {
  display: block;
  width: min(390px, 100%);
}

.receipt-empty-preview {
  display: grid;
  justify-items: center;
  gap: 8px;
  width: min(360px, 100%);
  border: 1px dashed rgba(111, 103, 89, 0.24);
  border-radius: 8px;
  padding: 42px 24px;
  color: #6f6759;
  background: #fffdf8;
  text-align: center;
}

.receipt-empty-preview i {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  color: #5f574e;
  background: #f4eee4;
}

.receipt-empty-preview strong {
  color: #292622;
  font-size: 15px;
  font-weight: 700;
}

.receipt-empty-preview p {
  max-width: 260px;
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
}

.receipt-paper,
.receipt-paper * {
  box-sizing: border-box;
}

.receipt-paper {
  --receipt-accent: #4d463d;
  --receipt-ink: #292622;
  --receipt-muted: #6f6759;
  --receipt-line: rgba(43, 41, 37, 0.42);
  --receipt-paper: #fffdf8;
  width: min(372px, 100%);
  min-width: 0;
  border: 1px solid #ded7cc;
  padding: 22px 20px;
  color: var(--receipt-ink);
  background: radial-gradient(circle at 10px 10px, rgba(83, 71, 51, 0.045) 0 1px, transparent 1.6px), linear-gradient(180deg, #ffffff 0%, var(--receipt-paper) 100%);
  background-size: 16px 16px, auto;
  box-shadow: 0 18px 36px rgba(51, 43, 31, 0.13);
  font-family: var(--ownit-font-typewriter, OwnitTypewriter, OwnitNanumGothic, "Courier New", monospace);
}

.receipt-edge {
  height: 10px;
  margin: -22px -20px 14px;
  border-bottom: 1px dashed var(--receipt-line);
  background: repeating-linear-gradient(90deg, transparent 0 7px, rgba(43, 41, 37, 0.12) 7px 8px);
}

.receipt-edge.bottom-edge {
  margin: 14px -20px -22px;
  border-top: 1px dashed var(--receipt-line);
  border-bottom: 0;
}

.receipt-paper-header {
  display: grid;
  justify-items: center;
  gap: 5px;
  padding-bottom: 14px;
  text-align: center;
}

.receipt-brand-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 20px;
  color: var(--receipt-accent);
  font-family: var(--ownit-font-nanum-gothic, OwnitNanumGothic, "Malgun Gothic", sans-serif);
  font-size: 11px;
  font-weight: 800;
}

.receipt-brand-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: var(--receipt-accent);
}

.receipt-kicker,
.receipt-paper-header p,
.receipt-paper-footer p,
.receipt-paper-footer span {
  margin: 0;
  color: var(--receipt-muted);
  font-size: 11px;
  font-weight: 500;
}

.receipt-paper-header h3 {
  margin: 0;
  color: var(--receipt-ink);
  font-family: var(--ownit-font-nanum-gothic, OwnitNanumGothic, "Malgun Gothic", sans-serif);
  font-size: 19px;
  font-weight: 800;
  line-height: 1.2;
}

.receipt-meta-box,
.receipt-mood-box,
.receipt-note-box,
.receipt-total-box {
  border-top: 1px dashed var(--receipt-line);
  padding: 12px 0;
}

.receipt-meta-row,
.receipt-section-title,
.receipt-line,
.receipt-total-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 12px;
  min-width: 0;
}

.receipt-meta-row + .receipt-meta-row {
  margin-top: 6px;
}

.receipt-meta-row span,
.receipt-section-title span,
.receipt-note-box span,
.receipt-total-row span,
.receipt-line span {
  min-width: 0;
  overflow: hidden;
  color: var(--receipt-muted);
  font-size: 11px;
  font-weight: 500;
  overflow-wrap: anywhere;
}

.receipt-meta-row strong,
.receipt-section-title strong,
.receipt-total-row strong {
  color: var(--receipt-ink);
  font-size: 12px;
  font-weight: 600;
  text-align: right;
}

.receipt-mini-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin: 2px 0 8px;
}

.receipt-mini-stats span {
  min-width: 0;
  border: 1px solid rgba(43, 41, 37, 0.12);
  padding: 5px 6px;
  overflow: hidden;
  color: var(--receipt-muted);
  background: rgba(255, 253, 248, 0.72);
  font-size: 10px;
  font-weight: 700;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.receipt-mood-box {
  display: flex;
  align-items: center;
  gap: 12px;
}

.receipt-emoji {
  display: grid;
  place-items: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border: 1px solid #d8d0c2;
  border-radius: 999px;
  background: var(--surface-tint);
  font-size: 26px;
}

.receipt-mood-box div {
  min-width: 0;
}

.receipt-mood-box span,
.receipt-mood-box strong {
  display: block;
}

.receipt-mood-box span {
  color: var(--receipt-muted);
  font-size: 11px;
  font-weight: 500;
}

.receipt-mood-box strong {
  margin-top: 3px;
  color: var(--receipt-ink);
  font-size: 17px;
  font-weight: 600;
}

.receipt-mood-box small {
  display: block;
  margin-top: 4px;
  color: #7b7367;
  font-size: 10px;
  font-weight: 600;
}

.receipt-section {
  border-top: 1px dashed var(--receipt-line);
  padding: 12px 0 8px;
}

.receipt-section-title {
  margin-bottom: 8px;
}

.receipt-line {
  border-left: 3px solid #ded7cc;
  padding: 6px 0 6px 8px;
  border-top: 1px solid rgba(43, 41, 37, 0.12);
}

.receipt-line:first-of-type {
  border-top: 0;
}

.receipt-line div {
  min-width: 0;
}

.receipt-line strong {
  display: block;
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--receipt-ink);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.receipt-line em {
  color: #5f574e;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
}

.receipt-line.open em {
  color: #b35f4f;
}

.receipt-empty-line {
  border: 1px dashed rgba(43, 41, 37, 0.2);
  padding: 8px;
  color: #8b8376;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
}

.receipt-note-box p {
  margin: 8px 0 0;
  color: #292622;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.55;
  white-space: pre-wrap;
}

.receipt-total-box {
  padding-bottom: 0;
}

.receipt-total-row + .receipt-total-row {
  margin-top: 8px;
}

.receipt-total-row.grand-total {
  border-top: 2px solid var(--receipt-ink);
  padding-top: 10px;
}

.receipt-total-row.grand-total strong {
  font-size: 16px;
}

.receipt-paper-footer {
  display: grid;
  justify-items: center;
  gap: 3px;
  padding-top: 16px;
  text-align: center;
}

.receipt-barcode {
  width: 118px;
  height: 28px;
  margin-bottom: 5px;
  background: repeating-linear-gradient(90deg, var(--receipt-ink) 0 2px, transparent 2px 5px, var(--receipt-ink) 5px 6px, transparent 6px 10px);
  opacity: 0.72;
}

.bottom-tabs {
  position: sticky;
  bottom: 0;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 4px;
  border-top: 1px solid var(--line);
  padding: 8px 10px calc(8px + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, 0.96);
}

.tab-button {
  display: grid;
  place-items: center;
  gap: 3px;
  min-width: 0;
  min-height: 58px;
  border-radius: var(--radius-md);
  color: var(--muted);
  background: transparent;
  font-size: 11px;
  font-weight: 600;
}

.tab-button.active {
  color: #fff;
  background: var(--ink);
}

.tab-icon,
.tab-label {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tab-icon {
  font-size: 17px;
  line-height: 1;
}

.shell-sidebar,
.shell-detail {
  min-width: 0;
}

.sidebar-section {
  display: grid;
  gap: 8px;
  border-top: 1px solid var(--line);
  padding-top: 18px;
}

.sidebar-title {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.side-nav {
  display: grid;
  gap: 6px;
}

.side-nav-button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  width: 100%;
  color: var(--muted);
  background: transparent;
}

.side-nav-button.active {
  color: var(--ink);
  background: var(--surface-tint);
}

.nav-icon {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: var(--radius-sm);
  background: #fff;
}

.theme-token-row {
  display: grid;
  gap: 8px;
}

.theme-token {
  width: 100%;
  border: 1px solid var(--line);
  justify-content: flex-start;
  gap: 8px;
  background: #fff;
}

.theme-token.active {
  border-color: var(--green);
  background: var(--surface-tint);
  box-shadow: inset 0 0 0 2px var(--green);
}

.theme-token:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 2px;
}

.theme-dot {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  border: 1px solid var(--line);
  border-radius: 999px;
}

.detail-section {
  display: grid;
  gap: 10px;
  padding: 16px;
}

.segmented-control {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 4px;
  background: var(--surface-soft);
}

.segmented-control button {
  color: var(--muted);
  background: transparent;
}

.segmented-control button.active {
  color: #fff;
  background: var(--ink);
}

.event-editor,
.event-form,
.todo-editor,
.todo-form {
  display: grid;
  gap: 12px;
}

.event-editor,
.todo-editor {
  margin-top: 12px;
  border-top: 1px solid var(--line);
  padding-top: 12px;
}

.event-editor-cta {
  display: grid;
  gap: 10px;
}

.event-editor-cta p {
  margin: 0;
  color: var(--muted);
  line-height: 1.5;
}

.event-scope-control {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.event-form-hint {
  margin: -4px 0 0;
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  color: var(--muted);
  background: var(--surface-soft);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.event-complete-field {
  align-self: end;
  min-height: 42px;
}

.quick-action-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.todo-textarea {
  min-height: 86px;
  padding-top: 12px;
  padding-bottom: 12px;
  resize: vertical;
}

.form-error {
  margin: 0;
  border-radius: var(--radius-md);
  padding: 10px 12px;
  color: #b84536;
  background: #fff0ee;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
}

.checkbox-field {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
}

.checkbox-field input {
  width: 18px;
  height: 18px;
  accent-color: var(--green);
}

.color-choice-row {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
}

.color-choice {
  position: relative;
  display: grid;
  place-items: center;
  min-width: 0;
}

.color-choice input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.color-swatch {
  display: block;
  width: 100%;
  min-width: 28px;
  height: 34px;
  border: 2px solid #fff;
  border-radius: var(--radius-md);
  box-shadow: 0 0 0 1px var(--line);
}

.color-choice input:checked + .color-swatch {
  box-shadow: 0 0 0 2px var(--ink);
}

.form-actions {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 8px;
}

.add-event-button {
  min-height: 34px;
  padding: 0 10px;
  white-space: nowrap;
}

@media (max-width: 760px) {
  .receipt-modal-backdrop {
    align-items: end;
    padding: 8px;
  }
  .receipt-modal {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
    width: 100%;
    max-height: calc(100dvh - 16px);
    border-radius: 14px 14px 0 0;
    padding: 10px;
  }
  .receipt-controls,
  .receipt-preview-shell {
    padding: 12px;
  }
  .receipt-preview-shell {
    order: -1;
  }
  .receipt-template-grid,
  .receipt-form-grid,
  .receipt-actions {
    grid-template-columns: minmax(0, 1fr);
  }
  .receipt-template-grid .field:nth-child(3) {
    grid-column: auto;
  }
  .receipt-preview-frame {
    padding: 10px;
    overflow-x: hidden;
  }
  .receipt-preview-save-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 40px;
    padding: 0 12px;
    white-space: nowrap;
  }
  .receipt-preview-actions .status-chip {
    display: none;
  }
  .receipt-paper {
    width: 100%;
    padding: 18px 16px;
  }
  .receipt-edge {
    margin: -18px -16px 14px;
  }
  .receipt-edge.bottom-edge {
    margin: 14px -16px -18px;
  }
  .receipt-paper-header h3 {
    font-size: 18px;
  }
}
@media (min-width: 768px) {
  .ownit-page {
    display: grid;
    place-items: center;
    padding: 28px;
  }
  .auth-screen,
  .app-shell {
    min-height: min(900px, 100dvh - 56px);
    border: 1px solid var(--line);
    border-radius: var(--radius-lg);
    overflow: hidden;
    box-shadow: var(--shadow-panel);
  }
  .auth-screen {
    grid-template-columns: minmax(0, 1.1fr) minmax(260px, 0.8fr);
    align-items: center;
    max-width: 900px;
  }
  .two-field-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .app-shell {
    max-width: 1120px;
  }
  .shell-header,
  .status-strip,
  .route-row {
    padding-left: 24px;
    padding-right: 24px;
  }
  .shell-content {
    padding-left: 24px;
    padding-right: 24px;
  }
  .calendar-layout,
  .content-grid {
    grid-template-columns: minmax(0, 1.3fr) minmax(280px, 0.8fr);
    align-items: start;
  }
  .calendar-week,
  .calendar-day {
    min-height: 110px;
  }
  .calendar-day {
    padding-bottom: 62px;
  }
  .week-events {
    grid-auto-rows: 18px;
  }
  .event-bar {
    min-height: 18px;
    font-size: 10px;
    line-height: 18px;
  }
}
@media (min-width: 1180px) {
  .ownit-page {
    padding: 32px;
  }
  .app-shell {
    display: grid;
    grid-template-columns: 248px minmax(0, 1fr) 320px;
    max-width: 1360px;
  }
  .shell-sidebar,
  .shell-detail {
    display: flex;
    flex-direction: column;
    gap: 18px;
    min-height: 0;
    overflow: auto;
    background: var(--surface);
  }
  .shell-sidebar {
    padding: 22px 16px;
  }
  .shell-detail {
    padding: 22px 16px;
  }
  .shell-main {
    border-left: 1px solid var(--line);
    border-right: 1px solid var(--line);
  }
  .mobile-brand {
    display: none;
  }
  .shell-content {
    padding-bottom: 24px;
  }
  .calendar-layout,
  .content-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .calendar-layout .day-panel {
    display: none;
  }
  .bottom-tabs {
    display: none;
  }
  .calendar-week,
  .calendar-day {
    min-height: 92px;
  }
}
@media (max-width: 420px) {
  .mobile-brand {
    display: none;
  }
}
@media (max-width: 360px) {
  .shell-header,
  .status-strip,
  .route-row,
  .shell-content {
    padding-left: 12px;
    padding-right: 12px;
  }
  .calendar-day {
    padding: 5px 3px 54px;
  }
  .day-token {
    display: none;
  }
  .tab-label {
    font-size: 10px;
  }
}
/* Stage 14 calendar-first reset */
.ownit-page {
  background: var(--ownit-color-app-bg, #ffffff);
}

.app-shell {
  width: 100%;
  min-height: 100dvh;
  background: var(--ownit-color-app-bg, #ffffff);
}

.shell-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px 12px;
  align-items: center;
  border-bottom: 1px solid var(--line);
  padding: 14px 14px 10px;
  background: #ffffff;
}

.month-heading {
  display: flex;
  grid-column: 1/-1;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.month-heading .header-title {
  display: grid;
  gap: 2px;
}

.month-heading h1 {
  margin: 0;
  color: var(--ink);
  font-size: 34px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0;
}

.month-subtitle {
  display: block;
  overflow: hidden;
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.month-actions,
.header-tools {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.month-actions {
  justify-content: flex-start;
}

.header-tools {
  justify-content: flex-end;
}

.icon-button {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  font-size: 14px;
}

.notification-button {
  position: relative;
}

.notification-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  display: grid;
  place-items: center;
  min-width: 17px;
  height: 17px;
  border: 2px solid #ffffff;
  border-radius: 999px;
  padding: 0 4px;
  color: #ffffff;
  background: #d9468f;
  font-size: 10px;
  font-weight: 850;
  line-height: 1;
}

.today-button {
  min-height: 36px;
  border-radius: var(--radius-sm);
  padding: 0 12px;
}

.status-strip.compact {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  border-bottom: 1px solid var(--line);
  padding: 0;
  background: #ffffff;
}

.status-strip.compact .status-item {
  border: 0;
  border-right: 1px solid var(--line);
  border-radius: 0;
  padding: 7px 10px;
  background: #ffffff;
}

.status-strip.compact .status-item:last-child {
  border-right: 0;
}

.status-strip.compact .status-label {
  margin-bottom: 1px;
  font-size: 10px;
}

.status-strip.compact strong {
  font-size: 13px;
}

.in-app-reminder-toast {
  position: fixed;
  right: 18px;
  bottom: calc(18px + env(safe-area-inset-bottom));
  z-index: 74;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  width: min(420px, 100vw - 28px);
  border: 1px solid #e7e9ed;
  border-radius: 12px;
  padding: 10px;
  color: #111111;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 18px 46px rgba(17, 17, 17, 0.16);
}

.reminder-toast-icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  color: #ffffff;
}

.reminder-toast-copy {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.reminder-toast-copy strong,
.reminder-toast-copy span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reminder-toast-copy strong {
  font-size: 13px;
  font-weight: 850;
}

.reminder-toast-copy span {
  color: #747b84;
  font-size: 12px;
  font-weight: 700;
}

.reminder-toast-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.reminder-toast-actions button {
  min-height: 30px;
  border-radius: 999px;
  padding: 0 10px;
  color: #111111;
  background: #f3f4f6;
  font-size: 12px;
  font-weight: 800;
}

.reminder-toast-actions button:last-child {
  width: 30px;
  padding: 0;
}

.reminder-permission-banner {
  position: fixed;
  top: 76px;
  right: 18px;
  z-index: 72;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  width: min(420px, 100vw - 28px);
  border: 1px solid #ebe2d3;
  border-radius: 10px;
  padding: 9px;
  color: #4f463b;
  background: rgba(255, 250, 241, 0.98);
  box-shadow: 0 14px 34px rgba(79, 70, 59, 0.12);
}

.reminder-permission-banner i {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  color: #7b5b45;
  background: #f3eadc;
}

.reminder-permission-banner span {
  min-width: 0;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.45;
}

.reminder-permission-banner button {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  color: #6f665d;
  background: transparent;
}

.reminder-permission-banner .reminder-permission-action {
  width: auto;
  min-width: 68px;
  padding: 0 10px;
  color: #ffffff;
  background: #4f463b;
  font-size: 11px;
  font-weight: 850;
  white-space: nowrap;
}

.network-status-banner {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid var(--line);
  padding: 9px 18px;
  color: #4f463b;
  background: #fff9ed;
}

.network-status-banner.offline {
  color: #7a3f36;
  background: #fff3f0;
}

.network-status-banner i {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  color: currentColor;
  background: rgba(255, 255, 255, 0.68);
}

.network-status-banner span {
  min-width: 0;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.45;
}

.reminder-center-panel {
  position: fixed;
  top: 84px;
  right: 18px;
  z-index: 73;
  display: grid;
  gap: 12px;
  width: min(360px, 100vw - 28px);
  max-height: min(560px, 100dvh - 112px);
  overflow: auto;
  border: 1px solid #e6e8ec;
  border-radius: 14px;
  padding: 14px;
  color: #111111;
  background: #ffffff;
  box-shadow: 0 22px 56px rgba(17, 17, 17, 0.16);
}

.reminder-center-header {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.reminder-center-header h2 {
  margin: 2px 0;
  font-size: 17px;
  font-weight: 850;
}

.reminder-center-header span {
  color: #8b929a;
  font-size: 12px;
  font-weight: 750;
}

.reminder-center-empty {
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 24px 10px;
  color: #8b929a;
  text-align: center;
  font-size: 13px;
  font-weight: 750;
}

.reminder-center-list {
  display: grid;
  gap: 4px;
}

.reminder-center-item {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) 14px;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  border-radius: 8px;
  padding: 8px;
  color: #111111;
  background: #f8f9fa;
  text-align: left;
}

.reminder-center-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
}

.reminder-center-copy {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.reminder-center-copy strong,
.reminder-center-copy small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reminder-center-copy strong {
  font-size: 13px;
  font-weight: 850;
}

.reminder-center-copy small {
  color: #8b929a;
  font-size: 11px;
  font-weight: 750;
}

.reminder-center-actions {
  display: flex;
  justify-content: flex-end;
}

.reminder-center-permission {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px 10px;
}

.reminder-center-permission p {
  grid-column: 1/-1;
  margin: 0;
  color: #6f7680;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.45;
}

.shell-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: 0;
}

.shell-content > ng-container,
.calendar-layout {
  flex: 1 1 auto;
  min-height: 0;
}

.calendar-layout {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
}

.calendar-panel {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  border: 0;
  border-radius: 0;
  padding: 0;
  overflow: hidden;
  background: var(--calendar-texture), var(--calendar-bg);
  background-size: var(--calendar-texture-size), auto;
  box-shadow: none;
}

.week-grid {
  flex: 0 0 30px;
  gap: 0;
  margin: 0;
  border-bottom: 1px solid var(--line-strong);
  background: #ffffff;
}

.week-label {
  display: grid;
  place-items: center;
  min-width: 0;
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
}

.week-label.sunday {
  color: var(--weekend-sunday);
}

.week-label.saturday {
  color: var(--weekend-saturday);
}

.month-grid {
  display: grid;
  flex: 1 1 auto;
  min-height: 0;
  border-top: 0;
  border-left: 1px solid var(--line);
  grid-template-rows: repeat(6, minmax(78px, 1fr));
  gap: 0;
}

.calendar-week {
  position: relative;
  min-height: 0;
}

.week-days {
  gap: 0;
  height: 100%;
}

.calendar-day {
  min-height: 0;
  border: 0;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  border-radius: 0;
  padding: 7px 6px 64px;
  background: var(--calendar-texture), var(--calendar-bg);
  background-size: var(--calendar-texture-size), auto;
  box-shadow: none;
}

.calendar-day:hover {
  background-color: var(--calendar-hover);
}

.calendar-day.outside {
  color: #a1a8ad;
  background: #fafafa;
}

.calendar-day.saturday .day-number,
.calendar-day.saturday:not(.today):not(.selected) {
  color: var(--weekend-saturday);
}

.calendar-day.sunday .day-number,
.calendar-day.sunday:not(.today):not(.selected) {
  color: var(--weekend-sunday);
}

.calendar-day.outside.saturday,
.calendar-day.outside.saturday .day-number {
  color: var(--weekend-saturday-muted);
}

.calendar-day.outside.sunday,
.calendar-day.outside.sunday .day-number {
  color: var(--weekend-sunday-muted);
}

.calendar-day.today {
  border-color: var(--line);
}

.calendar-day.today .day-number {
  color: #ffffff;
  background: var(--green);
}

.calendar-day.selected {
  border-color: var(--line);
  background: var(--calendar-selected, var(--surface-tint));
  box-shadow: inset 0 0 0 2px rgba(111, 98, 87, 0.34);
}

.calendar-day.today.selected {
  background: var(--calendar-selected, var(--surface-tint));
  box-shadow: inset 0 0 0 2px rgba(111, 98, 87, 0.48);
}

.calendar-day.has-items:not(.selected) {
  background: var(--calendar-texture), var(--calendar-bg);
}

.day-top {
  align-items: center;
  justify-content: space-between;
}

.day-number {
  width: 24px;
  height: 24px;
  font-size: 12px;
}

.day-counts {
  position: absolute;
  z-index: 3;
  top: 8px;
  right: 7px;
  margin: 0;
}

.day-count {
  min-width: 18px;
  height: 18px;
  border: 1px solid rgba(255, 255, 255, 0.9);
  font-size: 10px;
}

.day-overflow {
  right: 6px;
  bottom: 6px;
  min-width: 22px;
  height: 16px;
  border-radius: var(--radius-sm);
  background: rgba(255, 255, 255, 0.94);
  font-size: 9px;
  line-height: 16px;
  box-shadow: none;
}

.week-events {
  right: 2px;
  bottom: 9px;
  left: 2px;
  grid-auto-rows: 18px;
  gap: 2px 2px;
  pointer-events: none;
}

.event-bar {
  min-height: 18px;
  border-radius: 4px;
  padding: 0 7px;
  font-size: 10px;
  font-weight: 600;
  line-height: 18px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.28);
}

.event-bar.single-day {
  border-radius: 999px;
}

.event-bar.multi-day {
  border-radius: 4px;
}

.event-bar-label {
  display: block;
}

.calendar-sticker-layer {
  inset: 30px 0 0;
}

.bottom-sheet,
.bottom-sheet::before {
  display: none;
}

.calendar-toggle {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  cursor: pointer;
}

.calendar-toggle input {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  accent-color: var(--green);
}

.calendar-toggle input,
.checkbox-field input {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid var(--line-strong);
  border-radius: 4px;
  background: #ffffff;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 11px 11px;
}

.calendar-toggle input:checked,
.checkbox-field input:checked {
  border-color: var(--green);
  background-color: var(--green);
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='10' viewBox='0 0 12 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.5 5.1L4.5 8L10.5 1.5' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
}

.calendar-row {
  align-items: center;
  justify-content: space-between;
  padding: 9px;
}

.calendar-row.off {
  opacity: 0.52;
}

.calendar-row.off .calendar-dot {
  filter: grayscale(1);
}

.calendar-dot {
  flex: 0 0 9px;
  width: 9px;
  height: 28px;
}

.sidebar-icon-button {
  display: grid;
  place-items: center;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  color: var(--muted);
  background: #ffffff;
}

.nav-icon,
.tab-icon {
  color: currentColor;
}

.check-button {
  font-size: 12px;
}

.detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 41;
  background: rgba(31, 41, 55, 0.24);
}

.shell-detail.open {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 45;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: min(78dvh, 720px);
  border: 1px solid var(--line);
  border-bottom: 0;
  border-radius: 16px 16px 0 0;
  padding: 10px 12px calc(14px + env(safe-area-inset-bottom));
  overflow: auto;
  background: #ffffff;
  box-shadow: 0 -22px 60px rgba(31, 41, 55, 0.22);
}

.detail-handle {
  flex: 0 0 auto;
  width: 42px;
  height: 4px;
  margin: 0 auto 2px;
  border-radius: 999px;
  background: var(--line-strong);
}

.detail-section {
  border-radius: var(--radius-md);
  background: #ffffff;
}

.bottom-tabs {
  z-index: 40;
}

@media (min-width: 768px) {
  .ownit-page {
    display: block;
    place-items: initial;
    padding: 0;
  }
  .auth-screen {
    max-width: 900px;
    min-height: min(900px, 100dvh - 56px);
    margin: 28px auto;
    border: 1px solid var(--line);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-panel);
  }
  .app-shell {
    display: grid;
    grid-template-columns: 224px minmax(0, 1fr);
    width: 100%;
    max-width: none;
    min-height: 100dvh;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }
  .shell-sidebar {
    display: flex;
    flex-direction: column;
    gap: 18px;
    min-width: 0;
    height: 100dvh;
    border-right: 1px solid var(--line);
    padding: 18px 14px;
    overflow: auto;
    background: #ffffff;
  }
  .shell-main {
    min-height: 100dvh;
    border: 0;
  }
  .shell-header {
    grid-template-columns: minmax(220px, 1fr) auto auto;
    padding: 18px 20px 14px;
  }
  .month-heading {
    grid-column: auto;
  }
  .month-heading h1 {
    font-size: 38px;
  }
  .mobile-brand {
    display: none;
  }
  .status-strip.compact {
    grid-template-columns: repeat(3, minmax(80px, 120px));
    justify-content: end;
    padding: 0 20px;
  }
  .status-strip.compact .status-item {
    border-left: 1px solid var(--line);
    border-right: 0;
  }
  .shell-content {
    padding: 0 20px 20px;
  }
  .calendar-panel {
    border-right: 1px solid var(--line);
    border-bottom: 1px solid var(--line);
  }
  .month-grid {
    grid-template-rows: repeat(6, minmax(94px, 1fr));
  }
  .calendar-day {
    padding: 8px 7px 70px;
  }
  .week-events {
    bottom: 10px;
    grid-auto-rows: 20px;
  }
  .event-bar {
    min-height: 20px;
    font-size: 11px;
    line-height: 20px;
  }
  .bottom-tabs {
    display: none;
  }
  .detail-overlay {
    background: rgba(31, 41, 55, 0.18);
  }
  .shell-detail.open {
    top: 0;
    right: 0;
    bottom: 0;
    left: auto;
    width: min(420px, 100vw - 240px);
    max-height: none;
    border: 0;
    border-left: 1px solid var(--line);
    border-radius: 0;
    padding: 18px 16px;
    box-shadow: -22px 0 60px rgba(31, 41, 55, 0.16);
  }
  .detail-handle {
    display: none;
  }
}
@media (min-width: 1180px) {
  .ownit-page {
    padding: 0;
  }
  .app-shell {
    grid-template-columns: 248px minmax(0, 1fr);
    max-width: none;
  }
  .shell-sidebar {
    padding: 22px 16px;
  }
  .shell-detail.open {
    display: flex;
    background: #ffffff;
  }
  .calendar-week,
  .calendar-day {
    min-height: 0;
  }
}
@media (max-width: 520px) {
  .shell-header {
    grid-template-columns: minmax(0, 1fr) auto;
  }
  .month-actions {
    grid-column: 1/2;
  }
  .header-tools {
    grid-column: 2/3;
  }
  .month-heading h1 {
    font-size: 30px;
  }
  .header-tools .icon-button {
    width: 34px;
    height: 34px;
  }
  .status-strip.compact {
    display: none;
  }
  .month-grid {
    grid-template-rows: repeat(6, minmax(82px, 1fr));
  }
  .calendar-day {
    padding: 6px 4px 60px;
  }
  .day-number {
    width: 22px;
    height: 22px;
    font-size: 11px;
  }
  .week-events {
    bottom: 7px;
    grid-auto-rows: 16px;
  }
  .event-bar {
    min-height: 16px;
    padding: 0 4px;
    font-size: 9px;
    line-height: 16px;
  }
}
/* Stage 17 auth screen center alignment */
.auth-screen {
  --auth-form-width: 400px;
  --auth-form-field-gap: 14px;
  --auth-form-half-height: 124px;
  --auth-wordmark-box-height: 72px;
  --auth-wordmark-gap: 50px;
  --auth-brand-top: clamp(44px, 7dvh, 68px);
  --auth-copy-offset: 0px;
  --auth-button-bg: #EEEAE3;
  --auth-accent-text: #4F483F;
  --auth-focus-base: #EEEAE3;
  --auth-focus-ring: transparent;
  --auth-focus-outline: rgba(205, 197, 184, 0.92);
  --auth-focus-soft: rgba(246, 243, 238, 0.72);
  --auth-focus-border: #c9bda9;
  --auth-toast-border: #ded8ce;
  --auth-toast-bg: rgba(255, 255, 255, 0.96);
  --auth-button-bg-hover: color-mix(in srgb, var(--auth-button-bg) 88%, var(--auth-accent-text) 12%);
  --auth-button-bg-active: color-mix(in srgb, var(--auth-button-bg) 78%, var(--auth-accent-text) 22%);
  --auth-button-bg-disabled: color-mix(in srgb, var(--auth-button-bg) 84%, #ffffff 16%);
  --auth-button-text-disabled: color-mix(in srgb, var(--auth-accent-text) 72%, #ffffff 28%);
  --auth-typewriter-letter-spacing: 0;
  --auth-password-mask-letter-spacing: 0;
  position: relative;
  display: block;
  width: 100%;
  min-height: 100dvh;
  margin: 0;
  padding: calc(env(safe-area-inset-top) + 24px) 24px calc(env(safe-area-inset-bottom) + 40px);
  overflow: auto;
  background: #ffffff;
  font-family: var(--ownit-font-nanum-gothic);
}

.auth-screen[data-auth-mode=signup] {
  --auth-form-half-height: 244px;
  --auth-wordmark-gap: 26px;
}

.auth-screen[data-auth-mode=reset] {
  --auth-form-half-height: 86px;
  --auth-wordmark-gap: 28px;
  --auth-copy-offset: 88px;
}

.auth-screen[data-auth-mode=profile] {
  --auth-form-half-height: 244px;
  --auth-wordmark-gap: 20px;
  --auth-copy-offset: 96px;
}

.auth-content {
  display: contents;
}

.auth-panel,
.auth-note {
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.auth-note {
  display: none;
}

.auth-brand {
  position: absolute;
  top: calc(env(safe-area-inset-top) + var(--auth-brand-top));
  left: 50%;
  display: grid;
  place-items: center;
  width: min(100% - 48px, var(--auth-form-width));
  height: var(--auth-wordmark-box-height);
  min-height: 0;
  transform: translateX(-50%);
  pointer-events: none;
}

.auth-brand-link {
  appearance: none;
  -webkit-appearance: none;
  display: grid;
  place-items: center;
  width: min(208px, 56vw);
  height: 100%;
  border: 0;
  border-radius: 8px;
  padding: 0 6px;
  color: inherit;
  background: transparent;
  text-decoration: none;
  outline: none;
  cursor: pointer;
  pointer-events: auto;
  touch-action: manipulation;
}

.auth-brand-link:hover {
  background: var(--auth-focus-soft);
}

.auth-brand-link:focus-visible {
  background: var(--auth-focus-soft);
  outline: 1px solid var(--auth-focus-outline);
  outline-offset: 3px;
  box-shadow: none;
}

.auth-brand-link:active {
  background: color-mix(in srgb, var(--auth-focus-soft) 84%, var(--auth-accent-text) 16%);
}

.auth-wordmark {
  display: block;
  width: min(196px, 50vw);
  max-width: 100%;
  max-height: 76px;
  object-fit: contain;
}

.auth-brand-fallback {
  color: #6f767c;
  font-family: var(--ownit-font-brand, var(--ownit-font-typewriter, OwnitTypewriter, OwnitNanumGothic, "Courier New", monospace));
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
}

.auth-panel {
  display: contents;
  padding: 0;
  background: transparent;
}

.auth-form,
.auth-form-heading {
  width: min(100% - 48px, var(--auth-form-width));
}

.auth-form {
  display: contents;
}

.auth-form-core {
  position: absolute;
  top: 50%;
  left: 50%;
  display: grid;
  gap: var(--auth-form-field-gap);
  width: min(100% - 48px, var(--auth-form-width));
  transform: translate(-50%, -50%);
}

.auth-form-heading {
  position: absolute;
  bottom: calc(50dvh + var(--auth-form-half-height) + 18px);
  left: 50%;
  display: grid;
  gap: 14px;
  margin: 0;
  text-align: center;
  transform: translateX(-50%);
}

.auth-panel h1 {
  margin: 0;
  color: #24282c;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: 0;
}

.auth-form-heading .auth-copy {
  max-width: 340px;
  margin: 0 auto;
  color: #6f767c;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.65;
}

.auth-message {
  position: absolute;
  bottom: calc(50dvh + var(--auth-form-half-height) + 14px);
  left: 50%;
  z-index: 2;
  width: min(100% - 48px, var(--auth-form-width));
  margin: 0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
  transform: translateX(-50%);
}

.auth-toast {
  position: fixed;
  right: auto;
  bottom: calc(env(safe-area-inset-bottom) + 26px);
  left: 50%;
  z-index: 60;
  width: max-content;
  max-width: min(100vw - 48px, 360px);
  margin: 0;
  border: 1px solid var(--auth-toast-border);
  border-radius: 8px;
  padding: 10px 14px;
  color: var(--auth-accent-text);
  background: var(--auth-toast-bg);
  box-shadow: 0 8px 24px rgba(36, 48, 43, 0.08);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
  text-align: center;
  opacity: 1;
  transform: translate(-50%, 0);
  transition: opacity 180ms ease, transform 180ms ease;
  pointer-events: none;
}

.auth-toast.is-leaving {
  opacity: 0;
  transform: translate(-50%, 6px);
}

.auth-screen[data-auth-mode=reset] .auth-message,
.auth-screen[data-auth-mode=profile] .auth-message {
  bottom: calc(50dvh + var(--auth-form-half-height) + 112px);
}

.auth-screen .auth-message.success {
  color: var(--auth-accent-text);
  background: var(--auth-focus-soft);
}

.field {
  gap: 8px;
  color: #5f666d;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
}

.field > span,
.field-label-row span {
  font-weight: 400;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.auth-screen .ui-input,
.auth-screen .auth-form input:not([type=checkbox]) {
  width: 100%;
  height: 48px;
  border: 1px solid #dfe3e6;
  border-radius: 8px;
  padding: 0 14px;
  color: #23282d;
  background: #ffffff;
  background-clip: padding-box;
  caret-color: var(--auth-accent-text);
  -webkit-text-fill-color: #23282d;
  font-family: var(--ownit-font-nanum-gothic);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
  outline: none;
  box-shadow: none;
  transition: border-color 120ms ease, background-color 120ms ease;
}

.auth-screen .ui-input::placeholder,
.auth-screen .auth-form input:not([type=checkbox])::placeholder {
  color: #a1a7ad;
  font-family: var(--ownit-font-nanum-gothic);
  font-weight: 400;
  letter-spacing: 0;
}

.auth-screen .auth-value-input,
.auth-screen .auth-form input.auth-value-input:not([type=checkbox]) {
  font-family: var(--ownit-font-typewriter);
  font-size: 14px;
  font-weight: 400;
  font-synthesis: none;
  letter-spacing: var(--auth-typewriter-letter-spacing);
}

.auth-screen .auth-value-input::placeholder,
.auth-screen .auth-form input.auth-value-input:not([type=checkbox])::placeholder {
  font-family: var(--ownit-font-nanum-gothic);
  font-weight: 400;
  letter-spacing: 0;
}

.auth-screen .ui-input:hover,
.auth-screen .auth-form input:not([type=checkbox]):hover {
  border-color: #d6d0c6;
  background: #ffffff;
}

.auth-screen .ui-input:active,
.auth-screen .auth-form input:not([type=checkbox]):active,
.auth-screen .ui-input:focus,
.auth-screen .auth-form input:not([type=checkbox]):focus {
  border-color: #d6d0c6;
  background: #ffffff;
  outline: none;
  box-shadow: none;
}

.auth-screen .ui-input:focus-visible,
.auth-screen .auth-form input:not([type=checkbox]):focus-visible {
  border-color: var(--auth-focus-border);
  background: #ffffff;
  outline: none;
  box-shadow: none;
}

.auth-screen .ui-input:not(:placeholder-shown),
.auth-screen .auth-form input:not([type=checkbox]):not(:placeholder-shown) {
  background: #ffffff;
  font-weight: 400;
}

.auth-screen .ui-input:-webkit-autofill,
.auth-screen .ui-input:-webkit-autofill:hover,
.auth-screen .ui-input:-webkit-autofill:focus,
.auth-screen .ui-input:-webkit-autofill:active,
.auth-screen .auth-form input:not([type=checkbox]):-webkit-autofill,
.auth-screen .auth-form input:not([type=checkbox]):-webkit-autofill:hover,
.auth-screen .auth-form input:not([type=checkbox]):-webkit-autofill:focus,
.auth-screen .auth-form input:not([type=checkbox]):-webkit-autofill:active {
  border-color: #dfe3e6;
  background: #ffffff !important;
  -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
  box-shadow: 0 0 0 1000px #ffffff inset !important;
  -webkit-text-fill-color: #23282d !important;
  caret-color: var(--auth-accent-text);
  font-family: var(--ownit-font-nanum-gothic);
  font-weight: 400;
  transition: background-color 9999s ease-out 0s;
}

.auth-screen .ui-input:autofill,
.auth-screen .auth-form input:not([type=checkbox]):autofill {
  background: #ffffff !important;
  box-shadow: 0 0 0 1000px #ffffff inset !important;
  font-weight: 400;
}

.auth-screen .ui-input:-webkit-autofill:focus-visible,
.auth-screen .auth-form input:not([type=checkbox]):-webkit-autofill:focus-visible {
  border-color: var(--auth-focus-border);
}

.auth-screen .password-input-wrap {
  position: relative;
  width: 100%;
}

.auth-screen .password-input-wrap .ui-input {
  padding-right: 58px;
}

.auth-screen .password-toggle {
  position: absolute;
  top: 50%;
  right: 5px;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  color: #9aa1a7;
  background: transparent;
  font-size: 16px;
  line-height: 1;
  transform: translateY(-50%);
  touch-action: manipulation;
}

.auth-screen .password-toggle i {
  display: block;
  line-height: 1;
}

.auth-screen .password-toggle:hover,
.auth-screen .password-toggle:focus-visible {
  color: var(--auth-accent-text);
  background: var(--auth-focus-soft);
}

.auth-screen .password-toggle:focus-visible {
  outline: 1px solid var(--auth-focus-outline);
  outline-offset: 2px;
  box-shadow: none;
}

.auth-screen .auth-inline-link,
.auth-screen .auth-text-link {
  min-height: 0;
  border-radius: 6px;
  padding: 0;
  color: var(--auth-accent-text);
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
}

.auth-screen .auth-inline-link:hover,
.auth-screen .auth-text-link:hover,
.auth-screen .auth-inline-link:focus-visible,
.auth-screen .auth-text-link:focus-visible {
  color: color-mix(in srgb, var(--auth-accent-text) 86%, #000000 14%);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.auth-screen .auth-inline-link:focus-visible,
.auth-screen .auth-text-link:focus-visible {
  outline: 1px solid var(--auth-focus-outline);
  outline-offset: 3px;
  box-shadow: none;
}

.auth-screen .primary-action.auth-submit {
  width: 100%;
  min-height: 48px;
  margin-top: 16px;
  border-radius: 8px;
  color: var(--auth-accent-text);
  background: var(--auth-button-bg);
  font-size: 14px;
  font-weight: 600;
  opacity: 1;
}

.auth-screen .primary-action.auth-submit:not(:disabled):hover,
.auth-screen .primary-action.auth-submit:focus-visible {
  background: var(--auth-button-bg-hover);
}

.auth-screen .primary-action.auth-submit:not(:disabled):active {
  background: var(--auth-button-bg-active);
}

.auth-screen .primary-action.auth-submit:focus-visible {
  outline: 1px solid var(--auth-focus-outline);
  outline-offset: 3px;
  box-shadow: none;
}

.auth-screen .primary-action.auth-submit:disabled {
  color: var(--auth-button-text-disabled);
  background: var(--auth-button-bg-disabled);
  opacity: 1;
}

.auth-screen .terms-field {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 20px 0 0;
  color: #4f565c;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
}

.auth-screen .terms-field input {
  appearance: auto;
  -webkit-appearance: checkbox;
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border-radius: 3px;
  padding: 0;
  accent-color: var(--auth-accent-text);
}

.auth-screen .terms-field input:focus-visible {
  outline: 1px solid var(--auth-focus-outline);
  outline-offset: 3px;
  box-shadow: none;
}

.auth-switch {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
  color: #7a8188;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
}

.auth-screen button,
.auth-screen input,
.auth-screen textarea,
.auth-screen select {
  font-family: inherit;
}

.auth-screen[data-auth-mode=signin] .auth-value-input,
.auth-screen[data-auth-mode=signup] .auth-value-input,
.auth-screen[data-auth-mode=reset] .auth-value-input,
.auth-screen[data-auth-mode=signin] .auth-form input.auth-value-input:not([type=checkbox]),
.auth-screen[data-auth-mode=signup] .auth-form input.auth-value-input:not([type=checkbox]),
.auth-screen[data-auth-mode=reset] .auth-form input.auth-value-input:not([type=checkbox]) {
  font-family: var(--ownit-font-typewriter);
  font-weight: 400;
  font-synthesis: none;
  letter-spacing: var(--auth-typewriter-letter-spacing);
}

.auth-screen[data-auth-mode=signin] input.auth-value-input[type=password],
.auth-screen[data-auth-mode=signup] input.auth-value-input[type=password] {
  font-family: var(--ownit-font-typewriter);
  font-size: 14px;
  font-weight: 400;
  font-synthesis: none;
  letter-spacing: var(--auth-typewriter-letter-spacing);
}

.auth-screen .auth-value-input:-webkit-autofill,
.auth-screen .auth-value-input:-webkit-autofill:hover,
.auth-screen .auth-value-input:-webkit-autofill:focus,
.auth-screen .auth-value-input:-webkit-autofill:active,
.auth-screen .auth-form input.auth-value-input:not([type=checkbox]):-webkit-autofill,
.auth-screen .auth-form input.auth-value-input:not([type=checkbox]):-webkit-autofill:hover,
.auth-screen .auth-form input.auth-value-input:not([type=checkbox]):-webkit-autofill:focus,
.auth-screen .auth-form input.auth-value-input:not([type=checkbox]):-webkit-autofill:active {
  font-family: var(--ownit-font-typewriter);
  font-weight: 400;
  font-synthesis: none;
  letter-spacing: var(--auth-typewriter-letter-spacing);
}

@media (min-width: 768px) {
  .auth-screen {
    max-width: none;
    min-height: 100dvh;
    margin: 0;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }
}
@media (max-width: 520px) {
  .auth-screen {
    --auth-brand-top: 46px;
    padding-right: 22px;
    padding-left: 22px;
  }
  .auth-brand {
    width: min(100% - 44px, var(--auth-form-width));
  }
  .auth-wordmark {
    width: min(184px, 54vw);
    max-height: 70px;
  }
  .auth-form,
  .auth-form-heading,
  .auth-form-core,
  .auth-message {
    width: min(100% - 44px, var(--auth-form-width));
  }
}
@media (max-height: 720px) {
  .auth-screen {
    --auth-wordmark-box-height: 62px;
    --auth-wordmark-gap: 26px;
    --auth-brand-top: 24px;
    padding-bottom: calc(env(safe-area-inset-bottom) + 28px);
    padding-top: calc(env(safe-area-inset-top) + 18px);
  }
  .auth-screen[data-auth-mode=signup] {
    --auth-wordmark-gap: 22px;
  }
  .auth-screen[data-auth-mode=reset],
  .auth-screen[data-auth-mode=profile] {
    --auth-copy-offset: 74px;
  }
  .auth-wordmark {
    width: min(176px, 50vw);
    max-height: 62px;
  }
}
/* Stage 22 monthly calendar detail polish */
.ownit-page {
  --calendar-line: rgba(47, 42, 37, 0.105);
  --calendar-line-soft: rgba(47, 42, 37, 0.075);
  --calendar-hover: rgba(79, 72, 63, 0.045);
  --calendar-selected: rgba(79, 72, 63, 0.07);
  --calendar-selected-border: rgba(79, 72, 63, 0.2);
  --calendar-today: #4F483F;
  --quiet-fill: rgba(79, 72, 63, 0.055);
  --quiet-fill-hover: rgba(79, 72, 63, 0.08);
  --quiet-border: rgba(79, 72, 63, 0.14);
  --quiet-border-strong: rgba(79, 72, 63, 0.22);
}

.ownit-page.theme-decorative-cloud-dancer {
  --calendar-hover: #fbfaf7;
  --calendar-selected: #f6f2eb;
  --calendar-selected-border: rgba(95, 87, 78, 0.2);
  --calendar-today: #6b6259;
  --quiet-fill: #f6f2eb;
  --quiet-fill-hover: #eee7dd;
  --quiet-border: rgba(95, 87, 78, 0.16);
  --quiet-border-strong: rgba(95, 87, 78, 0.24);
}

.ownit-page.theme-cosmic-latte {
  --calendar-hover: #fffaf0;
  --calendar-selected: #fff8e7;
  --calendar-selected-border: rgba(95, 87, 78, 0.2);
  --calendar-today: #6b6259;
  --quiet-fill: #fff8e7;
  --quiet-fill-hover: #f6f1e8;
  --quiet-border: rgba(95, 87, 78, 0.16);
  --quiet-border-strong: rgba(95, 87, 78, 0.24);
}

.shell-header {
  border-bottom-color: var(--calendar-line-soft);
}

.month-heading h1 {
  font-weight: 500;
}

.month-heading .eyebrow {
  display: none;
}

.month-subtitle {
  color: var(--soft-text);
  font-size: 11px;
  font-weight: 400;
}

.month-actions,
.header-tools {
  gap: 5px;
}

.icon-button,
.today-button,
.sidebar-icon-button {
  border-color: var(--quiet-border);
  color: var(--muted);
  background: #ffffff;
  box-shadow: none;
}

.icon-button {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  font-size: 13px;
  line-height: 1;
}

.today-button {
  min-height: 34px;
  padding: 0 11px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
}

.range-mode-header-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: #ffffff;
  line-height: 1;
}

.range-mode-header-button.active {
  border-color: rgba(111, 98, 87, 0.42);
  color: #ffffff;
  background: var(--green);
}

.range-mode-header-button i {
  font-size: 12px;
}

.icon-button:hover,
.today-button:hover,
.sidebar-icon-button:hover,
.theme-token:hover {
  border-color: var(--quiet-border-strong);
  background: var(--calendar-hover);
}

.icon-button:focus-visible,
.today-button:focus-visible,
.sidebar-icon-button:focus-visible,
.theme-token:focus-visible,
.side-nav-button:focus-visible,
.calendar-day:focus-visible {
  outline: 1px solid var(--quiet-border-strong);
  outline-offset: 2px;
}

.status-strip.compact {
  border-bottom-color: var(--calendar-line-soft);
}

.status-strip.compact .status-item {
  border-color: var(--calendar-line-soft);
  padding: 6px 10px;
}

.status-strip.compact .status-label {
  color: var(--soft-text);
  font-size: 10px;
  font-weight: 400;
}

.status-strip.compact strong {
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
}

.week-grid {
  flex-basis: 28px;
  border-bottom-color: var(--calendar-line);
}

.week-label {
  color: var(--soft-text);
  font-size: 10px;
  font-weight: 500;
}

.week-label.sunday {
  color: var(--weekend-sunday);
}

.week-label.saturday {
  color: var(--weekend-saturday);
}

.month-grid {
  border-left-color: var(--calendar-line);
}

.calendar-panel {
  border-color: var(--calendar-line);
}

.calendar-day {
  border-right-color: var(--calendar-line);
  border-bottom-color: var(--calendar-line);
  color: var(--muted);
  transition: background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
}

.calendar-day:hover {
  background-color: var(--calendar-hover);
}

.calendar-day.outside {
  color: #b7b1aa;
  background: #fbfbfa;
}

.calendar-day.saturday .day-number,
.calendar-day.saturday:not(.today):not(.selected) {
  color: var(--weekend-saturday);
}

.calendar-day.sunday .day-number,
.calendar-day.sunday:not(.today):not(.selected) {
  color: var(--weekend-sunday);
}

.calendar-day.outside.saturday,
.calendar-day.outside.saturday .day-number {
  color: var(--weekend-saturday-muted);
}

.calendar-day.outside.sunday,
.calendar-day.outside.sunday .day-number {
  color: var(--weekend-sunday-muted);
}

.calendar-day.today {
  border-color: var(--calendar-line);
}

.calendar-day.selected {
  border-color: var(--calendar-selected-border);
  background: var(--calendar-selected);
  box-shadow: inset 0 0 0 1px rgba(95, 87, 78, 0.14);
}

.calendar-day.today.selected {
  border-color: var(--calendar-selected-border);
  background: var(--calendar-selected);
  box-shadow: inset 0 0 0 1px rgba(95, 87, 78, 0.18);
}

.day-number {
  width: 21px;
  height: 21px;
  color: inherit;
  font-size: 11px;
  font-weight: 400;
}

.calendar-day.today .day-number {
  color: #ffffff;
  background: var(--calendar-today);
  font-weight: 500;
}

.calendar-day.selected .day-number {
  color: var(--ink);
  background: transparent;
  font-weight: 500;
}

.calendar-day.today.selected .day-number {
  color: #ffffff;
  background: var(--calendar-today);
}

.calendar-day.selected.saturday .day-number {
  color: var(--weekend-saturday);
  background: transparent;
}

.calendar-day.selected.sunday .day-number {
  color: var(--weekend-sunday);
  background: transparent;
}

.calendar-day.today.saturday .day-number,
.calendar-day.today.selected.saturday .day-number {
  color: #ffffff;
  background: var(--weekend-saturday);
}

.calendar-day.today.sunday .day-number,
.calendar-day.today.selected.sunday .day-number {
  color: #ffffff;
  background: var(--weekend-sunday);
}

.day-count {
  min-width: 17px;
  height: 17px;
  border-color: rgba(255, 255, 255, 0.75);
  font-size: 9px;
  font-weight: 500;
}

.day-overflow {
  color: var(--muted);
  background: rgba(255, 255, 255, 0.88);
  font-weight: 500;
}

.day-sticker-layer {
  opacity: 0.42;
  overflow: hidden;
  contain: layout paint;
}

.day-sticker {
  max-width: min(28px, 46%);
  max-height: min(28px, 46%);
  border: 0;
  border-radius: 999px;
  box-shadow: none;
}

.event-bar {
  font-weight: 500;
  box-shadow: none;
}

.event-bar.single-day,
.event-bar.shared {
  box-shadow: none;
}

.event-bar.external {
  border-color: rgba(47, 42, 37, 0.16);
  box-shadow: inset 0 0 0 1px rgba(47, 42, 37, 0.12);
}

.sidebar-section {
  border-top-color: var(--calendar-line-soft);
}

.sidebar-title {
  color: var(--soft-text);
  font-size: 11px;
  font-weight: 500;
}

.side-nav-button {
  min-height: 34px;
  border-radius: var(--radius-sm);
  padding: 0 8px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 500;
}

.side-nav-button.active {
  color: var(--green);
  background: var(--quiet-fill);
}

.side-nav-button:hover {
  background: var(--calendar-hover);
}

.nav-icon {
  background: transparent;
}

.calendar-row {
  border-color: var(--quiet-border);
  border-radius: var(--radius-sm);
  padding: 8px;
  background: #ffffff;
  box-shadow: none;
}

.calendar-row.shared-calendar,
.calendar-row.personal-calendar {
  border-color: var(--quiet-border);
  background: #ffffff;
}

.calendar-row strong {
  color: var(--text);
  font-size: 13px;
  font-weight: 500;
}

.calendar-row p {
  color: var(--soft-text);
  font-size: 11px;
  font-weight: 400;
}

.calendar-dot {
  flex-basis: 8px;
  width: 8px;
  height: 24px;
}

.calendar-toggle input,
.checkbox-field input {
  border-color: var(--quiet-border-strong);
  color: var(--green);
  accent-color: var(--green);
}

.calendar-toggle input:checked,
.checkbox-field input:checked {
  border-color: var(--green);
  background-color: var(--green);
}

.theme-token {
  min-height: 32px;
  border-color: var(--quiet-border);
  border-radius: var(--radius-sm);
  color: var(--muted);
  background: #ffffff;
  font-size: 12px;
  font-weight: 500;
}

.theme-token.active {
  border-color: var(--quiet-border-strong);
  color: var(--green);
  background: var(--quiet-fill);
  box-shadow: inset 0 0 0 1px rgba(95, 87, 78, 0.1);
}

.theme-dot {
  width: 16px;
  height: 16px;
  border-color: var(--quiet-border);
}

@media (min-width: 768px) {
  .shell-sidebar {
    border-right-color: var(--calendar-line-soft);
    background: #ffffff;
  }
  .shell-header {
    padding: 16px 20px 12px;
  }
  .status-strip.compact {
    padding: 0 20px;
  }
  .shell-content {
    padding: 0 20px 20px;
  }
  .calendar-panel {
    border-right-color: var(--calendar-line);
    border-bottom-color: var(--calendar-line);
  }
  .month-heading h1 {
    font-size: 36px;
  }
}
@media (min-width: 1180px) {
  .app-shell {
    grid-template-columns: 240px minmax(0, 1fr);
  }
  .shell-sidebar {
    padding: 20px 16px;
  }
  .month-grid {
    grid-template-rows: repeat(6, minmax(96px, 1fr));
  }
}
@media (max-width: 520px) {
  .shell-header {
    padding: 12px 12px 9px;
  }
  .month-heading h1 {
    font-size: 28px;
    font-weight: 500;
  }
  .month-subtitle {
    font-size: 10px;
  }
  .icon-button {
    width: 32px;
    height: 32px;
  }
  .today-button {
    min-height: 32px;
    padding: 0 10px;
  }
  .week-grid {
    flex-basis: 26px;
  }
  .week-label {
    font-size: 10px;
  }
  .month-grid {
    grid-template-rows: repeat(6, minmax(76px, 1fr));
  }
  .calendar-day {
    padding: 5px 4px 56px;
  }
  .day-number {
    width: 19px;
    height: 19px;
    font-size: 10px;
  }
  .day-counts {
    top: 6px;
    right: 5px;
  }
  .day-count {
    min-width: 15px;
    height: 15px;
    font-size: 8px;
  }
  .day-sticker {
    max-width: 18px;
    max-height: 18px;
    font-size: 10px;
  }
  .day-overflow {
    right: 4px;
    bottom: 5px;
    min-width: 19px;
    height: 15px;
    padding: 0 4px;
    font-size: 8px;
    line-height: 15px;
  }
  .week-events {
    right: 1px;
    bottom: 6px;
    left: 1px;
    grid-auto-rows: 15px;
  }
  .event-bar {
    min-height: 15px;
    padding: 0 4px;
    font-size: 8px;
    line-height: 15px;
  }
}
/* Stage 25 Korean holiday layer */
.ownit-page {
  --holiday-red: #a84f47;
  --holiday-red-muted: #c38d86;
  --holiday-fill: #fff6f3;
  --holiday-line: rgba(168, 79, 71, 0.18);
}

.day-holiday-label {
  display: block;
  width: 100%;
  min-width: 0;
  margin-top: 1px;
  color: var(--holiday-red);
  font-size: 9px;
  font-weight: 500;
  line-height: 1.15;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
}

.calendar-day.has-holiday:not(.selected):hover {
  background-color: var(--holiday-fill);
}

.calendar-day.public-holiday:not(.today):not(.selected) {
  color: var(--holiday-red);
}

.calendar-day.public-holiday .day-number,
.calendar-day.selected.public-holiday .day-number,
.calendar-day.selected.saturday.public-holiday .day-number,
.calendar-day.selected.sunday.public-holiday .day-number {
  color: var(--holiday-red);
  background: transparent;
}

.calendar-day.outside.public-holiday,
.calendar-day.outside.public-holiday .day-number,
.calendar-day.outside.public-holiday .day-holiday-label {
  color: var(--holiday-red-muted);
}

.calendar-day.today.public-holiday .day-number,
.calendar-day.today.selected.public-holiday .day-number {
  color: #ffffff;
  background: var(--holiday-red);
}

.holiday-detail-list {
  display: grid;
  gap: 6px;
  margin: 2px 0 12px;
}

.holiday-detail-row {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  border: 1px solid var(--holiday-line);
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  background: var(--holiday-fill);
}

.holiday-layer-dot {
  width: 8px;
  height: 24px;
  border-radius: 999px;
  background: var(--holiday-red);
}

.holiday-detail-row strong {
  display: block;
  color: var(--holiday-red);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
}

.holiday-detail-row p {
  margin: 2px 0 0;
  color: var(--soft-text);
  font-size: 11px;
  line-height: 1.3;
}

.holiday-detail-row.substitute {
  background: #fff8f5;
}

.holiday-detail-row.observance {
  border-color: var(--quiet-border);
  background: #ffffff;
}

.holiday-detail-row.observance .holiday-layer-dot {
  background: var(--soft-text);
}

.holiday-detail-row.observance strong {
  color: var(--muted);
}

@media (max-width: 520px) {
  .day-holiday-label {
    margin-top: 0;
    font-size: 8px;
    line-height: 1.1;
  }
  .holiday-detail-row {
    padding: 7px 9px;
  }
}
/* Stage 28 TodoMate workspace foundation */
.ownit-page.todo-workspace-active {
  --ownit-color-app-bg: #ffffff;
  --ownit-color-calendar-bg: #ffffff;
  --surface: #ffffff;
  --surface-soft: #f9fafb;
  --surface-tint: #f3f4f6;
  --line: #e5e7eb;
  --line-strong: #d1d5db;
  --text: #111827;
  --muted: #6b7280;
  --soft-text: #9ca3af;
  --ink: #111827;
  --green: #111827;
  --calendar-hover: #f9fafb;
  --calendar-selected: #ffffff;
  --calendar-selected-border: #111827;
  --quiet-fill: #f3f4f6;
  --quiet-fill-hover: #e5e7eb;
  --quiet-border: #e5e7eb;
  --quiet-border-strong: #d1d5db;
}

.todo-workspace-active .shell-main,
.todo-workspace-active .shell-header,
.todo-workspace-active .status-strip.compact,
.todo-workspace-active .shell-content {
  background: #ffffff;
}

.todomate-page {
  display: grid;
  gap: 18px;
  width: 100%;
  min-height: 100%;
  padding: 14px 0 22px;
  color: #111827;
  background: #ffffff;
}

.todomate-header {
  display: grid;
  gap: 18px;
}

.todomate-profile-switch,
.todomate-header-actions,
.todo-action-buttons,
.todo-item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.todomate-profile-pill,
.todomate-profile-icon,
.todo-icon-action,
.todo-row-icon {
  display: inline-grid;
  place-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  color: #111827;
  background: #f3f4f6;
}

.todomate-profile-pill {
  grid-auto-flow: column;
  gap: 8px;
  min-height: 42px;
  padding: 4px 14px 4px 5px;
  font-size: 16px;
  font-weight: 700;
}

.todomate-profile-pill.active {
  border-color: #111827;
  color: #ffffff;
  background: #111827;
}

.todomate-profile-icon,
.todo-icon-action,
.todo-row-icon {
  width: 42px;
  height: 42px;
  font-size: 16px;
}

.todomate-mini-avatar,
.todomate-avatar {
  display: grid;
  place-items: center;
  border-radius: 999px;
  color: #ffffff;
  background: #d1d5db;
  font-weight: 700;
}

.todomate-mini-avatar {
  width: 32px;
  height: 32px;
  color: #111827;
  background: #f9fafb;
}

.todomate-header-main {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  min-width: 0;
}

.todomate-avatar {
  width: 76px;
  height: 76px;
  font-size: 26px;
}

.todomate-kicker,
.todomate-date-copy,
.todomate-section-eyebrow,
.todo-section-header p,
.todo-action-sheet-header span,
.todo-item-main p,
.todo-item-meta,
.todo-compact-empty p {
  margin: 0;
  color: #6b7280;
}

.todomate-kicker {
  font-size: 13px;
  font-weight: 700;
}

.todomate-header-main h2 {
  margin: 3px 0 0;
  color: #111827;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
}

.todomate-date-copy {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
}

.todomate-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}

.todomate-calendar-pane,
.todomate-goals,
.todo-action-sheet,
.todo-goal-section,
.todo-routine-section,
.todo-daily-summary {
  min-width: 0;
}

.todomate-calendar,
.todo-daily-summary,
.todo-goal-section,
.todo-routine-section,
.todo-action-sheet {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
}

.todomate-calendar,
.todo-goal-section,
.todo-routine-section,
.todo-action-sheet {
  padding: 14px;
}

.todomate-calendar-heading,
.todo-section-header,
.todo-action-sheet-header,
.todo-daily-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.todomate-calendar-heading h3,
.todo-section-header h3,
.todo-action-sheet-header h3,
.todo-daily-summary h3 {
  margin: 0;
  color: #111827;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
}

.todomate-section-eyebrow {
  margin-bottom: 3px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.todomate-week-row,
.todomate-date-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.todomate-week-row {
  margin-top: 16px;
}

.todomate-week-row span {
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}

.todomate-week-row .sunday,
.todomate-week-row .saturday {
  color: #6b7280;
}

.todomate-date-grid {
  gap: 6px 4px;
  margin-top: 10px;
}

.todomate-date-cell {
  position: relative;
  display: grid;
  justify-items: center;
  align-content: start;
  gap: 4px;
  min-width: 0;
  min-height: 58px;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 5px 2px;
  color: #111827;
  background: #ffffff;
}

.todomate-date-cell:hover {
  border-color: #e5e7eb;
  background: #f9fafb;
}

.todomate-date-cell.outside {
  color: #9ca3af;
}

.todomate-date-cell.selected {
  border-color: #111827;
  background: #ffffff;
}

.todomate-marker-stack {
  display: grid;
  grid-template-columns: repeat(2, 9px);
  grid-template-rows: repeat(2, 9px);
  gap: 1px;
  width: 19px;
  height: 19px;
  overflow: hidden;
}

.todomate-marker-slot {
  width: 9px;
  height: 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: #d5dadd;
  box-sizing: border-box;
}

.todo-day-marker-slot.slot-top-left {
  grid-column: 1;
  grid-row: 1;
}

.todo-day-marker-slot.slot-top-right {
  grid-column: 2;
  grid-row: 1;
}

.todo-day-marker-slot.slot-bottom-right {
  grid-column: 2;
  grid-row: 2;
}

.todo-day-marker-slot.slot-bottom-left {
  grid-column: 1;
  grid-row: 2;
}

.todomate-marker-slot.filled {
  box-shadow: none;
}

.todomate-day-number {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
}

.todomate-date-cell.today .todomate-day-number,
.todomate-date-cell.selected .todomate-day-number {
  color: #ffffff;
  background: #111827;
}

.todomate-date-count {
  color: #6b7280;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
}

.todo-daily-summary {
  margin-top: 10px;
  padding: 12px 14px;
}

.todo-summary-meter {
  display: grid;
  justify-items: end;
  gap: 2px;
}

.todo-summary-meter strong {
  color: #111827;
  font-size: 18px;
  font-weight: 800;
}

.todo-summary-meter span {
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
}

.todo-progress-track {
  grid-column: 1/-1;
  width: 100%;
  height: 5px;
  border-radius: 999px;
  overflow: hidden;
  background: #e5e7eb;
}

.todo-progress-bar {
  height: 100%;
  border-radius: inherit;
  background: #111827;
}

.todomate-goals {
  display: grid;
  gap: 12px;
}

.todo-section-header {
  align-items: center;
}

.todo-section-header > div,
.todo-action-sheet-header > div,
.todo-item-main {
  min-width: 0;
}

.goal-color-dot {
  flex: 0 0 14px;
  width: 14px;
  height: 34px;
  border-radius: 999px;
}

.goal-color-dot.neutral {
  background: #111827;
}

.todo-section-header p {
  margin-top: 3px;
  font-size: 11px;
  font-weight: 700;
}

.todo-icon-action {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  color: #111827;
  background: #f3f4f6;
}

.todo-goal-section .todo-progress-track {
  margin: 12px 0 10px;
}

.todo-compact-empty {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 42px;
  padding: 8px 0 0;
}

.todo-compact-empty p {
  font-size: 12px;
  font-weight: 700;
}

.todo-item-row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-width: 0;
  border-top: 1px solid #f3f4f6;
  padding: 10px 0;
  background: #ffffff;
}

.todo-item-row:first-of-type {
  border-top: 0;
}

.todo-item-row.completed .todo-item-main h4 {
  color: #9ca3af;
  text-decoration: line-through;
}

.todo-item-check {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  font-size: 12px;
}

.todo-item-main h4 {
  margin: 0;
  overflow: hidden;
  color: #111827;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-item-main p {
  margin-top: 3px;
  overflow: hidden;
  font-size: 12px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 5px;
  font-size: 10px;
  font-weight: 800;
}

.todo-row-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  color: #6b7280;
  background: #ffffff;
}

.todo-row-icon.danger {
  color: #b84536;
}

.todo-routine-section {
  border-style: dashed;
}

.todo-action-sheet {
  display: grid;
  gap: 12px;
}

.todo-action-sheet-header span {
  display: block;
  margin-top: 3px;
  font-size: 12px;
  font-weight: 700;
}

.todo-action-buttons {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.todo-action-buttons .primary-action,
.todo-action-buttons .secondary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
}

.todomate-page .primary-action {
  color: #ffffff;
  background: #111827;
}

.todomate-page .secondary-action {
  border-color: #e5e7eb;
  color: #111827;
  background: #f9fafb;
}

.todomate-page .todo-editor {
  margin-top: 0;
  border-top: 1px solid #e5e7eb;
}

.todomate-page .ui-input {
  border-color: #e5e7eb;
  color: #111827;
  background: #ffffff;
}

.todomate-page .color-choice input:checked + .color-swatch {
  box-shadow: 0 0 0 2px #111827;
}

@media (min-width: 760px) {
  .todomate-page {
    padding-top: 18px;
  }
  .todomate-header {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
  }
  .todomate-profile-switch {
    grid-column: 1/-1;
  }
  .todomate-layout {
    grid-template-columns: minmax(270px, 320px) minmax(0, 1fr);
    gap: 16px;
  }
  .todo-action-sheet {
    grid-column: 1/-1;
  }
}
@media (min-width: 1180px) {
  .todomate-layout {
    grid-template-columns: minmax(300px, 360px) minmax(380px, 1fr) minmax(300px, 360px);
    gap: 18px;
  }
  .todo-action-sheet {
    position: sticky;
    top: 16px;
    grid-column: auto;
  }
}
@media (max-width: 520px) {
  .todomate-page {
    gap: 14px;
    padding: 10px 0 18px;
  }
  .todomate-header-main {
    grid-template-columns: 58px minmax(0, 1fr);
    gap: 12px;
  }
  .todomate-avatar {
    width: 58px;
    height: 58px;
    font-size: 20px;
  }
  .todomate-header-main h2 {
    font-size: 25px;
  }
  .todomate-calendar,
  .todo-goal-section,
  .todo-routine-section,
  .todo-action-sheet {
    padding: 12px;
  }
  .todomate-date-grid {
    gap: 4px 2px;
  }
  .todomate-date-cell {
    min-height: 54px;
    padding: 4px 1px;
  }
  .todomate-marker-stack {
    grid-template-columns: repeat(2, 8px);
    grid-template-rows: repeat(2, 8px);
    width: 17px;
    height: 17px;
  }
  .todomate-day-number {
    width: 22px;
    height: 22px;
    font-size: 12px;
  }
  .todo-item-row {
    grid-template-columns: 28px minmax(0, 1fr);
  }
  .todo-item-actions {
    grid-column: 2;
    justify-content: flex-start;
  }
}
/* Stage 30 TodoMate mobile home */
.ownit-page.todo-workspace-active {
  --ownit-color-app-bg: #ffffff;
  --ownit-color-calendar-bg: #ffffff;
  --surface: #ffffff;
  --surface-soft: #f7f7f8;
  --surface-tint: #f1f2f4;
  --line: #e6e7ea;
  --line-strong: #d6d9de;
  --text: #111111;
  --muted: #71757d;
  --soft-text: #a5abb3;
  --ink: #111111;
  --green: #111111;
  --weekend-saturday: #0a7cff;
  --weekend-saturday-muted: #8cbfff;
  --weekend-sunday: #ff454f;
  --weekend-sunday-muted: #ff9ca2;
  background: #ffffff;
}

.todo-workspace-active .shell-header,
.todo-workspace-active .status-strip.compact {
  display: none;
}

.todo-workspace-active .shell-main,
.todo-workspace-active .shell-content {
  background: #ffffff;
}

.todo-workspace-active .shell-content {
  display: block;
  overflow: visible;
  padding: 0 20px calc(88px + env(safe-area-inset-bottom));
}

.todo-workspace-active .bottom-tabs {
  border-top-color: #f0f1f3;
  background: rgba(255, 255, 255, 0.98);
}

.todo-workspace-active .tab-button {
  min-height: 58px;
  border-radius: 8px;
  color: #9aa1aa;
}

.todo-workspace-active .tab-button.active {
  color: #111111;
  background: transparent;
}

.todo-workspace-active .tab-button.active .tab-icon {
  color: #111111;
}

.todomate-page {
  gap: 26px;
  min-height: auto;
  padding: calc(env(safe-area-inset-top) + 18px) 0 0;
  color: #111111;
  background: #ffffff;
}

.todomate-header {
  gap: 28px;
}

.todomate-topbar,
.todomate-identity-row,
.todomate-month-actions,
.todo-feed-summary,
.todo-feed-actions {
  display: flex;
  align-items: center;
  min-width: 0;
}

.todomate-topbar {
  justify-content: space-between;
  gap: 14px;
}

.todomate-profile-switch {
  gap: 10px;
}

.todomate-profile-pill,
.todomate-profile-icon {
  border: 0;
  box-shadow: none;
}

.todomate-profile-pill {
  min-height: 50px;
  padding: 5px 18px 5px 6px;
  font-size: 18px;
  font-weight: 800;
}

.todomate-profile-pill.active {
  color: #ffffff;
  background: #000000;
}

.todomate-profile-icon {
  width: 50px;
  height: 50px;
  color: #111111;
  background: #f2f3f5;
  font-size: 20px;
}

.todomate-mini-avatar {
  width: 38px;
  height: 38px;
  color: #ffffff;
  background: #e2e5e8;
  font-size: 14px;
}

.todomate-header-actions {
  gap: 6px;
}

.todomate-header-actions .icon-button {
  width: 36px;
  height: 36px;
  border: 0;
  color: #111111;
  background: #f7f7f8;
}

.todomate-identity-row {
  gap: 18px;
}

.todomate-avatar {
  width: 74px;
  height: 74px;
  color: #ffffff;
  background: #e2e5e8;
  font-size: 26px;
}

.todomate-kicker,
.todomate-date-copy {
  color: #70747c;
}

.todomate-kicker {
  font-size: 12px;
  font-weight: 700;
}

.todomate-identity-row h2 {
  margin: 4px 0 0;
  color: #111111;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.05;
}

.todomate-date-copy {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 700;
}

.todomate-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 30px;
}

.todomate-calendar-pane,
.todomate-goals {
  min-width: 0;
}

.todomate-calendar-pane {
  justify-self: center;
  width: 100%;
  max-width: 432px;
}

.todomate-calendar,
.todo-feed-summary,
.todo-goal-section,
.todo-routine-section,
.todo-action-sheet {
  border: 0;
  border-radius: 0;
  padding: 0;
  background: #ffffff;
  box-shadow: none;
}

.todomate-calendar-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.todomate-calendar-heading h3 {
  margin: 0;
  color: #111111;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
}

.todomate-month-actions {
  gap: 10px;
}

.todomate-month-actions .icon-button {
  width: 34px;
  height: 34px;
  border: 0;
  color: #111111;
  background: #f5f6f7;
}

.todomate-week-row {
  margin-top: 22px;
}

.todomate-week-row span {
  color: #34363a;
  font-size: 14px;
  font-weight: 700;
}

.todomate-week-row .saturday {
  color: #0a7cff;
}

.todomate-week-row .sunday {
  color: #ff454f;
}

.todomate-date-grid {
  gap: 9px 6px;
  margin-top: 14px;
}

.todomate-date-cell {
  gap: 2px;
  min-height: 58px;
  border: 0;
  border-radius: 999px;
  padding: 0 1px;
  color: #111111;
  background: transparent;
}

.todomate-date-cell:hover {
  border-color: transparent;
  background: #fafafa;
}

.todomate-date-cell.outside {
  color: #aab0b7;
  opacity: 0.58;
}

.todomate-date-cell.outside .todomate-marker-stack,
.todomate-date-cell.outside .todomate-day-number,
.todomate-date-cell.outside .todomate-date-count {
  visibility: hidden;
}

.todomate-date-cell.saturday:not(.selected) .todomate-day-number {
  color: #0a7cff;
}

.todomate-date-cell.sunday:not(.selected) .todomate-day-number {
  color: #ff454f;
}

.todomate-date-cell.today:not(.selected) .todomate-day-number {
  border: 1px solid #111111;
  color: #111111;
  background: #ffffff;
}

.todomate-date-cell.selected {
  border-color: transparent;
  background: transparent;
}

.todomate-date-cell.selected .todomate-day-number,
.todomate-date-cell.today.selected .todomate-day-number {
  border-color: #000000;
  color: #ffffff;
  background: #000000;
}

.todomate-marker-stack {
  grid-template-columns: repeat(2, 10px);
  grid-template-rows: repeat(2, 10px);
  gap: 1px;
  width: 21px;
  height: 21px;
  overflow: hidden;
}

.todomate-marker-slot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #d5dadd;
}

.todomate-day-number {
  width: 25px;
  height: 25px;
  color: #111111;
  font-size: 14px;
  font-weight: 700;
}

.todomate-date-count {
  min-height: 10px;
  color: #8b929a;
  font-size: 9px;
  font-weight: 800;
}

.todomate-goals {
  display: grid;
  gap: 22px;
  justify-self: center;
  width: 100%;
  max-width: 680px;
}

.todo-feed-summary {
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 10px 12px;
  padding-top: 2px;
}

.todo-feed-summary h3 {
  margin: 2px 0 0;
  color: #111111;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}

.todo-daily-metrics {
  flex: 1 0 100%;
  margin: -2px 0 0;
  color: #7b818a;
  font-size: 12px;
  font-weight: 800;
}

.todo-feed-actions {
  gap: 8px;
}

.todo-goal-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.todo-goal-pill {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  min-height: 38px;
  border: 1px solid #e8eaed;
  border-radius: 999px;
  padding: 3px;
  background: #f5f6f7;
}

.todo-goal-pill.add {
  gap: 7px;
  padding: 0 13px;
  color: #111111;
  font-size: 13px;
  font-weight: 800;
}

.goal-pill-main {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  min-height: 30px;
  border-radius: 999px;
  padding: 0 7px 0 8px;
  background: transparent;
}

.goal-pill-chip {
  flex: 0 0 10px;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  box-shadow: 0 0 0 2px #ffffff;
}

.goal-pill-name {
  overflow: hidden;
  max-width: 118px;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.goal-pill-count {
  display: grid;
  place-items: center;
  min-width: 19px;
  height: 19px;
  border-radius: 999px;
  padding: 0 5px;
  color: #7d838b;
  background: #ffffff;
  font-size: 10px;
  font-weight: 800;
}

.goal-pill-actions,
.todo-section-controls {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
}

.goal-pill-icon {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  color: #8b929a;
  background: #ffffff;
  font-size: 12px;
}

.goal-pill-icon.danger,
.todo-icon-action.danger {
  color: #b84536;
}

.todo-feed-actions .primary-action,
.todo-feed-actions .secondary-action,
.todo-compact-empty .secondary-action {
  min-height: 34px;
  border-radius: 999px;
  padding: 0 12px;
  font-size: 13px;
}

.todo-feed-summary .todo-progress-track {
  flex: 1 0 100%;
  height: 4px;
}

.todomate-section-eyebrow {
  margin: 0;
  color: #8b929a;
  font-size: 11px;
  font-weight: 800;
  text-transform: none;
}

.todo-goal-section,
.todo-routine-section {
  display: grid;
  gap: 8px;
  border-top: 1px dashed #e2e5e8;
  padding-top: 16px;
}

.todo-section-header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
}

.goal-color-dot {
  width: 5px;
  height: 36px;
  border-radius: 999px;
}

.goal-color-dot.neutral {
  background: #111111;
}

.todo-section-header h3 {
  margin: 0;
  overflow: hidden;
  color: #111111;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-section-header p {
  margin-top: 3px;
  color: #8b929a;
  font-size: 11px;
  font-weight: 700;
}

.todo-icon-action {
  width: 36px;
  height: 36px;
  border: 1px solid #e7e9ec;
  color: #111111;
  background: #f7f7f8;
}

.todo-progress-track {
  height: 4px;
  background: #e7e9ec;
}

.todo-progress-bar {
  background: #111111;
}

.todo-goal-section .todo-progress-track {
  margin: 0 0 2px;
}

.todo-compact-empty {
  min-height: 34px;
  padding: 0;
}

.todo-compact-empty p {
  color: #8b929a;
  font-size: 13px;
  font-weight: 700;
}

.todo-inline-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 34px 34px;
  gap: 7px;
  align-items: center;
  min-height: 42px;
  scroll-margin-block-end: 42dvh;
}

.todo-inline-input {
  width: 100%;
  min-width: 0;
  height: 40px;
  border: 1px solid #e3e6ea;
  border-radius: 999px;
  padding: 0 14px;
  color: #111111;
  background: #ffffff;
  font-size: 16px;
  font-weight: 700;
  outline: none;
}

.todo-inline-input:focus {
  border-color: #111111;
  box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.08);
}

.todo-inline-submit,
.todo-inline-cancel,
.todo-inline-add-affordance {
  display: inline-grid;
  place-items: center;
  min-width: 0;
  border-radius: 999px;
  color: #111111;
  background: #f7f7f8;
}

.todo-inline-submit,
.todo-inline-cancel {
  width: 34px;
  height: 34px;
}

.todo-inline-submit {
  color: #ffffff;
  background: #111111;
}

.todo-inline-add-affordance {
  grid-auto-flow: column;
  justify-content: start;
  gap: 7px;
  width: fit-content;
  min-height: 34px;
  padding: 0 11px;
  color: #8b929a;
  background: transparent;
  font-size: 13px;
  font-weight: 800;
}

.todo-item-row {
  grid-template-columns: 30px minmax(0, 1fr) auto;
  gap: 10px;
  border-top: 0;
  padding: 7px 0;
}

.todo-item-row.completed {
  opacity: 1;
}

.todo-item-row.completed .todo-item-main h4 {
  color: #626974;
  text-decoration-color: rgba(98, 105, 116, 0.58);
}

.todo-item-row.completed .todo-item-meta {
  color: #8b929a;
}

.todo-item-check {
  width: 30px;
  height: 30px;
  border-radius: 9px;
}

.todo-item-main h4 {
  color: #111111;
  font-size: 15px;
  font-weight: 700;
}

.todo-item-main p {
  color: #8b929a;
}

.todo-item-meta {
  color: #8b929a;
}

.todo-meta-goal {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  border-radius: 999px;
  padding: 3px 7px;
  background: #f3f4f5;
}

.todo-meta-dot {
  flex: 0 0 7px;
  width: 7px;
  height: 7px;
  border-radius: 999px;
}

.todo-meta-timer {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.todo-meta-reminder {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #d9468f;
}

.todo-meta-timer i,
.todo-meta-reminder i {
  font-size: 9px;
}

.todo-item-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 102px;
}

.todo-row-icon {
  width: 30px;
  height: 30px;
  border: 0;
  color: #8b929a;
  background: transparent;
}

.todo-row-icon:disabled {
  opacity: 0.32;
}

.todo-row-icon.timer {
  color: #111111;
  background: #f3f4f6;
}

.todo-row-icon.timer.running {
  color: #ffffff;
  background: #111111;
}

.todo-row-icon.timer.paused {
  color: #111111;
  background: #e7e9ec;
}

.todo-row-icon.timer-stop {
  color: #9a625a;
  background: #fff0ee;
}

.todo-timer-card.running,
.todo-timer-card.paused {
  border-color: #111111;
}

.todo-active-timer-readout {
  color: #111111;
  font-size: 28px;
  font-weight: 900;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.todo-timer-controls {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.todo-timer-controls .primary-action,
.todo-timer-controls .secondary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  min-height: 40px;
  padding: 0 10px;
  white-space: nowrap;
}

.todo-running-timer-bar {
  position: fixed;
  right: 14px;
  bottom: calc(14px + env(safe-area-inset-bottom));
  left: 14px;
  z-index: 42;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  min-height: 62px;
  border: 1px solid #111111;
  border-radius: 18px;
  padding: 9px 10px 9px 12px;
  color: #ffffff;
  background: #111111;
  box-shadow: 0 16px 38px rgba(17, 17, 17, 0.2);
}

.todo-running-timer-bar.paused {
  color: #111111;
  background: #ffffff;
}

.todo-running-timer-main {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}

.todo-running-dot {
  flex: 0 0 9px;
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: currentColor;
}

.todo-running-timer-main p,
.todo-running-timer-main strong {
  display: block;
  overflow: hidden;
  margin: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-running-timer-main p {
  opacity: 0.72;
  font-size: 11px;
  font-weight: 800;
}

.todo-running-timer-main strong {
  max-width: 42vw;
  font-size: 13px;
  font-weight: 900;
}

.todo-running-timer-time {
  font-size: 17px;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.todo-running-timer-actions {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.todo-running-timer-actions button {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  color: #111111;
  background: #ffffff;
}

.todo-running-timer-bar.paused .todo-running-timer-actions button {
  color: #ffffff;
  background: #111111;
}

.todo-action-sheet {
  display: grid;
  gap: 12px;
  border-top: 1px solid #e6e7ea;
  padding-top: 16px;
}

.todo-action-buttons {
  gap: 8px;
}

.todomate-page .primary-action {
  color: #ffffff;
  background: #111111;
}

.todomate-page .secondary-action {
  border: 1px solid #e7e9ec;
  color: #111111;
  background: #f7f7f8;
}

.todomate-page .todo-editor {
  border-top-color: #e6e7ea;
}

.todo-sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 48;
  background: rgba(17, 17, 17, 0.28);
}

.todo-create-backdrop {
  position: fixed;
  inset: 0;
  z-index: 58;
  background: rgba(17, 17, 17, 0.32);
}

.todo-create-sheet {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 59;
  display: grid;
  gap: 14px;
  max-height: min(88dvh, 780px);
  overflow: auto;
  border-radius: 18px 18px 0 0;
  padding: 14px 20px calc(18px + env(safe-area-inset-bottom));
  background: #ffffff;
  box-shadow: 0 -18px 44px rgba(17, 17, 17, 0.16);
}

.todo-create-handle {
  justify-self: center;
  width: 44px;
  height: 4px;
  border-radius: 999px;
  background: #d7dbe0;
}

.todo-create-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.todo-create-header > div {
  min-width: 0;
}

.todo-create-header h3 {
  margin: 2px 0 0;
  color: #111111;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
}

.todo-create-header span {
  display: block;
  margin-top: 4px;
  color: #7d858e;
  font-size: 12px;
  font-weight: 800;
}

.todo-create-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  border-radius: 999px;
  padding: 4px;
  background: #f3f4f5;
}

.todo-create-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  min-height: 36px;
  border-radius: 999px;
  color: #686f78;
  background: transparent;
  font-size: 13px;
  font-weight: 800;
}

.todo-create-tab.active {
  color: #ffffff;
  background: #111111;
}

.todo-create-context-row {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  min-width: 0;
}

.todo-create-context-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  min-height: 30px;
  border-radius: 999px;
  padding: 0 10px;
  color: #5f6368;
  background: #f6f7f8;
  font-size: 12px;
  font-weight: 800;
}

.todo-create-context-chip span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-create-context-dot {
  flex: 0 0 9px;
  width: 9px;
  height: 9px;
  border-radius: 999px;
}

.todo-create-form {
  display: grid;
  gap: 13px;
}

.todo-goal-sheet {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 49;
  display: grid;
  gap: 14px;
  max-height: min(82dvh, 720px);
  overflow: auto;
  border-radius: 18px 18px 0 0;
  padding: 16px 20px calc(18px + env(safe-area-inset-bottom));
  background: #ffffff;
  box-shadow: 0 -18px 44px rgba(17, 17, 17, 0.16);
}

.todo-goal-sheet::before {
  content: "";
  justify-self: center;
  width: 44px;
  height: 4px;
  border-radius: 999px;
  background: #d7dbe0;
}

.todo-goal-sheet-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.todo-goal-sheet-header > div {
  min-width: 0;
}

.todo-goal-sheet-header h3 {
  margin: 2px 0 0;
  color: #111111;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.todo-goal-sheet-header span {
  display: block;
  margin-top: 4px;
  color: #8b929a;
  font-size: 12px;
  font-weight: 700;
}

.todo-goal-form {
  display: grid;
  gap: 13px;
}

.todo-goal-collapse {
  margin-top: 2px;
}

.goal-palette-tabs {
  display: inline-flex;
  gap: 6px;
  width: fit-content;
  border-radius: 999px;
  padding: 4px;
  background: #f3f4f5;
}

.goal-palette-tab {
  min-height: 30px;
  border-radius: 999px;
  padding: 0 12px;
  color: #686f78;
  background: transparent;
  font-size: 12px;
  font-weight: 800;
}

.goal-palette-tab.active {
  color: #ffffff;
  background: #111111;
}

.goal-color-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 11px;
  margin-top: 10px;
}

.goal-color-button {
  display: grid;
  place-items: center;
  aspect-ratio: 1;
  min-width: 0;
  border-radius: 999px;
  background: #ffffff;
}

.goal-color-swatch {
  display: block;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  box-shadow: 0 0 0 1px rgba(17, 17, 17, 0.08);
}

.goal-color-button.active .goal-color-swatch {
  box-shadow: 0 0 0 3px #ffffff, 0 0 0 5px #111111;
}

.goal-form-preview {
  display: flex;
  min-width: 0;
}

.todo-category-delete-options {
  display: grid;
  gap: 8px;
}

.todo-category-delete-option {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: flex-start;
  gap: 10px;
  border: 1px solid #e6e7ea;
  border-radius: 8px;
  padding: 11px 12px;
  background: #ffffff;
  color: #111111;
}

.todo-category-delete-option input {
  margin-top: 3px;
}

.todo-category-delete-option strong,
.todo-category-delete-option small {
  display: block;
}

.todo-category-delete-option strong {
  font-size: 13px;
  font-weight: 700;
}

.todo-category-delete-option small {
  margin-top: 3px;
  color: #7d858e;
  font-size: 12px;
  line-height: 1.45;
}

.todo-category-delete-option.danger strong {
  color: #b84536;
}

.todo-category-delete-option.disabled {
  color: #8b929a;
  background: #f7f8f9;
}

.todo-category-delete-help {
  margin: -2px 2px 2px 28px;
  color: #7d858e;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.todo-category-delete-move {
  margin: 0;
}

.todo-inline-error {
  grid-column: 1/-1;
  margin: -2px 0 0;
}

.goal-preview-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 38px;
  border-radius: 999px;
  padding: 0 13px;
  background: #f3f4f5;
  font-size: 13px;
  font-weight: 800;
}

.routine-sheet {
  gap: 15px;
}

.routine-form {
  gap: 14px;
}

.routine-choice-row {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}

.routine-date-grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 6px;
}

.routine-choice-chip {
  display: grid;
  place-items: center;
  min-width: 0;
  min-height: 34px;
  border: 1px solid #e7e9ec;
  border-radius: 999px;
  color: #686f78;
  background: #f7f7f8;
  font-size: 12px;
  font-weight: 800;
}

.routine-choice-chip.active {
  border-color: #111111;
  color: #ffffff;
  background: #111111;
}

.routine-flag-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.todo-item-row.routine {
  border-top: 1px solid #eef0f2;
}

.todo-item-row.routine .todo-item-check {
  border-style: dashed;
}

.todo-card.routine-card {
  border-style: dashed;
}

.todo-card.routine-card .card-body h3::before {
  content: "루틴";
  display: inline-flex;
  align-items: center;
  min-height: 18px;
  margin-right: 7px;
  border-radius: 999px;
  padding: 0 6px;
  color: #ffffff;
  background: #111111;
  font-size: 10px;
  font-weight: 800;
  vertical-align: 2px;
}

@media (max-width: 420px) {
  .todo-workspace-active .shell-content {
    padding-right: 20px;
    padding-left: 20px;
  }
  .todomate-page {
    gap: 24px;
    padding-top: calc(env(safe-area-inset-top) + 14px);
  }
  .todomate-header {
    gap: 26px;
  }
  .todomate-identity-row {
    gap: 16px;
  }
  .todomate-avatar {
    width: 68px;
    height: 68px;
  }
  .todomate-calendar-heading h3 {
    font-size: 26px;
  }
  .todomate-week-row {
    margin-top: 20px;
  }
  .todomate-week-row span {
    font-size: 13px;
  }
  .todomate-date-grid {
    gap: 8px 4px;
  }
  .todomate-date-cell {
    min-height: 56px;
  }
  .todomate-marker-stack {
    grid-template-columns: repeat(2, 10px);
    grid-template-rows: repeat(2, 10px);
    width: 21px;
    height: 21px;
  }
  .todomate-day-number {
    width: 25px;
    height: 25px;
    font-size: 14px;
  }
  .todo-item-row {
    grid-template-columns: 30px minmax(0, 1fr);
    align-items: start;
  }
  .todo-item-actions {
    grid-column: 2;
    justify-content: flex-start;
  }
  .routine-date-grid {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
  .routine-choice-chip {
    min-height: 32px;
    font-size: 11px;
  }
}
@media (min-width: 760px) {
  .todo-workspace-active .shell-content {
    padding: 0 24px 32px;
  }
  .todo-sheet-backdrop {
    display: none;
  }
  .todo-create-sheet {
    top: 50%;
    right: auto;
    bottom: auto;
    left: 50%;
    width: min(640px, 100vw - 48px);
    max-height: min(84dvh, 780px);
    border: 1px solid #e6e7ea;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 22px 70px rgba(17, 17, 17, 0.18);
    transform: translate(-50%, -50%);
  }
  .todo-create-handle {
    display: none;
  }
  .todo-goal-sheet {
    position: sticky;
    top: 24px;
    grid-column: 1/-1;
    align-self: start;
    max-height: none;
    border: 1px solid #e6e7ea;
    border-radius: 8px;
    padding: 18px;
    box-shadow: none;
  }
  .todo-goal-sheet::before {
    display: none;
  }
  .goal-color-grid {
    grid-template-columns: repeat(8, minmax(0, 1fr));
  }
  .todomate-calendar-pane {
    justify-self: stretch;
    max-width: 390px;
  }
  .todomate-layout {
    grid-template-columns: minmax(320px, 380px) minmax(0, 1fr);
    gap: 28px;
  }
  .todo-action-sheet {
    grid-column: 1/-1;
  }
}
@media (min-width: 1180px) {
  .todomate-layout {
    grid-template-columns: minmax(330px, 390px) minmax(420px, 1fr) minmax(300px, 360px);
  }
  .todo-action-sheet {
    position: sticky;
    top: 24px;
    grid-column: auto;
  }
  .todo-goal-sheet {
    grid-column: auto;
  }
}
.todo-item-row {
  cursor: pointer;
}

.todo-item-row:focus-visible {
  outline: 2px solid #111111;
  outline-offset: 3px;
}

.todo-action-sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 68;
  background: rgba(17, 17, 17, 0.34);
}

.todo-action-sheet-panel {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 69;
  display: grid;
  gap: 14px;
  max-height: min(88dvh, 760px);
  overflow: auto;
  overscroll-behavior: contain;
  border-radius: 18px 18px 0 0;
  padding: 12px 20px calc(18px + env(safe-area-inset-bottom));
  color: #111111;
  background: #ffffff;
  box-shadow: 0 -22px 54px rgba(17, 17, 17, 0.18);
}

.todo-action-sheet-handle {
  justify-self: center;
  width: 42px;
  height: 4px;
  border-radius: 999px;
  background: #d8dce1;
}

.todo-action-panel-header,
.todo-action-primary-grid,
.todo-action-option,
.todo-photo-picker,
.todo-delete-confirm {
  min-width: 0;
}

.todo-action-panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.todo-action-panel-header h3 {
  margin: 2px 0 4px;
  overflow: hidden;
  color: #111111;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-action-panel-header span {
  display: block;
  overflow: hidden;
  color: #8b929a;
  font-size: 12px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-action-message {
  margin: 0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.45;
}

.todo-action-message.success {
  color: #166534;
  background: #edf8f0;
}

.todo-action-message.error {
  color: #b42318;
  background: #fff1f0;
}

.todo-action-menu,
.todo-action-mode,
.todo-action-form,
.todo-action-option-list,
.todo-photo-proof {
  display: grid;
  gap: 12px;
}

.todo-action-primary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.todo-action-tile {
  display: grid;
  justify-items: center;
  gap: 7px;
  min-height: 72px;
  border-radius: 8px;
  padding: 12px 8px;
  color: #111111;
  background: #f6f7f8;
  font-size: 13px;
}

.todo-action-tile.danger {
  color: #d92d20;
}

.todo-action-tile-icon,
.todo-action-option-icon,
.todo-delete-icon {
  display: grid;
  place-items: center;
  border-radius: 999px;
}

.todo-action-tile-icon {
  width: 28px;
  height: 28px;
  background: #ffffff;
}

.todo-action-tile-icon.edit {
  color: #2563eb;
}

.todo-action-tile-icon.delete {
  color: #ef4444;
}

.todo-action-option-list {
  gap: 2px;
}

.todo-action-option {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) 16px;
  align-items: center;
  gap: 10px;
  min-height: 54px;
  border-radius: 8px;
  padding: 8px 4px;
  color: #111111;
  background: #ffffff;
  text-align: left;
}

.todo-action-option:disabled {
  opacity: 0.34;
}

.todo-action-option-icon {
  width: 28px;
  height: 28px;
  color: #ffffff;
  font-size: 13px;
}

.todo-action-option.yellow .todo-action-option-icon {
  background: #facc15;
}

.todo-action-option.pink .todo-action-option-icon {
  background: #ec4899;
}

.todo-action-option.green .todo-action-option-icon {
  background: #10b981;
}

.todo-action-option.orange .todo-action-option-icon {
  background: #f97316;
}

.todo-action-option.indigo .todo-action-option-icon {
  background: #6366f1;
}

.todo-action-option.blue .todo-action-option-icon {
  background: #3b82f6;
}

.todo-action-option-copy {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.todo-action-option-copy strong,
.todo-action-option-copy small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-action-option-copy strong {
  color: #111111;
  font-size: 14px;
  font-weight: 800;
}

.todo-action-option-copy small {
  color: #8b929a;
  font-size: 11px;
  font-weight: 700;
}

.todo-action-help {
  margin: 0;
  color: #7b818a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
}

.reminder-option-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.reminder-option-button {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  align-items: center;
  gap: 9px;
  min-width: 0;
  min-height: 58px;
  border: 1px solid #edf0f3;
  border-radius: 8px;
  padding: 9px;
  color: #111111;
  background: #f8f9fa;
  text-align: left;
}

.reminder-option-button.active {
  border-color: #111111;
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(17, 17, 17, 0.06);
}

.reminder-option-button:disabled {
  opacity: 0.36;
}

.reminder-option-button i {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  color: #ffffff;
  background: #ec4899;
  font-size: 12px;
}

.reminder-option-button span {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.reminder-option-button strong,
.reminder-option-button small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reminder-option-button strong {
  font-size: 13px;
  font-weight: 850;
}

.reminder-option-button small {
  color: #8b929a;
  font-size: 11px;
  font-weight: 700;
}

.notification-permission-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-radius: 8px;
  padding: 10px 12px;
  background: #f6f7f8;
}

.notification-permission-card > div {
  display: grid;
  gap: 2px;
}

.notification-permission-card strong {
  color: #111111;
  font-size: 13px;
  font-weight: 850;
}

.notification-permission-card span {
  color: #8b929a;
  font-size: 12px;
  font-weight: 700;
}

.todo-action-sheet-panel .form-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.todo-action-sheet-panel .danger-action {
  width: auto;
  margin-top: 0;
}

.todo-photo-picker {
  position: relative;
  display: grid;
  border: 1px dashed #d8dde3;
  border-radius: 8px;
  padding: 16px;
  background: #f8fafc;
  cursor: pointer;
}

.todo-photo-picker input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.todo-photo-picker span,
.todo-photo-empty {
  display: grid;
  justify-items: center;
  gap: 6px;
  color: #6b7280;
  text-align: center;
}

.todo-photo-picker i,
.todo-photo-empty i {
  color: #10b981;
  font-size: 22px;
}

.todo-photo-picker strong {
  color: #111111;
  font-size: 14px;
  font-weight: 800;
}

.todo-photo-picker small,
.todo-photo-empty span {
  font-size: 12px;
  font-weight: 700;
}

.todo-photo-preview {
  width: 100%;
  max-height: 220px;
  border-radius: 8px;
  object-fit: cover;
  background: #f3f4f6;
}

.todo-photo-empty {
  min-height: 120px;
  border-radius: 8px;
  background: #f6f7f8;
}

.todo-delete-confirm {
  display: grid;
  justify-items: center;
  gap: 8px;
  border-radius: 8px;
  padding: 18px;
  background: #fff7f6;
  text-align: center;
}

.todo-delete-icon {
  width: 42px;
  height: 42px;
  color: #d92d20;
  background: #ffe5e1;
}

.todo-delete-confirm h4,
.todo-delete-confirm p {
  margin: 0;
}

.todo-delete-confirm h4 {
  max-width: 100%;
  overflow: hidden;
  color: #111111;
  font-size: 16px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-delete-confirm p {
  color: #7b818a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
}

@media (min-width: 760px) {
  .todo-action-sheet-backdrop {
    background: rgba(17, 17, 17, 0.18);
  }
  .todo-action-sheet-panel {
    top: 28px;
    right: 28px;
    bottom: auto;
    left: auto;
    width: min(390px, 100vw - 56px);
    max-height: calc(100dvh - 56px);
    border: 1px solid #e6e7ea;
    border-radius: 8px;
    padding: 18px;
    box-shadow: 0 20px 70px rgba(17, 17, 17, 0.18);
  }
  .todo-action-sheet-handle {
    display: none;
  }
}
/* Stage 36 TodoMate tablet and desktop workspace */
.ownit-page.todo-workspace-active {
  background: #ffffff !important;
}

.todo-workspace-active .app-shell,
.todo-workspace-active .shell-main,
.todo-workspace-active .shell-content,
.todo-workspace-active .todomate-page {
  background: #ffffff !important;
}

.todo-support-panel {
  display: grid;
  order: 2;
  gap: 12px;
  min-width: 0;
}

.todomate-calendar-pane {
  order: 1;
}

.todomate-goals {
  order: 3;
}

.todo-action-sheet {
  order: 4;
}

.todo-support-card {
  display: grid;
  gap: 12px;
  min-width: 0;
  border: 1px solid #e6e7ea;
  border-radius: 8px;
  padding: 14px;
  background: #ffffff;
}

.todo-timer-card,
.todo-quick-card {
  display: none;
}

.todo-daily-record-card {
  scroll-margin-bottom: calc(140px + env(safe-area-inset-bottom));
}

.todo-support-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.todo-support-header > div {
  min-width: 0;
}

.todo-support-tools {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 7px;
  min-width: 0;
}

.todo-receipt-entry {
  min-height: 36px;
  border: 1px solid rgba(216, 208, 194, 0.86);
  padding: 0 11px;
  font-size: 12px;
  white-space: nowrap;
}

.todo-support-header .todo-receipt-entry i {
  display: inline-block;
  flex: 0 0 auto;
  width: auto;
  height: auto;
  border-radius: 0;
  color: currentColor;
  background: transparent;
}

.todo-support-header h3 {
  margin: 2px 0 0;
  overflow-wrap: anywhere;
  color: #111111;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.25;
}

.todo-support-header i {
  display: grid;
  place-items: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  color: #111111;
  background: #f3f4f6;
}

.todo-support-emoji {
  display: grid;
  place-items: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border: 1px solid #e6e7ea;
  border-radius: 999px;
  background: #f7f7f8;
  font-size: 22px;
  line-height: 1;
}

.todo-support-stat-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.todo-support-stat {
  min-width: 0;
  border-radius: 8px;
  padding: 10px 8px;
  background: #f7f7f8;
}

.todo-support-stat span,
.todo-support-note {
  margin: 0;
  color: #777d86;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.todo-support-stat strong {
  display: block;
  margin-top: 3px;
  color: #111111;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.18;
  overflow-wrap: anywhere;
}

.todo-daily-stat-list,
.todo-daily-record-form {
  display: grid;
  gap: 10px;
}

.todo-daily-stat-list {
  border-top: 1px dashed #e1e4e8;
  padding-top: 10px;
}

.todo-daily-stat-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 7px 10px;
  align-items: center;
}

.todo-daily-stat-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.todo-daily-stat-main > div {
  min-width: 0;
}

.todo-daily-stat-main strong,
.todo-daily-stat-main small {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-daily-stat-main strong {
  color: #111111;
  font-size: 13px;
  font-weight: 900;
}

.todo-daily-stat-main small,
.todo-daily-stat-time {
  color: #777d86;
  font-size: 11px;
  font-weight: 800;
}

.todo-daily-stat-time {
  white-space: nowrap;
}

.todo-daily-stat-row .todo-progress-track {
  grid-column: 1/-1;
  height: 3px;
}

.todo-daily-record-form {
  border-top: 1px dashed #e1e4e8;
  padding-top: 12px;
}

.daily-emoji-picker {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
}

.daily-emoji-choice {
  display: grid;
  place-items: center;
  min-width: 0;
  min-height: 38px;
  border: 1px solid #e6e7ea;
  border-radius: 999px;
  color: #111111;
  background: #f7f7f8;
  font-size: 19px;
  line-height: 1;
}

.daily-emoji-choice.active {
  border-color: #111111;
  background: #ffffff;
  box-shadow: inset 0 0 0 2px #111111;
}

.todo-daily-diary-input {
  min-height: 142px;
  max-height: min(38dvh, 320px);
  padding-top: 12px;
  padding-bottom: 12px;
  line-height: 1.55;
  resize: vertical;
  scroll-margin-bottom: calc(140px + env(safe-area-inset-bottom));
}

.todo-ai-draft-panel {
  display: grid;
  gap: 10px;
  min-width: 0;
  border: 1px solid #e6e7ea;
  border-radius: 8px;
  padding: 12px;
  background: #ffffff;
}

.todo-ai-draft-header,
.todo-ai-draft-tools,
.todo-ai-tomorrow-row,
.todo-ai-tomorrow-actions {
  display: flex;
  align-items: center;
  min-width: 0;
}

.todo-ai-draft-header {
  justify-content: space-between;
  gap: 10px;
}

.todo-ai-draft-header > div:first-child {
  min-width: 0;
}

.todo-ai-draft-header h4 {
  margin: 1px 0 0;
  color: #111111;
  font-size: 14px;
  font-weight: 900;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.todo-ai-draft-tools {
  justify-content: flex-end;
  gap: 6px;
}

.todo-ai-provider {
  max-width: 112px;
  overflow: hidden;
  border: 1px solid #e6e7ea;
  border-radius: 999px;
  padding: 5px 8px;
  color: #777d86;
  background: #f7f7f8;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-ai-tab-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  border-radius: 8px;
  padding: 3px;
  background: #f3f4f6;
}

.todo-ai-tab-row button {
  min-width: 0;
  min-height: 32px;
  border: 0;
  border-radius: 6px;
  color: #777d86;
  background: transparent;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.2;
}

.todo-ai-tab-row button.active {
  color: #111111;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(17, 17, 17, 0.08);
}

.todo-ai-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  border-radius: 8px;
  padding: 10px;
  color: #4b5563;
  background: #f7f7f8;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
}

.todo-ai-loading i {
  color: #111111;
}

.todo-ai-result-field {
  gap: 6px;
}

.todo-ai-diary-textarea {
  min-height: 128px;
  max-height: 260px;
  padding-top: 11px;
  padding-bottom: 11px;
  line-height: 1.55;
  resize: vertical;
}

.todo-ai-apply-button {
  width: 100%;
  justify-content: center;
}

.todo-ai-tomorrow-list {
  display: grid;
  gap: 7px;
  min-width: 0;
}

.todo-ai-tomorrow-row {
  gap: 7px;
}

.todo-ai-todo-input {
  min-width: 0;
  flex: 1 1 auto;
}

.todo-ai-tomorrow-actions {
  justify-content: stretch;
  gap: 8px;
}

.todo-ai-tomorrow-actions .primary-action,
.todo-ai-tomorrow-actions .secondary-action {
  flex: 1 1 0;
  justify-content: center;
  min-width: 0;
  min-height: 38px;
  padding: 0 10px;
}

.todo-ai-apply-button span,
.todo-ai-tomorrow-actions span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-daily-record-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.todo-daily-record-actions .primary-action,
.todo-daily-record-actions .secondary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  min-height: 40px;
  padding: 0 10px;
  white-space: nowrap;
}

.todo-quick-action-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.todo-quick-action-grid .primary-action,
.todo-quick-action-grid .secondary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  min-height: 42px;
  padding: 0 10px;
  white-space: nowrap;
}

.todo-item-main h4,
.todo-item-main p,
.todo-section-header h3 {
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  overflow-wrap: anywhere;
}

.todo-item-main p {
  line-height: 1.45;
}

.todomate-profile-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: auto;
  min-width: 108px;
  height: 50px;
  padding: 0 13px;
}

.todomate-profile-icon span {
  color: inherit;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
}

.todomate-profile-icon.active {
  color: #ffffff;
  background: #111111;
}

.mate-layout {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.mate-list-panel,
.mate-feed-panel {
  display: grid;
  gap: 12px;
  min-width: 0;
  border: 1px solid #e6e7ea;
  border-radius: 8px;
  padding: 14px;
  background: #ffffff;
}

.mate-panel-header,
.mate-name-row,
.mate-mini-stats,
.mate-feed-card-header,
.mate-feed-kind,
.mate-reaction-row {
  display: flex;
  align-items: center;
  min-width: 0;
}

.mate-panel-header {
  justify-content: space-between;
  gap: 12px;
}

.mate-panel-header h3 {
  margin: 2px 0 0;
  color: #111111;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.mate-panel-header span:not(.mate-count-badge) {
  display: block;
  margin-top: 4px;
  color: #777d86;
  font-size: 12px;
  font-weight: 800;
}

.mate-count-badge,
.mate-admin-badge,
.mate-visibility-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  border-radius: 999px;
  padding: 0 9px;
  color: #777d86;
  background: #f3f4f6;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
  white-space: nowrap;
}

.mate-list,
.mate-feed-list {
  display: grid;
  gap: 10px;
  min-width: 0;
}

.mate-card {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-width: 0;
  border: 1px solid #eceef0;
  border-radius: 8px;
  padding: 10px;
  background: #fbfbfc;
}

.mate-card.following {
  border-color: #d7dbe0;
  background: #ffffff;
}

.mate-avatar {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 900;
  line-height: 1;
}

.mate-avatar.small {
  width: 36px;
  height: 36px;
  font-size: 13px;
}

.mate-card-main {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.mate-name-row {
  gap: 6px;
}

.mate-card-main strong,
.mate-feed-card-header strong {
  overflow: hidden;
  color: #111111;
  font-size: 14px;
  font-weight: 900;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mate-card-main > span,
.mate-feed-card-header span {
  overflow: hidden;
  color: #777d86;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mate-card-main p,
.mate-feed-card-body p,
.mate-feed-privacy-note,
.mate-empty-state p {
  margin: 0;
  color: #777d86;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.mate-mini-stats {
  flex-wrap: wrap;
  gap: 5px;
}

.mate-mini-stats span {
  border-radius: 999px;
  padding: 4px 7px;
  color: #676d75;
  background: #f1f2f4;
  font-size: 11px;
  font-weight: 900;
}

.mate-follow-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-width: 82px;
  min-height: 36px;
  border: 1px solid #111111;
  border-radius: 999px;
  color: #ffffff;
  background: #111111;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
}

.mate-follow-button.following {
  color: #111111;
  background: #ffffff;
}

.mate-feed-card {
  display: grid;
  gap: 12px;
  min-width: 0;
  border: 1px solid #e6e7ea;
  border-radius: 8px;
  padding: 13px;
  background: #ffffff;
}

.mate-feed-card.private-feed {
  background: #fbfaf9;
}

.mate-feed-card-header {
  justify-content: space-between;
  gap: 10px;
}

.mate-feed-card-header > div:nth-child(2) {
  display: grid;
  gap: 3px;
  min-width: 0;
  margin-right: auto;
}

.mate-visibility-badge.private {
  color: #9a625a;
  background: #fff0ee;
}

.mate-feed-card-body {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.mate-feed-kind {
  gap: 6px;
  color: #777d86;
  font-size: 12px;
  font-weight: 900;
}

.mate-feed-dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 999px;
}

.mate-feed-card-body h4 {
  margin: 0;
  color: #111111;
  font-size: 17px;
  font-weight: 900;
  line-height: 1.28;
  overflow-wrap: anywhere;
}

.mate-category-chip {
  width: fit-content;
  border-radius: 999px;
  padding: 5px 8px;
  background: #f3f4f6;
  font-size: 12px;
  font-weight: 900;
}

.mate-reaction-row {
  flex-wrap: wrap;
  gap: 7px;
  padding-top: 2px;
}

.mate-reaction-button,
.mate-reaction-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  border: 1px solid #e6e7ea;
  border-radius: 999px;
  background: #ffffff;
  font-weight: 900;
}

.mate-reaction-button {
  gap: 5px;
  min-width: 54px;
  padding: 0 10px;
}

.mate-reaction-button span {
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", OwnitNanumGothic, sans-serif;
  font-size: 15px;
  line-height: 1;
}

.mate-reaction-button strong {
  color: #111111;
  font-size: 12px;
  line-height: 1;
}

.mate-reaction-button.active {
  border-color: #111111;
  background: #f3f4f6;
}

.mate-reaction-clear {
  padding: 0 12px;
  color: #777d86;
  font-size: 12px;
}

.mate-empty-state {
  display: grid;
  place-items: center;
  gap: 10px;
  min-height: 180px;
  border: 1px dashed #d7dbe0;
  border-radius: 8px;
  padding: 18px;
  text-align: center;
}

.mate-empty-state i {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  color: #777d86;
  background: #f3f4f6;
}

@media (min-width: 760px) {
  .todo-workspace-active .app-shell {
    display: flex;
    max-width: none;
    min-height: 100dvh;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }
  .todo-workspace-active .shell-sidebar {
    display: none;
  }
  .todo-workspace-active .bottom-tabs {
    display: grid;
  }
  .todo-workspace-active .shell-main {
    width: 100%;
    min-height: 100dvh;
    border: 0;
  }
  .todo-workspace-active .shell-content {
    width: 100%;
    max-width: 1120px;
    margin: 0 auto;
    padding: 22px 28px 36px;
  }
  .todo-workspace-active .todomate-page {
    gap: 22px;
    padding-top: 0;
  }
  .todomate-header {
    gap: 18px;
  }
  .todomate-identity-row {
    gap: 14px;
  }
  .todomate-avatar {
    width: 62px;
    height: 62px;
    font-size: 22px;
  }
  .todomate-identity-row h2 {
    font-size: 26px;
  }
  .todomate-layout {
    grid-template-columns: minmax(300px, 342px) minmax(0, 1fr);
    gap: 20px;
    align-items: start;
  }
  .todomate-calendar-pane {
    max-width: 342px;
  }
  .todomate-calendar-heading h3 {
    font-size: 24px;
  }
  .todomate-month-actions .icon-button,
  .todomate-header-actions .icon-button,
  .todo-icon-action,
  .goal-pill-icon,
  .todo-row-icon {
    min-width: 44px;
    width: 44px;
    min-height: 44px;
    height: 44px;
  }
  .todomate-week-row {
    margin-top: 18px;
  }
  .todomate-date-grid {
    gap: 7px 5px;
    max-width: 342px;
  }
  .todomate-date-cell {
    min-height: 50px;
    max-height: 56px;
    padding: 2px 1px;
  }
  .todo-feed-summary {
    align-items: flex-start;
  }
  .todo-feed-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .todo-feed-actions .primary-action,
  .todo-feed-actions .secondary-action,
  .todo-compact-empty .secondary-action {
    min-height: 44px;
    border-radius: 999px;
  }
  .todo-goal-pills {
    gap: 9px;
  }
  .todo-goal-pill {
    min-height: 44px;
  }
  .goal-pill-main {
    min-height: 36px;
  }
  .todo-inline-input {
    height: 44px;
  }
  .todo-inline-submit,
  .todo-inline-cancel {
    width: 44px;
    height: 44px;
  }
  .todo-item-row {
    grid-template-columns: 32px minmax(0, 1fr) auto;
    gap: 11px;
    align-items: start;
    padding: 9px 0;
  }
  .todo-item-check {
    width: 32px;
    height: 32px;
  }
  .todo-item-actions {
    flex-wrap: wrap;
    max-width: 96px;
  }
  .todo-support-panel {
    display: grid;
    grid-column: 1/-1;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }
  .todo-timer-card,
  .todo-quick-card {
    display: grid;
  }
  .todo-running-timer-bar {
    display: none;
  }
  .todo-action-sheet {
    grid-column: 1/-1;
  }
  .todo-action-buttons .primary-action,
  .todo-action-buttons .secondary-action {
    min-height: 44px;
  }
  .mate-layout {
    grid-template-columns: minmax(280px, 330px) minmax(0, 1fr);
    align-items: start;
    gap: 18px;
  }
  .mate-list-panel,
  .mate-feed-panel {
    padding: 16px;
  }
}
@media (min-width: 1180px) and (max-width: 1319px) {
  .todo-workspace-active .app-shell {
    display: grid;
    grid-template-columns: 224px minmax(0, 1fr);
  }
  .todo-workspace-active .shell-sidebar {
    display: flex;
  }
  .todo-workspace-active .bottom-tabs {
    display: none;
  }
  .todo-workspace-active .shell-content {
    max-width: 980px;
    padding-right: 28px;
    padding-left: 28px;
  }
  .todomate-layout {
    grid-template-columns: minmax(300px, 352px) minmax(0, 1fr);
    gap: 22px;
  }
  .todomate-calendar-pane,
  .todomate-date-grid {
    max-width: 352px;
  }
}
@media (min-width: 1320px) {
  .todo-workspace-active .app-shell {
    display: grid;
    grid-template-columns: 224px minmax(0, 1fr);
  }
  .todo-workspace-active .shell-sidebar {
    display: flex;
  }
  .todo-workspace-active .bottom-tabs {
    display: none;
  }
  .todo-workspace-active .shell-content {
    max-width: 1360px;
    padding: 24px 34px 40px;
  }
  .todo-workspace-active .todomate-page {
    gap: 20px;
  }
  .todomate-header {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
  }
  .todomate-layout {
    grid-template-columns: minmax(320px, 368px) minmax(430px, 1fr) minmax(286px, 330px);
    gap: 24px;
  }
  .todomate-calendar-pane {
    order: 1;
  }
  .todomate-goals {
    order: 2;
  }
  .todo-support-panel {
    order: 3;
  }
  .todo-action-sheet {
    order: 4;
  }
  .mate-layout {
    grid-template-columns: minmax(300px, 350px) minmax(0, 760px);
    justify-content: center;
    gap: 24px;
  }
  .todomate-calendar-pane,
  .todomate-date-grid {
    max-width: 368px;
  }
  .todomate-calendar {
    width: 100%;
    max-width: 368px;
  }
  .todomate-date-cell {
    max-height: 52px;
  }
  .todo-support-panel {
    position: sticky;
    top: 24px;
    grid-column: auto;
    grid-template-columns: minmax(0, 1fr);
    align-self: start;
    gap: 12px;
  }
  .todo-action-sheet {
    position: sticky;
    top: 24px;
    grid-column: 3;
    align-self: start;
  }
}
@media (min-width: 1680px) {
  .todo-workspace-active .shell-content {
    max-width: 1420px;
  }
  .todomate-layout {
    grid-template-columns: minmax(340px, 384px) minmax(500px, 1fr) minmax(300px, 340px);
  }
  .todomate-calendar-pane,
  .todomate-calendar,
  .todomate-date-grid {
    max-width: 384px;
  }
}
/* Stage 41 TodoMate visual QA */
.ownit-page.todo-workspace-active {
  --todo-bg: #ffffff;
  --todo-fill: #f7f7f8;
  --todo-fill-hover: #f1f2f4;
  --todo-fill-active: #e9ecef;
  --todo-line: #e6e7ea;
  --todo-line-strong: #d6d9de;
  --todo-text: #111111;
  --todo-muted: #777d86;
  --todo-soft: #9aa1aa;
  --todo-marker: #d5dadd;
  --todo-focus: rgba(17, 17, 17, 0.12);
  --ownit-color-app-bg: #ffffff;
  --ownit-color-calendar-bg: #ffffff;
  --surface: #ffffff;
  --surface-soft: #f7f7f8;
  --surface-tint: #f1f2f4;
  color: var(--todo-text);
  background: #ffffff !important;
  font-family: var(--ownit-font-active, var(--ownit-font-nanum-gothic, OwnitNanumGothic, "Apple SD Gothic Neo", "Malgun Gothic", sans-serif));
}

.ownit-page.todo-workspace-active.theme-cloud-dancer,
.ownit-page.todo-workspace-active.theme-cosmic-latte {
  --ownit-color-app-bg: #ffffff;
  --ownit-color-calendar-bg: #ffffff;
  --surface: #ffffff;
  --surface-soft: #f7f7f8;
  --surface-tint: #f1f2f4;
  background: #ffffff !important;
}

.todo-workspace-active .app-shell,
.todo-workspace-active .shell-main,
.todo-workspace-active .shell-content,
.todo-workspace-active .todomate-page {
  color: var(--todo-text);
  background: #ffffff !important;
  font-family: var(--ownit-font-active, var(--ownit-font-nanum-gothic, OwnitNanumGothic, "Apple SD Gothic Neo", "Malgun Gothic", sans-serif));
}

.todomate-page,
.todomate-page button,
.todomate-page input,
.todomate-page select,
.todomate-page textarea {
  font-family: var(--ownit-font-active, var(--ownit-font-nanum-gothic, OwnitNanumGothic, "Apple SD Gothic Neo", "Malgun Gothic", sans-serif));
  letter-spacing: 0;
}

.todomate-page {
  gap: 22px;
}

.todomate-page :is(button, input, select, textarea):focus-visible {
  outline: 2px solid var(--todo-text);
  outline-offset: 2px;
  box-shadow: 0 0 0 4px var(--todo-focus);
}

.todo-workspace-active .shell-content {
  padding: 0 clamp(16px, 5vw, 20px) calc(86px + env(safe-area-inset-bottom));
}

.todomate-calendar-pane {
  order: 1;
}

.todomate-goals {
  order: 2;
}

.todo-support-panel {
  order: 3;
}

.todo-action-sheet {
  order: 4;
}

.todomate-header {
  gap: 24px;
}

.todomate-profile-pill {
  min-height: 48px;
  font-size: 17px;
  font-weight: 700;
}

.todomate-profile-icon {
  min-width: 100px;
  height: 48px;
  color: var(--todo-text);
  background: var(--todo-fill);
}

.todomate-profile-icon.active,
.todomate-profile-pill.active {
  color: #ffffff;
  background: #000000;
}

.todomate-header-actions .icon-button,
.todomate-month-actions .icon-button,
.todo-icon-action,
.goal-pill-icon,
.todo-row-icon {
  border: 1px solid transparent;
  color: var(--todo-text);
  background: var(--todo-fill);
}

.todomate-header-actions .icon-button:hover,
.todomate-month-actions .icon-button:hover,
.todo-icon-action:hover,
.goal-pill-icon:hover,
.todo-row-icon:hover,
.todomate-profile-icon:hover,
.todo-goal-pill:hover,
.todo-inline-add-affordance:hover {
  background: var(--todo-fill-hover);
}

.todomate-header-actions .icon-button:active,
.todomate-month-actions .icon-button:active,
.todo-icon-action:active,
.goal-pill-icon:active,
.todo-row-icon:active,
.todomate-profile-icon:active,
.todo-goal-pill:active,
.todo-inline-add-affordance:active {
  background: var(--todo-fill-active);
}

.todomate-header-actions .icon-button,
.todomate-month-actions .icon-button {
  min-width: 40px;
  width: 40px;
  min-height: 40px;
  height: 40px;
}

.todomate-mini-avatar,
.todomate-avatar {
  background: #e1e5e8;
  font-weight: 700;
}

.todomate-kicker,
.todomate-date-copy,
.todomate-section-eyebrow,
.todo-section-header p,
.todo-action-sheet-header span,
.todo-action-panel-header span,
.todo-item-main p,
.todo-item-meta,
.todo-compact-empty p,
.todo-support-note {
  color: var(--todo-muted);
}

.todomate-kicker,
.todomate-date-copy,
.todo-section-header p,
.todo-action-sheet-header span,
.todo-action-panel-header span,
.todo-compact-empty p,
.todo-support-note {
  font-weight: 600;
}

.todomate-identity-row h2 {
  font-size: 27px;
  font-weight: 700;
}

.todomate-calendar-pane,
.todomate-calendar,
.todomate-date-grid {
  max-width: min(100%, 420px);
}

.todomate-calendar-heading h3 {
  font-size: 26px;
  font-weight: 700;
}

.todomate-week-row {
  margin-top: 20px;
}

.todomate-week-row span {
  color: #34363a;
  font-size: 13px;
  font-weight: 600;
}

.todomate-date-grid {
  gap: 8px 4px;
}

.todomate-date-cell {
  min-height: 56px;
  border: 1px solid transparent;
  border-radius: 8px;
  color: var(--todo-text);
  background: transparent;
}

.todomate-date-cell:hover {
  border-color: transparent;
  background: var(--todo-fill);
}

.todomate-date-cell:active {
  background: var(--todo-fill-active);
}

.todomate-date-cell.selected {
  border-color: transparent;
  background: transparent;
}

.todomate-marker-slot:not(.filled) {
  background: var(--todo-marker);
}

.todomate-day-number {
  font-weight: 600;
}

.todo-feed-summary {
  align-items: flex-start;
  gap: 9px 12px;
}

.todo-feed-summary h3 {
  font-size: 17px;
  font-weight: 700;
}

.todo-daily-metrics {
  color: var(--todo-muted);
  font-weight: 600;
}

.todo-feed-actions {
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
}

.todo-feed-actions .primary-action,
.todo-feed-actions .secondary-action,
.todo-action-buttons .primary-action,
.todo-action-buttons .secondary-action,
.todo-compact-empty .secondary-action,
.todo-daily-record-actions .primary-action,
.todo-daily-record-actions .secondary-action,
.todo-quick-action-grid .primary-action,
.todo-quick-action-grid .secondary-action,
.todo-timer-controls .primary-action,
.todo-timer-controls .secondary-action {
  font-weight: 600;
}

.todomate-page .primary-action {
  color: #ffffff;
  background: #111111;
}

.todomate-page .primary-action:hover {
  background: #000000;
}

.todomate-page .secondary-action {
  border-color: var(--todo-line);
  color: var(--todo-text);
  background: var(--todo-fill);
}

.todomate-page .secondary-action:hover {
  background: var(--todo-fill-hover);
}

.todomate-page .secondary-action:active {
  background: var(--todo-fill-active);
}

.todo-progress-track {
  height: 3px;
  background: var(--todo-line);
}

.todo-feed-summary .todo-progress-track,
.todo-goal-section .todo-progress-track {
  height: 3px;
}

.todo-goal-pills {
  gap: 7px;
}

.todo-goal-pill {
  min-height: 38px;
  border-color: var(--todo-line);
  background: var(--todo-fill);
}

.todo-goal-pill.add {
  font-weight: 600;
}

.goal-pill-main {
  min-height: 32px;
}

.goal-pill-name {
  max-width: 128px;
  font-weight: 700;
}

.goal-pill-count {
  color: var(--todo-muted);
  background: #ffffff;
  font-weight: 600;
}

.goal-pill-icon,
.todo-icon-action {
  min-width: 38px;
  width: 38px;
  min-height: 38px;
  height: 38px;
}

.todo-goal-section,
.todo-routine-section {
  gap: 7px;
  border-top-color: var(--todo-line);
  padding-top: 15px;
}

.goal-color-dot {
  width: 4px;
  height: 34px;
}

.goal-color-dot.neutral {
  background: var(--todo-line-strong);
}

.todo-section-header {
  gap: 9px;
}

.todo-section-header h3 {
  font-size: 16px;
  font-weight: 700;
}

.todo-section-header p {
  font-size: 11px;
}

.todo-inline-input {
  border-color: var(--todo-line);
  color: var(--todo-text);
  background: #ffffff;
  font-size: 15px;
  font-weight: 600;
}

.todo-inline-input:focus,
.todo-inline-input:focus-visible {
  border-color: var(--todo-text);
  box-shadow: 0 0 0 3px var(--todo-focus);
}

.todo-inline-submit,
.todo-inline-cancel,
.todo-inline-add-affordance {
  font-weight: 600;
}

.todo-inline-cancel,
.todo-inline-add-affordance {
  color: var(--todo-muted);
  background: transparent;
}

.todo-inline-add-affordance {
  min-height: 38px;
}

.todo-empty-inline,
.todo-category-empty,
.todo-record-empty {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  width: 100%;
  color: var(--todo-muted);
  background: #ffffff;
}

.todo-empty-inline,
.todo-record-empty {
  border-top: 1px dashed var(--todo-line);
  padding-top: 10px;
}

.todo-empty-inline {
  flex: 1 0 100%;
}

.todo-category-empty {
  flex: 1 0 100%;
  min-height: 38px;
}

.todo-empty-inline p,
.todo-category-empty p,
.todo-record-empty p {
  min-width: 0;
  margin: 0;
  color: var(--todo-muted);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.todo-empty-inline .secondary-action,
.todo-category-empty .secondary-action,
.todo-record-empty .secondary-action {
  flex: 0 0 auto;
  min-height: 34px;
  border-radius: 999px;
  padding: 0 11px;
  font-size: 12px;
  white-space: nowrap;
}

.todo-item-row {
  grid-template-columns: 34px minmax(0, 1fr) auto;
  gap: 10px;
  min-height: 48px;
  padding: 8px 0;
  border-top: 0;
  background: #ffffff;
}

.todo-item-row:hover {
  background: #fbfbfc;
}

.todo-item-row:active {
  background: var(--todo-fill);
}

.todo-item-check {
  width: 34px;
  height: 34px;
  border-width: 2px;
  border-radius: 8px;
}

.todo-item-main h4 {
  color: var(--todo-text);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.36;
}

.todo-item-row.completed .todo-item-main h4 {
  color: #7b818a;
  text-decoration-color: rgba(123, 129, 138, 0.58);
}

.todo-item-main p {
  font-size: 12px;
}

.todo-item-meta {
  gap: 5px;
  font-size: 10.5px;
  font-weight: 600;
}

.todo-meta-goal {
  background: var(--todo-fill);
}

.todo-meta-reminder {
  color: var(--todo-muted);
}

.todo-row-icon {
  min-width: 38px;
  width: 38px;
  min-height: 38px;
  height: 38px;
  color: var(--todo-muted);
  background: transparent;
}

.todo-row-icon.timer {
  color: var(--todo-text);
  background: var(--todo-fill);
}

.todo-row-icon.timer.paused,
.todo-row-icon.timer-stop {
  color: var(--todo-text);
  background: var(--todo-fill-hover);
}

.todo-support-panel {
  gap: 10px;
}

.todo-support-card {
  gap: 11px;
  border-color: var(--todo-line);
  border-radius: 8px;
  padding: 13px;
  background: #ffffff;
}

.todo-support-header h3,
.todo-support-stat strong,
.todo-daily-stat-main strong,
.todo-action-panel-header h3,
.todo-action-option-copy strong,
.notification-permission-card strong,
.todo-photo-picker strong,
.todo-delete-confirm h4 {
  font-weight: 700;
}

.todo-support-header i,
.todo-support-emoji,
.todo-support-stat,
.daily-emoji-choice,
.notification-permission-card,
.todo-photo-empty,
.todo-action-tile,
.reminder-option-button,
.goal-palette-tabs,
.goal-preview-pill,
.routine-choice-chip {
  background: var(--todo-fill);
}

.todo-support-stat span,
.todo-daily-stat-main small,
.todo-daily-stat-time,
.todo-action-option-copy small,
.notification-permission-card span,
.todo-photo-picker small,
.todo-photo-empty span,
.todo-delete-confirm p {
  font-weight: 600;
}

.todo-action-message.success {
  color: #4b5563;
  background: var(--todo-fill);
}

.todo-action-option.yellow .todo-action-option-icon,
.todo-action-option.pink .todo-action-option-icon,
.todo-action-option.green .todo-action-option-icon,
.todo-action-option.orange .todo-action-option-icon,
.todo-action-option.indigo .todo-action-option-icon,
.todo-action-option.blue .todo-action-option-icon,
.reminder-option-button i {
  color: #5f6368;
  background: var(--todo-fill-hover);
}

.todo-action-tile-icon.edit,
.todo-photo-picker i,
.todo-photo-empty i {
  color: #5f6368;
}

.todo-action-sheet-panel,
.todo-create-sheet,
.todo-goal-sheet {
  background: #ffffff;
}

.todo-goal-sheet,
.todo-create-sheet,
.todo-action-sheet-panel,
.mate-list-panel,
.mate-feed-panel,
.mate-feed-card,
.mate-empty-state {
  border-radius: 8px;
}

@media (max-width: 759px) {
  .todo-support-panel {
    margin-top: 2px;
  }
  .todo-item-row {
    grid-template-columns: 34px minmax(0, 1fr);
    align-items: start;
  }
  .todo-item-actions {
    grid-column: 2;
    justify-content: flex-start;
    max-width: none;
  }
  .todo-feed-actions {
    justify-content: flex-start;
    width: 100%;
  }
  .todo-feed-actions .primary-action,
  .todo-feed-actions .secondary-action,
  .todo-compact-empty .secondary-action {
    min-height: 38px;
    padding: 0 11px;
    font-size: 12px;
  }
  .todo-empty-inline,
  .todo-category-empty,
  .todo-record-empty {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
}
@media (max-width: 420px) {
  .todo-workspace-active .shell-content {
    padding-right: 16px;
    padding-left: 16px;
  }
  .todomate-page {
    gap: 22px;
  }
  .todomate-header {
    gap: 23px;
  }
  .todomate-profile-pill {
    min-height: 46px;
    padding-right: 15px;
    font-size: 16px;
  }
  .todomate-profile-icon {
    min-width: 92px;
    height: 46px;
    padding: 0 11px;
  }
  .todomate-profile-icon span {
    font-size: 12px;
    font-weight: 700;
  }
  .todomate-avatar {
    width: 64px;
    height: 64px;
  }
  .todomate-calendar-heading h3 {
    font-size: 25px;
  }
  .todomate-date-cell {
    min-height: 55px;
  }
  .todomate-day-number {
    font-size: 14px;
  }
  .todo-goal-pills {
    gap: 6px;
  }
  .goal-pill-name {
    max-width: 104px;
  }
}
@media (min-width: 760px) {
  .todo-workspace-active .shell-content {
    max-width: 1040px;
    padding: 22px 28px 36px;
  }
  .todo-workspace-active .todomate-page {
    gap: 21px;
  }
  .todomate-layout {
    grid-template-columns: minmax(300px, 342px) minmax(0, 1fr);
    gap: 22px;
    align-items: start;
  }
  .todomate-calendar-pane,
  .todomate-calendar,
  .todomate-date-grid {
    max-width: 342px;
  }
  .todomate-calendar-heading h3 {
    font-size: 23px;
  }
  .todomate-header-actions .icon-button,
  .todomate-month-actions .icon-button,
  .todo-icon-action,
  .goal-pill-icon,
  .todo-row-icon {
    min-width: 38px;
    width: 38px;
    min-height: 38px;
    height: 38px;
  }
  .todomate-date-cell {
    min-height: 50px;
    max-height: 54px;
  }
  .todo-feed-actions .primary-action,
  .todo-feed-actions .secondary-action,
  .todo-compact-empty .secondary-action,
  .todo-action-buttons .primary-action,
  .todo-action-buttons .secondary-action {
    min-height: 38px;
  }
  .todo-goal-pill {
    min-height: 40px;
  }
  .goal-pill-main {
    min-height: 34px;
  }
  .todo-inline-input {
    height: 40px;
  }
  .todo-inline-submit,
  .todo-inline-cancel {
    width: 40px;
    height: 40px;
  }
  .todo-item-row {
    grid-template-columns: 32px minmax(0, 1fr) auto;
    gap: 10px;
    min-height: 46px;
    padding: 8px 0;
  }
  .todo-item-check {
    width: 32px;
    height: 32px;
  }
  .todo-item-actions {
    max-width: 88px;
  }
  .todo-support-panel {
    grid-column: 1/-1;
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (min-width: 980px) and (max-width: 1319px) {
  .todo-support-panel {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (min-width: 1180px) and (max-width: 1319px) {
  .todo-workspace-active .shell-content {
    max-width: 960px;
    padding-right: 26px;
    padding-left: 26px;
  }
  .todomate-layout {
    grid-template-columns: minmax(296px, 338px) minmax(0, 1fr);
    gap: 20px;
  }
  .todomate-calendar-pane,
  .todomate-calendar,
  .todomate-date-grid {
    max-width: 338px;
  }
}
@media (min-width: 1320px) {
  .todo-workspace-active .shell-content {
    max-width: 1180px;
    padding: 24px 28px 38px;
  }
  .todo-workspace-active .todomate-page {
    gap: 18px;
  }
  .todomate-layout {
    grid-template-columns: minmax(300px, 348px) minmax(390px, 1fr) minmax(270px, 300px);
    gap: 20px;
  }
  .todomate-calendar-pane,
  .todomate-calendar,
  .todomate-date-grid {
    max-width: 348px;
  }
  .todo-support-panel {
    grid-column: auto;
    grid-template-columns: minmax(0, 1fr);
    align-self: start;
    gap: 10px;
    position: sticky;
    top: 24px;
  }
}
@media (min-width: 1680px) {
  .todo-workspace-active .shell-content {
    max-width: 1240px;
  }
  .todomate-layout {
    grid-template-columns: minmax(320px, 360px) minmax(430px, 1fr) minmax(280px, 320px);
  }
  .todomate-calendar-pane,
  .todomate-calendar,
  .todomate-date-grid {
    max-width: 360px;
  }
}
.sidebar-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.sidebar-title-row .sidebar-title {
  margin-bottom: 0;
}

.calendar-create-button {
  display: inline-grid;
  place-items: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  color: var(--text);
  background: var(--surface);
  box-shadow: 0 6px 16px rgba(47, 42, 37, 0.06);
}

.calendar-create-button:hover,
.calendar-create-button:focus-visible {
  color: #fff;
  background: var(--ink);
  outline: 0;
}

.calendar-sheet-backdrop,
.calendar-action-backdrop,
.member-management-backdrop {
  position: fixed;
  inset: 0;
  z-index: 88;
  background: rgba(47, 42, 37, 0.28);
}

.calendar-management-sheet,
.calendar-action-sheet,
.member-management-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 89;
  display: grid;
  align-content: start;
  gap: 16px;
  width: 100%;
  max-height: min(86dvh, 720px);
  overflow: auto;
  border: 1px solid var(--line-strong);
  border-bottom: 0;
  border-radius: 8px 8px 0 0;
  padding: 12px 20px calc(18px + env(safe-area-inset-bottom));
  color: var(--text);
  background: var(--surface);
  box-shadow: 0 -18px 46px rgba(47, 42, 37, 0.18);
}

.calendar-action-sheet,
.member-management-sheet {
  gap: 14px;
}

.calendar-sheet-handle {
  justify-self: center;
  width: 42px;
  height: 4px;
  border-radius: 999px;
  background: var(--line-strong);
}

.calendar-sheet-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.calendar-sheet-header > div {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.calendar-sheet-header h2 {
  margin: 0;
  color: var(--text);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.25;
}

.calendar-sheet-header span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
}

.calendar-management-form {
  display: grid;
  gap: 13px;
}

.calendar-visible-field {
  align-items: center;
  min-height: 42px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 10px 12px;
  background: var(--surface-soft);
}

.calendar-action-list,
.calendar-action-danger-row {
  display: grid;
  gap: 8px;
}

.calendar-action-button {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 58px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 10px 12px;
  color: var(--text);
  background: var(--surface-soft);
  text-align: left;
}

.calendar-action-button:hover,
.calendar-action-button:focus-visible {
  border-color: var(--line-strong);
  background: var(--surface-tint);
  outline: 0;
}

.calendar-action-button i {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-md);
  color: var(--text);
  background: var(--surface);
}

.calendar-action-button span {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.calendar-action-button strong,
.calendar-action-button small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.calendar-action-button strong {
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
}

.calendar-action-button small {
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  white-space: normal;
}

.calendar-action-button.warning i {
  color: #806224;
  background: #fff7e2;
}

.calendar-action-button.danger {
  border-color: rgba(166, 90, 79, 0.34);
  color: #9a4035;
  background: #fff4f2;
}

.calendar-action-button.danger i {
  color: #9a4035;
  background: #ffe8e4;
}

.calendar-action-default-note {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 11px 12px;
  color: var(--muted);
  background: var(--surface-soft);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}

.calendar-action-default-note i {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-md);
  color: var(--text);
  background: var(--surface);
}

.member-management-notice {
  margin: 0;
  border: 1px solid rgba(79, 72, 63, 0.16);
  border-radius: var(--radius-md);
  padding: 10px 12px;
  color: var(--text);
  background: var(--surface-soft);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.member-management-section {
  display: grid;
  gap: 10px;
  border-top: 1px solid var(--line);
  padding-top: 12px;
}

.invite-code-card,
.member-invite-form {
  display: grid;
  gap: 10px;
}

.invite-join-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
}

.form-actions.compact {
  margin-top: 0;
}

.member-management-empty {
  border: 1px dashed var(--line-strong);
  border-radius: var(--radius-md);
  padding: 14px;
  color: var(--muted);
  background: var(--surface-soft);
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.member-management-row {
  display: grid;
  gap: 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 11px;
  background: var(--surface-soft);
}

.member-management-row.inactive {
  opacity: 0.68;
}

.member-management-main {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.member-avatar {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  color: var(--text);
  background: var(--surface);
  font-size: 13px;
  font-weight: 800;
}

.member-management-main strong,
.member-management-main p,
.member-management-main small {
  display: block;
  overflow: hidden;
  margin: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-management-main strong {
  color: var(--text);
  font-size: 13px;
  font-weight: 800;
}

.member-management-main p,
.member-management-main small {
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.45;
}

.member-management-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
}

.member-management-footer {
  display: grid;
  gap: 8px;
  border-top: 1px solid var(--line);
  padding-top: 12px;
}

.member-management-footer p {
  margin: 0;
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.45;
}

@media (min-width: 560px) {
  .member-management-row {
    grid-template-columns: minmax(0, 1fr) minmax(190px, 220px);
    align-items: center;
  }
}
@media (max-width: 420px) {
  .invite-join-row,
  .member-management-controls {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (min-width: 768px) {
  .calendar-management-sheet,
  .calendar-action-sheet,
  .member-management-sheet {
    top: 24px;
    right: 24px;
    bottom: 24px;
    left: auto;
    width: min(420px, 100vw - 48px);
    max-height: calc(100dvh - 48px);
    border-bottom: 1px solid var(--line-strong);
    border-radius: var(--radius-lg);
    padding: 18px;
    box-shadow: -18px 20px 60px rgba(47, 42, 37, 0.16);
  }
  .calendar-action-sheet,
  .member-management-sheet {
    bottom: auto;
    max-height: calc(100dvh - 48px);
  }
  .calendar-sheet-handle {
    display: none;
  }
  .calendar-action-danger-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
/* Stage 50 responsive management QA */
.shared-guard-backdrop {
  position: fixed;
  inset: 0;
  z-index: 92;
  background: rgba(47, 42, 37, 0.3);
}

.shared-guard-sheet {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 93;
  display: grid;
  align-content: start;
  gap: 14px;
  width: 100%;
  max-height: min(82dvh, 520px);
  overflow: auto;
  border: 1px solid var(--line-strong);
  border-bottom: 0;
  border-radius: 8px 8px 0 0;
  padding: 12px 20px calc(18px + env(safe-area-inset-bottom));
  color: var(--text);
  background: var(--surface);
  box-shadow: 0 -18px 46px rgba(47, 42, 37, 0.18);
}

.shared-guard-warning {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(166, 90, 79, 0.22);
  border-radius: var(--radius-md);
  padding: 10px 12px;
  color: #8d4138;
  background: #fff7f5;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.shared-guard-warning i {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-md);
  color: #8d4138;
  background: #ffe9e5;
}

.primary-action.danger {
  color: #ffffff;
  background: #8d4138;
}

.primary-action.danger:hover,
.primary-action.danger:focus-visible {
  background: #78362f;
}

.panel-header .header-actions,
.section-title-row,
.card-actions,
.form-actions {
  min-width: 0;
}

.panel-header .header-actions {
  flex-wrap: wrap;
}

.empty-state .primary-action,
.empty-state .secondary-action,
.mate-empty-state .primary-action {
  justify-self: center;
  width: min(100%, 220px);
}

@media (max-width: 520px) {
  .calendar-management-sheet,
  .calendar-action-sheet,
  .member-management-sheet,
  .shared-guard-sheet {
    max-height: calc(100dvh - 12px);
    padding-right: 16px;
    padding-left: 16px;
  }
  .calendar-management-form,
  .event-form,
  .todo-form,
  .member-invite-form {
    gap: 12px;
  }
  .shell-detail.open {
    right: 0;
    left: 0;
    width: 100%;
    max-height: calc(92dvh - env(safe-area-inset-bottom));
  }
  .content-grid,
  .calendar-layout,
  .shared-summary-grid,
  .shared-filter-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .calendar-row,
  .event-card,
  .todo-card,
  .member-management-row {
    max-width: 100%;
  }
}
@media (min-width: 768px) {
  .shared-guard-sheet {
    top: 24px;
    right: 24px;
    bottom: auto;
    left: auto;
    width: min(420px, 100vw - 48px);
    max-height: calc(100dvh - 48px);
    border-bottom: 1px solid var(--line-strong);
    border-radius: var(--radius-lg);
    padding: 18px;
    box-shadow: -18px 20px 60px rgba(47, 42, 37, 0.16);
  }
}
@media (min-width: 768px) and (max-width: 1179px) {
  .ownit-page:not(.todo-workspace-active) .app-shell {
    grid-template-columns: 208px minmax(0, 1fr);
  }
  .ownit-page:not(.todo-workspace-active) .shell-content {
    padding-right: 18px;
    padding-left: 18px;
  }
  .ownit-page:not(.todo-workspace-active) .calendar-layout,
  .ownit-page:not(.todo-workspace-active) .content-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .ownit-page:not(.todo-workspace-active) .day-panel {
    display: none;
  }
  .ownit-page:not(.todo-workspace-active) .calendar-panel,
  .ownit-page:not(.todo-workspace-active) .panel {
    min-width: 0;
  }
}
@media (min-width: 1180px) {
  .ownit-page:not(.todo-workspace-active) {
    display: grid;
    justify-items: center;
    padding: 0;
  }
  .ownit-page:not(.todo-workspace-active) .app-shell {
    width: min(100%, 1320px);
    max-width: 1320px;
    grid-template-columns: 240px minmax(0, 1fr);
    border-right: 1px solid var(--line);
    border-left: 1px solid var(--line);
  }
  .ownit-page:not(.todo-workspace-active) .shell-main {
    min-width: 0;
  }
  .ownit-page:not(.todo-workspace-active) .shell-content {
    width: 100%;
    max-width: 1060px;
    margin: 0 auto;
    padding-right: 24px;
    padding-left: 24px;
  }
  .ownit-page:not(.todo-workspace-active) .calendar-layout,
  .ownit-page:not(.todo-workspace-active) .content-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
/* Stage 51 large text typography scale */
.text-card-title,
.CardTitle {
  margin: 0;
  color: var(--text);
  font-size: var(--font-card-title);
  font-weight: var(--font-card-title-weight);
  line-height: 1.25;
  letter-spacing: 0;
}

.todo-display-settings {
  display: grid;
  gap: 12px;
}

.font-settings {
  display: grid;
  gap: 12px;
}

.font-option-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.font-option-button {
  display: grid;
  align-content: space-between;
  gap: 10px;
  min-width: 0;
  min-height: 112px;
  border: 1px solid var(--quiet-border, var(--line));
  border-radius: var(--radius-md);
  padding: 12px;
  color: var(--text);
  background: #ffffff;
  text-align: left;
}

.font-option-button.active {
  border-color: var(--ink);
  box-shadow: inset 0 0 0 1px var(--ink);
}

.font-option-button.brand-special {
  border-style: dashed;
}

.font-option-copy {
  display: grid;
  gap: 3px;
  min-width: 0;
  font-family: var(--ownit-font-sans);
}

.font-option-copy strong,
.font-option-copy small,
.font-option-sample {
  display: block;
  overflow-wrap: anywhere;
  letter-spacing: 0;
}

.font-option-copy strong {
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
}

.font-option-copy small {
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
}

.font-option-sample {
  color: var(--ink);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
}

.todo-display-toggle-list {
  display: grid;
  gap: 8px;
}

.todo-display-master,
.todo-display-option {
  width: 100%;
  min-height: 38px;
  border: 1px solid var(--quiet-border, var(--line));
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  background: #ffffff;
}

.todo-display-option.disabled {
  color: var(--soft-text);
  background: var(--surface-soft);
}

.todo-category-preference-note {
  flex: 1 0 100%;
  margin: 0 0 2px;
  color: #7d858e;
  font-size: 11px;
  font-weight: 700;
}

.goal-pill-marker,
.goal-color-dot {
  display: inline-grid;
  place-items: center;
  border-style: solid;
  border-width: 1px;
  line-height: 1;
}

.goal-pill-marker {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  box-shadow: 0 0 0 2px #ffffff;
  font-size: 8px;
}

.goal-pill-marker.marker-square,
.goal-color-dot.marker-square {
  border-radius: 4px;
}

.goal-pill-marker.marker-ring,
.goal-color-dot.marker-ring {
  border-width: 2px;
}

.goal-pill-marker.marker-check,
.goal-pill-marker.marker-star,
.goal-color-dot.marker-check,
.goal-color-dot.marker-star {
  color: currentColor;
}

.goal-color-dot {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  min-width: 16px;
  border-radius: 999px;
}

.goal-pill-badge {
  display: inline-flex;
  align-items: center;
  height: 18px;
  border-radius: 999px;
  padding: 0 6px;
  color: #5f6368;
  background: #ffffff;
  font-size: 10px;
  font-weight: 800;
}

.goal-pill-icon.active {
  color: #111111;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px #d7dbe0;
}

.goal-pill-icon.warning,
.todo-icon-action.warning {
  color: #7a6842;
}

.goal-marker-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.goal-marker-button {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid #e6e7ea;
  border-radius: 999px;
  background: #ffffff;
}

.goal-marker-button.active {
  border-color: #111111;
  box-shadow: inset 0 0 0 1px #111111;
}

.todo-goal-preference-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.todo-archived-category-list {
  display: grid;
  gap: 8px;
  border-top: 1px solid #eceef0;
  padding-top: 12px;
}

.todo-archived-category-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  padding: 10px;
  background: #ffffff;
}

.todo-archived-category-row strong,
.todo-archived-category-row small {
  display: block;
}

.todo-archived-category-row strong {
  color: #111111;
  font-size: 13px;
}

.todo-archived-category-row small {
  margin-top: 2px;
  color: #7d858e;
  font-size: 11px;
}

@media (max-width: 520px) {
  .font-option-list {
    grid-template-columns: 1fr;
  }
  .font-option-button {
    min-height: 96px;
  }
  .sticker-upload-preview {
    gap: 10px;
  }
  .sticker-removal-actions,
  .sticker-style-options {
    grid-template-columns: 1fr;
  }
  .sticker-asset-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .sticker-asset-actions {
    grid-template-columns: 1fr;
  }
}
.panel-header h2,
.detail-section h2,
.calendar-sheet-header h2,
.todo-action-panel-header h3,
.todo-create-header h3,
.todo-goal-sheet-header h3,
.todo-support-header h3,
.mate-panel-header h3 {
  font-size: var(--font-card-title);
  font-weight: var(--font-card-title-weight);
  line-height: 1.25;
  letter-spacing: 0;
}

.todomate-calendar-heading h3 {
  font-size: var(--font-card-title-large);
  font-weight: var(--font-card-title-weight);
  line-height: 1.18;
  letter-spacing: 0;
}

.section-title-row h3,
.todo-section-header h3,
.todo-feed-summary h3,
.card-body h3,
.todo-main h3,
.focused-event-detail h3,
.permission-row strong,
.member-chip strong,
.member-management-main strong,
.shared-calendar-select strong {
  font-weight: var(--font-card-title-weight);
}

.primary-action,
.secondary-action,
.danger-action,
.text-action,
.receipt-open-button,
.todo-receipt-entry,
.add-event-button,
.add-todo-button,
.quick-action-row .primary-action,
.quick-action-row .secondary-action,
.todo-create-tab,
.todo-action-buttons .primary-action,
.todo-action-buttons .secondary-action,
.todo-daily-record-actions .primary-action,
.todo-daily-record-actions .secondary-action,
.todo-quick-action-grid .primary-action,
.todo-quick-action-grid .secondary-action,
.todo-timer-controls .primary-action,
.todo-timer-controls .secondary-action {
  font-size: var(--font-card-action);
  font-weight: var(--font-card-action-weight);
  line-height: 1.12;
  letter-spacing: 0;
}

.shared-summary-card strong,
.todo-support-stat strong,
.todo-summary-meter strong {
  font-size: var(--font-card-metric);
  font-weight: var(--font-card-metric-weight);
  line-height: 1.15;
  letter-spacing: 0;
}

.todo-active-timer-readout {
  font-size: var(--font-card-metric-large);
  font-weight: var(--font-card-metric-weight);
  letter-spacing: 0;
}

.todo-daily-stat-main strong,
.todo-action-option-copy strong,
.notification-permission-card strong,
.todo-photo-picker strong,
.todo-delete-confirm h4 {
  font-weight: var(--font-card-title-weight);
}

@media (display-mode: standalone) and (max-width: 767px) {
  .ownit-page {
    min-height: 100dvh;
  }
  .ownit-page:not(.todo-workspace-active) .app-shell {
    min-height: 100dvh;
  }
  .ownit-page:not(.todo-workspace-active) .shell-header {
    padding-top: calc(18px + env(safe-area-inset-top));
  }
}
/* Stage 65 desktop calendar width restoration */
.ownit-page.dashboard-shell-active:not(.todo-workspace-active) {
  width: 100%;
  min-height: 100dvh;
  background: #ffffff;
}

.ownit-page.dashboard-shell-active:not(.todo-workspace-active) .app-shell,
.ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-main,
.ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-content,
.ownit-page.dashboard-shell-active:not(.todo-workspace-active) .calendar-layout,
.ownit-page.dashboard-shell-active:not(.todo-workspace-active) .calendar-panel {
  min-width: 0;
  max-width: none;
}

.ownit-page.dashboard-shell-active:not(.todo-workspace-active) .app-shell {
  width: 100%;
  min-height: 100dvh;
  margin: 0;
  border: 0;
  border-radius: 0;
  background: #ffffff;
  box-shadow: none;
}

.ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-content {
  width: 100%;
  margin: 0;
}

.ownit-page.dashboard-shell-active:not(.todo-workspace-active) .calendar-layout,
.ownit-page.dashboard-shell-active:not(.todo-workspace-active) .calendar-panel,
.ownit-page.dashboard-shell-active:not(.todo-workspace-active) .month-grid {
  width: 100%;
}

@media (min-width: 768px) {
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) {
    display: block;
    justify-items: initial;
    padding: 0;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .app-shell {
    display: grid;
    grid-template-columns: 224px minmax(0, 1fr);
    align-items: stretch;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-sidebar {
    display: flex;
    flex-direction: column;
    width: 224px;
    min-width: 224px;
    height: 100dvh;
    border-right: 1px solid var(--line);
    background: #ffffff;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-main {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100dvh;
    border: 0;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-content {
    flex: 1 1 auto;
    overflow: hidden;
    padding: 0 18px 18px;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .calendar-layout,
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .calendar-panel {
    flex: 1 1 auto;
    height: 100%;
    min-height: 0;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .month-grid {
    grid-template-rows: repeat(6, minmax(92px, 1fr));
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .calendar-week,
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .calendar-day {
    min-height: 0;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .content-grid,
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .calendar-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (min-width: 1180px) {
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .app-shell {
    grid-template-columns: 240px minmax(0, 1fr);
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-sidebar {
    width: 240px;
    min-width: 240px;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-content {
    padding: 0 24px 24px;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .month-grid {
    grid-template-rows: repeat(6, minmax(100px, 1fr));
  }
}
@media (min-width: 1440px) {
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-content {
    padding-right: 28px;
    padding-left: 28px;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .month-grid {
    grid-template-rows: repeat(6, minmax(108px, 1fr));
  }
}
@media (min-width: 1680px) {
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .app-shell.detail-open {
    grid-template-columns: 240px minmax(0, 1fr) minmax(320px, 360px);
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .app-shell.detail-open .detail-overlay {
    display: none;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .app-shell.detail-open .shell-detail.open {
    position: static;
    inset: auto;
    width: auto;
    max-height: 100dvh;
    min-height: 100dvh;
    border: 0;
    border-left: 1px solid var(--line);
    border-radius: 0;
    padding: 18px 16px;
    overflow: auto;
    background: #ffffff;
    box-shadow: none;
  }
}
@media (min-width: 2048px) {
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .app-shell.detail-open {
    grid-template-columns: 240px minmax(0, 1fr) 380px;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-content {
    padding-right: 32px;
    padding-left: 32px;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .month-grid {
    grid-template-rows: repeat(6, minmax(112px, 1fr));
  }
}
/* Stage 69 Todo mobile-first flow review */
.todo-mobile-record-strip {
  display: none;
}

@media (max-width: 759px) {
  .todo-workspace-active .shell-content {
    padding-bottom: calc(92px + env(safe-area-inset-bottom));
  }
  .todomate-page {
    gap: 20px;
  }
  .todomate-header {
    gap: 20px;
  }
  .todomate-identity-row {
    gap: 14px;
  }
  .todomate-avatar {
    width: 60px;
    height: 60px;
    font-size: 22px;
  }
  .todomate-identity-row h2 {
    font-size: 24px;
  }
  .todomate-date-copy {
    margin-top: 6px;
    font-size: 11px;
  }
  .todomate-calendar-heading h3 {
    overflow: hidden;
    font-size: 23px;
    line-height: 1.14;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .todomate-month-actions {
    flex: 0 0 auto;
    gap: 6px;
  }
  .todomate-week-row {
    margin-top: 18px;
  }
  .todomate-date-grid {
    gap: 6px 3px;
  }
  .todomate-date-cell {
    min-height: 50px;
    padding: 0;
  }
  .todomate-marker-stack {
    grid-template-columns: repeat(2, 8px);
    grid-template-rows: repeat(2, 8px);
    width: 17px;
    height: 17px;
  }
  .todomate-marker-slot {
    width: 8px;
    height: 8px;
  }
  .todomate-day-number {
    width: 24px;
    height: 24px;
    font-size: 13px;
  }
  .todo-feed-summary {
    gap: 9px 10px;
  }
  .todo-feed-actions {
    justify-content: flex-start;
    flex-wrap: nowrap;
    width: 100%;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
  }
  .todo-feed-actions::-webkit-scrollbar {
    display: none;
  }
  .todo-feed-actions .primary-action,
  .todo-feed-actions .secondary-action,
  .todo-mobile-record-strip .secondary-action {
    flex: 0 0 auto;
    min-height: 42px;
    padding: 0 12px;
    font-size: 12px;
    line-height: 1.1;
    white-space: nowrap;
  }
  .todo-mobile-record-strip {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    flex: 1 0 100%;
    gap: 8px;
    min-width: 0;
    margin-top: 1px;
  }
  .todo-mobile-record-strip .secondary-action {
    justify-content: center;
    min-width: 0;
  }
  .todo-mobile-record-strip .secondary-action span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .todo-category-preference-note {
    flex: 1 0 100%;
    font-size: 11px;
  }
  .goal-pill-actions {
    display: none;
  }
  .todo-goal-pill,
  .todo-goal-pill.add {
    min-height: 42px;
  }
  .goal-pill-main {
    min-height: 36px;
  }
  .todo-section-header {
    align-items: start;
  }
  .todo-section-controls {
    justify-content: flex-end;
    gap: 4px;
    max-width: 184px;
  }
  .todo-section-controls .move-action,
  .todo-section-controls .archive-action {
    display: none;
  }
  .todo-icon-action,
  .todo-row-icon {
    min-width: 42px;
    width: 42px;
    min-height: 42px;
    height: 42px;
  }
  .todo-item-row {
    grid-template-columns: 34px minmax(0, 1fr) auto;
    align-items: start;
    min-height: 54px;
    padding: 8px 0;
  }
  .todo-item-actions {
    grid-column: auto;
    justify-content: flex-end;
    flex-wrap: nowrap;
    gap: 3px;
    max-width: none;
  }
  .todo-item-actions .move-action,
  .todo-item-actions .delete-action {
    display: none;
  }
  .todo-item-main h4 {
    font-size: 14px;
    line-height: 1.38;
  }
  .todo-item-meta {
    gap: 4px;
    font-size: 10px;
  }
  .todo-support-panel {
    gap: 0;
    margin-top: 10px;
  }
  .todo-support-card {
    border: 0;
    border-top: 1px dashed var(--todo-line);
    border-radius: 0;
    padding: 18px 0 0;
    background: #ffffff;
  }
  .todo-daily-record-card {
    scroll-margin-top: 18px;
  }
  .todo-support-stat-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 5px;
  }
  .todo-support-stat {
    padding: 8px 4px;
    text-align: center;
  }
  .todo-support-stat span {
    font-size: 10px;
    line-height: 1.2;
  }
  .todo-support-stat strong {
    font-size: 14px;
  }
  .todo-daily-diary-input {
    min-height: 118px;
    max-height: min(32dvh, 260px);
    font-size: 14px;
  }
  .todo-daily-record-actions .primary-action,
  .todo-daily-record-actions .secondary-action {
    min-height: 42px;
    font-size: 12px;
  }
  .todo-create-sheet,
  .todo-goal-sheet,
  .todo-action-sheet-panel {
    max-height: min(92dvh, 820px);
    border-radius: 18px 18px 0 0;
  }
}
@media (max-width: 420px) {
  .todo-workspace-active .shell-content {
    padding-right: 16px;
    padding-left: 16px;
  }
  .todomate-topbar {
    gap: 10px;
  }
  .todomate-header-actions {
    flex: 0 0 auto;
  }
  .todomate-header-actions .icon-button,
  .todomate-month-actions .icon-button {
    min-width: 38px;
    width: 38px;
    min-height: 38px;
    height: 38px;
  }
  .todomate-profile-pill {
    min-height: 44px;
    font-size: 15px;
  }
  .todomate-profile-icon {
    min-width: 44px;
    width: 44px;
    height: 44px;
    padding: 0;
  }
  .todomate-profile-icon span {
    display: none;
  }
  .todomate-calendar-heading h3 {
    font-size: 22px;
  }
  .todomate-date-cell {
    min-height: 49px;
  }
  .todo-feed-summary h3 {
    font-size: 16px;
  }
  .todo-section-controls {
    max-width: 134px;
  }
}
/* Stage 79 PWA and responsive final QA */
.status-strip.compact {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

@media (max-width: 420px) {
  .status-strip.compact .status-item {
    padding: 6px 4px;
  }
  .status-strip.compact .status-label {
    font-size: 9px;
  }
  .status-strip.compact strong {
    font-size: 11px;
  }
  .network-status-banner {
    padding-right: 14px;
    padding-left: 14px;
  }
}
@media (min-width: 768px) {
  .status-strip.compact {
    grid-template-columns: repeat(4, minmax(74px, 112px));
    justify-content: end;
  }
  .network-status-banner {
    padding-right: 24px;
    padding-left: 24px;
  }
}
@media (min-width: 768px) and (max-width: 1179px) {
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .app-shell {
    grid-template-columns: minmax(208px, 224px) minmax(0, 1fr);
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .shell-content {
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .calendar-layout,
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active) .content-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (min-width: 1180px) {
  .status-strip.compact {
    grid-template-columns: repeat(4, minmax(84px, 120px));
  }
}
@media (display-mode: standalone) and (max-width: 767px) {
  .network-status-banner {
    padding-right: max(14px, env(safe-area-inset-right));
    padding-left: max(14px, env(safe-area-inset-left));
  }
}
/* Stage 84 main calendar date-range drag selection */
.calendar-range-toolbar {
  position: absolute;
  z-index: 5;
  top: 34px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: min(420px, 100% - 20px);
  border: 1px solid rgba(111, 98, 87, 0.18);
  border-radius: var(--radius-md);
  padding: 6px 7px 6px 10px;
  color: var(--ink);
  background: rgba(255, 255, 255, 0.94);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  box-shadow: 0 8px 20px rgba(36, 48, 43, 0.12);
}

.calendar-range-toolbar.range-mode {
  border-color: rgba(111, 98, 87, 0.3);
  background: rgba(255, 255, 255, 0.97);
}

.calendar-range-toolbar span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.calendar-range-toolbar button {
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  min-width: 30px;
  min-height: 28px;
  border-radius: var(--radius-sm);
  padding: 0 9px;
  color: #ffffff;
  background: var(--green);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.calendar-range-toolbar button[aria-label] {
  width: 28px;
  padding: 0;
  color: var(--ink);
  background: var(--surface-soft);
}

.range-selection-copy {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.month-grid.range-dragging,
.month-grid.range-dragging .calendar-day,
.month-grid.range-dragging .event-bar {
  -webkit-user-select: none;
  user-select: none;
}

.month-grid,
.calendar-day {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.month-grid.range-dragging {
  cursor: crosshair;
}

.month-grid.range-tap-mode .calendar-day {
  cursor: pointer;
}

.calendar-day.range-selected {
  border-color: rgba(111, 98, 87, 0.28);
  background: linear-gradient(0deg, rgba(111, 98, 87, 0.08), rgba(111, 98, 87, 0.08)), var(--calendar-texture), var(--calendar-bg);
  box-shadow: inset 0 0 0 1px rgba(111, 98, 87, 0.12);
}

.calendar-day.range-selected.outside {
  background: linear-gradient(0deg, rgba(111, 98, 87, 0.07), rgba(111, 98, 87, 0.07)), #fbfbfa;
}

.calendar-day.range-start,
.calendar-day.range-end,
.calendar-day.range-hovered {
  border-color: rgba(111, 98, 87, 0.42);
  box-shadow: inset 0 0 0 2px rgba(111, 98, 87, 0.18);
}

.calendar-day.range-start .day-number,
.calendar-day.range-end .day-number {
  color: #ffffff;
  background: var(--green);
  font-weight: 600;
}

.calendar-day.range-start.saturday .day-number,
.calendar-day.range-end.saturday .day-number {
  color: #ffffff;
  background: var(--weekend-saturday);
}

.calendar-day.range-start.sunday .day-number,
.calendar-day.range-end.sunday .day-number,
.calendar-day.range-start.public-holiday .day-number,
.calendar-day.range-end.public-holiday .day-number {
  color: #ffffff;
  background: var(--weekend-sunday);
}

.week-events {
  z-index: 5;
  align-content: start;
}

.event-bar {
  position: relative;
  isolation: isolate;
  border: 0;
  text-shadow: none;
}

.event-bar::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.34);
  pointer-events: none;
}

.event-bar > * {
  position: relative;
  z-index: 1;
}

.event-bar.multi-day {
  border-radius: 6px;
}

.event-bar.multi-day.segment-start {
  border-top-left-radius: 999px;
  border-bottom-left-radius: 999px;
}

.event-bar.multi-day.segment-end {
  border-top-right-radius: 999px;
  border-bottom-right-radius: 999px;
}

.event-bar.multi-day.continues-before {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.event-bar.multi-day.continues-after {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.event-bar.single-day {
  border-radius: 999px;
}

.day-overflow {
  z-index: 8;
}

.month-grid.range-dragging .week-events {
  z-index: 6;
}

.month-grid.range-dragging .event-bar {
  filter: saturate(0.86) opacity(0.86);
}

@media (max-width: 767px) {
  .calendar-range-toolbar {
    top: 30px;
    right: 8px;
    max-width: calc(100% - 16px);
    padding: 6px 6px 6px 8px;
  }
  .calendar-range-toolbar span {
    max-width: min(64vw, 240px);
  }
  .calendar-range-toolbar button {
    min-width: 28px;
    min-height: 26px;
    padding: 0 8px;
    font-size: 11px;
  }
  .range-mode-header-button span {
    display: none;
  }
}
/* Stage 91 iPhone 16 Pro mobile usability QA */
@media (max-width: 767px) {
  .ownit-page,
  .app-shell,
  .shell-main,
  .shell-content,
  .content-grid,
  .calendar-layout,
  .todomate-page,
  .todomate-layout,
  .receipt-modal {
    max-width: 100%;
    overflow-x: hidden;
  }
  .ownit-page.dashboard-shell-active {
    width: 100%;
    height: 100dvh;
    min-height: 100dvh;
    overflow: hidden;
  }
  .ownit-page.dashboard-shell-active .app-shell {
    display: grid;
    grid-template-rows: minmax(0, 1fr) auto;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }
  .ownit-page.dashboard-shell-active .shell-main {
    min-height: 0;
    overflow: hidden;
  }
  .ownit-page.dashboard-shell-active .bottom-tabs {
    position: relative;
    z-index: 20;
    bottom: auto;
    flex: 0 0 auto;
  }
  .ownit-page.dashboard-shell-active .icon-button {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }
  .ownit-page.dashboard-shell-active .today-button,
  .ownit-page.dashboard-shell-active .range-mode-header-button {
    min-width: 44px;
    min-height: 44px;
  }
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active):not(.calendar-workspace-active) .shell-content {
    display: block;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 14px 16px calc(92px + env(safe-area-inset-bottom));
  }
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .shell-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    padding: 0;
  }
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .calendar-layout,
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .calendar-panel {
    height: 100%;
    min-height: 0;
  }
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .month-grid {
    flex: 1 1 auto;
    min-height: 0;
    grid-template-rows: repeat(6, minmax(0, 1fr));
  }
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .calendar-week,
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .week-days,
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .calendar-day {
    min-height: 0;
  }
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .shell-header {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    padding: 10px 12px 8px;
  }
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .month-heading,
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .month-actions,
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .header-tools {
    grid-column: 1;
    width: 100%;
  }
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .month-heading h1 {
    font-size: 28px;
  }
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .month-actions {
    display: grid;
    grid-template-columns: 44px minmax(54px, 1fr) 44px 44px;
    gap: 6px;
  }
  .ownit-page.dashboard-shell-active.calendar-workspace-active:not(.todo-workspace-active) .header-tools {
    display: grid;
    grid-template-columns: repeat(4, minmax(44px, 1fr));
    gap: 6px;
  }
  .calendar-range-toolbar button {
    min-width: 44px;
    min-height: 40px;
  }
  .todo-workspace-active .shell-content {
    display: block;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0 16px 18px;
  }
  .todo-workspace-active .todomate-page {
    min-height: auto;
    padding-bottom: 18px;
  }
  .content-grid,
  .shared-summary-grid,
  .shared-filter-grid,
  .share-selector-grid,
  .member-grid,
  .sticker-compare-grid,
  .sticker-upload-fields,
  .sticker-removal-actions,
  .sticker-style-options,
  .todo-goal-preference-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .panel,
  .calendar-panel,
  .day-panel,
  .event-card,
  .todo-card,
  .shared-summary-card,
  .member-chip,
  .permission-row,
  .sticker-upload-box,
  .sticker-asset-card {
    min-width: 0;
    max-width: 100%;
  }
  .panel-header,
  .calendar-sheet-header,
  .todo-create-header,
  .todo-action-panel-header,
  .todo-goal-sheet-header,
  .todo-support-header,
  .section-title-row {
    align-items: flex-start;
  }
  .panel-header h2,
  .calendar-sheet-header h2,
  .todo-create-header h3,
  .todo-action-panel-header h3,
  .todo-goal-sheet-header h3,
  .todo-support-header h3,
  .section-title-row h3 {
    overflow-wrap: anywhere;
  }
  .todo-feed-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
    overflow-x: hidden;
    padding-bottom: 0;
  }
  .todo-feed-actions .primary-action,
  .todo-feed-actions .secondary-action,
  .todo-mobile-record-strip .secondary-action {
    justify-content: center;
    width: 100%;
    min-width: 0;
    min-height: 44px;
    padding: 0 10px;
    white-space: normal;
  }
  .todo-feed-actions .primary-action span,
  .todo-feed-actions .secondary-action span,
  .todo-mobile-record-strip .secondary-action span,
  .todo-daily-record-actions span,
  .todo-quick-action-grid span,
  .receipt-actions span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .todo-mobile-record-strip {
    margin-top: 0;
  }
  .todo-inline-form {
    grid-template-columns: minmax(0, 1fr) 44px 44px;
    min-height: 48px;
    scroll-margin-bottom: 46dvh;
  }
  .todo-inline-input {
    height: 44px;
  }
  .todo-inline-submit,
  .todo-inline-cancel {
    width: 44px;
    height: 44px;
  }
  .todo-row-icon,
  .todo-icon-action,
  .goal-pill-icon,
  .goal-marker-button,
  .daily-emoji-choice,
  .emoji-choice,
  .calendar-action-button,
  .reminder-option-button,
  .todo-action-tile {
    min-height: 44px;
  }
  .todo-workspace-active .icon-button,
  .todo-workspace-active .todo-icon-action,
  .todo-workspace-active .todo-row-icon,
  .todo-workspace-active .goal-pill-icon,
  .todo-workspace-active .goal-marker-button,
  .todo-workspace-active .todo-inline-submit,
  .todo-workspace-active .todo-inline-cancel {
    min-width: 44px;
    width: 44px;
    min-height: 44px;
    height: 44px;
  }
  .todo-workspace-active .primary-action,
  .todo-workspace-active .secondary-action,
  .todo-workspace-active .todo-receipt-entry,
  .todo-workspace-active .todo-create-tab,
  .todo-workspace-active .goal-palette-tab,
  .todo-workspace-active .daily-emoji-choice,
  .sticker-list-button {
    min-height: 44px;
  }
  .todo-item-check,
  .check-button {
    min-width: 40px;
    min-height: 40px;
  }
  .calendar-action-button {
    grid-template-columns: 44px minmax(0, 1fr);
  }
  .calendar-action-button i {
    width: 36px;
    height: 36px;
  }
  .calendar-management-sheet,
  .calendar-action-sheet,
  .member-management-sheet,
  .shared-guard-sheet,
  .shell-detail.open,
  .todo-create-sheet,
  .todo-goal-sheet,
  .todo-action-sheet-panel,
  .receipt-modal {
    max-height: calc(100dvh - max(10px, env(safe-area-inset-top)));
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    scroll-padding-bottom: 44dvh;
  }
  .calendar-management-sheet,
  .calendar-action-sheet,
  .member-management-sheet,
  .shared-guard-sheet,
  .todo-create-sheet,
  .todo-goal-sheet,
  .todo-action-sheet-panel {
    padding-right: max(16px, env(safe-area-inset-right));
    padding-left: max(16px, env(safe-area-inset-left));
    padding-bottom: calc(20px + env(safe-area-inset-bottom));
  }
  .shell-detail.open {
    width: 100%;
    max-height: calc(92dvh - env(safe-area-inset-bottom));
    padding-right: max(12px, env(safe-area-inset-right));
    padding-left: max(12px, env(safe-area-inset-left));
  }
  .calendar-management-sheet .form-actions,
  .member-management-sheet .form-actions,
  .shared-guard-sheet .form-actions,
  .todo-create-sheet .form-actions,
  .todo-action-sheet-panel .form-actions,
  .shell-detail.open .form-actions,
  .receipt-actions {
    position: sticky;
    bottom: calc(-1 * env(safe-area-inset-bottom));
    z-index: 3;
    margin-right: -2px;
    margin-left: -2px;
    padding: 8px 2px 0;
    background: inherit;
  }
  .field,
  .checkbox-field,
  .ui-input,
  input,
  select,
  textarea,
  .color-choice-row,
  .goal-color-grid,
  .routine-choice-row,
  .routine-date-grid,
  .daily-emoji-picker,
  .emoji-choice-row {
    scroll-margin-bottom: 44dvh;
  }
  .receipt-modal-backdrop {
    align-items: stretch;
    padding: max(8px, env(safe-area-inset-top)) 8px 0;
  }
  .receipt-modal {
    align-content: start;
    width: 100%;
    border-bottom: 0;
    border-radius: 16px 16px 0 0;
    padding: 10px;
  }
  .receipt-preview-shell {
    order: -1;
    max-height: none;
    overflow: visible;
    -webkit-overflow-scrolling: touch;
  }
  .receipt-preview-frame {
    height: min(360px, 42dvh);
    max-width: 100%;
    overflow: auto;
    overflow-x: hidden;
  }
  .receipt-controls {
    gap: 10px;
  }
  .receipt-save-button {
    min-height: 52px;
    font-size: 15px;
  }
  .receipt-section-control {
    grid-template-columns: minmax(0, 1fr);
  }
  .receipt-order-actions {
    justify-content: stretch;
  }
  .receipt-order-actions .icon-button {
    flex: 1 1 0;
    min-height: 40px;
  }
}
@media (min-width: 768px) and (max-width: 1179px) {
  .ownit-page.dashboard-shell-active .icon-button,
  .ownit-page.dashboard-shell-active .calendar-create-button,
  .ownit-page.dashboard-shell-active .sidebar-icon-button {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }
  .ownit-page.dashboard-shell-active .today-button,
  .ownit-page.dashboard-shell-active .range-mode-header-button,
  .ownit-page.dashboard-shell-active .side-nav-button,
  .ownit-page.dashboard-shell-active .sticker-list-button {
    min-width: 44px;
    min-height: 44px;
  }
  .ownit-page.todo-workspace-active.dashboard-shell-active {
    height: 100dvh;
    min-height: 100dvh;
    overflow: hidden;
  }
  .todo-workspace-active.dashboard-shell-active .app-shell {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) auto;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }
  .todo-workspace-active.dashboard-shell-active .shell-sidebar {
    display: none;
  }
  .todo-workspace-active.dashboard-shell-active .shell-main {
    min-height: 0;
    overflow: hidden;
  }
  .todo-workspace-active.dashboard-shell-active .shell-content {
    display: block;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0 20px 18px;
  }
  .todo-workspace-active.dashboard-shell-active .bottom-tabs {
    position: relative;
    bottom: auto;
  }
  .todo-workspace-active .icon-button,
  .todo-workspace-active .todo-icon-action,
  .todo-workspace-active .todo-row-icon,
  .todo-workspace-active .goal-pill-icon,
  .todo-workspace-active .goal-marker-button {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }
  .todo-workspace-active .primary-action,
  .todo-workspace-active .secondary-action,
  .todo-workspace-active .todo-receipt-entry,
  .todo-workspace-active .todo-create-tab,
  .todo-workspace-active .goal-palette-tab,
  .todo-workspace-active .daily-emoji-choice {
    min-height: 44px;
  }
}
@media (max-width: 420px) {
  .ownit-page.dashboard-shell-active:not(.todo-workspace-active):not(.calendar-workspace-active) .shell-content {
    padding-right: 14px;
    padding-left: 14px;
  }
  .todo-feed-actions,
  .todo-mobile-record-strip,
  .todo-daily-record-actions,
  .todo-timer-controls,
  .todo-quick-action-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .todo-action-primary-grid,
  .reminder-option-grid,
  .routine-flag-grid,
  .receipt-actions {
    grid-template-columns: minmax(0, 1fr);
  }
  .todo-item-row {
    grid-template-columns: 34px minmax(0, 1fr);
  }
  .todo-item-actions {
    grid-column: 2;
    justify-content: flex-start;
  }
  .calendar-management-sheet,
  .calendar-action-sheet,
  .member-management-sheet,
  .shared-guard-sheet,
  .todo-create-sheet,
  .todo-goal-sheet,
  .todo-action-sheet-panel {
    padding-right: 14px;
    padding-left: 14px;
  }
}`],
})
export class PageDashboardComponent implements OnInit, OnDestroy {
    public isAuthenticated = false;
    public needsProfileSetup = false;
    public authMode: AuthMode = 'signin';
    public authError = '';
    public authNotice = '';
    public authToastMessage = '';
    public authToastLeaving = false;
    public authLoading = false;
    public signinForm = { identifier: '', password: '' };
    public signupForm = { email: '', password: '', passwordConfirm: '', name: '', nickname: '', termsAccepted: false };
    public resetForm = { email: '' };
    public readonly authWordmarkSrc = '/assets/brand/ownit-auth-wordmark-with-tagline-typewriter-nanumgothic.png';
    public authWordmarkMissing = false;
    public showSigninPassword = false;
    public profileForm = {
        name: '',
        nickname: '',
        profileImage: '',
        defaultFont: OWNIT_DEFAULT_FONT_ID,
        defaultTheme: OWNIT_DEFAULT_THEME_ID
    };
    public settingsNotice = '';
    public settingsError = '';

    public activeTab: AppTab = 'calendar';
    public selectedDate = this.toDateKey(new Date());
    public currentMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    public monthDays: CalendarDay[] = [];
    public monthWeeks: CalendarWeek[] = [];
    public dragStartDate: string | null = null;
    public dragEndDate: string | null = null;
    public isDragging = false;
    public hoveredDate: string | null = null;
    public selectedRange: CalendarDateRange | null = null;
    public calendarTapRangeMode = false;
    public calendarTapRangeStartDate: string | null = null;
    public calendarTapRangeEndDate: string | null = null;
    public detailPanelOpen = false;
    public hiddenCalendarIds: string[] = [];
    public externalProviderSummaries: ExternalCalendarProviderSummary[] = [];
    public externalOAuthConnections: ExternalOAuthConnection[] = [];
    public externalSyncPolicies: ExternalCalendarSyncPolicy[] = [];
    public externalEventMappings: ExternalEventMapping[] = [];
    public externalOAuthNotice = '';
    public externalOAuthError = '';
    public readonly externalOAuthProviderIds: ExternalOAuthProviderId[] = ['google', 'microsoft', 'naver'];
    public readonly externalSyncDirectionOptions: Array<{ value: CalendarSyncDirection; label: string; description: string }> = [
        { value: 'import_only', label: '가져오기만', description: '외부 변경을 Ownit에 표시하고 외부에는 쓰지 않습니다.' },
        { value: 'export_only', label: 'Ownit에서 외부로 내보내기', description: 'Ownit 변경을 외부에 쓰기 전 대기열로 보냅니다.' },
        { value: 'bidirectional', label: '양방향', description: '양쪽 변경을 비교하고 충돌은 사용자 선택을 기다립니다.' }
    ];
    public readonly externalDeletionPolicyOptions: Array<{ value: ExternalCalendarDeletionPolicy; label: string; description: string }> = [
        { value: 'mark_deleted_only', label: '삭제 표시만 반영', description: '외부/Ownit 삭제를 바로 전파하지 않고 숨김 상태로 둡니다.' },
        { value: 'mirror_after_confirm', label: '확인 후 삭제 전파', description: '삭제 동기화는 충돌 검토 후 사용자 확인이 있을 때만 진행합니다.' }
    ];
    public readonly holidayLayer = { id: 'kr-public-holidays', label: '한국 공휴일', enabled: true, system: true };
    public readonly weekLabels = ['일', '월', '화', '수', '목', '금', '토'];
    public readonly todoWeekLabels = ['월', '화', '수', '목', '금', '토', '일'];
    public readonly tabs: Array<{ id: AppTab; label: string; iconClass: string }> = [
        { id: 'calendar', label: '캘린더', iconClass: 'fa-regular fa-calendar-days' },
        { id: 'todo', label: '할 일', iconClass: 'fa-solid fa-list-check' },
        { id: 'share', label: '공유', iconClass: 'fa-solid fa-user-group' },
        { id: 'decorate', label: '꾸미기', iconClass: 'fa-solid fa-palette' },
        { id: 'settings', label: '설정', iconClass: 'fa-solid fa-gear' }
    ];
    public themeTokens: Theme[] = createDefaultThemes();
    public readonly fontOptions = OWNIT_FONT_OPTIONS;
    public readonly backgroundOptions: DecorationBackgroundOption[] = [
        { id: 'white', label: '화이트', color: OWNIT_WHITE_BACKGROUND, texture: OWNIT_EMPTY_TEXTURE },
        { id: OWNIT_CLOUD_DANCER_THEME_ID, label: 'Cloud Dancer', color: OWNIT_NEUTRAL_COLORS.cloudDancer, texture: OWNIT_EMPTY_TEXTURE },
        { id: 'warm-paper', label: '종이 질감', color: '#fbf7ee', texture: 'radial-gradient(circle at 16px 14px, rgba(91, 72, 48, 0.08) 0 1px, transparent 1.5px), linear-gradient(transparent, transparent)' },
        { id: 'soft-grid', label: '소프트 그리드', color: '#fbf8f2', texture: 'linear-gradient(rgba(111, 98, 87, 0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(111, 98, 87, 0.12) 1px, transparent 1px)' }
    ];
    public readonly emojiStickerCategories: EmojiStickerCategory[] = [
        {
            id: 'mood',
            label: '감정',
            description: '하루 기분',
            stickers: [
                { id: 'emoji-mood-smile', label: '좋음', emoji: '🙂', categoryId: 'mood', iconClass: '', color: '#4f5d75', background: '#f3f7fb' },
                { id: 'emoji-mood-laugh', label: '웃음', emoji: '😆', categoryId: 'mood', iconClass: '', color: '#7a5a2f', background: '#fff7df' },
                { id: 'emoji-mood-sad', label: '속상함', emoji: '😢', categoryId: 'mood', iconClass: '', color: '#4f6f8f', background: '#eef6ff' },
                { id: 'emoji-mood-angry', label: '화남', emoji: '😡', categoryId: 'mood', iconClass: '', color: '#934840', background: '#fff1ed' },
                { id: 'emoji-mood-love', label: '좋아함', emoji: '😍', categoryId: 'mood', iconClass: '', color: '#8f4d61', background: '#fff1f5' },
                { id: 'emoji-mood-party', label: '신남', emoji: '🥳', categoryId: 'mood', iconClass: '', color: '#6d5b98', background: '#f4f1ff' }
            ]
        },
        {
            id: 'workout-study-work',
            label: '운동/공부/업무',
            description: '집중 기록',
            stickers: [
                { id: 'emoji-workout-muscle', label: '운동', emoji: '💪', categoryId: 'workout-study-work', iconClass: '', color: '#66744a', background: '#f3f7ea' },
                { id: 'emoji-workout-run', label: '러닝', emoji: '🏃', categoryId: 'workout-study-work', iconClass: '', color: '#7a5a2f', background: '#fff4e6' },
                { id: 'emoji-study-book', label: '공부', emoji: '📚', categoryId: 'workout-study-work', iconClass: '', color: '#4e615a', background: '#eef8f3' },
                { id: 'emoji-study-pencil', label: '필기', emoji: '✏️', categoryId: 'workout-study-work', iconClass: '', color: '#7d5b36', background: '#fff8e8' },
                { id: 'emoji-work-laptop', label: '업무', emoji: '💻', categoryId: 'workout-study-work', iconClass: '', color: '#4f5d75', background: '#eef3fb' },
                { id: 'emoji-work-check', label: '완료', emoji: '✅', categoryId: 'workout-study-work', iconClass: '', color: '#527a61', background: '#edf8f0' }
            ]
        },
        {
            id: 'weather',
            label: '날씨',
            description: '오늘의 하늘',
            stickers: [
                { id: 'emoji-weather-sun', label: '맑음', emoji: '☀️', categoryId: 'weather', iconClass: '', color: '#8a6427', background: '#fff7df' },
                { id: 'emoji-weather-cloud', label: '구름', emoji: '☁️', categoryId: 'weather', iconClass: '', color: '#61707f', background: '#f1f5f8' },
                { id: 'emoji-weather-rain', label: '비', emoji: '🌧️', categoryId: 'weather', iconClass: '', color: '#4f6f8f', background: '#eef6ff' },
                { id: 'emoji-weather-snow', label: '눈', emoji: '❄️', categoryId: 'weather', iconClass: '', color: '#5d7485', background: '#f4fbff' },
                { id: 'emoji-weather-rainbow', label: '무지개', emoji: '🌈', categoryId: 'weather', iconClass: '', color: '#6d5b98', background: '#f5f2ff' },
                { id: 'emoji-weather-moon', label: '밤', emoji: '🌙', categoryId: 'weather', iconClass: '', color: '#5b557a', background: '#f2f0fb' }
            ]
        },
        {
            id: 'celebration',
            label: '기념일',
            description: '특별한 날',
            stickers: [
                { id: 'emoji-celebration-birthday', label: '생일', emoji: '🎂', categoryId: 'celebration', iconClass: '', color: '#8f4d61', background: '#fff1f5' },
                { id: 'emoji-celebration-party', label: '축하', emoji: '🎉', categoryId: 'celebration', iconClass: '', color: '#7a5a2f', background: '#fff6e5' },
                { id: 'emoji-celebration-gift', label: '선물', emoji: '🎁', categoryId: 'celebration', iconClass: '', color: '#934840', background: '#fff1ed' },
                { id: 'emoji-celebration-flowers', label: '꽃다발', emoji: '💐', categoryId: 'celebration', iconClass: '', color: '#6f5b65', background: '#fff3f8' },
                { id: 'emoji-celebration-letter', label: '편지', emoji: '💌', categoryId: 'celebration', iconClass: '', color: '#865a67', background: '#fff5f7' },
                { id: 'emoji-celebration-trophy', label: '성취', emoji: '🏆', categoryId: 'celebration', iconClass: '', color: '#7a5a2f', background: '#fff8e6' }
            ]
        },
        {
            id: 'rest',
            label: '휴식',
            description: '쉬는 시간',
            stickers: [
                { id: 'emoji-rest-coffee', label: '커피', emoji: '☕', categoryId: 'rest', iconClass: '', color: '#6d5844', background: '#f7f1ea' },
                { id: 'emoji-rest-sleep', label: '수면', emoji: '💤', categoryId: 'rest', iconClass: '', color: '#546178', background: '#eff3fb' },
                { id: 'emoji-rest-bath', label: '목욕', emoji: '🛁', categoryId: 'rest', iconClass: '', color: '#527184', background: '#eef8fb' },
                { id: 'emoji-rest-meditation', label: '명상', emoji: '🧘', categoryId: 'rest', iconClass: '', color: '#66744a', background: '#f2f7ed' },
                { id: 'emoji-rest-headphone', label: '음악', emoji: '🎧', categoryId: 'rest', iconClass: '', color: '#4f5d75', background: '#f1f4fa' },
                { id: 'emoji-rest-tea', label: '차', emoji: '🍵', categoryId: 'rest', iconClass: '', color: '#60764f', background: '#f1f8ea' }
            ]
        }
    ];
    public readonly basicStickerAssets: BasicStickerAsset[] = this.emojiStickerCategories.reduce((assets, category) => [...assets, ...category.stickers], [] as BasicStickerAsset[]);
    public readonly stickerUploadAccept = 'image/png,image/jpeg,image/webp,image/gif';
    public readonly stickerUploadAllowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    public readonly stickerUploadMaxBytes = 12 * 1024 * 1024;
    public readonly stickerUploadTargetBytes = 1.4 * 1024 * 1024;
    public readonly stickerUploadMaxDimension = 1280;
    public readonly stickerBackgroundRemovalMaxDimension = 1024;
    public readonly eventColorOptions: Array<{ label: string; value: string }> = [
        { label: '토프', value: OWNIT_NEUTRAL_COLORS.personal },
        { label: '모브 그레이', value: OWNIT_NEUTRAL_COLORS.shared },
        { label: '브라운 레드', value: OWNIT_NEUTRAL_COLORS.red },
        { label: '오커', value: OWNIT_NEUTRAL_COLORS.ochre },
        { label: '스톤', value: OWNIT_NEUTRAL_COLORS.stone },
        { label: '로즈 브라운', value: OWNIT_NEUTRAL_COLORS.rose }
    ];
    public readonly reminderPresetOptions: ReminderPresetOption[] = [
        { value: 'none', label: '없음', description: '알림을 끕니다.', iconClass: 'fa-regular fa-bell-slash' },
        { value: 'at_time', label: '정시', description: '설정한 시간에 알립니다.', iconClass: 'fa-regular fa-clock' },
        { value: 'before_5', label: '5분 전', description: '설정 시간 5분 전에 알립니다.', iconClass: 'fa-solid fa-clock-rotate-left' },
        { value: 'before_10', label: '10분 전', description: '설정 시간 10분 전에 알립니다.', iconClass: 'fa-solid fa-clock-rotate-left' },
        { value: 'before_30', label: '30분 전', description: '설정 시간 30분 전에 알립니다.', iconClass: 'fa-solid fa-clock-rotate-left' },
        { value: 'before_60', label: '1시간 전', description: '설정 시간 1시간 전에 알립니다.', iconClass: 'fa-regular fa-hourglass-half' },
        { value: 'before_1440', label: '하루 전', description: '설정 시간 하루 전에 알립니다.', iconClass: 'fa-regular fa-calendar-minus' },
        { value: 'custom', label: '사용자 지정', description: '직접 날짜와 시간을 정합니다.', iconClass: 'fa-regular fa-calendar' }
    ];
    public readonly eventReminderOptions = this.reminderPresetOptions;
    public readonly calendarVisibilityOptions: Array<{ label: string; value: Visibility; description: string }> = [
        { label: '그룹 멤버', value: 'group', description: '센터 구성원만 공유 일정에 참여합니다.' },
        { label: '초대 전용', value: 'private', description: '소유자와 관리자가 초대한 사람만 봅니다.' },
        { label: '공개 보기', value: 'public', description: '링크를 가진 사람이 읽을 수 있습니다.' }
    ];
    public readonly todoColorOptions: Array<{ label: string; value: string }> = [
        { label: '계획', value: OWNIT_NEUTRAL_COLORS.personal },
        { label: '공유', value: OWNIT_NEUTRAL_COLORS.shared },
        { label: '기록', value: OWNIT_NEUTRAL_COLORS.red },
        { label: '루틴', value: OWNIT_NEUTRAL_COLORS.ochre },
        { label: '업무', value: OWNIT_NEUTRAL_COLORS.stone },
        { label: '개인', value: OWNIT_NEUTRAL_COLORS.rose }
    ];
    public readonly mateReactionOptions: MateReactionOption[] = [
        { emoji: '♥', label: '하트', message: '응원해요' },
        { emoji: '☺', label: '미소', message: '좋아요' },
        { emoji: '★', label: '칭찬', message: '멋져요' },
        { emoji: '✦', label: '반짝', message: '몰입 최고' }
    ];
    public readonly mateProfiles: MateProfile[] = [];
    public readonly todoGoalPalettes: TodoGoalPalette[] = [
        {
            id: 'default',
            label: '기본',
            colors: [
                { label: '잉크', value: '#191919' },
                { label: '네이비', value: '#3A4267' },
                { label: '블루', value: '#406EA8' },
                { label: '스카이', value: '#5B91B8' },
                { label: '틸', value: '#3F8D91' },
                { label: '그린', value: '#5E8F67' },
                { label: '세이지', value: '#7EA176' },
                { label: '올리브', value: '#878B51' },
                { label: '머스타드', value: '#C0A24B' },
                { label: '앰버', value: '#C6862D' },
                { label: '테라코타', value: '#C56E52' },
                { label: '코랄', value: '#D77C70' },
                { label: '로즈', value: '#C96B8A' },
                { label: '모브', value: '#A57598' },
                { label: '라벤더', value: '#8F80B8' },
                { label: '인디고', value: '#676FA6' },
                { label: '토프', value: '#9A8268' },
                { label: '브라운', value: '#7B5B45' },
                { label: '스톤', value: '#817A74' },
                { label: '슬레이트', value: '#6F7E86' },
                { label: '플럼', value: '#714B60' },
                { label: '와인', value: '#A24255' },
                { label: '딥그린', value: '#416F5E' },
                { label: '차콜', value: '#4B4B4B' }
            ]
        },
        {
            id: 'ss-2024',
            label: '포인트',
            colors: [
                { label: '소프트 블랙', value: '#202124' },
                { label: '클라우드 블루', value: '#76A6C7' },
                { label: '아쿠아', value: '#58B7B5' },
                { label: '피스타치오', value: '#94BA68' },
                { label: '버터', value: '#D9BC50' },
                { label: '멜론', value: '#E49B7F' },
                { label: '피오니', value: '#DB7199' },
                { label: '라일락', value: '#9A86CF' },
                { label: '라군', value: '#318BA2' },
                { label: '팜그린', value: '#4C9159' },
                { label: '선셋', value: '#D26C4B' },
                { label: '카카오', value: '#7B5A42' }
            ]
        }
    ];
    public readonly todoGoalMarkerOptions: TodoGoalMarkerOption[] = [
        { value: 'dot', label: '점', iconClass: 'fa-solid fa-circle' },
        { value: 'ring', label: '링', iconClass: 'fa-regular fa-circle' },
        { value: 'square', label: '사각', iconClass: 'fa-solid fa-square' },
        { value: 'check', label: '체크', iconClass: 'fa-solid fa-check' },
        { value: 'star', label: '별', iconClass: 'fa-solid fa-star' }
    ];
    public readonly todoCategoryOptions = ['계획', '공유', '기록', '루틴', '업무', '개인'];
    public readonly routineRepeatTypeOptions: RoutineRepeatTypeOption[] = [
        { value: 'daily', label: '매일' },
        { value: 'weekly', label: '매주' },
        { value: 'monthly', label: '매월' },
        { value: 'custom', label: '사용자 지정' }
    ];
    public readonly routineWeekdayOptions: RoutineWeekdayOption[] = [
        { value: 1, label: '월' },
        { value: 2, label: '화' },
        { value: 3, label: '수' },
        { value: 4, label: '목' },
        { value: 5, label: '금' },
        { value: 6, label: '토' },
        { value: 0, label: '일' }
    ];
    public readonly routineDateOptions = Array.from({ length: 31 }, (_, index) => index + 1);
    public readonly reminderQuickOptions = this.reminderPresetOptions;
    public readonly uncategorizedTodoLabel = '미분류';
    public readonly uncategorizedTodoColor = OWNIT_NEUTRAL_COLORS.stone;
    public readonly sharedCompletionFilterOptions: Array<{ value: SharedCompletionFilter; label: string }> = [
        { value: 'all', label: '전체 상태' },
        { value: 'open', label: '미완료' },
        { value: 'completed', label: '완료' }
    ];
    public readonly permissionRows: PermissionRow[] = [
        {
            role: 'owner',
            label: 'owner',
            description: '센터와 공유 캘린더 전체 관리',
            actions: ['manage_group', 'manage_shared_calendar', 'create_shared_event', 'edit_own_shared_event', 'view_shared_event']
        },
        {
            role: 'admin',
            label: 'admin',
            description: '공유 캘린더, 일정, 멤버 관리',
            actions: ['manage_group', 'manage_shared_calendar', 'create_shared_event', 'edit_own_shared_event', 'view_shared_event']
        },
        {
            role: 'member',
            label: 'member',
            description: '공유 일정 생성과 본인 일정 수정',
            actions: ['create_shared_event', 'edit_own_shared_event', 'view_shared_event']
        },
        {
            role: 'viewer',
            label: 'viewer',
            description: '공유 일정 읽기 전용',
            actions: ['view_shared_event']
        }
    ];
    public readonly groupRoleOptions: Array<{ value: GroupRole; label: string }> = [
        { value: 'owner', label: 'owner' },
        { value: 'admin', label: 'admin' },
        { value: 'member', label: 'member' },
        { value: 'viewer', label: 'viewer' }
    ];
    public readonly groupMemberStatusOptions: Array<{ value: GroupMemberStatus; label: string }> = [
        { value: 'invited', label: '초대됨' },
        { value: 'active', label: '참여 중' },
        { value: 'left', label: '탈퇴' },
        { value: 'removed', label: '내보냄' }
    ];

    public user: OwnitUser = { ...createEmptyUser(), profileComplete: false };
    public calendars: OwnitCalendar[] = [];
    public groups: OwnitGroup[] = [];
    public group: OwnitGroup = createEmptyGroup(this.user);
    public selectedSharedGroupId = '';
    public selectedSharedCalendarId = '';
    public sharedAssigneeFilter = 'all';
    public sharedCompletionFilter: SharedCompletionFilter = 'all';
    public events: OwnitEvent[] = [];
    public todos: OwnitTodo[] = [];
    public todoCategories: OwnitTodoCategory[] = [];
    public todoGoals: OwnitTodoGoal[] = [];
    public todoRoutines: OwnitTodoRoutine[] = [];
    public todoDailyRecords: OwnitTodoDailyRecord[] = [];
    public todoTimerSessions: OwnitTodoTimerSession[] = [];
    public todoCompletionRecords: OwnitTodoCompletionRecord[] = [];
    public todoReactions: OwnitTodoReaction[] = [];
    public todoSocialView: TodoSocialView = 'me';
    public todoCalendarDisplayOptions: TodoCalendarDisplayOptions = this.createDefaultTodoCalendarDisplayOptions();
    public activeTodoTimer: ActiveTodoTimerState | null = null;
    public todoTimerNotice = '';
    public todoTimerNow = Date.now();
    public followedMateIds: string[] = [];
    public defaultTodoGoalId = '';
    public recentTodoGoalId = '';
    public stickerAssets: OwnitStickerAsset[] = [];
    public decorations: DecorationState[] = [];
    public decoration: DecorationState = createEmptyDecoration(this.user);
    public selectedStickerId = '';
    public stickerTarget: StickerPlacementTarget = 'calendar';
    public customEmojiInput = '';
    public customEmojiError = '';
    public customEmojiNotice = '';
    public stickerUploadDraft: StickerUploadDraftState = this.createStickerUploadDraft();
    public editingStickerAssetId = '';
    public stickerAssetRenameDraft = '';
    public eventFormOpen = false;
    public editingEventId: string | null = null;
    public focusedEventId: string | null = null;
    public eventFormError = '';
    public eventForm: EventFormState = this.createEventForm(this.selectedDate);
    public calendarFormOpen = false;
    public editingCalendarId: string | null = null;
    public calendarFormError = '';
    public calendarForm: CalendarFormState = this.createCalendarForm('personal');
    public calendarActionCalendarId: string | null = null;
    public memberManagementOpen = false;
    public memberManagementError = '';
    public memberManagementNotice = '';
    public memberInviteForm: MemberInviteDraftState = this.createMemberInviteDraft();
    public memberJoinCode = '';
    public sharedGuardDialogMode: SharedGuardDialogMode | null = null;
    public sharedGuardError = '';
    public groupRenameDraft = '';
    public memberRemovalTargetId: string | null = null;
    public leaveGroupTransferMemberId = '';
    public todoFormOpen = false;
    public editingTodoId: string | null = null;
    public todoFormError = '';
    public todoForm: TodoFormState = this.createTodoForm(this.selectedDate);
    public todoActionTodoId: string | null = null;
    public todoActionMode: TodoActionMode = 'menu';
    public todoActionError = '';
    public todoActionNotice = '';
    public todoActionDraft: TodoActionDraftState = this.createTodoActionDraft();
    public notificationPermission: NotificationPermissionState = 'unsupported';
    public reminderPermissionMessage = '';
    public reminderNotices: DueReminderNotice[] = [];
    public reminderToast: DueReminderNotice | null = null;
    public reminderCenterOpen = false;
    public inlineTodoDraft: InlineTodoDraftState = this.createInlineTodoDraft();
    public todoCreateFlowOpen = false;
    public todoCreateMode: TodoCreateMode = 'todo';
    public todoCreateContext: TodoCreateFlowContext = this.createTodoCreateContext(this.selectedDate);
    public todoGoalFormOpen = false;
    public editingTodoGoalId: string | null = null;
    public todoGoalFormError = '';
    public todoGoalForm: TodoGoalFormState = this.createTodoGoalForm();
    public todoCategoryDeleteDialogOpen = false;
    public todoCategoryDeleteTargetId: string | null = null;
    public todoCategoryDeleteMode: TodoCategoryDeleteMode = 'uncategorized';
    public todoCategoryDeleteMoveTargetId = '';
    public todoCategoryDeleteError = '';
    public todoCategoryDeleteSubmitting = false;
    public routineFormOpen = false;
    public editingRoutineId: string | null = null;
    public routineFormError = '';
    public routineForm: RoutineFormState = this.createRoutineForm(this.selectedDate);
    public routineSectionCollapsed = false;
    public completedItemsCollapsed = false;
    public activeTodoGoalPaletteId: TodoGoalPaletteId = 'default';
    public receiptModalOpen = false;
    public receiptSaving = false;
    public receiptSaveError = '';
    public receiptPreviewSavedAt = new Date();
    public receiptDrafts: Record<string, ReceiptDraft> = {};
    public receiptDraft: ReceiptDraft = this.createReceiptDraft();
    public readonly receiptEmojiOptions = ['☁', '🙂', '🌿', '✨', '☕'];
    public readonly receiptDateFormatOptions: DailyReceiptDateFormatOption[] = [
        { value: 'korean-full', label: '2026년 7월 3일 금요일' },
        { value: 'korean-short', label: '7월 3일 금' },
        { value: 'iso', label: '2026-07-03' },
        { value: 'dot', label: '2026.07.03' }
    ];
    public readonly receiptTemplateStyles: DailyReceiptTemplateStyle[] = [
        {
            id: 'basic-receipt',
            label: '기본 영수증',
            paperClass: 'receipt-paper-style-basic',
            cssVars: {
                '--receipt-accent': '#4d463d',
                '--receipt-ink': '#292622',
                '--receipt-muted': '#6f6759',
                '--receipt-line': 'rgba(43, 41, 37, 0.42)',
                '--receipt-paper': '#fffdf8'
            }
        }
    ];
    public receiptTemplate: DailyReceiptTemplateState = this.createDefaultReceiptTemplate();
    public dailyRecordDraft: DailyRecordDraft = this.createDailyRecordDraft();
    public dailyRecordSaving = false;
    public dailyRecordError = '';
    public dailyRecordNotice = '';
    public readonly dailyRecordEmojiOptions = ['☁', '🙂', '💪', '🌿', '✨', '☕', '🔥', '🧘'];
    public todoAiSuggestionDraft: TodoAiSuggestionDraftState = this.createTodoAiSuggestionDraftState();
    public widgetSummary: OwnitWidgetSummary = this.createWidgetSummary();
    public widgetSummaryCacheLabel = '최근 위젯 캐시 없음';
    public cloudSyncStatus: CloudRepositoryStatus = this.createLocalCloudStatus();
    public cloudSyncLoading = false;
    public cloudSyncNotice = '';
    public cloudSyncError = '';
    public networkIsOffline = false;
    public networkConnectionIsSlow = false;
    public readonly widgetShapeRows: WidgetShapeRow[] = [
        { size: 'small', label: '작은 위젯', fields: 'headline, completionRate, nextTitle, accentColor' },
        { size: 'medium', label: '중간 위젯', fields: 'scheduleItems[2], todoItems[3], nextSchedule' },
        { size: 'large', label: '큰 위젯', fields: 'todaySchedule[5], todayTodo[6], routineStatus[4]' }
    ];

    private readonly authProvider = createAuthRepository();
    private readonly dataRepository: OwnitRepositoryAdapter = createOwnitRepositoryAdapter();
    private readonly aiSuggestionProvider: AiSuggestionProvider = createAiSuggestionProvider();
    private readonly externalCalendarProviders: ExternalCalendarProvider[] = createMockExternalCalendarProviders();
    private readonly holidayService = new HolidayService();
    private readonly knownTabs: AppTab[] = ['calendar', 'todo', 'share', 'decorate', 'settings'];
    private readonly routineOccurrencePrefix = 'routine-occurrence::';
    private readonly mateFollowStorageKey = 'ownit.todo.mate.following.v1';
    private readonly defaultTodoGoalStorageKey = 'ownit.todo.category.default.v1';
    private readonly recentTodoGoalStorageKey = 'ownit.todo.category.recent.v1';
    private readonly todoCalendarDisplayStorageKey = 'ownit.todo.calendar.display.v1';
    private readonly receiptTemplateStorageKey = 'ownit.dailyReceipt.template.v1';
    private readonly externalOAuthStorageKey = 'ownit.externalCalendar.oauth.connections.v1';
    private readonly externalOAuthPendingStorageKey = 'ownit.externalCalendar.oauth.pending.v1';
    private readonly externalSyncPolicyStorageKey = 'ownit.externalCalendar.sync.policies.v1';
    private readonly externalMappingStorageKey = 'ownit.externalCalendar.eventMappings.v1';
    private readonly reminderFiredStoragePrefix = 'ownit.reminder.fired.v1';
    private readonly reminderLookbackMs = 24 * 60 * 60 * 1000;
    private readonly reminderScheduleWindowMs = 7 * 24 * 60 * 60 * 1000;
    private readonly maxReminderTimeoutMs = 2147483647;
    private readonly customEmojiStickerPrefix = 'emoji-custom:';
    private routerSub?: Subscription;
    private authToastHideTimer: number | null = null;
    private authToastClearTimer: number | null = null;
    private cloudStatusPollTimer: number | null = null;
    private todoTimerTickTimer: number | null = null;
    private reminderCheckTimer: number | null = null;
    private reminderToastHideTimer: number | null = null;
    private reminderScheduleTimers = new Map<string, number>();
    private reminderRuntimeUserId = '';
    private reminderFiredKeys = new Set<string>();
    private previousBodyOverflow: string | null = null;
    private stickerDragState: StickerDragState | null = null;
    private rangeDragPointerId: number | null = null;
    private rangeDragMoved = false;
    private suppressNextDateClick = false;
    private calendarTapRangeHistoryPushed = false;
    private calendarEventBarPointerState: CalendarEventBarPointerState | null = null;
    private suppressNextEventBarClick = false;

    constructor(@Inject( Service) public service: Service,@Inject( Router)  private router: Router) { }

    @HostListener('document:keydown.escape', ['$event'])
    public async handleEscapeKey(event: KeyboardEvent) {
        if (this.isDragging) {
            event.preventDefault();
            await this.cancelCalendarRangeDrag();
            return;
        }
        if (this.calendarTapRangeMode) {
            event.preventDefault();
            await this.cancelCalendarTapRangeMode('escape');
            return;
        }
        if (this.selectedRange) {
            event.preventDefault();
            await this.clearCalendarRangeSelection();
            return;
        }
        if (this.todoCategoryDeleteDialogOpen) {
            event.preventDefault();
            await this.closeTodoCategoryDeleteDialog();
            return;
        }
        if (this.todoCreateFlowOpen) {
            event.preventDefault();
            await this.closeTodoCreateFlow();
        }
    }

    @HostListener('document:pointerup', ['$event'])
    public async handleDocumentPointerUp(event: PointerEvent) {
        this.finishCalendarEventBarPointer(event);
        await this.endCalendarRangeDrag(event);
    }

    @HostListener('document:pointercancel', ['$event'])
    public async handleDocumentPointerCancel(event: PointerEvent) {
        this.finishCalendarEventBarPointer(event);
        await this.cancelCalendarRangeDrag(event);
    }

    @HostListener('window:popstate')
    public async handleWindowPopState() {
        if (!this.calendarTapRangeHistoryPushed) return;
        await this.cancelCalendarTapRangeMode('history');
    }

    @HostListener('window:online')
    public async handleWindowOnline() {
        this.syncNetworkStatus();
        await this.refreshCloudDataIfStale(0);
    }

    @HostListener('window:offline')
    public async handleWindowOffline() {
        this.syncNetworkStatus();
        await this.service.render();
    }

    @HostListener('window:focus')
    public async handleWindowFocus() {
        this.syncNetworkStatus();
        await this.refreshCloudDataIfStale();
    }

    public async ngOnInit() {
        await this.service.init(null);
        this.syncNotificationPermissionState();
        this.syncNetworkStatus();
        await this.restoreAuthSession();
        await this.handleExternalOAuthCallback();
        this.syncCloudStatus();
        this.syncRoute();
        this.buildMonth();
        this.startCloudStatusWatcher();
        this.routerSub = this.router.events.subscribe(async (event) => {
            if (event instanceof NavigationEnd) {
                this.syncRoute();
                await this.service.render();
            }
        });
        await this.service.render();
    }

    public ngOnDestroy() {
        if (this.routerSub) this.routerSub.unsubscribe();
        this.clearAuthToastTimers();
        this.stopCloudStatusWatcher();
        this.stopTodoTimerTicker();
        this.stopReminderWatcher();
        this.clearReminderToastTimer();
        this.unlockTodoActionScroll();
    }

    public get canShowApp() {
        return this.isAuthenticated && !this.needsProfileSetup;
    }

    public get visibleTabs() {
        return this.tabs.filter((tab) => this.canAccessTab(tab.id));
    }

    public get activeTabLabel() {
        return this.tabs.find((tab) => tab.id === this.activeTab)?.label || '캘린더';
    }

    public get reminderCenterCountLabel() {
        return `${this.reminderNotices.length}개`;
    }

    public get cloudSyncStatusLabel() {
        if (!this.cloudSyncStatus.enabled) return '로컬';
        if (this.cloudSyncStatus.state === 'syncing' || this.cloudSyncLoading) return '동기화 중';
        if (this.cloudSyncStatus.state === 'error') return '확인 필요';
        return this.cloudSyncStatus.pendingWrites ? '대기 중' : '연결됨';
    }

    public get canRunCloudActions() {
        return this.cloudSyncStatus.enabled && this.isAuthenticated && !this.needsProfileSetup && !this.cloudSyncLoading;
    }

    public get selectedUserFontOption() {
        const fontId = normalizeOwnitFontId(this.user.defaultFont);
        return this.fontOptions.find((font) => font.id === fontId) || this.fontOptions[0];
    }

    public get activeBodyFontLabel() {
        return this.selectedUserFontOption?.label || OWNIT_DEFAULT_FONT_ID;
    }

    public fontOptionStack(fontId: string) {
        return ownitFontStack(fontId);
    }

    public async setUserDefaultFont(fontId: string) {
        if (!this.canManageSettingsAction) return;

        const nextFont = normalizeOwnitFontId(fontId);
        this.settingsNotice = '';
        this.settingsError = '';

        try {
            const nextProfile = {
                ...this.profileForm,
                defaultFont: nextFont
            };
            const session = await this.authProvider.updateProfile(nextProfile);
            const nextUser = {
                ...session.user,
                defaultFont: normalizeOwnitFontId(session.user.defaultFont)
            };

            this.user = nextUser;
            this.isAuthenticated = true;
            this.needsProfileSetup = !nextUser.profileComplete;
            this.profileForm = {
                name: nextUser.name,
                nickname: nextUser.nickname,
                profileImage: nextUser.profileImage,
                defaultFont: nextUser.defaultFont,
                defaultTheme: nextUser.defaultTheme || OWNIT_DEFAULT_THEME_ID
            };

            this.dataRepository.updateUser(nextUser.id, {
                defaultFont: nextUser.defaultFont,
                updatedAt: new Date().toISOString()
            });
            this.settingsNotice = '기본 본문 폰트를 저장했습니다.';
        } catch (error) {
            this.settingsError = this.dataErrorMessage(error, '기본 폰트를 저장하지 못했습니다.');
        }

        await this.service.render();
    }

    public get networkStatusMessage() {
        if (this.networkIsOffline) {
            return '오프라인 상태입니다. 저장된 캘린더와 Todo는 계속 볼 수 있고, 클라우드 동기화는 온라인 복귀 후 다시 시도됩니다.';
        }
        if (this.networkConnectionIsSlow) {
            return '느린 네트워크 상태입니다. 일정과 Todo 저장은 유지되며, 동기화와 이미지 저장은 조금 늦을 수 있습니다.';
        }
        return '';
    }

    public get notificationPermissionLabel() {
        if (this.notificationPermission === 'granted') return '허용됨';
        if (this.notificationPermission === 'denied') return '차단됨';
        if (this.notificationPermission === 'default') return '요청 전';
        return '미지원';
    }

    public get notificationPermissionDescription() {
        if (this.notificationPermission === 'granted') return '앱이 열려 있는 동안 예약된 일정, Todo, 루틴 알림을 브라우저 알림으로 표시합니다.';
        if (this.notificationPermission === 'denied') return '브라우저 설정에서 알림을 허용하기 전까지 앱 안 알림 센터와 toast로만 표시합니다.';
        if (this.notificationPermission === 'default') return '권한을 허용하면 지원 브라우저에서 가까운 시간 알림이 실제 브라우저 알림으로 표시됩니다.';
        return '이 브라우저나 iOS 환경에서는 Notification API를 사용할 수 없어 앱 안 알림만 표시합니다.';
    }

    public get canRequestNotificationPermission() {
        return this.notificationPermission === 'default';
    }

    public isReminderPresetEnabled(preset: ReminderPreset | string | null | undefined) {
        return this.normalizeReminderPreset(preset) !== 'none';
    }

    public isReminderPresetCustom(preset: ReminderPreset | string | null | undefined) {
        return this.normalizeReminderPreset(preset) === 'custom';
    }

    public async setTodoCalendarDisplayOption(key: TodoCalendarDisplayOptionKey, value: boolean) {
        const next = this.normalizeTodoCalendarDisplayOptions({
            ...this.todoCalendarDisplayOptions,
            [key]: Boolean(value)
        });

        if (key === 'enabled' && next.enabled && !next.showTodoCompleted && !next.showRoutineCompleted && !next.showCompletionRate) {
            next.showTodoCompleted = true;
            next.showRoutineCompleted = true;
        }

        this.todoCalendarDisplayOptions = next;
        this.saveTodoCalendarDisplayOptions();
        this.buildMonth();
        await this.service.render();
    }

    public async refreshCloudData(silent = false) {
        if (!this.dataRepository.refreshFromCloud || !this.isAuthenticated || this.needsProfileSetup) {
            this.syncCloudStatus();
            return;
        }

        this.cloudSyncLoading = true;
        if (!silent) {
            this.cloudSyncNotice = '';
            this.cloudSyncError = '';
        }
        this.syncCloudStatus();

        try {
            const snapshot = await this.dataRepository.refreshFromCloud(this.user);
            this.initializeUserData(snapshot);
            const status = this.dataRepository.getCloudStatus?.();
            if (status?.lastError || status?.state === 'error') {
                this.cloudSyncError = status.lastError || status.message || '클라우드 데이터를 가져오지 못했습니다.';
            } else if (!silent) {
                this.cloudSyncNotice = '클라우드 데이터를 가져와 로컬 데이터와 병합했습니다.';
            }
        } catch (error) {
            this.cloudSyncError = this.dataErrorMessage(error, '클라우드 데이터를 가져오지 못했습니다.');
        } finally {
            this.cloudSyncLoading = false;
            this.syncCloudStatus();
            await this.service.render();
        }
    }

    public async importLocalDataToCloud() {
        if (!this.dataRepository.importLocalDataToCloud || !this.isAuthenticated || this.needsProfileSetup) {
            this.syncCloudStatus();
            return;
        }

        this.cloudSyncLoading = true;
        this.cloudSyncNotice = '';
        this.cloudSyncError = '';
        this.syncCloudStatus();

        try {
            const result = await this.dataRepository.importLocalDataToCloud(this.user);
            const snapshot = this.dataRepository.getSnapshotForUser(this.user.id);
            this.initializeUserData(snapshot);
            const staleNotice = result.skippedStaleWrites > 0
                ? ` 클라우드 최신 데이터 ${result.skippedStaleWrites}개는 덮어쓰지 않았습니다.`
                : '';
            this.cloudSyncNotice = `로컬 데이터 ${result.pushedRecords}개를 클라우드에 저장하고 ${result.pulledRecords}개를 확인했습니다.${staleNotice}`;
        } catch (error) {
            this.cloudSyncError = this.dataErrorMessage(error, '로컬 데이터를 클라우드로 가져오지 못했습니다.');
        } finally {
            this.cloudSyncLoading = false;
            this.syncCloudStatus();
            await this.service.render();
        }
    }

    public get todoReminderPreviewLabel() {
        const todo = this.todoActionTodo;
        if (!todo) return '';
        const preview = this.previewReminderAtFromDraft(todo);
        if (!preview) return '알림 시간을 선택해 주세요.';
        return `${this.reminderDateTimeLabel(preview)}에 알려드립니다.`;
    }

    public get isAdminAccount() {
        return isAdminUser(this.user);
    }

    public get accountRoleLabel() {
        return this.isAdminAccount ? '관리자' : userRoleLabel(this.user.role);
    }

    public get accountRoleBadgeLabel() {
        return this.isAdminAccount ? 'ADMIN' : this.accountRoleLabel;
    }

    public get accountIdentifierLabel() {
        return this.user.username || this.user.email || this.user.id;
    }

    public get canCreateEventAction() {
        return this.calendars.some((calendar) => canManageEventForUser(this.user, null, calendar, { group: this.groupForCalendar(calendar) }));
    }

    public get canCreateSharedEventAction() {
        return this.writableSharedEventCalendars.some((calendar) => this.selectedSharedGroupCalendars.some((item) => item.id === calendar.id));
    }

    public get canCreateCalendarAction() {
        return this.canManageSettingsAction;
    }

    public get canCreateSharedCalendarAction() {
        return this.canManageSettingsAction || this.activeSharedGroups.some((group) => this.canManageSharedGroup(group));
    }

    public get canCreateSharedCalendarForSelectedGroupAction() {
        if (!this.selectedSharedGroup.id) return this.canCreateSharedCalendarAction;
        return this.canManageSharedGroup(this.selectedSharedGroup);
    }

    public get calendarFormTitle() {
        if (this.editingCalendarId) return this.calendarForm.type === 'shared' ? '공유 캘린더 수정' : '개인 캘린더 수정';
        return this.calendarForm.type === 'shared' ? '공유 캘린더 추가' : '개인 캘린더 추가';
    }

    public get calendarFormActionLabel() {
        return this.editingCalendarId ? '수정 저장' : '캘린더 추가';
    }

    public get calendarActionCalendar() {
        return this.calendarActionCalendarId ? this.calendarById(this.calendarActionCalendarId) : null;
    }

    public get calendarActionEventCount() {
        return this.calendarActionCalendar ? this.eventCountForCalendar(this.calendarActionCalendar.id) : 0;
    }

    public get canCreateTodoAction() {
        return canManageTodoForUser(this.user, null, { group: this.group });
    }

    public get activeTodoGoals() {
        return this.sortTodoGoals(this.todoGoals
            .map((goal) => this.todoGoalWithCategory(goal))
            .filter((goal) => goal.status !== 'archived'));
    }

    public get archivedTodoGoals() {
        return this.sortTodoGoals(this.todoGoals
            .map((goal) => this.todoGoalWithCategory(goal))
            .filter((goal) => goal.status === 'archived'));
    }

    public get defaultTodoGoal() {
        return this.defaultTodoGoalId ? this.todoGoalById(this.defaultTodoGoalId) : null;
    }

    public get recentTodoGoal() {
        return this.recentTodoGoalId ? this.todoGoalById(this.recentTodoGoalId) : null;
    }

    public get preferredTodoGoal() {
        return this.defaultTodoGoal || this.recentTodoGoal || null;
    }

    public get preferredTodoGoalLabel() {
        const defaultGoal = this.defaultTodoGoal;
        const recentGoal = this.recentTodoGoal;
        if (defaultGoal && recentGoal && defaultGoal.id !== recentGoal.id) return `기본 ${defaultGoal.name} · 최근 ${recentGoal.name}`;
        if (defaultGoal) return `기본 ${defaultGoal.name}`;
        if (recentGoal) return `최근 ${recentGoal.name}`;
        return '카테고리를 선택하면 최근 항목으로 기억됩니다.';
    }

    public get activeTodoGoalPalette() {
        return this.todoGoalPalettes.find((palette) => palette.id === this.activeTodoGoalPaletteId) || this.todoGoalPalettes[0];
    }

    public get todoCreateDialogLabel() {
        return `${this.todoCreateTitle} 창`;
    }

    public get todoCreateTitle() {
        if (this.todoCreateMode === 'category') return '카테고리 추가';
        if (this.todoCreateMode === 'routine') return '루틴 추가';
        return '할 일 추가';
    }

    public get todoCreateSubtitle() {
        const parts = [this.dateLabel(this.todoCreateContext.date || this.selectedDate)];
        if (this.todoCreateMode !== 'category') parts.push(this.todoCreateCategoryLabel);
        return parts.join(' · ');
    }

    public get todoCreateCategoryLabel() {
        const goal = this.todoCreateContext.goalId ? this.todoGoalById(this.todoCreateContext.goalId) : null;
        return goal?.name || this.todoCreateContext.category || this.uncategorizedTodoLabel;
    }

    public get todoCreateModeHelp() {
        if (this.todoCreateMode === 'category') return '색상과 공개 범위를 정하면 할 일과 루틴에서 바로 선택할 수 있습니다.';
        if (this.todoCreateMode === 'routine') return '선택 날짜를 시작일로 반복 규칙을 만듭니다.';
        return '선택 날짜와 카테고리가 미리 연결되어 있습니다.';
    }

    public get todoGoalFormTitle() {
        return this.editingTodoGoalId ? '카테고리 수정' : '카테고리 추가';
    }

    public get todoGoalFormActionLabel() {
        return this.editingTodoGoalId ? '수정 저장' : '카테고리 추가';
    }

    public get todoCategoryDeleteTarget() {
        return this.todoCategoryDeleteTargetId ? this.todoGoalById(this.todoCategoryDeleteTargetId) : null;
    }

    public get todoCategoryDeleteAffectedTodos() {
        const target = this.todoCategoryDeleteTarget;
        if (!target) return [];
        return this.todos.filter((todo) => this.todoBelongsToGoalCategory(todo, target));
    }

    public get todoCategoryDeleteAffectedRoutines() {
        const target = this.todoCategoryDeleteTarget;
        if (!target) return [];
        return this.todoRoutines.filter((routine) => this.routineBelongsToGoalCategory(routine, target));
    }

    public get todoCategoryDeleteMoveOptions() {
        const targetId = this.todoCategoryDeleteTarget?.id || '';
        return this.activeTodoGoals.filter((goal) => goal.id !== targetId && !goal.locked);
    }

    public get todoCategoryDeleteCanApply() {
        if (this.todoCategoryDeleteSubmitting || !this.todoCategoryDeleteTarget) return false;
        if (this.todoCategoryDeleteMode === 'move') {
            return Boolean(this.todoCategoryDeleteMoveTargetId && this.todoCategoryDeleteMoveOptions.some((goal) => goal.id === this.todoCategoryDeleteMoveTargetId));
        }
        return this.todoCategoryDeleteMode === 'uncategorized' || this.todoCategoryDeleteMode === 'delete-todos';
    }

    public get todoCategoryDeleteMoveHelp() {
        if (this.todoCategoryDeleteMoveOptions.length > 0) return '';
        return '이동할 다른 카테고리가 없습니다. 카테고리 없음으로 이동하거나 함께 삭제를 선택해 주세요.';
    }

    public get todoCategoryDeleteApplyLabel() {
        return this.todoCategoryDeleteSubmitting ? '적용 중' : '적용';
    }

    public get routineFormTitle() {
        return this.editingRoutineId ? '루틴 수정' : '루틴 추가';
    }

    public get routineFormActionLabel() {
        return this.editingRoutineId ? '수정 저장' : '루틴 추가';
    }

    public get canManageDecorationAction() {
        return canManageDecorationForUser(this.user, this.user.id);
    }

    public get canManageSettingsAction() {
        return canManageSettingsForUser(this.user, this.user.id);
    }

    public get canManageGroupAction() {
        return this.canManageSharedGroup(this.selectedSharedGroup);
    }

    public get selectedSharedGroupMembers() {
        return this.sortGroupMembers(this.selectedSharedGroup.members || []);
    }

    public get activeSelectedSharedGroupMembers() {
        return this.selectedSharedGroupMembers.filter((member) => member.status === 'active');
    }

    public get memberManagementTitle() {
        return this.selectedSharedGroup.id ? `${this.selectedSharedGroup.name} 구성원` : '구성원 관리';
    }

    public get memberManagementSummaryLabel() {
        const members = this.selectedSharedGroupMembers;
        const activeCount = members.filter((member) => member.status === 'active').length;
        return `전체 ${members.length}명 · 참여 중 ${activeCount}명`;
    }

    public get sharedGuardDialogOpen() {
        return Boolean(this.sharedGuardDialogMode);
    }

    public get sharedGuardDialogTitle() {
        if (this.sharedGuardDialogMode === 'rename-group') return '센터/그룹 이름 변경';
        if (this.sharedGuardDialogMode === 'remove-member') return '구성원 내보내기';
        if (this.sharedGuardDialogMode === 'leave-group') return '그룹 탈퇴';
        return '';
    }

    public get sharedGuardDialogMessage() {
        if (this.sharedGuardDialogMode === 'rename-group') return '구성원이 보는 센터/그룹 이름을 변경합니다.';
        if (this.sharedGuardDialogMode === 'remove-member') {
            const member = this.memberRemovalTarget;
            return `${member ? this.memberDisplayName(member) : '선택한 구성원'}을 이 그룹에서 내보냅니다.`;
        }
        if (this.sharedGuardDialogMode === 'leave-group') {
            if (this.leaveGroupDeletesGroup) return '다른 참여 구성원이 없어 그룹을 삭제하고 탈퇴합니다.';
            if (this.leaveGroupRequiresOwnerTransfer) return '마지막 owner는 탈퇴 전에 다른 참여 구성원을 owner로 지정해야 합니다.';
            return '내 계정을 이 그룹에서 탈퇴 처리합니다.';
        }
        return '';
    }

    public get sharedGuardDialogActionLabel() {
        if (this.sharedGuardDialogMode === 'rename-group') return '변경 저장';
        if (this.sharedGuardDialogMode === 'remove-member') return '내보내기';
        if (this.sharedGuardDialogMode === 'leave-group') return this.leaveGroupDeletesGroup ? '삭제 후 탈퇴' : '탈퇴';
        return '확인';
    }

    public get sharedGuardDialogIsDanger() {
        return this.sharedGuardDialogMode === 'remove-member' || this.sharedGuardDialogMode === 'leave-group';
    }

    public get memberRemovalTarget() {
        return this.selectedSharedGroupMembers.find((member) => member.id === this.memberRemovalTargetId) || null;
    }

    public get leaveOwnerCandidates() {
        const member = this.currentMember;
        if (!member || member.role !== 'owner') return [];
        return this.selectedSharedGroupMembers.filter((item) => item.id !== member.id && item.status === 'active');
    }

    public get leaveGroupRequiresOwnerTransfer() {
        const member = this.currentMember;
        return Boolean(member && member.role === 'owner' && this.isLastActiveOwner(this.selectedSharedGroup, member) && this.leaveOwnerCandidates.length > 0);
    }

    public get leaveGroupDeletesGroup() {
        const member = this.currentMember;
        return Boolean(member && member.role === 'owner' && this.isLastActiveOwner(this.selectedSharedGroup, member) && this.leaveOwnerCandidates.length === 0);
    }

    public get canInviteMemberAction() {
        return Boolean(this.selectedSharedGroup.id && this.canManageGroupAction);
    }

    public get canLeaveSelectedGroupAction() {
        return Boolean(this.currentMember && this.currentMember.status === 'active');
    }

    public get canSaveReceiptAction() {
        return canSaveDailyReceipt(this.user, this.user.id);
    }

    public get canSaveDailyRecordAction() {
        return this.canSaveReceiptAction;
    }

    public get authTitle() {
        if (this.authMode === 'signup') return '회원가입';
        if (this.authMode === 'reset') return '비밀번호를 잊어버리셨나요?';
        if (this.authMode === 'profile') return '프로필 초기 설정';
        return '로그인';
    }

    public get authSubtitle() {
        if (this.authMode === 'signup') return '이름 또는 닉네임과 이메일로 개인 캘린더를 시작합니다.';
        if (this.authMode === 'reset') return '가입한 이메일을 입력하면 재설정 안내를 준비해 드립니다.';
        if (this.authMode === 'profile') return '캘린더에 표시될 이름과 기본 테마를 정합니다.';
        return '아이디 또는 이메일로 캘린더와 할 일 화면에 들어갑니다.';
    }

    public get profileInitial() {
        const value = this.user.nickname || this.user.name || this.user.email || 'O';
        return value.slice(0, 1).toUpperCase();
    }

    public get monthTitle() {
        return `${this.currentMonth.getFullYear()}년 ${this.currentMonth.getMonth() + 1}월`;
    }

    public get monthTitleCompact() {
        const year = this.currentMonth.getFullYear();
        const month = `${this.currentMonth.getMonth() + 1}`.padStart(2, '0');
        return `${year}.${month}`;
    }

    public get selectedDateLabel() {
        const date = this.fromDateKey(this.selectedDate);
        return `${date.getMonth() + 1}월 ${date.getDate()}일`;
    }

    public dateLabel(dateKey: string) {
        if (!dateKey) return '';
        const date = this.fromDateKey(dateKey);
        return `${date.getMonth() + 1}월 ${date.getDate()}일`;
    }

    public reminderDateTimeLabel(value: string | null) {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hour = `${date.getHours()}`.padStart(2, '0');
        const minute = `${date.getMinutes()}`.padStart(2, '0');
        return `${month}월 ${day}일 ${hour}:${minute}`;
    }

    public get todayDate() {
        return this.toDateKey(new Date());
    }

    public get visibleEvents() {
        return this.events.filter((event) => {
            const calendar = this.calendarById(event.calendarId);
            return !event.deletedAt
                && this.isCalendarVisible(event.calendarId)
                && canViewEventForUser(this.user, event, calendar, this.groupForCalendar(calendar));
        });
    }

    public get selectedEvents() {
        return this.visibleEvents.filter((event) => this.isDateWithin(this.selectedDate, event.startDate, event.endDate));
    }

    public get selectedPersonalEvents() {
        return this.selectedEvents.filter((event) => !this.isSharedEvent(event));
    }

    public get selectedSharedEvents() {
        return this.selectedEvents.filter((event) => this.isSharedEvent(event));
    }

    public get selectedTodos() {
        return this.todosForDay(this.selectedDate);
    }

    public get todoActionTodo() {
        if (!this.todoActionTodoId) return null;
        return this.routineOccurrenceTodoById(this.todoActionTodoId)
            || this.todos.find((todo) => todo.id === this.todoActionTodoId)
            || null;
    }

    public get todoActionRoutine() {
        const todo = this.todoActionTodo;
        if (!todo?.routineId) return null;
        return this.routineById(todo.routineId);
    }

    public get todoActionIsRoutineOccurrence() {
        const todo = this.todoActionTodo;
        return todo ? this.isRoutineOccurrenceTodo(todo) : false;
    }

    public get todoActionSubtitle() {
        const todo = this.todoActionTodo;
        if (!todo) return '';
        const parts = [this.dateLabel(todo.date), this.todoScopeLabel(todo)];
        if (this.todoActionIsRoutineOccurrence) parts.push('루틴');
        const reminderLabel = this.todoReminderLabel(todo, this.todoActionRoutine);
        if (reminderLabel) parts.push(reminderLabel);
        if (todo.photoProofUrl) parts.push('사진 인증');
        const timerSeconds = this.todoTimerTotalSeconds(todo);
        if (timerSeconds > 0) parts.push(`${this.formatTimerDuration(timerSeconds)} 기록`);
        return parts.join(' · ');
    }

    public get todoActionModeTitle() {
        const labels: Record<TodoActionMode, string> = {
            menu: '할 일 설정',
            edit: '수정하기',
            memo: '메모',
            reminder: '시간 알림',
            photo: '사진 인증',
            repeat: '다른 날 또 하기',
            date: '날짜 바꾸기',
            routine: '루틴 등록하기',
            delete: '삭제하기'
        };
        return labels[this.todoActionMode];
    }

    public get todoActionItems(): TodoActionSheetItem[] {
        const todo = this.todoActionTodo;
        const canEdit = Boolean(todo && this.canEditTodo(todo));
        const regularTodoOnly = !canEdit || Boolean(todo && this.isRoutineOccurrenceTodo(todo));
        const reminderLabel = todo ? this.todoReminderLabel(todo, this.todoActionRoutine) : '';
        return [
            {
                id: 'memo',
                label: '메모',
                description: todo?.memo ? '저장된 메모를 수정합니다.' : '할 일에 짧은 기록을 남깁니다.',
                iconClass: 'fa-regular fa-note-sticky',
                tone: 'yellow',
                disabled: regularTodoOnly
            },
            {
                id: 'reminder',
                label: '시간 알림',
                description: reminderLabel || '알림 시간을 저장합니다.',
                iconClass: 'fa-regular fa-clock',
                tone: 'pink',
                disabled: !canEdit
            },
            {
                id: 'photo',
                label: '사진 인증',
                description: todo?.photoProofUrl ? '인증 사진을 다시 선택합니다.' : '파일 선택 후 미리보기로 저장합니다.',
                iconClass: 'fa-regular fa-image',
                tone: 'green',
                disabled: regularTodoOnly
            },
            {
                id: 'tomorrow',
                label: '내일 또 하기',
                description: todo ? `${this.dateLabel(this.nextDateKey(todo.date))}에 복사합니다.` : '내일 할 일을 만듭니다.',
                iconClass: 'fa-solid fa-arrow-right',
                tone: 'orange',
                disabled: regularTodoOnly || !this.canCreateTodoAction
            },
            {
                id: 'repeat',
                label: '다른 날 또 하기',
                description: '선택한 날짜에 같은 할 일을 복사합니다.',
                iconClass: 'fa-solid fa-repeat',
                tone: 'orange',
                disabled: regularTodoOnly || !this.canCreateTodoAction
            },
            {
                id: 'date',
                label: '날짜 바꾸기',
                description: '현재 할 일의 날짜를 이동합니다.',
                iconClass: 'fa-regular fa-calendar',
                tone: 'indigo',
                disabled: regularTodoOnly
            },
            {
                id: 'routine',
                label: '루틴 등록하기',
                description: '이 할 일을 반복 루틴으로 저장합니다.',
                iconClass: 'fa-solid fa-rotate-right',
                tone: 'blue',
                disabled: !todo || !this.canCreateTodoAction
            }
        ];
    }

    public get todoMatePage(): TodoMatePage {
        const selectedRows = this.todoItemRows(this.selectedTodos);
        const regularRows = selectedRows.filter((row) => row.kind === 'todo');
        const categoryStats = this.selectedDailyCategoryStats;
        const totalTimerSeconds = this.selectedTodoTimerSeconds;
        return {
            header: {
                title: '할 일',
                monthLabel: this.monthTitle,
                selectedDateLabel: this.selectedDateLabel,
                todayLabel: this.todayDate === this.selectedDate ? '오늘' : this.todayDate,
                progress: this.selectedTodoProgress
            },
            calendar: {
                monthLabel: this.monthTitle,
                weekdays: this.todoWeekLabels,
                weeks: this.buildTodoMateCalendarWeeks()
            },
            dailySummary: {
                dateLabel: this.selectedDateLabel,
                totalCount: this.selectedTodos.length,
                completedCount: this.selectedCompletedCount,
                openCount: this.selectedOpenCount,
                progress: this.selectedTodoProgress,
                categoryStats,
                totalTimerSeconds,
                timerLabel: this.formatTimerDuration(totalTimerSeconds),
                representativeEmoji: this.selectedRepresentativeEmoji,
                diary: this.dailyRecordDraft.diary
            },
            goalSections: this.buildTodoGoalSections(regularRows),
            routineSection: this.buildTodoRoutineSection(selectedRows),
            actionSheet: {
                title: this.todoFormOpen ? this.todoFormTitle : '할 일 추가',
                dateLabel: this.selectedDateLabel,
                canCreate: this.canCreateTodoAction,
                canMoveOpen: this.selectedMovableOpenTodoCount > 0,
                openCount: this.selectedOpenCount
            }
        };
    }

    public get visibleMates() {
        return this.isAdminAccount ? this.mateProfiles : this.mateProfiles.filter((mate) => !mate.adminOnly);
    }

    public get followedMates() {
        const followed = new Set(this.followedMateIds);
        return this.visibleMates.filter((mate) => followed.has(mate.id));
    }

    public get mateFollowingCount() {
        return this.followedMates.length;
    }

    public get mateFeedItems() {
        return this.buildMateFeedItems();
    }

    public get visibleMateFeedItems() {
        const followed = new Set(this.followedMateIds);
        return this.mateFeedItems
            .filter((item) => {
                const mate = this.mateById(item.mateId);
                if (!mate) return false;
                if (this.isAdminAccount) return true;
                if (mate.adminOnly) return false;
                if (item.visibility !== 'public') return false;
                return followed.has(item.mateId);
            })
            .sort((a, b) => {
                if (a.date !== b.date) return b.date.localeCompare(a.date);
                return b.completedAt.localeCompare(a.completedAt);
            });
    }

    public get privateMateFeedBlockedCount() {
        if (this.isAdminAccount) return 0;
        const followed = new Set(this.followedMateIds);
        return this.mateFeedItems.filter((item) => followed.has(item.mateId) && item.visibility === 'private').length;
    }

    public get mateFeedSummaryLabel() {
        if (this.isAdminAccount) return '관리자 전체 보기';
        if (this.mateFollowingCount === 0) return '팔로우한 메이트 없음';
        return `${this.mateFollowingCount}명 팔로잉`;
    }

    public get mateFeedEmptyMessage() {
        if (this.mateFollowingCount === 0) return '공유된 완료 기록이 아직 없습니다.';
        return '볼 수 있는 공개 완료 기록이 없습니다.';
    }

    public get selectedHolidays() {
        if (!this.holidayLayer.enabled) return [];
        return this.holidayService.getHolidaysForDate(this.selectedDate);
    }

    public get todayTodos() {
        return this.todosForDay(this.todayDate);
    }

    public get todoProgress() {
        return this.todoCompletionPercent(this.todayTodos);
    }

    public get selectedTodoProgress() {
        return this.todoCompletionPercent(this.selectedTodos);
    }

    public get todayCompletedCount() {
        return this.todayTodos.filter((todo) => todo.completed).length;
    }

    public get todayOpenCount() {
        return this.todayTodos.filter((todo) => !todo.completed).length;
    }

    public get selectedCompletedCount() {
        return this.selectedTodos.filter((todo) => todo.completed).length;
    }

    public get selectedOpenCount() {
        return this.selectedTodos.filter((todo) => !todo.completed).length;
    }

    public get selectedMovableOpenTodoCount() {
        return this.todos.filter((todo) => todo.date === this.selectedDate && !todo.completed && !todo.routineId).length;
    }

    public get selectedCompletedTodos() {
        return this.selectedTodos.filter((todo) => todo.completed);
    }

    public get selectedOpenTodos() {
        return this.selectedTodos.filter((todo) => !todo.completed);
    }

    public get selectedTodoDailyRecord() {
        return this.todoDailyRecords.find((record) => record.userId === this.user.id && record.date === this.selectedDate) || null;
    }

    public get selectedDailyCategoryStats() {
        return this.buildDailyCategoryStats(this.selectedTodos);
    }

    public get receiptViewModel(): DailyReceiptViewModel {
        return this.buildDailyReceiptViewModel(this.selectedDate);
    }

    public get receiptTemplateSections(): DailyReceiptTemplateSectionState[] {
        return this.sortedReceiptTemplateSections(this.receiptTemplate.sections);
    }

    public get receiptEvents() {
        return this.sortEvents(this.selectedEvents);
    }

    public get receiptCompletedTodos() {
        return this.selectedCompletedTodos;
    }

    public get receiptOpenTodos() {
        return this.selectedOpenTodos;
    }

    public get receiptDateFullLabel() {
        const date = this.fromDateKey(this.selectedDate);
        const weekday = this.weekLabels[date.getDay()];
        return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일 ${weekday}요일`;
    }

    public get receiptProgressLabel() {
        return this.receiptViewModel.completionLabel;
    }

    public get receiptBalanceLabel() {
        return this.receiptViewModel.balanceLabel;
    }

    public get todayTodoSummary() {
        return `${this.todayCompletedCount}/${this.todayTodos.length} 완료`;
    }

    public get selectedTodoSummary() {
        return `${this.selectedCompletedCount}/${this.selectedTodos.length} 완료`;
    }

    public get selectedTodoListTitle() {
        return this.selectedDate === this.todayDate ? '오늘의 할 일' : '선택한 날의 할 일';
    }

    public get selectedTodoDensityLabel() {
        if (this.selectedTodos.length === 0) return '할 일 없음';
        if (this.selectedOpenCount === 0) return '모두 완료';
        return `${this.selectedOpenCount}개 진행 중`;
    }

    public get selectedTodoTimerSeconds() {
        return this.selectedTodos.reduce((total, todo) => total + this.todoTimerTotalSeconds(todo), 0);
    }

    public get selectedTodoTimerLabel() {
        return this.formatTimerDuration(this.selectedTodoTimerSeconds);
    }

    public get activeTodoTimerTodo() {
        if (!this.activeTodoTimer) return null;
        return this.todos.find((todo) => todo.id === this.activeTodoTimer?.todoId) || null;
    }

    public get activeTodoTimerStatus(): TodoTimerStatus {
        return this.activeTodoTimer?.status || 'idle';
    }

    public get activeTodoTimerStatusLabel() {
        const labels: Record<TodoTimerStatus, string> = {
            idle: '대기',
            running: '기록 중',
            paused: '일시정지',
            completed: '기록 완료'
        };
        return labels[this.activeTodoTimerStatus];
    }

    public get activeTodoTimerElapsedSeconds() {
        const timer = this.activeTodoTimer;
        if (!timer) return 0;
        const activeSeconds = timer.status === 'running'
            ? this.secondsBetween(timer.lastResumedAt, this.todoTimerNow)
            : 0;
        return Math.max(0, timer.accumulatedSeconds + activeSeconds);
    }

    public get activeTodoTimerElapsedLabel() {
        return this.formatClockDuration(this.activeTodoTimerElapsedSeconds);
    }

    public get activeTodoTimerDateRangeLabel() {
        const timer = this.activeTodoTimer;
        if (!timer) return '';
        const endDate = this.toDateKey(new Date());
        if (timer.startedDate === endDate) return timer.startedDate;
        return `${timer.startedDate} → ${endDate}`;
    }

    public get selectedRepresentativeEmoji() {
        return this.dailyRecordDraft.representativeEmoji || this.selectedTodoDailyRecord?.representativeEmoji || '☁';
    }

    public get hasDailyRecordContent() {
        return Boolean(this.selectedTodoDailyRecord || (this.dailyRecordDraft.diary || '').trim());
    }

    public get todoAiSuggestionHasResult() {
        return Boolean(this.todoAiSuggestionDraft.diaryDraft.trim())
            || this.todoAiSuggestionDraft.tomorrowTodos.some((title) => Boolean((title || '').trim()));
    }

    public get todoAiSuggestionTomorrowDate() {
        return this.nextDateKey(this.selectedDate);
    }

    public get todoAiSuggestionTomorrowDateLabel() {
        return this.dateLabel(this.todoAiSuggestionTomorrowDate);
    }

    public get canApplyTodoAiTomorrowTodos() {
        return this.canCreateTodoAction
            && !this.todoAiSuggestionDraft.loading
            && this.todoAiSuggestionDraft.tomorrowTodos.some((title) => Boolean((title || '').trim()));
    }

    public get todoFormTitle() {
        return this.editingTodoId ? '할 일 수정' : '할 일 추가';
    }

    public get todoFormActionLabel() {
        return this.editingTodoId ? '수정 저장' : '할 일 추가';
    }

    public get sharedBaseEvents() {
        const calendarIds = new Set(this.selectedSharedGroupCalendars.map((calendar) => calendar.id));
        const selectedCalendarId = this.selectedSharedCalendarId;
        return this.visibleEvents.filter((event) => {
            const calendar = this.calendarById(event.calendarId);
            if (!calendar || calendar.type !== 'shared') return false;
            if (selectedCalendarId) return calendar.id === selectedCalendarId;
            return calendarIds.has(calendar.id);
        });
    }

    public get sharedEvents() {
        return this.sharedBaseEvents.filter((event) => this.sharedEventMatchesAssigneeFilter(event) && this.sharedEventMatchesCompletionFilter(event));
    }

    public get personalEvents() {
        return this.visibleEvents.filter((event) => this.calendarById(event.calendarId)?.type === 'personal' && !this.isExternalEvent(event));
    }

    public get externalEvents() {
        return this.visibleEvents.filter((event) => this.isExternalEvent(event));
    }

    public get personalCalendars() {
        return this.sortCalendars(this.calendars.filter((calendar) => {
            return calendar.type === 'personal' && !this.isExternalCalendar(calendar) && canViewCalendarForUser(this.user, calendar, null);
        }));
    }

    public get externalCalendars() {
        return this.sortCalendars(this.calendars.filter((calendar) => this.isExternalCalendar(calendar)));
    }

    public get activeSharedGroups() {
        return this.groups.filter((group) => !group.archivedAt && !group.deletedAt);
    }

    public get manageableSharedGroups() {
        return this.activeSharedGroups.filter((group) => this.canManageSharedGroup(group));
    }

    public get selectedSharedGroup() {
        return this.activeSharedGroups.find((group) => group.id === this.selectedSharedGroupId)
            || this.activeSharedGroups[0]
            || createEmptyGroup(this.user);
    }

    public get sharedCalendars() {
        const activeGroupIds = new Set(this.activeSharedGroups.map((group) => group.id));
        return this.sortCalendars(this.calendars.filter((calendar) => {
            return calendar.type === 'shared'
                && Boolean(calendar.groupId && activeGroupIds.has(calendar.groupId))
                && canViewCalendarForUser(this.user, calendar, this.groupForCalendar(calendar));
        }));
    }

    public get selectedSharedGroupCalendars() {
        const group = this.selectedSharedGroup;
        if (!group.id) return [];
        return this.sortCalendars(this.sharedCalendars.filter((calendar) => calendar.groupId === group.id));
    }

    public get selectedSharedCalendar() {
        if (!this.selectedSharedCalendarId) return null;
        return this.selectedSharedGroupCalendars.find((calendar) => calendar.id === this.selectedSharedCalendarId) || null;
    }

    public get sharedAssigneeFilterOptions() {
        const options: Array<{ value: string; label: string }> = [
            { value: 'all', label: '전체 담당자' },
            { value: 'unassigned', label: '미지정' }
        ];
        const seen = new Set(options.map((option) => option.value));
        const append = (value: string | null | undefined, label: string) => {
            if (!value || seen.has(value)) return;
            options.push({ value, label });
            seen.add(value);
        };

        this.selectedSharedGroupMembers
            .filter((member) => member.status === 'active')
            .forEach((member) => append(member.userId, this.memberDisplayName(member)));
        this.sharedBaseEvents.forEach((event) => append(event.assigneeId, this.eventAssigneeName(event)));

        return options;
    }

    public get sharedFiltersActive() {
        return this.sharedAssigneeFilter !== 'all' || this.sharedCompletionFilter !== 'all';
    }

    public get hasSharedEventsBeforeFilters() {
        return this.sharedBaseEvents.length > 0;
    }

    public get writablePersonalEventCalendars() {
        return this.sortCalendars(this.personalCalendars.filter((calendar) => canManageEventForUser(this.user, null, calendar, { group: null })));
    }

    public get writableSharedEventCalendars() {
        return this.sortCalendars(this.sharedCalendars.filter((calendar) => canManageEventForUser(this.user, null, calendar, { group: this.groupForCalendar(calendar) })));
    }

    public get sharedEventCalendarOptions() {
        const selectedGroupIds = new Set(this.selectedSharedGroupCalendars.map((calendar) => calendar.id));
        const preferred = this.writableSharedEventCalendars.filter((calendar) => selectedGroupIds.has(calendar.id));
        return preferred.length > 0 ? preferred : this.writableSharedEventCalendars;
    }

    public get eventFormCalendarOptions() {
        if (this.eventForm.scope === 'shared') {
            if (this.activeTab === 'share') return this.sharedEventCalendarOptions;
            return this.writableSharedEventCalendars;
        }
        return this.writablePersonalEventCalendars;
    }

    public get eventFormCalendar() {
        return this.calendarById(this.eventForm.calendarId);
    }

    public get eventFormIsShared() {
        return this.eventForm.scope === 'shared' || this.eventFormCalendar?.type === 'shared';
    }

    public get eventAssigneeOptions() {
        if (!this.eventFormIsShared) return [];
        const group = this.groupForCalendar(this.eventFormCalendar) || this.selectedSharedGroup;
        return this.sortGroupMembers(group.members || []).filter((member) => member.status === 'active');
    }

    public get eventFormScopeLabel() {
        return this.eventFormIsShared ? '공유' : '개인';
    }

    public get focusedEventDetail() {
        if (!this.focusedEventId) return null;
        return this.events.find((event) => event.id === this.focusedEventId) || null;
    }

    public get personalAssigneeLabel() {
        return this.user.nickname || this.user.name || this.user.email || '나';
    }

    public get selectedWeekLabel() {
        const range = this.weekRangeFor(this.selectedDate);
        return `${this.shortDateLabel(range.start)} - ${this.shortDateLabel(range.end)}`;
    }

    public get sharedDailyEvents() {
        return this.sharedEventsForRange(this.selectedDate, this.selectedDate);
    }

    public get sharedWeeklyEvents() {
        const range = this.weekRangeFor(this.selectedDate);
        return this.sharedEventsForRange(range.start, range.end);
    }

    public get sharedMonthlyEvents() {
        const start = this.toDateKey(new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1));
        const end = this.toDateKey(new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0));
        return this.sharedEventsForRange(start, end);
    }

    public get sharedSummaryCards(): SharedSummaryCard[] {
        return [
            { id: 'day', label: '일간', rangeLabel: this.selectedDateLabel, events: this.sharedDailyEvents },
            { id: 'week', label: '주간', rangeLabel: this.selectedWeekLabel, events: this.sharedWeeklyEvents },
            { id: 'month', label: '월간', rangeLabel: this.monthTitle, events: this.sharedMonthlyEvents }
        ];
    }

    public get sharedCompletedCount() {
        return this.sharedEvents.filter((event) => event.completed).length;
    }

    public get sharedOpenCount() {
        return this.sharedEvents.filter((event) => !event.completed).length;
    }

    public get sharedCompletionRate() {
        if (this.sharedEvents.length === 0) return 0;
        return Math.round((this.sharedCompletedCount / this.sharedEvents.length) * 100);
    }

    public get currentMember() {
        return this.selectedSharedGroup.members.find((member) => member.userId === this.user.id && member.status === 'active') || null;
    }

    public get currentRoleLabel() {
        if (this.isAdminAccount) return '관리자';
        if (!this.selectedSharedGroup.id) return '권한 없음';
        return this.currentMember ? this.memberRoleLabel(this.currentMember.role) : '권한 없음';
    }

    public get eventFormTitle() {
        return this.editingEventId ? `${this.eventFormScopeLabel} 일정 수정` : `${this.eventFormScopeLabel} 일정 추가`;
    }

    public get eventFormActionLabel() {
        return this.editingEventId ? '수정 저장' : '일정 추가';
    }

    public get currentMonthKey() {
        return createMonthKey(this.currentMonth);
    }

    public get selectedTheme() {
        return this.themeTokens.find((theme) => theme.id === this.decoration.themeId) || createDefaultThemes()[0];
    }

    public get selectedBackgroundOption() {
        return this.backgroundOptions.find((option) => option.id === this.decoration.background) || this.backgroundOptions[0];
    }

    public get selectedSticker() {
        return this.decoration.stickers.find((sticker) => sticker.id === this.selectedStickerId) || null;
    }

    public get customStickerAssets() {
        return this.stickerAssets.filter((asset) => asset.ownerId === this.user.id && !asset.deletedAt);
    }

    public get stickerUploadLimitLabel() {
        return `${this.formatFileSize(this.stickerUploadMaxBytes)} 이하`;
    }

    public get stickerUploadResultImageUrl() {
        return this.stickerUploadDraft.processedDataUrl || this.stickerUploadDraft.dataUrl;
    }

    public get stickerUploadPreviewFilter() {
        return this.stickerImageFilterFromOptions({
            border: this.stickerUploadDraft.borderEnabled,
            shadow: this.stickerUploadDraft.shadowEnabled
        });
    }

    public get stickerBackgroundRemovalActionLabel() {
        if (this.stickerUploadDraft.backgroundRemovalStatus === 'processing') return '처리 중';
        if (this.stickerUploadDraft.backgroundRemovalStatus === 'done') return '다시 배경 제거';
        return '배경 제거';
    }

    public get stickerUploadSaveButtonLabel() {
        return this.stickerUploadDraft.backgroundRemovalStatus === 'done' ? '투명 PNG 저장' : '원본 저장';
    }

    public get calendarStickers() {
        return this.sortStickerPlacements(this.decoration.stickers.filter((sticker) => (sticker.target || 'calendar') === 'calendar'));
    }

    public get pageThemeClasses() {
        const themeId = this.decoration.themeId || OWNIT_DEFAULT_THEME_ID;
        return {
            [`theme-${themeId}`]: true,
            'theme-default-white': themeId === OWNIT_DEFAULT_THEME_ID,
            'theme-decorative-cloud-dancer': themeId === OWNIT_CLOUD_DANCER_THEME_ID,
            'theme-cosmic-latte': themeId === OWNIT_COSMIC_LATTE_THEME_ID
        };
    }

    public get pageThemeStyles() {
        const theme = this.selectedTheme;
        const background = this.selectedBackgroundOption;
        const isCloudDancer = theme.id === OWNIT_CLOUD_DANCER_THEME_ID;
        const isCosmicLatte = theme.id === OWNIT_COSMIC_LATTE_THEME_ID;
        const isWarmTheme = isCloudDancer || isCosmicLatte;
        const pageBackground = isWarmTheme ? theme.background : OWNIT_WHITE_BACKGROUND;
        const surfaceBackground = isWarmTheme ? theme.surface : OWNIT_WHITE_BACKGROUND;
        const calendarBackground = isCloudDancer
            ? (background.id === 'white' ? theme.background : background.color)
            : (isCosmicLatte ? theme.background : OWNIT_WHITE_BACKGROUND);
        const calendarTexture = isCloudDancer ? background.texture : OWNIT_EMPTY_TEXTURE;
        return {
            '--ownit-color-app-bg': pageBackground,
            '--ownit-color-calendar-bg': calendarBackground,
            '--ownit-color-surface': surfaceBackground,
            '--ownit-color-surface-soft': isCloudDancer ? '#fbfaf7' : (isCosmicLatte ? OWNIT_NEUTRAL_COLORS.cosmicLatteSoft : '#f8f8f8'),
            '--ownit-color-surface-tint': isCloudDancer ? '#f6f2eb' : (isCosmicLatte ? OWNIT_NEUTRAL_COLORS.cosmicLatte : OWNIT_NEUTRAL_COLORS.softFill),
            '--ownit-color-line': isCloudDancer ? '#d7d2c9' : (isCosmicLatte ? '#E5DED3' : OWNIT_NEUTRAL_COLORS.border),
            '--ownit-color-line-strong': isCloudDancer ? '#cfc8bd' : (isCosmicLatte ? '#D8CFC1' : OWNIT_NEUTRAL_COLORS.borderStrong),
            '--ownit-color-text': theme.text,
            '--ownit-color-ink': theme.text,
            '--ownit-color-muted': isWarmTheme ? '#6f6860' : '#77736D',
            '--ownit-color-soft-text': isWarmTheme ? '#9a9187' : '#8E8982',
            '--ownit-color-green': theme.accent,
            '--ownit-color-blue': OWNIT_NEUTRAL_COLORS.shared,
            '--ownit-color-coral': OWNIT_NEUTRAL_COLORS.red,
            '--ownit-color-yellow': isCosmicLatte ? OWNIT_NEUTRAL_COLORS.ochre : '#8E8982',
            '--ownit-calendar-texture': calendarTexture,
            '--ownit-calendar-texture-size': isCloudDancer && background.id === 'soft-grid' ? '18px 18px' : 'auto',
            '--ownit-font-active': ownitFontStack(this.user.defaultFont),
            '--ownit-font-decoration': ownitFontStack(this.decoration.fontId || this.user.defaultFont)
        };
    }

    public canAccessTab(tab: AppTab) {
        return canAccessTabForUser(this.user, tab);
    }

    public canUsePermission(permission: AppPermissionAction) {
        return hasPermission(this.user, permission, { group: this.selectedSharedGroup });
    }

    public canEditEvent(event: OwnitEvent) {
        if (this.isReadOnlyEvent(event)) return false;
        const calendar = this.calendarById(event.calendarId);
        return canManageEventForUser(this.user, event, calendar, { group: this.groupForCalendar(calendar) });
    }

    public canToggleSharedEventCompletion(event: OwnitEvent) {
        return this.isSharedEvent(event) && this.canEditEvent(event);
    }

    public canEditTodo(todo: OwnitTodo) {
        return canManageTodoForUser(this.user, todo, { group: this.group });
    }

    public async setAuthMode(mode: AuthMode) {
        const previousMode = this.authMode;
        const identifier = this.authLoginIdentifierCandidate(mode);
        const email = this.authEmailCandidate(mode);
        this.authMode = mode;
        this.authError = '';
        this.authNotice = '';

        if (mode === 'signin' && previousMode !== 'signin') {
            this.signinForm = { identifier, password: '' };
        }
        if (mode === 'signup' && previousMode !== 'signup') {
            this.signupForm = { email, password: '', passwordConfirm: '', name: '', nickname: '', termsAccepted: false };
        }
        if (mode === 'reset' && previousMode !== 'reset') {
            this.resetForm = { email };
        }
        this.showSigninPassword = false;

        await this.service.render();
    }

    public async handleAuthWordmarkError() {
        this.authWordmarkMissing = true;
        await this.service.render();
    }

    public async goToOwnitMainFromAuth() {
        this.authError = '';
        this.authNotice = '';
        this.showSigninPassword = false;

        if (this.isAuthenticated && !this.needsProfileSetup) {
            this.activeTab = this.fallbackTab();
            this.service.href(`/dashboard/${this.activeTab}`);
            await this.service.render();
            return;
        }

        if (this.needsProfileSetup) {
            this.authMode = 'profile';
        } else {
            await this.setAuthMode('signin');
        }

        this.service.href('/dashboard');
        await this.service.render();
    }

    public async toggleSigninPassword() {
        this.showSigninPassword = !this.showSigninPassword;
        await this.service.render();
    }

    public async handleSignIn() {
        await this.runAuthAction(async () => {
            const session = await this.authProvider.signIn(this.signinForm);
            await this.applySession(session);
            this.authNotice = '';
            if (this.canShowApp) this.service.href(`/dashboard/${this.activeTab}`);
        });
    }

    public async handleSignUp() {
        await this.runAuthAction(async () => {
            if (!this.signupForm.termsAccepted) throw new Error('약관 동의 후 가입해 주세요.');
            const session = await this.authProvider.signUp({
                ...this.signupForm,
                nickname: this.signupForm.name
            });
            await this.applySession(session);
            this.authMode = 'profile';
            this.authNotice = '회원가입이 완료되었습니다. 프로필을 확인해 주세요.';
        });
    }

    public async handlePasswordReset() {
        await this.runAuthAction(async () => {
            await this.authProvider.requestPasswordReset(this.resetForm);
            this.signinForm.identifier = this.resetForm.email;
            this.signinForm.password = '';
            this.authMode = 'signin';
            this.authNotice = '비밀번호 재설정 안내가 준비되었습니다. 이메일을 확인해 주세요.';
        });
    }

    public async completeProfile() {
        await this.runAuthAction(async () => {
            const session = await this.authProvider.updateProfile(this.profileForm);
            await this.applySession(session);
            this.authMode = 'signin';
            this.authNotice = '';
            this.service.href(`/dashboard/${this.activeTab}`);
        });
    }

    public async signOut() {
        this.stopReminderWatcher();
        this.clearReminderRuntime();
        if (this.dataRepository.flushPendingWrites) await this.dataRepository.flushPendingWrites();
        await this.authProvider.signOut();
        this.isAuthenticated = false;
        this.needsProfileSetup = false;
        this.authMode = 'signin';
        this.authError = '';
        this.authNotice = '';
        this.showSigninPassword = false;
        this.user = { ...createEmptyUser(), profileComplete: false };
        this.initializeUserData();
        this.syncCloudStatus();
        this.showAuthToast('로그아웃되었습니다.');
        this.service.href('/dashboard');
        await this.service.render();
    }

    public async selectTab(tab: AppTab) {
        if (!this.canAccessTab(tab)) {
            this.activeTab = this.fallbackTab();
            this.service.href(`/dashboard/${this.activeTab}`);
            await this.service.render();
            return;
        }
        this.activeTab = tab;
        if (tab !== 'calendar') {
            this.detailPanelOpen = false;
            this.selectedRange = null;
            this.resetCalendarRangeDragState();
            this.resetCalendarTapRangeState();
        }
        if (tab !== 'todo') this.closeTodoCreateFlowState();
        this.service.href(`/dashboard/${tab}`);
        await this.service.render();
    }

    public async selectDate(date: string, openDetail = false) {
        const dateChanged = this.selectedDate !== date;
        this.selectedRange = null;
        this.resetCalendarRangeDragState();
        this.resetCalendarTapRangeState();
        this.selectedDate = date;
        const selected = this.fromDateKey(date);
        const monthChanged = selected.getFullYear() !== this.currentMonth.getFullYear() || selected.getMonth() !== this.currentMonth.getMonth();
        if (monthChanged) {
            this.currentMonth = new Date(selected.getFullYear(), selected.getMonth(), 1);
            this.syncDecorationForCurrentMonth();
        }
        if (dateChanged) {
            this.focusedEventId = null;
            this.closeInlineTodoDraft();
        }
        if (openDetail) this.detailPanelOpen = true;
        this.ensureReceiptDraftForDate(this.selectedDate);
        this.syncDailyRecordDraftForDate(this.selectedDate);
        this.buildMonth();
        await this.service.render();
    }

    public async openDayDetail(date = this.selectedDate) {
        await this.selectDate(date, true);
    }

    public async handleCalendarDayClick(date: string, event: MouseEvent) {
        if (this.suppressNextDateClick) {
            event.preventDefault();
            event.stopPropagation();
            this.suppressNextDateClick = false;
            await this.service.render();
            return;
        }
        if (this.calendarTapRangeMode) {
            event.preventDefault();
            event.stopPropagation();
            await this.selectCalendarTapRangeDate(date);
            return;
        }
        await this.openDayDetail(date);
    }

    public async startCalendarRangeDrag(date: string, event: PointerEvent) {
        if (this.activeTab !== 'calendar') return;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (this.calendarTapRangeMode) return;
        this.dragStartDate = date;
        this.dragEndDate = date;
        this.hoveredDate = date;
        this.selectedRange = null;
        this.isDragging = true;
        this.rangeDragMoved = false;
        this.rangeDragPointerId = event.pointerId;
        this.suppressNextDateClick = false;
        await this.service.render();
    }

    public async toggleCalendarTapRangeMode() {
        if (this.calendarTapRangeMode) {
            await this.cancelCalendarTapRangeMode('control');
            return;
        }
        await this.startCalendarTapRangeMode();
    }

    public async startCalendarTapRangeMode() {
        if (this.activeTab !== 'calendar' || !this.canCreateEventAction) return;
        this.resetCalendarRangeDragState();
        this.selectedRange = null;
        this.calendarTapRangeMode = true;
        this.calendarTapRangeStartDate = null;
        this.calendarTapRangeEndDate = null;
        this.pushCalendarTapRangeHistoryState();
        await this.service.render();
    }

    public async selectCalendarTapRangeDate(date: string) {
        if (!this.calendarTapRangeMode || !this.isVisibleMonthDate(date)) return;

        if (!this.calendarTapRangeStartDate) {
            this.calendarTapRangeStartDate = date;
            this.calendarTapRangeEndDate = date;
            this.selectedDate = date;
            this.focusedEventId = null;
            this.closeInlineTodoDraft();
            this.ensureReceiptDraftForDate(this.selectedDate);
            this.syncDailyRecordDraftForDate(this.selectedDate);
            this.buildMonth();
            await this.service.render();
            return;
        }

        this.calendarTapRangeEndDate = date;
        const range = this.normalizeCalendarDateRange(this.calendarTapRangeStartDate, date);
        this.selectedRange = range;
        this.selectedDate = range.startDate;
        this.calendarTapRangeMode = false;
        this.calendarTapRangeStartDate = null;
        this.calendarTapRangeEndDate = null;
        this.popCalendarTapRangeHistoryState();
        this.focusedEventId = null;
        this.closeInlineTodoDraft();
        this.ensureReceiptDraftForDate(this.selectedDate);
        this.syncDailyRecordDraftForDate(this.selectedDate);
        this.buildMonth();
        await this.openCreateEvent(range.startDate);
    }

    public async moveCalendarRangeDrag(event: PointerEvent) {
        if (!this.isDragging) return;
        if (this.rangeDragPointerId !== null && event.pointerId !== this.rangeDragPointerId) return;
        if (event.buttons === 0) {
            await this.endCalendarRangeDrag(event);
            return;
        }
        const date = this.calendarDateFromPointerEvent(event);
        if (!date || date === this.hoveredDate) return;
        event.preventDefault();
        this.updateCalendarRangeDragEnd(date);
        await this.service.render();
    }

    public async endCalendarRangeDrag(event?: PointerEvent) {
        if (!this.isDragging) return;
        if (event && this.rangeDragPointerId !== null && event.pointerId !== this.rangeDragPointerId) return;

        const endDate = event ? (this.calendarDateFromPointerEvent(event) || this.hoveredDate || this.dragEndDate) : (this.hoveredDate || this.dragEndDate);
        const startDate = this.dragStartDate;
        const moved = this.rangeDragMoved && !!startDate && !!endDate && startDate !== endDate;
        let rangeForCreate: CalendarDateRange | null = null;
        if (startDate && endDate && moved) {
            const range = this.normalizeCalendarDateRange(startDate, endDate);
            this.selectedRange = range;
            this.selectedDate = range.startDate;
            this.focusedEventId = null;
            this.closeInlineTodoDraft();
            this.ensureReceiptDraftForDate(this.selectedDate);
            this.syncDailyRecordDraftForDate(this.selectedDate);
            this.suppressNextDateClick = true;
            rangeForCreate = range;
        }
        this.resetCalendarRangeDragState();
        if (rangeForCreate) {
            await this.openCreateEvent(rangeForCreate.startDate);
            return;
        }
        await this.service.render();
    }

    public async cancelCalendarRangeDrag(event?: PointerEvent) {
        if (!this.isDragging) return;
        if (event && this.rangeDragPointerId !== null && event.pointerId !== this.rangeDragPointerId) return;
        this.resetCalendarRangeDragState();
        await this.service.render();
    }

    public isDateInCalendarSelection(date: string) {
        const range = this.calendarRangeForDisplay();
        return !!range && this.isDateWithin(date, range.startDate, range.endDate);
    }

    public isCalendarRangeStart(date: string) {
        return this.calendarRangeForDisplay()?.startDate === date;
    }

    public isCalendarRangeEnd(date: string) {
        return this.calendarRangeForDisplay()?.endDate === date;
    }

    public get selectedRangeLabel() {
        if (!this.selectedRange) return '';
        if (this.selectedRange.startDate === this.selectedRange.endDate) return this.dateLabel(this.selectedRange.startDate);
        return `${this.dateLabel(this.selectedRange.startDate)} - ${this.dateLabel(this.selectedRange.endDate)}`;
    }

    public get calendarRangeStatusLabel() {
        if (this.calendarTapRangeMode) {
            const startDate = this.calendarTapRangeStartDate;
            return startDate ? `${this.dateLabel(startDate)} - 종료일 선택` : '시작일 선택';
        }
        return this.selectedRangeLabel;
    }

    public async clearCalendarRangeSelection() {
        this.selectedRange = null;
        this.resetCalendarRangeDragState();
        this.resetCalendarTapRangeState();
        await this.service.render();
    }

    public async cancelCalendarRangeSelection() {
        if (this.calendarTapRangeMode) {
            await this.cancelCalendarTapRangeMode('control');
            return;
        }
        await this.clearCalendarRangeSelection();
    }

    public startCalendarEventBarPointer(event: PointerEvent) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (this.isDragging) this.resetCalendarRangeDragState();
        this.suppressNextEventBarClick = false;
        this.calendarEventBarPointerState = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            moved: false
        };
    }

    public moveCalendarEventBarPointer(event: PointerEvent) {
        if (!this.calendarEventBarPointerState || this.calendarEventBarPointerState.pointerId !== event.pointerId) return;
        const movedX = Math.abs(event.clientX - this.calendarEventBarPointerState.startX);
        const movedY = Math.abs(event.clientY - this.calendarEventBarPointerState.startY);
        if (movedX > 6 || movedY > 6) this.calendarEventBarPointerState.moved = true;
    }

    public finishCalendarEventBarPointer(event: PointerEvent) {
        if (!this.calendarEventBarPointerState || this.calendarEventBarPointerState.pointerId !== event.pointerId) return;
        this.suppressNextEventBarClick = this.calendarEventBarPointerState.moved;
        this.calendarEventBarPointerState = null;
    }

    public async handleCalendarEventBarClick(segment: CalendarEventSegment, event: MouseEvent) {
        event.stopPropagation();
        if (this.suppressNextEventBarClick) {
            event.preventDefault();
            this.suppressNextEventBarClick = false;
            await this.service.render();
            return;
        }
        await this.openEventDetail(segment.event, segment.focusDate);
    }

    public async openEventDetail(event: OwnitEvent, date = event.startDate) {
        this.selectedRange = null;
        this.resetCalendarRangeDragState();
        await this.selectDate(date, true);
        this.focusedEventId = event.id;
        await this.service.render();
    }

    public async closeDetailPanel() {
        this.detailPanelOpen = false;
        this.eventFormOpen = false;
        this.editingEventId = null;
        this.focusedEventId = null;
        this.eventFormError = '';
        this.todoFormOpen = false;
        this.editingTodoId = null;
        this.todoFormError = '';
        this.routineFormOpen = false;
        this.editingRoutineId = null;
        this.routineFormError = '';
        this.closeTodoCreateFlowState();
        await this.service.render();
    }

    public async previousMonth() {
        await this.moveMonth(-1);
    }

    public async nextMonth() {
        await this.moveMonth(1);
    }

    public async goToday() {
        const today = new Date();
        this.currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.selectedDate = this.toDateKey(today);
        this.selectedRange = null;
        this.resetCalendarRangeDragState();
        this.resetCalendarTapRangeState();
        this.syncDecorationForCurrentMonth();
        this.ensureReceiptDraftForDate(this.selectedDate);
        this.syncDailyRecordDraftForDate(this.selectedDate);
        this.closeInlineTodoDraft();
        this.refreshExternalCalendarData();
        this.buildMonth();
        await this.service.render();
    }

    public async refreshCalendar() {
        this.initializeUserData();
        this.buildMonth();
        await this.service.render();
    }

    public isCalendarVisible(calendarId: string) {
        const calendar = this.calendarById(calendarId);
        if (!calendar) return false;
        return calendar.isVisible !== false && !this.hiddenCalendarIds.includes(calendarId);
    }

    public async toggleCalendar(calendarId: string) {
        const calendar = this.calendarById(calendarId);
        if (!calendar || !this.canToggleCalendarVisibility(calendar)) return;

        if (this.isExternalCalendar(calendar)) {
            const nextVisible = !this.isCalendarVisible(calendarId);
            this.calendars = this.sortCalendars(this.calendars.map((item) => item.id === calendarId ? {
                ...item,
                isVisible: nextVisible
            } : item));
            this.hiddenCalendarIds = nextVisible
                ? this.hiddenCalendarIds.filter((id) => id !== calendarId)
                : Array.from(new Set([...this.hiddenCalendarIds, calendarId]));
            this.buildMonth();
            await this.service.render();
            return;
        }

        const updated = this.dataRepository.updateCalendar(calendarId, {
            isVisible: !this.isCalendarVisible(calendarId),
            updatedAt: new Date().toISOString()
        });
        if (updated) {
            this.calendars = this.sortCalendars(this.calendars.map((item) => item.id === updated.id ? updated : item));
            this.hiddenCalendarIds = this.hiddenCalendarIds.filter((id) => id !== calendarId);
        }
        this.buildMonth();
        await this.service.render();
    }

    public async openCalendarActions(calendar: OwnitCalendar, event?: Event) {
        if (event) event.stopPropagation();
        if (!this.canManageCalendar(calendar)) {
            this.calendarFormError = '캘린더를 수정할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.calendarActionCalendarId = calendar.id;
        this.calendarFormError = '';
        await this.service.render();
    }

    public async closeCalendarActions() {
        this.calendarActionCalendarId = null;
        await this.service.render();
    }

    public async toggleCalendarFromActions(calendar: OwnitCalendar) {
        await this.toggleCalendar(calendar.id);
        this.calendarActionCalendarId = null;
        await this.service.render();
    }

    public async selectSharedGroup(groupId: string) {
        this.selectedSharedGroupId = groupId || '';
        this.selectedSharedCalendarId = '';
        this.resetSharedFiltersForSelection();
        this.syncSharedSelection();
        await this.service.render();
    }

    public async selectSharedCalendar(calendarId: string) {
        this.selectedSharedCalendarId = calendarId || '';
        const calendar = this.calendarById(calendarId);
        if (calendar?.groupId) this.selectedSharedGroupId = calendar.groupId;
        this.syncSharedSelection();
        await this.service.render();
    }

    public async resetSharedEventFilters() {
        this.resetSharedFiltersForSelection();
        await this.service.render();
    }

    public syncCalendarFormGroup(groupId: string) {
        const group = this.activeSharedGroups.find((item) => item.id === groupId) || null;
        this.calendarForm.groupId = group?.id || '';
        this.calendarForm.groupName = group?.name || '';
    }

    public async openMembersForCalendar(calendar: OwnitCalendar) {
        if (calendar.groupId) this.selectedSharedGroupId = calendar.groupId;
        this.selectedSharedCalendarId = calendar.id;
        this.syncSharedSelection();
        this.calendarActionCalendarId = null;
        await this.selectTab('share');
        await this.openMemberManagement();
    }

    public async openMemberManagement() {
        if (!this.selectedSharedGroup.id) {
            this.memberManagementError = '먼저 센터/그룹을 만들어 주세요.';
            await this.service.render();
            return;
        }

        this.memberManagementOpen = true;
        this.memberManagementError = '';
        this.memberManagementNotice = '';
        this.memberInviteForm = this.createMemberInviteDraft();
        await this.service.render();
    }

    public async closeMemberManagement() {
        this.memberManagementOpen = false;
        this.memberManagementError = '';
        this.memberManagementNotice = '';
        await this.service.render();
    }

    public async generateInviteCodeForSelectedGroup() {
        if (!this.canInviteMemberAction) {
            this.memberManagementError = '초대 코드를 만들 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const code = this.createInviteCode();
        const updated = this.dataRepository.updateGroup(this.selectedSharedGroup.id, {
            inviteCode: code,
            inviteLink: this.createInviteLink(code),
            updatedAt: new Date().toISOString()
        });
        if (!updated) {
            this.memberManagementError = '초대 코드를 저장하지 못했습니다.';
            await this.service.render();
            return;
        }

        this.applyUpdatedGroup(updated);
        this.memberManagementError = '';
        this.memberManagementNotice = '새 초대 코드와 링크를 만들었습니다.';
        await this.service.render();
    }

    public async copyInviteCode() {
        if (!this.canInviteMemberAction) {
            this.memberManagementError = '초대 코드를 복사할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        let code = this.selectedSharedGroup.inviteCode;
        if (!code && this.canInviteMemberAction) {
            const nextCode = this.createInviteCode();
            const updated = this.dataRepository.updateGroup(this.selectedSharedGroup.id, {
                inviteCode: nextCode,
                inviteLink: this.createInviteLink(nextCode),
                updatedAt: new Date().toISOString()
            });
            if (updated) {
                this.applyUpdatedGroup(updated);
                code = updated.inviteCode;
            }
        }

        if (!code) {
            this.memberManagementError = '복사할 초대 코드가 없습니다.';
            await this.service.render();
            return;
        }

        try {
            if (!navigator.clipboard?.writeText) throw new Error('clipboard unsupported');
            await navigator.clipboard.writeText(code);
            this.memberManagementError = '';
            this.memberManagementNotice = '초대 코드를 복사했습니다.';
        } catch (error) {
            this.memberManagementNotice = '클립보드 권한이 없어 코드가 화면에 남아 있습니다.';
        }
        await this.service.render();
    }

    public async inviteGroupMember() {
        if (!this.canInviteMemberAction) {
            this.memberManagementError = '구성원을 초대할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const group = this.selectedSharedGroup;
        const name = (this.memberInviteForm.name || '').trim();
        const email = this.normalizeEmailInput(this.memberInviteForm.email);
        const role = this.normalizeGroupRole(this.memberInviteForm.role);
        if (!name && !email) {
            this.memberManagementError = '이름 또는 이메일을 입력해 주세요.';
            await this.service.render();
            return;
        }

        const now = new Date().toISOString();
        const existingIndex = group.members.findIndex((member) => Boolean(email && member.email.toLowerCase() === email));
        const members = [...group.members];
        if (existingIndex >= 0) {
            const existing = members[existingIndex];
            if (existing.status === 'active' || existing.status === 'invited') {
                this.memberManagementError = '이미 참여 중이거나 초대된 구성원입니다.';
                await this.service.render();
                return;
            }
            members[existingIndex] = {
                ...existing,
                name: name || existing.name || email,
                nickname: name || existing.nickname || existing.name || email,
                email: email || existing.email,
                role,
                status: 'invited',
                joinedAt: existing.joinedAt || now
            };
        } else {
            members.push({
                id: this.createId('member'),
                groupId: group.id,
                userId: this.createInviteUserId(email || name),
                name: name || email,
                nickname: name || email,
                email,
                role,
                status: 'invited',
                joinedAt: now
            });
        }

        const updated = this.updateGroupMembers(group, members);
        if (updated) {
            this.memberInviteForm = this.createMemberInviteDraft();
            this.memberManagementError = '';
            this.memberManagementNotice = '초대 명단에 구성원을 추가했습니다.';
        }
        await this.service.render();
    }

    public async joinGroupByInviteCode() {
        const code = (this.memberJoinCode || '').trim();
        if (!code) {
            this.memberManagementError = '참여할 초대 코드를 입력해 주세요.';
            await this.service.render();
            return;
        }

        const joined = this.dataRepository.joinGroupByInviteCode(this.user, code);
        if (!joined) {
            this.memberManagementError = '일치하는 초대 코드를 찾지 못했습니다.';
            await this.service.render();
            return;
        }

        const joinedGroupId = joined.id;
        this.initializeUserData();
        this.selectedSharedGroupId = joinedGroupId;
        this.syncSharedSelection(joinedGroupId);
        this.memberManagementOpen = true;
        this.memberManagementError = '';
        this.memberManagementNotice = `${joined.name}에 참여했습니다.`;
        await this.service.render();
    }

    public async changeMemberRole(member: OwnitGroupMember, role: GroupRole) {
        if (!this.canChangeMemberRole(member)) {
            this.memberManagementError = '역할을 변경할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const nextRole = this.normalizeGroupRole(role);
        const group = this.selectedSharedGroup;
        if (member.role === 'owner' && nextRole !== 'owner' && this.isLastActiveOwner(group, member)) {
            this.memberManagementError = '마지막 owner는 다른 owner를 먼저 지정해야 변경할 수 있습니다.';
            await this.service.render();
            return;
        }

        const updated = this.updateGroupMembers(group, group.members.map((item) => item.id === member.id ? { ...item, role: nextRole } : item));
        if (updated) {
            this.memberManagementError = '';
            this.memberManagementNotice = `${this.memberDisplayName(member)} 역할을 ${nextRole}로 변경했습니다.`;
        }
        await this.service.render();
    }

    public async removeGroupMember(member: OwnitGroupMember) {
        if (!this.canRemoveMember(member)) {
            this.memberManagementError = '이 구성원을 내보낼 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const group = this.selectedSharedGroup;
        if (member.role === 'owner' && this.isLastActiveOwner(group, member)) {
            this.memberManagementError = '마지막 owner는 다른 owner를 먼저 지정해야 내보낼 수 있습니다.';
            await this.service.render();
            return;
        }

        this.memberRemovalTargetId = member.id;
        this.sharedGuardDialogMode = 'remove-member';
        this.sharedGuardError = '';
        await this.service.render();
    }

    public async leaveSelectedGroup() {
        const group = this.selectedSharedGroup;
        const member = this.currentMember;
        if (!group.id || !member) {
            this.memberManagementError = '탈퇴할 수 있는 참여 상태가 아닙니다.';
            await this.service.render();
            return;
        }

        this.leaveGroupTransferMemberId = this.leaveOwnerCandidates[0]?.id || '';
        this.sharedGuardDialogMode = 'leave-group';
        this.sharedGuardError = '';
        await this.service.render();
    }

    public async renameSelectedSharedGroup() {
        const group = this.selectedSharedGroup;
        if (!this.canManageSharedGroup(group)) {
            this.calendarFormError = '센터/그룹을 수정할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.groupRenameDraft = group.name;
        this.sharedGuardDialogMode = 'rename-group';
        this.sharedGuardError = '';
        await this.service.render();
    }

    public async closeSharedGuardDialog() {
        this.sharedGuardDialogMode = null;
        this.sharedGuardError = '';
        this.groupRenameDraft = '';
        this.memberRemovalTargetId = null;
        this.leaveGroupTransferMemberId = '';
        await this.service.render();
    }

    public async confirmSharedGuardDialog() {
        if (this.sharedGuardDialogMode === 'rename-group') {
            await this.confirmSharedGroupRename();
            return;
        }
        if (this.sharedGuardDialogMode === 'remove-member') {
            await this.confirmMemberRemoval();
            return;
        }
        if (this.sharedGuardDialogMode === 'leave-group') {
            await this.confirmGroupLeave();
            return;
        }
        await this.service.render();
    }

    public async archiveSelectedSharedGroup() {
        await this.removeSharedGroup(this.selectedSharedGroup, 'archive');
    }

    public async deleteSelectedSharedGroup() {
        await this.removeSharedGroup(this.selectedSharedGroup, 'delete');
    }

    public async openCreateCalendar(type: OwnitCalendar['type'] = 'personal', createNewGroup = false) {
        const allowed = type === 'shared' ? this.canCreateSharedCalendarAction : this.canCreateCalendarAction;
        if (!allowed) {
            this.calendarFormError = '캘린더를 추가할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const shouldCreateNewGroup = type === 'shared'
            && (createNewGroup || Boolean(this.selectedSharedGroup.id && !this.canManageSharedGroup(this.selectedSharedGroup)));
        this.editingCalendarId = null;
        this.calendarFormError = '';
        this.calendarForm = this.createCalendarForm(type);
        if (type === 'shared' && shouldCreateNewGroup) {
            this.calendarForm.groupId = '';
            this.calendarForm.groupName = '';
        }
        this.calendarFormOpen = true;
        this.calendarActionCalendarId = null;
        await this.service.render();
    }

    public async editCalendar(calendar: OwnitCalendar) {
        if (!this.canManageCalendar(calendar)) {
            this.calendarFormError = '캘린더를 수정할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.editingCalendarId = calendar.id;
        this.calendarFormError = '';
        this.calendarForm = this.createCalendarForm(calendar.type, calendar);
        this.calendarFormOpen = true;
        this.calendarActionCalendarId = null;
        await this.service.render();
    }

    public async closeCalendarForm() {
        this.calendarFormOpen = false;
        this.editingCalendarId = null;
        this.calendarFormError = '';
        await this.service.render();
    }

    public async saveCalendar() {
        const payload = this.buildCalendarPayload();
        if (!payload) {
            await this.service.render();
            return;
        }

        try {
            if (this.editingCalendarId) {
                const existing = this.calendarById(this.editingCalendarId);
                if (!existing || !this.canManageCalendar(existing)) {
                    this.calendarFormError = '캘린더를 수정할 권한이 없습니다.';
                    await this.service.render();
                    return;
                }

                const group = payload.type === 'shared' ? this.resolveSharedGroupForCalendar(payload, existing) : null;
                if (payload.type === 'shared' && !group) {
                    await this.service.render();
                    return;
                }
                const updated = this.dataRepository.updateCalendar(this.editingCalendarId, payload);
                if (!updated) {
                    this.calendarFormError = '수정할 캘린더를 찾지 못했습니다.';
                    await this.service.render();
                    return;
                }
                this.calendars = this.sortCalendars(this.calendars.map((calendar) => calendar.id === updated.id ? updated : calendar));
            } else {
                const now = new Date().toISOString();
                const group = payload.type === 'shared' ? this.resolveSharedGroupForCalendar(payload) : null;
                if (payload.type === 'shared' && !group) {
                    await this.service.render();
                    return;
                }
                const saved = this.dataRepository.createCalendar({
                    id: this.createId('cal'),
                    name: payload.name,
                    type: payload.type,
                    ownerId: this.user.id,
                    groupId: payload.type === 'shared' ? group?.id || null : null,
                    color: payload.color,
                    description: payload.description,
                    visibility: payload.visibility,
                    isDefault: false,
                    isVisible: payload.isVisible,
                    order: this.nextCalendarOrder(payload.type),
                    archivedAt: null,
                    deletedAt: null,
                    createdAt: now,
                    updatedAt: now
                });
                this.calendars = this.sortCalendars([...this.calendars, saved]);
                if (saved.type === 'shared') {
                    this.selectedSharedGroupId = saved.groupId || '';
                    this.selectedSharedCalendarId = saved.id;
                }
            }
        } catch (error) {
            this.calendarFormError = this.dataErrorMessage(error, '캘린더를 저장하지 못했습니다.');
            await this.service.render();
            return;
        }

        this.syncSharedSelection();
        this.calendarFormOpen = false;
        this.editingCalendarId = null;
        this.calendarFormError = '';
        this.buildMonth();
        await this.service.render();
    }

    public async archiveCalendar(calendar: OwnitCalendar) {
        await this.removeCalendar(calendar, 'archive');
    }

    public async deleteCalendar(calendar: OwnitCalendar) {
        await this.removeCalendar(calendar, 'delete');
    }

    public async toggleTodo(todoId: string) {
        const routineOccurrence = this.routineOccurrenceTodoById(todoId);
        if (routineOccurrence) {
            await this.toggleRoutineCompletion(routineOccurrence);
            return;
        }

        const todo = this.todos.find((item) => item.id === todoId);
        if (!todo) return;
        if (!this.canEditTodo(todo)) {
            this.todoFormError = '할 일 완료 상태를 바꿀 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const completed = !todo.completed;
        const now = new Date().toISOString();
        try {
            const updated = this.dataRepository.updateTodo(todoId, {
                completed,
                completedAt: completed ? now : null,
                updatedAt: now
            });
            if (!updated) return;
            this.todos = this.sortTodos(this.todos.map((item) => item.id === todoId ? updated : item));
            this.refreshTodoRecordsFromRepository();
            this.buildMonth();
            this.refreshReminderSchedules();
            this.todoFormError = '';
        } catch (error) {
            this.todoFormError = this.dataErrorMessage(error, '할 일 완료 상태를 저장하지 못했습니다.');
        }
        await this.service.render();
    }

    public todoTimerStatus(todo: OwnitTodo): TodoTimerStatus {
        if (this.activeTodoTimer?.todoId === todo.id) return this.activeTodoTimer.status;
        return this.todoTimerTotalSeconds(todo) > 0 ? 'completed' : 'idle';
    }

    public todoTimerTotalSeconds(todo: OwnitTodo): number {
        if (this.isRoutineOccurrenceTodo(todo)) return 0;
        const sessionSeconds = this.todoTimerSessions
            .filter((session) => session.todoId === todo.id && session.status === 'completed')
            .reduce((sum, session) => sum + (Number(session.durationSeconds) || 0), 0);
        return Math.max(sessionSeconds, Number(todo.timerSeconds) || 0);
    }

    public todoTimerDisplaySeconds(todo: OwnitTodo): number {
        const savedSeconds = this.todoTimerTotalSeconds(todo);
        if (this.activeTodoTimer?.todoId !== todo.id) return savedSeconds;
        return savedSeconds + this.activeTodoTimerElapsedSeconds;
    }

    public todoTimerLabel(todo: OwnitTodo): string {
        return this.formatTimerDuration(this.todoTimerDisplaySeconds(todo));
    }

    public dailyStatProgress(stat: OwnitTodoDailyCategoryStat): number {
        if (!stat.totalCount) return 0;
        return Math.round((stat.completedCount / stat.totalCount) * 100);
    }

    public dailyStatTimerLabel(stat: OwnitTodoDailyCategoryStat): string {
        return this.formatTimerDuration(stat.timerSeconds);
    }

    public todoTimerButtonLabel(todo: OwnitTodo): string {
        const status = this.todoTimerStatus(todo);
        if (status === 'running') return '할 일 타이머 일시정지';
        if (status === 'paused') return '할 일 타이머 재개';
        return '할 일 타이머 시작';
    }

    public todoTimerActionText(todo: OwnitTodo): string {
        const status = this.todoTimerStatus(todo);
        if (status === 'running') return '일시정지';
        if (status === 'paused') return '재개';
        return this.todoTimerTotalSeconds(todo) > 0 ? '다시 기록' : '타이머';
    }

    public todoTimerIconClass(todo: OwnitTodo): string {
        const status = this.todoTimerStatus(todo);
        if (status === 'running') return 'fa-solid fa-pause';
        if (status === 'paused') return 'fa-solid fa-play';
        return 'fa-solid fa-play';
    }

    public isTodoTimerActive(todo: OwnitTodo): boolean {
        return this.activeTodoTimer?.todoId === todo.id;
    }

    public hasActiveTodoTimerForOther(todoId: string): boolean {
        return Boolean(this.activeTodoTimer && this.activeTodoTimer.todoId !== todoId);
    }

    public async handleTodoTimerButton(todo: OwnitTodo) {
        const status = this.todoTimerStatus(todo);
        if (status === 'running') {
            await this.pauseTodoTimer();
            return;
        }
        if (status === 'paused') {
            await this.resumeTodoTimer();
            return;
        }
        await this.startTodoTimer(todo);
    }

    public async startTodoTimer(todo: OwnitTodo) {
        if (this.isRoutineOccurrenceTodo(todo)) {
            this.todoTimerNotice = '루틴 발생 항목은 먼저 일반 할 일로 등록한 뒤 시간을 기록해 주세요.';
            await this.service.render();
            return;
        }
        if (!this.canEditTodo(todo)) {
            this.todoTimerNotice = '이 할 일의 시간을 기록할 권한이 없습니다.';
            await this.service.render();
            return;
        }
        if (this.activeTodoTimer && this.activeTodoTimer.todoId !== todo.id) {
            this.todoTimerNotice = '다른 할 일 타이머가 실행 중입니다. 먼저 종료해 주세요.';
            await this.service.render();
            return;
        }

        const now = new Date();
        this.todoTimerNow = now.getTime();
        this.activeTodoTimer = {
            todoId: todo.id,
            status: 'running',
            startedAt: now.toISOString(),
            startedDate: this.toDateKey(now),
            lastResumedAt: now.toISOString(),
            accumulatedSeconds: 0
        };
        this.todoTimerNotice = '';
        this.startTodoTimerTicker();
        await this.service.render();
    }

    public async pauseTodoTimer() {
        if (!this.activeTodoTimer || this.activeTodoTimer.status !== 'running') return;
        const now = Date.now();
        this.todoTimerNow = now;
        this.activeTodoTimer = {
            ...this.activeTodoTimer,
            status: 'paused',
            accumulatedSeconds: this.activeTodoTimerElapsedSeconds
        };
        this.stopTodoTimerTicker();
        await this.service.render();
    }

    public async resumeTodoTimer() {
        if (!this.activeTodoTimer || this.activeTodoTimer.status !== 'paused') return;
        const now = new Date();
        this.todoTimerNow = now.getTime();
        this.activeTodoTimer = {
            ...this.activeTodoTimer,
            status: 'running',
            lastResumedAt: now.toISOString()
        };
        this.todoTimerNotice = '';
        this.startTodoTimerTicker();
        await this.service.render();
    }

    public async completeTodoTimer() {
        const active = this.activeTodoTimer;
        if (!active) return;
        const todo = this.todos.find((item) => item.id === active.todoId);
        if (!todo) {
            this.clearActiveTodoTimer();
            await this.service.render();
            return;
        }

        const endedAt = new Date();
        this.todoTimerNow = endedAt.getTime();
        const durationSeconds = Math.max(1, this.activeTodoTimerElapsedSeconds);
        const session: OwnitTodoTimerSession = {
            id: this.createId('todo-timer'),
            todoId: todo.id,
            date: todo.date,
            status: 'completed',
            startedAt: active.startedAt,
            startedDate: active.startedDate,
            endedAt: endedAt.toISOString(),
            endedDate: this.toDateKey(endedAt),
            durationSeconds,
            createdAt: active.startedAt,
            updatedAt: endedAt.toISOString()
        };

        try {
            this.dataRepository.createTodoTimerSession(session);
            this.clearActiveTodoTimer();
            this.refreshTodoRecordsFromRepository();
            this.todoTimerNotice = `${this.formatTimerDuration(durationSeconds)} 기록했습니다.`;
            this.buildMonth();
        } catch (error) {
            this.todoTimerNotice = error instanceof Error ? error.message : '타이머 기록을 저장하지 못했습니다.';
        }
        await this.service.render();
    }

    public async openCreateEvent(date = this.selectedDate) {
        const draftRange = this.eventDraftRangeForDate(date);
        const writableCalendar = this.preferredEventCreateCalendar();
        if (!writableCalendar) {
            this.eventFormError = '일정을 추가할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.focusDate(draftRange.startDate);
        this.todoGoalFormOpen = false;
        this.editingTodoGoalId = null;
        this.routineFormOpen = false;
        this.editingRoutineId = null;
        this.routineFormError = '';
        this.todoFormOpen = false;
        this.editingTodoId = null;
        this.todoFormError = '';
        this.editingEventId = null;
        this.focusedEventId = null;
        this.eventFormError = '';
        this.eventForm = this.createEventForm(draftRange.startDate, writableCalendar.id, writableCalendar.type);
        this.eventForm.endDate = draftRange.endDate;
        this.eventFormOpen = true;
        if (this.activeTab === 'calendar') this.detailPanelOpen = true;
        await this.service.render();
    }

    public async openCreateSharedEvent(date = this.selectedDate) {
        const draftRange = this.eventDraftRangeForDate(date);
        const preferredCalendar = this.selectedSharedCalendar;
        const sharedCalendar = (preferredCalendar && canManageEventForUser(this.user, null, preferredCalendar, { group: this.groupForCalendar(preferredCalendar) }) ? preferredCalendar : null)
            || this.selectedSharedGroupCalendars.find((calendar) => canManageEventForUser(this.user, null, calendar, { group: this.groupForCalendar(calendar) }))
            || this.sharedCalendars.find((calendar) => canManageEventForUser(this.user, null, calendar, { group: this.groupForCalendar(calendar) }));
        if (!sharedCalendar) {
            this.eventFormError = '공유 일정을 추가할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.focusDate(draftRange.startDate);
        this.todoGoalFormOpen = false;
        this.editingTodoGoalId = null;
        this.routineFormOpen = false;
        this.editingRoutineId = null;
        this.routineFormError = '';
        this.todoFormOpen = false;
        this.editingTodoId = null;
        this.todoFormError = '';
        this.editingEventId = null;
        this.focusedEventId = null;
        this.eventFormError = '';
        this.eventForm = this.createEventForm(draftRange.startDate, sharedCalendar.id, 'shared');
        this.eventForm.endDate = draftRange.endDate;
        this.eventFormOpen = true;
        if (this.activeTab === 'calendar') this.detailPanelOpen = true;
        await this.service.render();
    }

    public async editEvent(event: OwnitEvent) {
        if (!this.canEditEvent(event)) {
            this.eventFormError = '일정을 수정할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.editingEventId = event.id;
        this.focusedEventId = event.id;
        this.eventFormError = '';
        const calendar = this.calendarById(event.calendarId);
        this.eventForm = {
            title: event.title,
            description: event.description || '',
            scope: calendar?.type || 'personal',
            startDate: event.startDate,
            endDate: event.endDate,
            startTime: event.startTime || '09:00',
            endTime: event.endTime || '10:00',
            allDay: event.allDay,
            color: event.color,
            calendarId: event.calendarId,
            assigneeId: event.assigneeId || this.user.id,
            completed: event.completed === true,
            ...this.createEventReminderFormState(event)
        };
        this.eventFormOpen = true;
        if (this.activeTab === 'calendar') this.detailPanelOpen = true;
        await this.service.render();
    }

    public async closeEventForm() {
        this.eventFormOpen = false;
        this.editingEventId = null;
        this.eventFormError = '';
        this.selectedRange = null;
        this.resetCalendarRangeDragState();
        this.resetCalendarTapRangeState();
        await this.service.render();
    }

    public async saveEvent() {
        const payload = this.buildEventPayload();
        if (!payload) {
            await this.service.render();
            return;
        }

        const calendar = this.calendarById(payload.calendarId);
        const existingEvent = this.editingEventId ? this.events.find((event) => event.id === this.editingEventId) || null : null;
        let savedEvent: OwnitEvent | null = null;
        let sharedChangeAction: 'created' | 'updated' | null = null;
        if (!canManageEventForUser(this.user, existingEvent, calendar, { group: this.groupForCalendar(calendar) })) {
            this.eventFormError = '일정을 저장할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        try {
            if (this.editingEventId) {
                const updated = this.dataRepository.updateEvent(this.editingEventId, payload);
                if (!updated) {
                    this.eventFormError = '수정할 일정을 찾지 못했습니다.';
                    await this.service.render();
                    return;
                }
                this.events = this.sortEvents(this.events.map((event) => event.id === updated.id ? updated : event));
                this.touchExternalMappingForLocalEvent(updated);
                this.focusedEventId = updated.id;
                savedEvent = updated;
                sharedChangeAction = 'updated';
            } else {
                const now = new Date().toISOString();
                const created: OwnitEvent = {
                    id: this.createId('event'),
                    authorId: this.user.id,
                    repeatRule: null,
                    createdAt: now,
                    ...payload
                };
                const saved = this.dataRepository.createEvent(created);
                this.events = this.sortEvents([...this.events, saved]);
                this.focusedEventId = saved.id;
                savedEvent = saved;
                sharedChangeAction = 'created';
            }
        } catch (error) {
            this.eventFormError = this.dataErrorMessage(error, '일정을 저장하지 못했습니다.');
            await this.service.render();
            return;
        }

        this.selectedDate = payload.startDate;
        this.selectedRange = null;
        this.resetCalendarRangeDragState();
        this.resetCalendarTapRangeState();
        const selected = this.fromDateKey(payload.startDate);
        this.currentMonth = new Date(selected.getFullYear(), selected.getMonth(), 1);
        this.syncDecorationForCurrentMonth();
        const savedCalendar = this.calendarById(payload.calendarId);
        if (savedCalendar?.type === 'shared') {
            this.selectedSharedGroupId = savedCalendar.groupId || '';
            this.selectedSharedCalendarId = savedCalendar.id;
            this.syncSharedSelection(savedCalendar.groupId || '', savedCalendar.id);
        }
        this.eventFormOpen = false;
        this.editingEventId = null;
        this.eventFormError = '';
        this.buildMonth();
        if (savedEvent && sharedChangeAction) this.addSharedEventChangeNotice(sharedChangeAction, savedEvent, savedCalendar);
        this.refreshReminderSchedules();
        if (payload.reminders.some((reminder) => reminder.enabled)) await this.ensureNotificationPermissionForReminder();
        await this.checkDueReminders(false);
        await this.service.render();
    }

    public async toggleSharedEventCompletion(event: OwnitEvent) {
        if (!this.canToggleSharedEventCompletion(event)) {
            this.eventFormError = '공유 일정 완료 상태를 변경할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        try {
            const updated = this.dataRepository.updateEvent(event.id, {
                completed: !event.completed,
                updatedAt: new Date().toISOString()
            });
            if (!updated) {
                this.eventFormError = '완료 상태를 저장할 공유 일정을 찾지 못했습니다.';
                await this.service.render();
                return;
            }

            this.events = this.sortEvents(this.events.map((item) => item.id === updated.id ? updated : item));
            this.eventFormError = '';
            this.buildMonth();
            this.addSharedEventChangeNotice('completed', updated, this.calendarById(updated.calendarId));
            this.refreshReminderSchedules();
        } catch (error) {
            this.eventFormError = this.dataErrorMessage(error, '공유 일정 완료 상태를 저장하지 못했습니다.');
        }
        await this.service.render();
    }

    public async deleteEvent(event: OwnitEvent) {
        if (!this.canEditEvent(event)) {
            this.eventFormError = '일정을 삭제할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const confirmed = await this.confirmEventRemoval(event);
        if (!confirmed) return;

        try {
            const deleted = this.dataRepository.deleteEvent(event.id);
            if (!deleted) return;
            this.touchExternalMappingForLocalEvent({ ...event, updatedAt: new Date().toISOString() }, new Date().toISOString());
            this.events = this.events.filter((item) => item.id !== event.id);
            if (this.focusedEventId === event.id) this.focusedEventId = null;
            if (this.editingEventId === event.id) {
                this.eventFormOpen = false;
                this.editingEventId = null;
                this.eventFormError = '';
            }
            this.addSharedEventChangeNotice('deleted', event);
            this.refreshReminderSchedules();
            this.buildMonth();
        } catch (error) {
            this.eventFormError = this.dataErrorMessage(error, '일정을 삭제하지 못했습니다.');
        }
        await this.service.render();
    }

    public async openCreateTodo(date = this.selectedDate) {
        if (this.activeTab === 'todo') {
            await this.openTodoCreateFlow('todo', date);
            return;
        }

        if (!this.canCreateTodoAction) {
            this.todoFormError = '할 일을 추가할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.focusDate(date);
        this.todoGoalFormOpen = false;
        this.editingTodoGoalId = null;
        this.routineFormOpen = false;
        this.editingRoutineId = null;
        this.routineFormError = '';
        this.eventFormOpen = false;
        this.editingEventId = null;
        this.eventFormError = '';
        this.editingTodoId = null;
        this.todoFormError = '';
        this.closeInlineTodoDraft();
        this.todoForm = this.createTodoForm(date);
        this.todoFormOpen = true;
        if (this.activeTab === 'calendar') this.detailPanelOpen = true;
        await this.service.render();
    }

    public async openCreateTodayTodo() {
        await this.openCreateTodo(this.toDateKey(new Date()));
    }

    public async openTodoCreateFlow(mode: TodoCreateMode = 'todo', date = this.selectedDate, goalId: string | null = null, sectionId = '', category = '', color = '') {
        if (!this.canCreateTodoAction) {
            const message = mode === 'category'
                ? '카테고리를 추가할 권한이 없습니다.'
                : mode === 'routine'
                    ? '루틴을 추가할 권한이 없습니다.'
                    : '할 일을 추가할 권한이 없습니다.';
            this.todoFormError = message;
            this.todoGoalFormError = message;
            this.routineFormError = message;
            await this.service.render();
            return;
        }

        const targetDate = date || this.selectedDate;
        let goal = goalId
            ? this.todoGoalById(goalId)
            : (mode !== 'category' && !category ? this.preferredTodoGoal : null);
        goal = this.expandTodoGoalForCreate(goal);
        const contextCategory = goal?.name || category || this.uncategorizedTodoLabel;
        const contextColor = goal?.color || color || this.uncategorizedTodoColor;

        this.focusDate(targetDate);
        this.closeInlineTodoDraft();
        this.todoActionTodoId = null;
        this.todoActionMode = 'menu';
        this.todoActionError = '';
        this.todoActionNotice = '';
        this.unlockTodoActionScroll();
        this.eventFormOpen = false;
        this.editingEventId = null;
        this.eventFormError = '';

        this.editingTodoId = null;
        this.todoFormError = '';
        this.todoFormOpen = false;
        this.todoForm = this.createTodoForm(targetDate);
        this.applyGoalToTodoForm(goal);
        if (!goal) {
            this.todoForm.category = contextCategory;
            this.todoForm.color = contextColor;
        }

        this.editingTodoGoalId = null;
        this.todoGoalFormError = '';
        this.todoGoalFormOpen = false;
        this.todoGoalForm = this.createTodoGoalForm();

        this.editingRoutineId = null;
        this.routineFormError = '';
        this.routineFormOpen = false;
        this.routineForm = this.createRoutineForm(targetDate);
        this.applyGoalToRoutineForm(goal);
        if (!goal && category) {
            this.routineForm.category = contextCategory;
            this.routineForm.color = contextColor;
        }

        this.todoCreateMode = this.normalizeTodoCreateMode(mode);
        this.todoCreateContext = this.createTodoCreateContext(targetDate, goal, sectionId, contextCategory, contextColor);
        this.todoCreateFlowOpen = true;
        await this.service.render();
        this.focusTodoCreatePrimaryInput();
    }

    public async setTodoCreateMode(mode: TodoCreateMode) {
        const nextMode = this.normalizeTodoCreateMode(mode);
        if (this.todoCreateMode === nextMode) return;
        this.todoCreateMode = nextMode;
        this.todoFormError = '';
        this.todoGoalFormError = '';
        this.routineFormError = '';

        const goal = this.todoCreateContext.goalId ? this.todoGoalById(this.todoCreateContext.goalId) : null;
        if (nextMode === 'todo') {
            this.todoForm.date = this.todoCreateContext.date || this.selectedDate;
            this.applyGoalToTodoForm(goal);
            if (!goal) {
                this.todoForm.category = this.todoCreateContext.category || this.uncategorizedTodoLabel;
                this.todoForm.color = this.todoCreateContext.color || this.uncategorizedTodoColor;
            }
        }
        if (nextMode === 'routine') {
            this.routineForm.startDate = this.todoCreateContext.date || this.selectedDate;
            this.applyGoalToRoutineForm(goal);
        }

        await this.service.render();
        this.focusTodoCreatePrimaryInput();
    }

    public async closeTodoCreateFlow() {
        this.closeTodoCreateFlowState();
        await this.service.render();
    }

    public async saveTodoFromCreateFlow() {
        await this.saveTodo();
        if (!this.todoFormError) this.closeTodoCreateFlowState();
        await this.service.render();
    }

    public async saveTodoGoalFromCreateFlow() {
        await this.saveTodoGoal();
        if (!this.todoGoalFormError) this.closeTodoCreateFlowState();
        await this.service.render();
    }

    public async saveRoutineFromCreateFlow() {
        await this.saveRoutine();
        if (!this.routineFormError) this.closeTodoCreateFlowState();
        await this.service.render();
    }

    public async editTodo(todo: OwnitTodo) {
        if (this.isRoutineOccurrenceTodo(todo)) {
            await this.editRoutine(todo.routineId ? this.routineById(todo.routineId) : null);
            return;
        }

        if (!this.canEditTodo(todo)) {
            this.todoFormError = '할 일을 수정할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.focusDate(todo.date);
        this.eventFormOpen = false;
        this.editingEventId = null;
        this.eventFormError = '';
        this.closeInlineTodoDraft();
        this.editingTodoId = todo.id;
        this.todoFormError = '';
        const goal = this.todoGoalForTodo(todo);
        this.todoForm = {
            title: todo.title,
            date: todo.date,
            memo: todo.memo,
            goalId: goal?.id || '',
            category: goal?.name || todo.category || this.uncategorizedTodoLabel,
            color: goal?.color || todo.color || this.uncategorizedTodoColor,
            scope: todo.scope,
            ...this.createTodoReminderFormState(todo)
        };
        this.todoFormOpen = true;
        if (this.activeTab === 'calendar') this.detailPanelOpen = true;
        await this.service.render();
    }

    public async closeTodoForm() {
        this.todoFormOpen = false;
        this.editingTodoId = null;
        this.todoFormError = '';
        await this.service.render();
    }

    public async saveTodo() {
        const payload = this.buildTodoPayload();
        if (!payload) {
            await this.service.render();
            return;
        }

        try {
            if (this.editingTodoId) {
                const existingTodo = this.todos.find((todo) => todo.id === this.editingTodoId) || null;
                if (!existingTodo || !this.canEditTodo(existingTodo)) {
                    this.todoFormError = '할 일을 저장할 권한이 없습니다.';
                    await this.service.render();
                    return;
                }

                const updated = this.dataRepository.updateTodo(this.editingTodoId, payload);
                if (!updated) {
                    this.todoFormError = '수정할 할 일을 찾지 못했습니다.';
                    await this.service.render();
                    return;
                }
                this.todos = this.sortTodos(this.todos.map((todo) => todo.id === updated.id ? updated : todo));
            } else {
                if (!this.canCreateTodoAction) {
                    this.todoFormError = '할 일을 추가할 권한이 없습니다.';
                    await this.service.render();
                    return;
                }

                const saved = this.createTodoFromPayload(payload);
                this.todos = this.sortTodos([...this.todos, saved]);
            }
        } catch (error) {
            this.todoFormError = this.dataErrorMessage(error, '할 일을 저장하지 못했습니다.');
            await this.service.render();
            return;
        }

        this.refreshTodoRecordsFromRepository();
        this.rememberTodoGoalUse(payload.goalId);
        this.focusDate(payload.date);
        this.todoFormOpen = false;
        this.editingTodoId = null;
        this.todoFormError = '';
        this.buildMonth();
        this.refreshReminderSchedules();
        if (payload.reminderAt) await this.ensureNotificationPermissionForReminder();
        await this.checkDueReminders(false);
        await this.service.render();
    }

    public async deleteTodo(todo: OwnitTodo) {
        if (this.isRoutineOccurrenceTodo(todo)) {
            await this.deleteRoutine(todo.routineId ? this.routineById(todo.routineId) : null);
            return;
        }

        if (!this.canEditTodo(todo)) {
            this.todoFormError = '할 일을 삭제할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const confirmed = window.confirm(`'${todo.title}' 할 일을 삭제할까요?`);
        if (!confirmed) return;

        try {
            const deleted = this.dataRepository.deleteTodo(todo.id);
            if (!deleted) return;
            this.todos = this.todos.filter((item) => item.id !== todo.id);
            this.refreshTodoRecordsFromRepository();
            if (this.editingTodoId === todo.id) {
                this.todoFormOpen = false;
                this.editingTodoId = null;
                this.todoFormError = '';
            }
            this.buildMonth();
            this.refreshReminderSchedules();
        } catch (error) {
            this.todoFormError = this.dataErrorMessage(error, '할 일을 삭제하지 못했습니다.');
        }
        await this.service.render();
    }

    public async openTodoActionSheet(todo: OwnitTodo, mode: TodoActionMode = 'menu') {
        this.closeInlineTodoDraft();
        this.todoActionTodoId = todo.id;
        this.todoActionMode = mode;
        this.todoActionError = '';
        this.todoActionNotice = '';
        this.todoActionDraft = this.createTodoActionDraft(todo);
        this.todoFormOpen = false;
        this.todoFormError = '';
        this.eventFormOpen = false;
        this.eventFormError = '';
        this.lockTodoActionScroll();

        if (mode === 'edit') this.prepareTodoActionEditForm(todo);
        if (mode === 'routine') this.prepareTodoActionRoutineForm(todo);
        await this.service.render();
    }

    public async closeTodoActionSheet(render = true) {
        this.todoActionTodoId = null;
        this.todoActionMode = 'menu';
        this.todoActionError = '';
        this.todoActionNotice = '';
        this.todoActionDraft = this.createTodoActionDraft();
        this.unlockTodoActionScroll();
        if (render) await this.service.render();
    }

    public async runTodoActionCommand(command: TodoActionCommand) {
        const todo = this.todoActionTodo;
        if (!todo) return;

        this.todoActionError = '';
        this.todoActionNotice = '';

        if (command === 'edit') {
            if (this.todoActionIsRoutineOccurrence) {
                await this.editRoutine(this.todoActionRoutine);
                await this.closeTodoActionSheet(false);
                await this.service.render();
                return;
            }
            this.prepareTodoActionEditForm(todo);
            this.todoActionMode = 'edit';
        }
        if (command === 'delete') {
            this.todoActionMode = 'delete';
        }
        if (command === 'memo') {
            this.todoActionDraft = this.createTodoActionDraft(todo);
            this.todoActionMode = 'memo';
        }
        if (command === 'reminder') {
            this.todoActionDraft = this.createTodoActionDraft(todo);
            this.todoActionMode = 'reminder';
        }
        if (command === 'photo') {
            this.todoActionDraft = this.createTodoActionDraft(todo);
            this.todoActionMode = 'photo';
        }
        if (command === 'tomorrow') {
            await this.duplicateTodoFromAction(todo, this.nextDateKey(todo.date));
        }
        if (command === 'repeat') {
            this.todoActionDraft = this.createTodoActionDraft(todo);
            this.todoActionMode = 'repeat';
        }
        if (command === 'date') {
            this.todoActionDraft = this.createTodoActionDraft(todo);
            this.todoActionMode = 'date';
        }
        if (command === 'routine') {
            this.prepareTodoActionRoutineForm(todo);
            this.todoActionMode = 'routine';
        }

        await this.service.render();
    }

    public async saveTodoFromActionSheet() {
        await this.saveTodo();
        if (!this.todoFormError) {
            await this.closeTodoActionSheet(false);
        }
        await this.service.render();
    }

    public async saveTodoActionMemo() {
        const updated = this.updateTodoFromAction({ memo: (this.todoActionDraft.memo || '').trim() });
        if (!updated) {
            await this.service.render();
            return;
        }
        this.todoActionDraft = this.createTodoActionDraft(updated);
        this.todoActionMode = 'menu';
        this.todoActionNotice = '메모를 저장했습니다.';
        await this.service.render();
    }

    public async selectTodoReminderOption(option: ReminderPreset) {
        const todo = this.todoActionTodo;
        if (!todo) return;

        this.todoActionDraft.reminderOption = this.normalizeReminderPreset(option);
        if (!this.todoActionDraft.reminderDate) this.todoActionDraft.reminderDate = todo.date || this.selectedDate;
        if (!this.todoActionDraft.reminderTime) this.todoActionDraft.reminderTime = this.todoReminderTime(todo) || this.defaultReminderTime();
        await this.service.render();
    }

    public async requestReminderPermission() {
        const granted = await this.ensureNotificationPermissionForReminder();
        this.reminderPermissionMessage = granted
            ? '브라우저 알림 권한을 허용했습니다. 예약된 알림은 브라우저 알림과 앱 안 알림으로 표시됩니다.'
            : (this.reminderPermissionMessage || '앱 안 알림으로 계속 알려드립니다.');
        this.todoActionNotice = granted
            ? '브라우저 알림 권한을 허용했습니다.'
            : '앱 안 알림으로 계속 알려드립니다.';
        await this.service.render();
    }

    public async toggleReminderCenter() {
        this.reminderCenterOpen = !this.reminderCenterOpen;
        await this.service.render();
    }

    public async dismissReminderToast() {
        this.clearReminderToastTimer();
        this.reminderToast = null;
        await this.service.render();
    }

    public async clearReminderNotices() {
        this.reminderNotices = [];
        this.reminderToast = null;
        this.reminderCenterOpen = false;
        this.reminderPermissionMessage = '';
        this.clearReminderToastTimer();
        await this.service.render();
    }

    public async dismissReminderPermissionMessage() {
        this.reminderPermissionMessage = '';
        await this.service.render();
    }

    public async saveTodoActionReminder() {
        const todo = this.todoActionTodo;
        if (!todo) return;
        const reminderPayload = this.buildReminderPayloadFromDraft(todo);
        if (!reminderPayload) {
            await this.service.render();
            return;
        }

        if (this.isRoutineOccurrenceTodo(todo)) {
            const updated = this.updateRoutineReminderFromAction(todo, reminderPayload);
            if (!updated) {
                await this.service.render();
                return;
            }
            this.todoActionDraft = this.createTodoActionDraft(this.createRoutineOccurrenceTodo(updated, todo.date));
        } else {
            const updated = this.updateTodoFromAction({
                reminderAt: reminderPayload.reminderAt,
                reminderRule: reminderPayload.reminderRule,
                reminders: [reminderPayload.reminder]
            });
            if (!updated) {
                await this.service.render();
                return;
            }
            this.todoActionDraft = this.createTodoActionDraft(updated);
        }

        this.persistReminderFiredKeys();
        await this.ensureNotificationPermissionForReminder();
        await this.checkDueReminders(false);
        this.todoActionMode = 'menu';
        this.todoActionNotice = '알림 시간을 저장했습니다.';
        await this.service.render();
    }

    public async clearTodoActionReminder() {
        const todo = this.todoActionTodo;
        if (!todo) return;

        if (this.isRoutineOccurrenceTodo(todo)) {
            const updated = this.updateRoutineReminderFromAction(todo, null);
            if (!updated) {
                await this.service.render();
                return;
            }
            this.todoActionDraft = this.createTodoActionDraft(this.createRoutineOccurrenceTodo(updated, todo.date));
        } else {
            const updated = this.updateTodoFromAction({
                reminderAt: null,
                reminderRule: null,
                reminders: []
            });
            if (!updated) {
                await this.service.render();
                return;
            }
            this.todoActionDraft = this.createTodoActionDraft(updated);
        }

        this.todoActionMode = 'menu';
        this.todoActionNotice = '알림을 해제했습니다.';
        await this.service.render();
    }

    public reminderNoticeTimeLabel(notice: DueReminderNotice) {
        return this.reminderDateTimeLabel(notice.scheduledAt);
    }

    public reminderNoticeTypeLabel(notice: DueReminderNotice) {
        if (notice.targetType === 'shared') return '공유 변경';
        if (notice.targetType === 'event') return '일정';
        return notice.targetType === 'routine' ? '루틴' : '할 일';
    }

    public async focusReminderNotice(notice: DueReminderNotice) {
        this.reminderCenterOpen = false;
        if (notice.targetType === 'event') {
            const event = this.events.find((item) => item.id === notice.targetId) || null;
            await this.selectTab('calendar');
            if (event) await this.openEventDetail(event, notice.date);
            else await this.selectDate(notice.date, true);
            return;
        }
        if (notice.targetType === 'shared') {
            const event = this.events.find((item) => item.id === notice.targetId) || null;
            await this.selectTab('share');
            if (event) this.focusedEventId = event.id;
            await this.selectDate(notice.date, false);
            return;
        }
        await this.selectTab('todo');
        await this.selectDate(notice.date, false);
    }

    public async closeReminderCenter() {
        this.reminderCenterOpen = false;
        await this.service.render();
    }

    public async handleTodoActionPhotoSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length > 0 ? input.files[0] : null;
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            this.todoActionError = '이미지 파일만 선택할 수 있습니다.';
            await this.service.render();
            return;
        }

        const reader = new FileReader();
        reader.onload = async () => {
            this.todoActionDraft.photoProofUrl = String(reader.result || '');
            this.todoActionError = '';
            await this.service.render();
        };
        reader.onerror = async () => {
            this.todoActionError = '사진 미리보기를 만들지 못했습니다.';
            await this.service.render();
        };
        reader.readAsDataURL(file);
    }

    public async saveTodoActionPhoto() {
        if (!this.todoActionDraft.photoProofUrl) {
            this.todoActionError = '사진을 선택해 주세요.';
            await this.service.render();
            return;
        }
        const updated = this.updateTodoFromAction({ photoProofUrl: this.todoActionDraft.photoProofUrl });
        if (!updated) {
            await this.service.render();
            return;
        }
        this.todoActionDraft = this.createTodoActionDraft(updated);
        this.todoActionMode = 'menu';
        this.todoActionNotice = '사진 인증을 저장했습니다.';
        await this.service.render();
    }

    public async clearTodoActionPhoto() {
        const updated = this.updateTodoFromAction({ photoProofUrl: '' });
        if (!updated) {
            await this.service.render();
            return;
        }
        this.todoActionDraft = this.createTodoActionDraft(updated);
        this.todoActionMode = 'menu';
        this.todoActionNotice = '사진 인증을 삭제했습니다.';
        await this.service.render();
    }

    public async saveTodoActionRepeatDate() {
        const todo = this.todoActionTodo;
        if (!todo) return;
        await this.duplicateTodoFromAction(todo, this.todoActionDraft.repeatDate);
        await this.service.render();
    }

    public async saveTodoActionDateChange() {
        const todo = this.todoActionTodo;
        const targetDate = this.todoActionDraft.changeDate;
        if (!todo) return;
        if (!targetDate) {
            this.todoActionError = '이동할 날짜를 선택해 주세요.';
            await this.service.render();
            return;
        }

        const updated = this.updateTodoFromAction({
            date: targetDate,
            movedFromDate: todo.movedFromDate || todo.date,
            movedToDate: targetDate,
            postponedFromDate: todo.postponedFromDate || todo.date,
            postponedToDate: targetDate,
            ...this.todoReminderPatchForDate(todo, targetDate)
        });
        if (!updated) {
            await this.service.render();
            return;
        }

        this.focusDate(targetDate);
        this.todoActionDraft = this.createTodoActionDraft(updated);
        this.todoActionMode = 'menu';
        this.todoActionNotice = `${this.dateLabel(targetDate)}로 이동했습니다.`;
        await this.service.render();
    }

    public async confirmTodoActionDelete() {
        const todo = this.todoActionTodo;
        if (!todo) return;
        if (!this.canEditTodo(todo)) {
            this.todoActionError = '할 일을 삭제할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        if (this.isRoutineOccurrenceTodo(todo)) {
            const routine = this.todoActionRoutine;
            if (!routine) {
                this.todoActionError = '삭제할 루틴을 찾지 못했습니다.';
                await this.service.render();
                return;
            }
            const deleted = this.dataRepository.deleteTodoRoutine(routine.id);
            if (!deleted) return;
            this.todoRoutines = this.todoRoutines.filter((item) => item.id !== routine.id);
            this.todoCompletionRecords = this.todoCompletionRecords.filter((record) => record.routineId !== routine.id);
            this.refreshTodoRecordsFromRepository();
            this.buildMonth();
            this.refreshReminderSchedules();
            await this.closeTodoActionSheet(false);
            await this.service.render();
            return;
        }

        const deleted = this.dataRepository.deleteTodo(todo.id);
        if (!deleted) return;
        this.todos = this.todos.filter((item) => item.id !== todo.id);
        this.refreshTodoRecordsFromRepository();
        if (this.editingTodoId === todo.id) {
            this.todoFormOpen = false;
            this.editingTodoId = null;
            this.todoFormError = '';
        }
        this.buildMonth();
        this.refreshReminderSchedules();
        await this.closeTodoActionSheet(false);
        await this.service.render();
    }

    public async saveRoutineFromTodoAction() {
        await this.saveRoutine();
        if (!this.routineFormError) {
            this.todoActionMode = 'menu';
            this.todoActionNotice = '루틴으로 등록했습니다.';
        }
        await this.service.render();
    }

    public async moveIncompleteTodosToTomorrow(date = this.selectedDate) {
        const openTodos = this.todos.filter((todo) => todo.date === date && !todo.completed && this.canEditTodo(todo));
        if (openTodos.length === 0) return;

        const targetDate = this.nextDateKey(date);
        const now = new Date().toISOString();
        const updatedById = new Map<string, OwnitTodo>();

        for (const todo of openTodos) {
            const updated = this.dataRepository.updateTodo(todo.id, {
                date: targetDate,
                movedFromDate: todo.movedFromDate || todo.date,
                movedToDate: targetDate,
                postponedFromDate: todo.postponedFromDate || todo.date,
                postponedToDate: targetDate,
                ...this.todoReminderPatchForDate(todo, targetDate),
                updatedAt: now
            });
            if (updated) updatedById.set(updated.id, updated);
        }

        if (updatedById.size === 0) return;
        this.todos = this.sortTodos(this.todos.map((todo) => updatedById.get(todo.id) || todo));
        this.refreshTodoRecordsFromRepository();
        this.buildMonth();
        this.refreshReminderSchedules();
        await this.service.render();
    }

    public isInlineTodoActive(section: TodoGoalSection) {
        return this.inlineTodoDraft.sectionId === section.id;
    }

    public trackByTodoGoalSection(_index: number, section: TodoGoalSection) {
        return section.id;
    }

    public trackByTodoItemRow(_index: number, row: TodoItemRow) {
        return row.todo.id;
    }

    public trackByTodoDayMarkerSlot(_index: number, slot: TodoDayMarkerSlot) {
        return slot.index;
    }

    public async openDefaultInlineTodoInput(date = this.selectedDate) {
        const firstGoal = this.activeTodoGoals[0] || null;
        await this.openInlineTodoInput(firstGoal?.id || null, date, firstGoal?.id || 'empty-goal', firstGoal?.name || this.uncategorizedTodoLabel, firstGoal?.color || this.uncategorizedTodoColor);
    }

    public async openInlineTodoInput(goalId: string | null, date = this.selectedDate, sectionId?: string, category?: string, color?: string) {
        if (!this.canCreateTodoAction) {
            this.todoFormError = '할 일을 추가할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const targetDate = date || this.selectedDate;
        let goal = goalId ? this.todoGoalById(goalId) : null;
        goal = this.expandTodoGoalForCreate(goal);
        const draftSectionId = sectionId || this.todoSectionKeyForGoal(goal);

        this.focusDate(targetDate);
        this.todoCreateFlowOpen = false;
        this.todoFormOpen = false;
        this.editingTodoId = null;
        this.todoGoalFormOpen = false;
        this.editingTodoGoalId = null;
        this.routineFormOpen = false;
        this.editingRoutineId = null;
        this.todoActionTodoId = null;
        this.todoActionMode = 'menu';
        this.todoActionError = '';
        this.todoActionNotice = '';
        this.unlockTodoActionScroll();
        this.todoFormError = '';
        this.inlineTodoDraft = {
            sectionId: draftSectionId,
            goalId: goal?.id || goalId || null,
            category: goal?.name || category || this.uncategorizedTodoLabel,
            color: goal?.color || color || this.uncategorizedTodoColor,
            title: ''
        };
        await this.service.render();
        this.focusInlineTodoInput(draftSectionId);
    }

    public async closeInlineTodoInput() {
        this.closeInlineTodoDraft();
        await this.service.render();
    }

    public activateInlineTodoInput(section: TodoGoalSection) {
        if (!section || section.collapsed) return;
        if (this.inlineTodoDraft.sectionId === section.id) return;

        const goal = section.goalId ? this.todoGoalById(section.goalId) : null;
        this.todoFormError = '';
        this.inlineTodoDraft = {
            sectionId: section.id,
            goalId: goal?.id || section.goalId || null,
            category: goal?.name || section.label || this.uncategorizedTodoLabel,
            color: goal?.color || section.color || this.uncategorizedTodoColor,
            title: ''
        };
    }

    public inlineTodoValueForSection(section: TodoGoalSection) {
        return this.inlineTodoDraft.sectionId === section.id ? this.inlineTodoDraft.title : '';
    }

    public updateInlineTodoTitle(event: Event) {
        const target = event.target as HTMLInputElement | null;
        this.inlineTodoDraft.title = target?.value || '';
    }

    public updateInlineTodoTitleForSection(event: Event, section: TodoGoalSection) {
        this.activateInlineTodoInput(section);
        this.updateInlineTodoTitle(event);
        if ((this.inlineTodoDraft.title || '').trim()) this.todoFormError = '';
    }

    public async saveInlineTodo(section?: TodoGoalSection) {
        if (section) this.activateInlineTodoInput(section);
        const payload = this.buildInlineTodoPayload();
        if (!payload) {
            await this.service.render();
            this.focusInlineTodoInput(this.inlineTodoDraft.sectionId);
            return;
        }

        let saved: OwnitTodo;
        try {
            saved = this.createTodoFromPayload(payload);
        } catch (error) {
            this.todoFormError = this.dataErrorMessage(error, '할 일을 저장하지 못했습니다.');
            await this.service.render();
            this.focusInlineTodoInput(this.inlineTodoDraft.sectionId);
            return;
        }
        this.todos = this.sortTodos([...this.todos, saved]);
        this.rememberTodoGoalUse(payload.goalId);
        this.refreshTodoRecordsFromRepository();
        if (this.selectedDate !== payload.date) {
            this.focusDate(payload.date);
        } else {
            this.buildMonth();
        }
        this.inlineTodoDraft = {
            ...this.inlineTodoDraft,
            sectionId: this.inlineTodoDraft.sectionId || this.todoSectionKeyForGoal(payload.goalId ? this.todoGoalById(payload.goalId) : null),
            title: ''
        };
        this.todoFormError = '';
        await this.service.render();
        this.focusInlineTodoInput(this.inlineTodoDraft.sectionId);
    }

    public canMoveTodoInSection(todo: OwnitTodo, direction: -1 | 1) {
        return this.todoMoveTarget(todo, direction) !== null;
    }

    public async moveTodoInSection(todoId: string, direction: -1 | 1) {
        const todo = this.todos.find((item) => item.id === todoId) || null;
        if (!todo || !this.canEditTodo(todo)) return;
        const target = this.todoMoveTarget(todo, direction);
        if (!target || !this.canEditTodo(target)) return;

        const now = new Date().toISOString();
        const currentOrder = this.normalizedTodoOrder(todo, 0);
        const targetOrder = this.normalizedTodoOrder(target, direction > 0 ? currentOrder + 1 : currentOrder - 1);
        const updatedCurrent = this.dataRepository.updateTodo(todo.id, {
            order: targetOrder,
            updatedAt: now
        });
        const updatedTarget = this.dataRepository.updateTodo(target.id, {
            order: currentOrder,
            updatedAt: now
        });
        if (!updatedCurrent || !updatedTarget) return;

        this.todos = this.sortTodos(this.todos.map((item) => {
            if (item.id === updatedCurrent.id) return updatedCurrent;
            if (item.id === updatedTarget.id) return updatedTarget;
            return item;
        }));
        this.buildMonth();
        await this.service.render();
    }

    public async openReceiptModal() {
        if (!this.canSaveReceiptAction) {
            this.receiptSaveError = '영수증 기록을 저장할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.ensureReceiptDraftForDate(this.selectedDate);
        this.receiptPreviewSavedAt = new Date();
        this.receiptSaveError = '';
        this.receiptModalOpen = true;
        await this.service.render();
    }

    public async closeReceiptModal() {
        this.receiptModalOpen = false;
        this.receiptSaveError = '';
        await this.service.render();
    }

    public async setReceiptEmoji(emoji: string) {
        this.receiptDraft.emoji = emoji;
        this.receiptDrafts[this.selectedDate] = this.receiptDraft;
        await this.service.render();
    }

    public persistReceiptTemplate() {
        this.receiptTemplate = this.normalizeReceiptTemplate(this.receiptTemplate);
        this.saveReceiptTemplateState();
    }

    public async toggleReceiptSharedSchedules() {
        this.receiptTemplate = this.normalizeReceiptTemplate({
            ...this.receiptTemplate,
            includeSharedSchedules: !this.receiptTemplate.includeSharedSchedules
        });
        this.saveReceiptTemplateState();
        await this.service.render();
    }

    public async toggleReceiptSection(sectionId: DailyReceiptSectionId) {
        this.receiptTemplate = this.normalizeReceiptTemplate({
            ...this.receiptTemplate,
            sections: this.receiptTemplate.sections.map((section) => {
                if (section.id !== sectionId) return section;
                return { ...section, enabled: !section.enabled };
            })
        });
        this.saveReceiptTemplateState();
        await this.service.render();
    }

    public async moveReceiptSection(sectionId: DailyReceiptSectionId, direction: -1 | 1) {
        const sections = this.receiptTemplateSections;
        const index = sections.findIndex((section) => section.id === sectionId);
        const targetIndex = index + direction;
        if (index < 0 || targetIndex < 0 || targetIndex >= sections.length) return;

        const next = [...sections];
        const [moving] = next.splice(index, 1);
        next.splice(targetIndex, 0, moving);
        this.receiptTemplate = this.normalizeReceiptTemplate({
            ...this.receiptTemplate,
            sections: next.map((section, order) => ({ ...section, order }))
        });
        this.saveReceiptTemplateState();
        await this.service.render();
    }

    public async selectDailyRecordEmoji(emoji: string) {
        this.dailyRecordDraft.representativeEmoji = emoji;
        await this.saveDailyRecordDraft('emoji');
    }

    public async saveDailyRecordDraft(source: 'manual' | 'emoji' = 'manual') {
        if (!this.canSaveDailyRecordAction) {
            this.dailyRecordError = '하루 기록을 저장할 권한이 없습니다.';
            this.dailyRecordNotice = '';
            await this.service.render();
            return;
        }

        this.dailyRecordSaving = true;
        this.dailyRecordError = '';
        this.dailyRecordNotice = '';

        try {
            const payload = this.buildDailyRecordPayload();
            const updated = this.dataRepository.updateTodoDailyRecord(payload.id, payload);
            const saved = updated || this.dataRepository.createTodoDailyRecord(payload);
            this.todoDailyRecords = this.replaceTodoDailyRecord(saved);
            this.dailyRecordDraft = this.createDailyRecordDraft(saved);
            this.syncReceiptDraftFromDailyRecord(saved);
            this.dailyRecordNotice = source === 'emoji' ? '대표 이모지를 저장했습니다.' : '하루 기록을 저장했습니다.';
        } catch (error) {
            this.dailyRecordError = this.dataErrorMessage(error, '하루 기록을 저장하지 못했습니다.');
        } finally {
            this.dailyRecordSaving = false;
            await this.service.render();
        }
    }

    public async prepareDailyRecordAiDraft() {
        this.dailyRecordError = '';
        this.dailyRecordNotice = '';
        this.todoAiSuggestionDraft = {
            ...this.todoAiSuggestionDraft,
            open: true,
            error: '',
            notice: ''
        };

        const context = this.collectTodoAiSuggestionInput();
        if (!hasTodoAiSuggestionInput(context)) {
            this.todoAiSuggestionDraft = {
                ...this.todoAiSuggestionDraft,
                loading: false,
                error: '',
                notice: '할 일, 루틴 체크, 대표 이모지나 메모가 생기면 초안을 만들 수 있습니다.'
            };
            await this.service.render();
            return;
        }

        this.todoAiSuggestionDraft = {
            ...this.todoAiSuggestionDraft,
            loading: true,
            activeTab: 'diary',
            error: '',
            notice: '',
            providerLabel: '',
            generatedAt: '',
            diaryDraft: '',
            tomorrowTodos: []
        };
        await this.service.render();

        try {
            const result = await this.aiSuggestionProvider.generate(context);
            this.applyTodoAiSuggestionResult(result);
            this.dailyRecordNotice = '초안을 만들었습니다. 검토 후 적용해 주세요.';
        } catch (error) {
            const message = error instanceof Error ? error.message : '초안을 만들지 못했습니다.';
            this.todoAiSuggestionDraft = {
                ...this.todoAiSuggestionDraft,
                error: `${message} 기존 할 일과 하루 기록은 그대로 유지됩니다.`,
                notice: '',
                loading: false
            };
            this.dailyRecordError = '초안 생성에 실패했습니다.';
        } finally {
            this.todoAiSuggestionDraft = {
                ...this.todoAiSuggestionDraft,
                loading: false
            };
            await this.service.render();
        }
    }

    public async setTodoAiSuggestionTab(tab: TodoAiSuggestionPanelTab) {
        this.todoAiSuggestionDraft.activeTab = tab;
        await this.service.render();
    }

    public async closeTodoAiSuggestionPanel() {
        this.todoAiSuggestionDraft = this.createTodoAiSuggestionDraftState();
        await this.service.render();
    }

    public updateTodoAiDiaryDraft(event: Event) {
        const target = event.target as HTMLTextAreaElement | null;
        this.todoAiSuggestionDraft.diaryDraft = target?.value || '';
    }

    public updateTodoAiTomorrowDraft(index: number, event: Event) {
        const target = event.target as HTMLInputElement | null;
        this.todoAiSuggestionDraft.tomorrowTodos = this.todoAiSuggestionDraft.tomorrowTodos.map((title, itemIndex) => {
            if (itemIndex !== index) return title;
            return target?.value || '';
        });
    }

    public async addTodoAiTomorrowDraft() {
        if (this.todoAiSuggestionDraft.tomorrowTodos.length >= 6) return;
        this.todoAiSuggestionDraft.tomorrowTodos = [...this.todoAiSuggestionDraft.tomorrowTodos, ''];
        await this.service.render();
    }

    public async removeTodoAiTomorrowDraft(index: number) {
        this.todoAiSuggestionDraft.tomorrowTodos = this.todoAiSuggestionDraft.tomorrowTodos.filter((_, itemIndex) => itemIndex !== index);
        await this.service.render();
    }

    public async applyTodoAiDiaryDraft() {
        const draft = this.todoAiSuggestionDraft.diaryDraft.trim();
        if (!draft) {
            this.todoAiSuggestionDraft.error = '적용할 일기 초안이 없습니다.';
            await this.service.render();
            return;
        }
        this.dailyRecordDraft.diary = draft;
        this.todoAiSuggestionDraft.notice = '일기 입력칸에 적용했습니다. 기록 저장을 눌러 보관해 주세요.';
        this.todoAiSuggestionDraft.error = '';
        this.dailyRecordNotice = '';
        await this.service.render();
    }

    public async applyTodoAiTomorrowTodos() {
        if (!this.canCreateTodoAction) {
            this.todoAiSuggestionDraft.error = '할 일을 추가할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const titles = this.normalizedTodoAiTomorrowTitles();
        if (titles.length === 0) {
            this.todoAiSuggestionDraft.error = '추가할 할 일 추천을 입력해 주세요.';
            await this.service.render();
            return;
        }

        try {
            const goal = this.activeTodoGoals[0] || null;
            const savedTodos = titles.map((title) => this.createTodoFromPayload({
                title,
                date: this.todoAiSuggestionTomorrowDate,
                memo: '',
                goalId: goal?.id || null,
                categoryId: goal?.categoryId || null,
                category: goal?.name || this.uncategorizedTodoLabel,
                color: goal?.color || this.uncategorizedTodoColor,
                scope: goal?.visibility === 'group' ? 'shared' : 'personal',
                visibility: goal?.visibility === 'public' ? 'public' : (goal?.visibility === 'group' ? 'group' : 'private'),
                updatedAt: new Date().toISOString()
            }));
            this.todos = this.sortTodos([...this.todos, ...savedTodos]);
            this.todoAiSuggestionDraft = {
                ...this.todoAiSuggestionDraft,
                tomorrowTodos: titles,
                notice: `${this.todoAiSuggestionTomorrowDateLabel} 할 일 ${savedTodos.length}개를 추가했습니다.`,
                error: ''
            };
            this.buildMonth();
        } catch (error) {
            this.todoAiSuggestionDraft = {
                ...this.todoAiSuggestionDraft,
                error: error instanceof Error ? error.message : '추천 할 일을 추가하지 못했습니다.',
                notice: ''
            };
        }
        await this.service.render();
    }

    public async downloadReceiptPng() {
        if (!this.canSaveReceiptAction) {
            this.receiptSaveError = '영수증 기록을 저장할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.ensureReceiptDraftForDate(this.selectedDate);
        this.receiptPreviewSavedAt = new Date();
        this.receiptSaving = true;
        this.receiptSaveError = '';
        await this.service.render();

        try {
            if (document.fonts && document.fonts.ready) await document.fonts.ready;

            const receipt = this.receiptViewModel;
            const preview = document.getElementById('daily-receipt-preview') as HTMLElement | null;
            let dataUrl = '';
            if (preview) {
                try {
                    dataUrl = await this.renderReceiptPreviewToPng(preview);
                } catch (error) {
                    dataUrl = '';
                }
            }
            if (!dataUrl) dataUrl = this.renderReceiptCanvasToPng(receipt);
            this.triggerReceiptDownload(dataUrl, `ownit-receipt-${this.selectedDate}.png`);
        } catch (error) {
            this.receiptSaveError = error instanceof Error ? error.message : 'PNG 저장 중 오류가 발생했습니다.';
        } finally {
            this.receiptSaving = false;
            await this.service.render();
        }
    }

    public async syncEventCalendarColor(calendarId: string) {
        const calendar = this.calendarById(calendarId);
        if (calendar) {
            this.eventForm.scope = calendar.type;
            this.eventForm.color = calendar.color;
            this.eventForm.assigneeId = calendar.type === 'shared' ? this.defaultSharedAssigneeId(this.eventForm.assigneeId, calendar) : this.user.id;
        }
        await this.service.render();
    }

    public async syncEventScope(scope: OwnitCalendar['type']) {
        this.eventForm.scope = scope === 'shared' ? 'shared' : 'personal';
        const options = this.eventFormCalendarOptions;
        const current = options.find((calendar) => calendar.id === this.eventForm.calendarId);
        const calendar = current || options[0] || null;
        this.eventForm.calendarId = calendar?.id || '';
        if (calendar) {
            this.eventForm.color = calendar.color;
            this.eventForm.assigneeId = calendar.type === 'shared' ? this.defaultSharedAssigneeId(this.eventForm.assigneeId, calendar) : this.user.id;
            this.eventFormError = '';
        } else {
            this.eventForm.assigneeId = this.eventForm.scope === 'shared' ? '' : this.user.id;
            this.eventFormError = this.eventForm.scope === 'shared'
                ? '일정을 만들 수 있는 공유 캘린더가 없습니다.'
                : '일정을 만들 수 있는 개인 캘린더가 없습니다.';
        }
        await this.service.render();
    }

    public async setDecorationTheme(themeId: string) {
        if (!this.canManageDecorationAction) return;
        const nextThemeId = themeId || OWNIT_DEFAULT_THEME_ID;
        await this.saveDecoration({
            themeId: nextThemeId,
            background: nextThemeId === OWNIT_CLOUD_DANCER_THEME_ID ? (this.decoration.background || 'white') : 'white'
        });
    }

    public async setDecorationBackground(background: string) {
        if (!this.canManageDecorationAction) return;
        const canUseDecorativeBackground = this.selectedTheme.id === OWNIT_CLOUD_DANCER_THEME_ID;
        await this.saveDecoration({ background: canUseDecorativeBackground ? (background || 'white') : 'white' });
    }

    public async setDecorationFont(fontId: string) {
        if (!this.canManageDecorationAction) return;
        await this.saveDecoration({ fontId: normalizeOwnitFontId(fontId) });
    }

    public async setStickerTarget(target: StickerPlacementTarget) {
        this.stickerTarget = target;
        await this.service.render();
    }

    public async addBasicSticker(stickerId: string) {
        await this.addStickerFromAsset(stickerId);
    }

    public async addCustomEmojiSticker() {
        if (!this.canManageDecorationAction) return;

        const emoji = this.normalizeCustomEmojiInput(this.customEmojiInput);
        if (!emoji) {
            this.customEmojiError = '기본 이모지 한 개를 입력해 주세요.';
            this.customEmojiNotice = '';
            await this.service.render();
            return;
        }
        if (!this.isLikelyEmoji(emoji)) {
            this.customEmojiError = '문자나 단어 대신 iPhone/Mac 기본 이모지처럼 보이는 문자를 입력해 주세요.';
            this.customEmojiNotice = '';
            await this.service.render();
            return;
        }

        this.customEmojiInput = '';
        this.customEmojiError = '';
        this.customEmojiNotice = `${emoji} 이모지를 배치했습니다.`;
        await this.addStickerFromAsset(this.encodeCustomEmojiStickerId(emoji));
    }

    public async addStickerFromAsset(stickerId: string) {
        if (!this.canManageDecorationAction) return;

        const asset = this.stickerAsset(stickerId);
        if (!asset) return;

        const existingCount = this.decoration.stickers.length;
        const maxZIndex = this.decoration.stickers.reduce((max, sticker) => Math.max(max, Number(sticker.zIndex) || 0), 0);
        const sticker: StickerPlacement = {
            id: this.createId('sticker'),
            stickerId: asset.id,
            label: asset.label,
            target: this.stickerTarget,
            date: this.stickerTarget === 'date' ? this.selectedDate : null,
            x: this.stickerTarget === 'date' ? 58 : Math.min(80, 18 + existingCount * 13),
            y: this.stickerTarget === 'date' ? 42 : Math.min(78, 20 + existingCount * 9),
            size: this.stickerTarget === 'date' ? 30 : 40,
            rotation: existingCount % 2 === 0 ? -8 : 8,
            zIndex: Math.min(99, maxZIndex + 1)
        };
        this.selectedStickerId = sticker.id;
        await this.saveDecoration({ stickers: [...this.decoration.stickers, sticker] });
    }

    public async handleStickerUploadSelected(event: Event) {
        if (!this.canManageDecorationAction) return;

        const input = event.target as HTMLInputElement | null;
        const file = input?.files?.[0] || null;
        if (input) input.value = '';
        if (!file) return;

        this.stickerUploadDraft = this.createStickerUploadDraft();
        const validationError = this.validateStickerUploadFile(file);
        if (validationError) {
            this.stickerUploadDraft.error = validationError;
            await this.service.render();
            return;
        }

        try {
            const prepared = await this.prepareStickerUploadImage(file);
            this.stickerUploadDraft = {
                ...this.createStickerUploadDraft(),
                name: this.defaultStickerName(file.name),
                dataUrl: prepared.dataUrl,
                originalDataUrl: prepared.dataUrl,
                fileName: file.name,
                fileType: prepared.fileType,
                fileSize: prepared.fileSize,
                originalFileSize: file.size,
                width: prepared.width,
                height: prepared.height,
                notice: prepared.notice
            };
        } catch (error) {
            this.stickerUploadDraft.error = '이미지를 읽지 못했습니다. 다른 파일을 선택해 주세요.';
        }
        await this.service.render();
    }

    public async removeStickerBackground() {
        if (!this.canManageDecorationAction || !this.stickerUploadDraft.originalDataUrl) return;
        if (this.stickerUploadDraft.backgroundRemovalStatus === 'processing') return;

        this.stickerUploadDraft = {
            ...this.stickerUploadDraft,
            dataUrl: this.stickerUploadDraft.originalDataUrl,
            processedDataUrl: '',
            processedWidth: 0,
            processedHeight: 0,
            backgroundRemovalStatus: 'processing',
            backgroundRemovalError: '',
            error: '',
            notice: '배경을 제거하는 중입니다. 잠시만 기다려 주세요.'
        };
        await this.service.render();
        await this.nextAnimationFrame();

        try {
            const result = await this.createTransparentStickerPng(this.stickerUploadDraft.originalDataUrl);
            const notice = result.alreadyTransparent
                ? '이미 투명 배경에 가까운 이미지입니다. PNG 스티커로 저장할 수 있습니다.'
                : `배경 제거가 완료되었습니다. 제거 영역 ${Math.round(result.removedRatio * 100)}%`;
            this.stickerUploadDraft = {
                ...this.stickerUploadDraft,
                dataUrl: result.dataUrl,
                processedDataUrl: result.dataUrl,
                fileType: 'image/png',
                fileSize: result.fileSize,
                processedWidth: result.width,
                processedHeight: result.height,
                backgroundRemovalStatus: 'done',
                backgroundRemovalError: '',
                notice
            };
        } catch (error) {
            this.stickerUploadDraft = {
                ...this.stickerUploadDraft,
                dataUrl: this.stickerUploadDraft.originalDataUrl,
                processedDataUrl: '',
                processedWidth: 0,
                processedHeight: 0,
                backgroundRemovalStatus: 'failed',
                backgroundRemovalError: this.dataErrorMessage(error, '배경을 자동으로 구분하지 못했습니다.'),
                notice: '원본 이미지는 유지되었습니다. 원본으로 저장하거나 다시 시도할 수 있습니다.'
            };
        }
        await this.service.render();
    }

    public async restoreOriginalStickerUpload() {
        if (!this.stickerUploadDraft.originalDataUrl) return;
        this.stickerUploadDraft = {
            ...this.stickerUploadDraft,
            dataUrl: this.stickerUploadDraft.originalDataUrl,
            processedDataUrl: '',
            processedWidth: 0,
            processedHeight: 0,
            backgroundRemovalStatus: 'idle',
            backgroundRemovalError: '',
            notice: '원본 이미지로 되돌렸습니다.'
        };
        await this.service.render();
    }

    public async saveUploadedSticker() {
        if (!this.canManageDecorationAction) return;
        const name = this.stickerUploadDraft.name.trim();
        if (!this.stickerUploadDraft.dataUrl) {
            this.stickerUploadDraft.error = '먼저 이미지 파일을 선택해 주세요.';
            await this.service.render();
            return;
        }
        if (!name) {
            this.stickerUploadDraft.error = '스티커 이름을 입력해 주세요.';
            await this.service.render();
            return;
        }

        const now = new Date().toISOString();
        const dataUrl = this.stickerUploadDraft.processedDataUrl || this.stickerUploadDraft.dataUrl;
        const isTransparentPng = this.stickerUploadDraft.backgroundRemovalStatus === 'done' && Boolean(this.stickerUploadDraft.processedDataUrl);
        const record: OwnitStickerAsset = {
            id: this.createId('sticker-asset'),
            ownerId: this.user.id,
            imageUrl: null,
            dataUrl,
            originalDataUrl: this.stickerUploadDraft.originalDataUrl || dataUrl,
            processedDataUrl: isTransparentPng ? dataUrl : '',
            name: name.slice(0, 40),
            sourceType: 'upload',
            fileType: isTransparentPng ? 'image/png' : this.stickerUploadDraft.fileType,
            fileSize: this.dataUrlByteSize(dataUrl) || this.stickerUploadDraft.fileSize,
            originalFileSize: this.stickerUploadDraft.originalFileSize || this.stickerUploadDraft.fileSize,
            width: this.stickerUploadDraft.processedWidth || this.stickerUploadDraft.width,
            height: this.stickerUploadDraft.processedHeight || this.stickerUploadDraft.height,
            backgroundRemovalStatus: this.stickerUploadDraft.backgroundRemovalStatus,
            backgroundRemovalError: this.stickerUploadDraft.backgroundRemovalError || null,
            renderOptions: {
                border: this.stickerUploadDraft.borderEnabled,
                shadow: this.stickerUploadDraft.shadowEnabled
            },
            createdAt: now,
            updatedAt: now
        };

        try {
            const saved = this.dataRepository.createStickerAsset(record);
            this.stickerAssets = this.sortStickerAssets([...this.stickerAssets, saved]);
            this.stickerUploadDraft = {
                ...this.createStickerUploadDraft(),
                notice: isTransparentPng ? '투명 PNG 스티커가 보관함에 저장되었습니다.' : '원본 스티커가 보관함에 저장되었습니다.'
            };
        } catch (error) {
            this.stickerUploadDraft.error = this.dataErrorMessage(error, '스티커를 저장하지 못했습니다.');
        }
        await this.service.render();
    }

    public async clearStickerUploadDraft() {
        this.stickerUploadDraft = this.createStickerUploadDraft();
        await this.service.render();
    }

    public async startStickerAssetRename(asset: OwnitStickerAsset) {
        this.editingStickerAssetId = asset.id;
        this.stickerAssetRenameDraft = asset.name;
        await this.service.render();
    }

    public async cancelStickerAssetRename() {
        this.editingStickerAssetId = '';
        this.stickerAssetRenameDraft = '';
        await this.service.render();
    }

    public async saveStickerAssetRename(asset: OwnitStickerAsset) {
        if (!this.canManageDecorationAction || asset.ownerId !== this.user.id) return;
        const name = this.stickerAssetRenameDraft.trim();
        if (!name) return;

        try {
            const updated = this.dataRepository.updateStickerAsset(asset.id, {
                name: name.slice(0, 40),
                updatedAt: new Date().toISOString()
            });
            if (updated) {
                this.stickerAssets = this.sortStickerAssets(this.stickerAssets.map((item) => item.id === updated.id ? updated : item));
                await this.renameStickerPlacements(updated.id, updated.name);
            }
        } catch (error) {
            this.stickerUploadDraft.error = this.dataErrorMessage(error, '스티커 이름을 저장하지 못했습니다.');
        }
        this.editingStickerAssetId = '';
        this.stickerAssetRenameDraft = '';
        await this.service.render();
    }

    public async deleteStickerAsset(asset: OwnitStickerAsset) {
        if (!this.canManageDecorationAction || asset.ownerId !== this.user.id) return;
        const confirmed = await this.service.modal.show({
            title: '스티커 삭제',
            message: `${asset.name} 스티커와 배치된 장식을 삭제할까요?`,
            action: '삭제',
            cancel: '취소',
            status: 'error',
            actionBtn: 'error'
        });
        if (!confirmed) return;

        try {
            const deleted = this.dataRepository.deleteStickerAsset(asset.id);
            if (!deleted) return;
            this.stickerAssets = this.stickerAssets.filter((item) => item.id !== asset.id);
            await this.removeStickerPlacementsByAsset(asset.id);
            if (this.editingStickerAssetId === asset.id) {
                this.editingStickerAssetId = '';
                this.stickerAssetRenameDraft = '';
            }
        } catch (error) {
            this.stickerUploadDraft.error = this.dataErrorMessage(error, '스티커를 삭제하지 못했습니다.');
        }
        await this.service.render();
    }

    public async selectSticker(stickerId: string) {
        this.selectedStickerId = stickerId;
        await this.service.render();
    }

    public async updateSelectedSticker(field: 'x' | 'y' | 'size' | 'rotation' | 'zIndex', value: number | string) {
        if (!this.canManageDecorationAction) return;

        const sticker = this.selectedSticker;
        if (!sticker) return;

        const numeric = Number(value);
        if (Number.isNaN(numeric)) return;

        const limits = {
            x: { min: 0, max: 100 },
            y: { min: 0, max: 100 },
            size: { min: 18, max: 72 },
            rotation: { min: -45, max: 45 },
            zIndex: { min: 1, max: 99 }
        };
        const limit = limits[field];
        const nextValue = Math.min(limit.max, Math.max(limit.min, numeric));
        const stickers = this.decoration.stickers.map((item) => item.id === sticker.id ? { ...item, [field]: nextValue } : item);
        await this.saveDecoration({ stickers });
    }

    public async moveSelectedStickerToTarget(target: StickerPlacementTarget) {
        if (!this.canManageDecorationAction) return;

        const sticker = this.selectedSticker;
        if (!sticker) return;

        const stickers = this.decoration.stickers.map((item) => item.id === sticker.id ? {
            ...item,
            target,
            date: target === 'date' ? this.selectedDate : null,
            x: target === 'date' ? Math.min(item.x, 82) : item.x,
            y: target === 'date' ? Math.min(item.y, 82) : item.y,
            size: target === 'date' ? Math.min(item.size, 34) : Math.max(item.size, 32)
        } : item);
        this.stickerTarget = target;
        await this.saveDecoration({ stickers });
    }

    public async removeSelectedSticker() {
        if (!this.canManageDecorationAction) return;

        const sticker = this.selectedSticker;
        if (!sticker) return;
        const stickers = this.decoration.stickers.filter((item) => item.id !== sticker.id);
        this.selectedStickerId = stickers[0]?.id || '';
        await this.saveDecoration({ stickers });
    }

    public stickersForDate(date: string) {
        return this.sortStickerPlacements(this.decoration.stickers.filter((sticker) => (sticker.target || 'calendar') === 'date' && sticker.date === date));
    }

    public stickerEmoji(stickerId: string) {
        return this.stickerAsset(stickerId).emoji;
    }

    public stickerIconClass(stickerId: string) {
        return this.stickerAsset(stickerId).iconClass;
    }

    public stickerImageUrl(stickerId: string) {
        return this.stickerAsset(stickerId).dataUrl;
    }

    public stickerImageFilter(stickerId: string) {
        return this.stickerImageFilterFromOptions(this.stickerAsset(stickerId).renderOptions);
    }

    public stickerAssetImageFilter(asset: OwnitStickerAsset) {
        return this.stickerImageFilterFromOptions(asset.renderOptions);
    }

    public stickerAssetStatusLabel(asset: OwnitStickerAsset) {
        if (asset.backgroundRemovalStatus === 'done') return '투명 PNG';
        if (asset.backgroundRemovalStatus === 'failed') return '원본 유지';
        return '원본';
    }

    public stickerColor(stickerId: string) {
        return this.stickerAsset(stickerId).color;
    }

    public stickerBackground(stickerId: string) {
        return this.stickerAsset(stickerId).background;
    }

    public stickerTransform(sticker: StickerPlacement) {
        return `translate(-50%, -50%) rotate(${sticker.rotation}deg)`;
    }

    public async startStickerDrag(stickerId: string, event: PointerEvent) {
        if (!this.canManageDecorationAction) return;
        const container = this.resolveStickerDragContainer(event.currentTarget as HTMLElement | null);
        if (!container) return;

        event.preventDefault();
        event.stopPropagation();
        this.selectedStickerId = stickerId;
        this.stickerDragState = {
            id: stickerId,
            pointerId: event.pointerId,
            container,
            moved: false
        };
        try {
            (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        } catch (error) {
            // Pointer capture is best-effort for older mobile webviews.
        }
        await this.service.render();
    }

    public async moveStickerDrag(event: PointerEvent) {
        if (!this.stickerDragState || this.stickerDragState.pointerId !== event.pointerId) return;
        event.preventDefault();
        await this.updateDraggedStickerPosition(event, true);
    }

    public async endStickerDrag(event?: PointerEvent) {
        if (!this.stickerDragState) return;
        if (event && this.stickerDragState.pointerId !== event.pointerId) return;

        const moved = this.stickerDragState.moved;
        this.stickerDragState = null;
        if (moved) {
            await this.saveDecoration({ stickers: this.decoration.stickers });
        } else {
            await this.service.render();
        }
    }

    public eventBarTextColor(color: string) {
        const hex = (color || '').replace('#', '').trim();
        if (!/^[0-9a-fA-F]{6}$/.test(hex)) return '#ffffff';
        const red = parseInt(hex.slice(0, 2), 16);
        const green = parseInt(hex.slice(2, 4), 16);
        const blue = parseInt(hex.slice(4, 6), 16);
        const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
        return luminance > 150 ? OWNIT_NEUTRAL_COLORS.text : '#ffffff';
    }

    public eventsForDay(date: string) {
        return this.visibleEvents.filter((event) => this.isDateWithin(date, event.startDate, event.endDate));
    }

    public todosForDay(date: string) {
        const regularTodos = this.todos.filter((todo) => todo.date === date && !todo.routineId);
        return this.sortTodos([...regularTodos, ...this.routineOccurrencesForDate(date)]);
    }

    public holidayTypeLabel(holiday: OwnitHoliday) {
        if (holiday.type === 'substitute') return '대체공휴일';
        if (holiday.type === 'observance') return '기념일';
        return '공휴일';
    }

    public dateAriaLabel(day: CalendarDay) {
        const date = this.fromDateKey(day.date);
        const parts = [`${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`];
        if (day.isToday) parts.push('오늘');
        if (day.date === this.selectedDate) parts.push('선택됨');
        if (this.isDateInCalendarSelection(day.date)) parts.push('기간 선택됨');
        if (this.isDragging && this.hoveredDate === day.date) parts.push('드래그 종료 후보');
        if (day.holidays.length > 0) parts.push(`공휴일 ${day.holidayLabel}`);
        if (day.eventCount > 0) parts.push(`일정 ${day.eventCount}개`);
        if (day.todoRecord) parts.push(day.todoRecord.label);
        return parts.join(', ');
    }

    public calendarById(calendarId: string) {
        return this.calendars.find((calendar) => calendar.id === calendarId) || null;
    }

    public calendarTypeLabel(calendar: OwnitCalendar | null | undefined) {
        if (!calendar) return '캘린더 없음';
        if (this.isExternalCalendar(calendar)) return '외부';
        return calendar.type === 'shared' ? '공유' : '개인';
    }

    public calendarDescriptionLabel(calendar: OwnitCalendar) {
        if (this.isExternalCalendar(calendar)) return calendar.description || `${this.externalCalendarSourceLabel(calendar)} 읽기 전용 캘린더`;
        if (calendar.description) return calendar.description;
        return calendar.isDefault ? '삭제할 수 없는 기본 개인 캘린더' : `${this.calendarTypeLabel(calendar)} 캘린더`;
    }

    public groupNameForCalendar(calendar: OwnitCalendar) {
        return this.groupForCalendar(calendar)?.name || '공유 그룹 없음';
    }

    public calendarVisibilityLabel(calendar: OwnitCalendar) {
        const labels: Record<Visibility, string> = {
            private: '초대 전용',
            group: '그룹 멤버',
            public: '공개 보기'
        };
        return labels[calendar.visibility || (calendar.type === 'shared' ? 'group' : 'private')];
    }

    public calendarStatusLabel(calendar: OwnitCalendar) {
        if (this.isExternalCalendar(calendar)) return this.isCalendarVisible(calendar.id) ? '읽기 전용' : '숨김';
        if (calendar.isDefault) return '기본';
        return calendar.isVisible === false ? '숨김' : '표시';
    }

    public sharedGroupCalendarCount(group: OwnitGroup) {
        return this.sharedCalendars.filter((calendar) => calendar.groupId === group.id).length;
    }

    public eventCountForCalendar(calendarId: string) {
        return this.events.filter((event) => event.calendarId === calendarId).length;
    }

    public canManageCalendar(calendar: OwnitCalendar | null | undefined) {
        if (!calendar || !this.user.id) return false;
        if (this.isExternalCalendar(calendar)) return false;
        if (this.isAdminAccount) return true;
        if (calendar.type === 'personal') return calendar.ownerId === this.user.id;
        return hasPermission(this.user, 'manage_shared_calendar', { group: this.groupForCalendar(calendar), calendar });
    }

    public canToggleCalendarVisibility(calendar: OwnitCalendar | null | undefined) {
        return Boolean(calendar && (this.isExternalCalendar(calendar) || this.canManageCalendar(calendar)));
    }

    public canArchiveCalendar(calendar: OwnitCalendar | null | undefined) {
        return Boolean(calendar && !calendar.isDefault && this.canManageCalendar(calendar));
    }

    public isExternalCalendar(calendar: OwnitCalendar | null | undefined) {
        return Boolean(calendar && (calendar.readOnly || calendar.externalId || this.isExternalSource(calendar.source)));
    }

    public isExternalEvent(event: OwnitEvent | null | undefined) {
        return Boolean(event && (event.readOnly || event.externalId || this.isExternalSource(event.source)));
    }

    public isReadOnlyEvent(event: OwnitEvent | null | undefined) {
        return Boolean(event && (event.readOnly || this.isExternalEvent(event)));
    }

    public isSharedEvent(event: OwnitEvent) {
        return this.calendarById(event.calendarId)?.type === 'shared';
    }

    public eventScopeLabel(event: OwnitEvent) {
        if (this.isExternalEvent(event)) return this.externalEventSourceLabel(event);
        return this.isSharedEvent(event) ? '공유' : '개인';
    }

    public eventStatusLabel(event: OwnitEvent) {
        if (this.isReadOnlyEvent(event)) return '읽기 전용';
        return event.completed ? '완료' : '진행';
    }

    public eventAuthorName(event: OwnitEvent) {
        return this.memberNameByUserId(event.authorId, this.groupForCalendar(this.calendarById(event.calendarId)));
    }

    public eventAssigneeName(event: OwnitEvent) {
        if (!event.assigneeId) return '미지정';
        return this.memberNameByUserId(event.assigneeId, this.groupForCalendar(this.calendarById(event.calendarId)));
    }

    public eventCalendarName(event: OwnitEvent) {
        return this.calendarById(event.calendarId)?.name || '캘린더 없음';
    }

    public externalCalendarSourceLabel(calendar: OwnitCalendar | null | undefined) {
        return externalSourceLabel(calendar?.provider || calendar?.source);
    }

    public externalEventSourceLabel(event: OwnitEvent | null | undefined) {
        return externalSourceLabel(event?.provider || event?.source);
    }

    public externalEventSourceShortLabel(event: OwnitEvent | null | undefined) {
        const source = event?.provider || event?.source;
        if (source === 'google') return 'G';
        if (source === 'microsoft') return 'M';
        if (source === 'apple') return 'A';
        if (source === 'naver') return 'N';
        if (source === 'ics_url') return 'ICS';
        return 'EXT';
    }

    public externalSyncedLabel(value: string | null | undefined) {
        if (!value) return '동기화 대기';
        return `${this.shortDateLabel(value.slice(0, 10))} 동기화`;
    }

    public externalEventMetaLabel(event: OwnitEvent) {
        const timezone = event.externalTimezone && event.externalTimezone !== 'local' ? ` · ${event.externalTimezone}` : '';
        const repeat = event.repeatRule ? ' · 반복' : '';
        return `${this.externalEventSourceLabel(event)} · ${this.externalSyncedLabel(event.lastSyncedAt || null)}${timezone}${repeat}`;
    }

    public externalCalendarSyncDirection(calendar: OwnitCalendar | null | undefined): CalendarSyncDirection {
        if (!calendar) return 'import_only';
        return this.ensureExternalSyncPolicy(calendar).direction;
    }

    public externalCalendarSyncDirectionLabel(calendar: OwnitCalendar | null | undefined) {
        const direction = this.externalCalendarSyncDirection(calendar);
        return this.externalSyncDirectionOptions.find((option) => option.value === direction)?.label || '가져오기만';
    }

    public externalCalendarSyncStatusLabel(calendar: OwnitCalendar | null | undefined) {
        if (!calendar) return '대기';
        const policy = this.ensureExternalSyncPolicy(calendar);
        if (policy.syncStatus === 'write_scope_required') return '쓰기 권한 필요';
        if (policy.syncStatus === 'pending_export') return '내보내기 대기';
        if (policy.syncStatus === 'pending_import') return '가져오기 대기';
        if (policy.syncStatus === 'conflict') return '충돌';
        if (policy.syncStatus === 'error') return '오류';
        return '안전';
    }

    public externalCalendarDeletionPolicyLabel(calendar: OwnitCalendar | null | undefined) {
        if (!calendar) return '삭제 표시만 반영';
        const policy = this.ensureExternalSyncPolicy(calendar);
        return this.externalDeletionPolicyOptions.find((option) => option.value === policy.deletionPolicy)?.label || '삭제 표시만 반영';
    }

    public externalCalendarDeletionPolicy(calendar: OwnitCalendar | null | undefined): ExternalCalendarDeletionPolicy {
        if (!calendar) return 'mark_deleted_only';
        return this.ensureExternalSyncPolicy(calendar).deletionPolicy;
    }

    public externalCalendarWritePermissionLabel(calendar: OwnitCalendar | null | undefined) {
        if (!calendar) return '쓰기 권한 미요청';
        const permission = this.ensureExternalSyncPolicy(calendar).writePermission;
        if (permission === 'granted') return '쓰기 권한 허용됨';
        if (permission === 'requested') return '쓰기 권한 요청됨';
        if (permission === 'denied') return '쓰기 권한 거부됨';
        return '쓰기 권한 미요청';
    }

    public externalCalendarSyncPolicyNote(calendar: OwnitCalendar | null | undefined) {
        if (!calendar) return '';
        const ownership = calendar.type === 'shared'
            ? '공유 캘린더는 owner/admin만 설정을 바꿀 수 있습니다.'
            : '개인 캘린더는 본인만 설정을 바꿀 수 있습니다.';
        const deletion = this.externalCalendarDeletionPolicyLabel(calendar);
        const direction = this.externalCalendarSyncDirection(calendar);
        const directionNote = direction === 'import_only'
            ? '기본값은 읽기 전용입니다.'
            : '사용자가 켠 캘린더만 내보내기/양방향 대기열에 들어갑니다.';
        return `${directionNote} ${ownership} 삭제 정책: ${deletion}.`;
    }

    public externalMappingCountForCalendar(calendarId: string) {
        return this.externalEventMappings.filter((mapping) => mapping.calendarId === calendarId && !mapping.deletedAt).length;
    }

    public externalConflictCountForCalendar(calendarId: string) {
        return this.externalEventMappings.filter((mapping) => mapping.calendarId === calendarId && mapping.syncStatus === 'conflict').length;
    }

    public get externalSyncConflicts(): ExternalSyncConflictView[] {
        return this.externalEventMappings
            .filter((mapping) => mapping.syncStatus === 'conflict')
            .map((mapping) => {
                const event = this.events.find((item) => item.id === mapping.eventId) || null;
                if (!event) return null;
                return {
                    mapping,
                    event,
                    calendar: this.calendarById(mapping.calendarId),
                    reason: mapping.syncError || '외부와 Ownit 일정이 모두 수정되었습니다.'
                };
            })
            .filter((item): item is ExternalSyncConflictView => Boolean(item));
    }

    public canChangeExternalCalendarSync(calendar: OwnitCalendar | null | undefined) {
        if (!calendar || !this.user.id || !this.isExternalCalendar(calendar)) return false;
        if (this.isAdminAccount) return true;
        if (calendar.type === 'personal') return calendar.ownerId === this.user.id;
        return hasPermission(this.user, 'manage_shared_calendar', { group: this.groupForCalendar(calendar), calendar });
    }

    public canRequestExternalWriteAccess(calendar: OwnitCalendar | null | undefined) {
        if (!calendar || !this.canChangeExternalCalendarSync(calendar)) return false;
        const policy = this.ensureExternalSyncPolicy(calendar);
        if (!isExternalOAuthProviderId(policy.source)) return false;
        const config = this.externalOAuthConfig(policy.source);
        return Boolean(config && config.writeScopes.length > 0 && policy.direction !== 'import_only' && policy.writePermission !== 'granted');
    }

    public async changeExternalCalendarSyncDirection(calendar: OwnitCalendar, direction: CalendarSyncDirection) {
        if (!this.canChangeExternalCalendarSync(calendar)) {
            this.externalOAuthError = '외부 캘린더 동기화 설정을 변경할 권한이 없습니다.';
            await this.service.render();
            return;
        }
        const policy = this.ensureExternalSyncPolicy(calendar);
        const normalizedDirection = normalizeExternalSyncDirection(direction);
        const now = new Date().toISOString();
        const config = isExternalOAuthProviderId(policy.source) ? this.externalOAuthConfig(policy.source) : null;
        const canRequestWriteScope = Boolean(config?.writeScopes.length);
        const needsWriteScope = normalizedDirection !== 'import_only' && policy.writePermission !== 'granted';
        const writeScopeError = needsWriteScope
            ? (canRequestWriteScope
                ? '외부 캘린더 쓰기 권한을 명시적으로 켜야 내보내기가 진행됩니다.'
                : `${this.externalCalendarSourceLabel(calendar)} 쓰기 API는 서버 token exchange와 provider 권한 검증이 준비된 뒤 진행됩니다.`)
            : null;
        this.upsertExternalSyncPolicy({
            ...policy,
            direction: normalizedDirection,
            syncStatus: needsWriteScope ? 'write_scope_required' : 'synced',
            syncError: writeScopeError,
            updatedAt: now
        });
        this.externalEventMappings = this.externalEventMappings.map((mapping) => {
            if (mapping.calendarId !== calendar.id) return mapping;
            return {
                ...mapping,
                syncDirection: normalizedDirection,
                syncStatus: needsWriteScope ? 'write_scope_required' : normalizeExternalSyncStatus(mapping.syncStatus || 'synced'),
                syncError: needsWriteScope ? writeScopeError : mapping.syncError,
                updatedAt: now
            };
        });
        this.saveExternalSyncPolicies();
        this.saveExternalEventMappings();
        this.refreshExternalCalendarData(false);
        this.buildMonth();
        this.externalOAuthNotice = needsWriteScope
            ? (canRequestWriteScope
                ? '동기화 방향은 저장했습니다. 외부 API 쓰기 권한은 아래 쓰기 권한 요청 버튼을 눌러야 요청됩니다.'
                : '동기화 방향은 저장했지만 서버 쓰기 adapter가 준비되기 전까지 내보내기는 대기 상태입니다.')
            : '외부 캘린더 동기화 방향을 저장했습니다.';
        this.externalOAuthError = '';
        await this.service.render();
    }

    public async changeExternalCalendarDeletionPolicy(calendar: OwnitCalendar, deletionPolicy: ExternalCalendarDeletionPolicy) {
        if (!this.canChangeExternalCalendarSync(calendar)) {
            this.externalOAuthError = '삭제 동기화 정책을 변경할 권한이 없습니다.';
            await this.service.render();
            return;
        }
        const policy = this.ensureExternalSyncPolicy(calendar);
        this.upsertExternalSyncPolicy({
            ...policy,
            deletionPolicy: normalizeExternalDeletionPolicy(deletionPolicy),
            updatedAt: new Date().toISOString()
        });
        this.saveExternalSyncPolicies();
        this.refreshExternalCalendarData(false);
        this.externalOAuthNotice = '삭제 동기화 정책을 저장했습니다. 자동 삭제 전파는 사용자 확인 전까지 실행하지 않습니다.';
        this.externalOAuthError = '';
        await this.service.render();
    }

    public async requestExternalCalendarWriteAccess(calendar: OwnitCalendar) {
        if (!this.canRequestExternalWriteAccess(calendar)) {
            this.externalOAuthError = '쓰기 권한 요청이 필요하지 않거나 권한이 없습니다.';
            await this.service.render();
            return;
        }
        const policy = this.ensureExternalSyncPolicy(calendar);
        if (!isExternalOAuthProviderId(policy.source)) {
            this.externalOAuthError = `${this.externalCalendarSourceLabel(calendar)}는 현재 읽기 전용 가져오기 adapter만 준비되어 있습니다.`;
            await this.service.render();
            return;
        }
        const config = this.externalOAuthConfig(policy.source);
        if (!config || config.writeScopes.length === 0) {
            this.externalOAuthError = `${this.externalCalendarSourceLabel(calendar)} 쓰기 권한은 서버 token exchange와 provider 권한 검증이 준비된 뒤 요청할 수 있습니다.`;
            await this.service.render();
            return;
        }
        const scopes = Array.from(new Set([...config.scopes, ...config.writeScopes]));
        const now = new Date().toISOString();
        const nextPolicy: ExternalCalendarSyncPolicy = {
            ...policy,
            writePermission: 'requested',
            writeScope: scopes.join(' '),
            writeRequestedAt: now,
            syncStatus: 'write_scope_required',
            syncError: 'OAuth 쓰기 scope 승인 대기 중입니다.',
            updatedAt: now
        };
        this.upsertExternalSyncPolicy(nextPolicy);
        this.saveExternalSyncPolicies();

        const state = this.createExternalOAuthState(policy.source);
        const redirectUri = this.externalOAuthRedirectUri(config);
        const start = buildExternalOAuthStartUrl({
            provider: policy.source,
            clientId: this.externalOAuthClientId(config),
            redirectUri,
            state,
            scopes
        });
        this.upsertExternalOAuthConnection(createExternalOAuthConnection(policy.source, {
            status: 'connecting',
            readOnly: true,
            scope: start.scopes.join(' '),
            writeEnabled: false,
            writeScope: start.scopes.join(' '),
            writeScopeRequestedAt: now,
            connectedAt: this.externalOAuthConnectionFor(policy.source).connectedAt,
            lastSyncedAt: this.externalOAuthConnectionFor(policy.source).lastSyncedAt,
            updatedAt: now,
            lastMessage: start.isReady ? `${config.label} 쓰기 scope 동의 화면으로 이동합니다.` : 'OAuth client_id가 없어 쓰기 scope 요청을 대기 상태로 저장했습니다.'
        }, now));
        this.saveExternalOAuthPendingState({
            provider: policy.source,
            state,
            redirectUri,
            scope: start.scopes.join(' '),
            createdAt: now
        });
        this.saveExternalOAuthConnections();

        if (!start.isReady) {
            this.externalOAuthNotice = '쓰기 권한 요청 상태를 저장했습니다. OAuth client_id를 설정한 뒤 다시 요청해 주세요.';
            this.externalOAuthError = '';
            await this.service.render();
            return;
        }

        this.externalOAuthNotice = `${config.label} 쓰기 권한 동의 화면으로 이동합니다. 서버 검증 전까지 캘린더는 계속 읽기 전용입니다.`;
        this.externalOAuthError = '';
        await this.service.render();
        window.location.assign(start.url);
    }

    public async resolveExternalSyncConflict(mapping: ExternalEventMapping, resolution: CalendarSyncConflictResolution) {
        const event = this.events.find((item) => item.id === mapping.eventId) || null;
        if (!event) return;
        const now = new Date().toISOString();
        let nextMapping: ExternalEventMapping = {
            ...mapping,
            lastSyncedAt: now,
            syncStatus: 'synced',
            syncError: null,
            updatedAt: now
        };

        if (resolution === 'use_local') {
            nextMapping = {
                ...nextMapping,
                localUpdatedAt: event.updatedAt || now,
                syncStatus: mapping.syncDirection === 'import_only' ? 'synced' : 'pending_export'
            };
        } else if (resolution === 'keep_both') {
            const targetCalendar = this.personalCalendars.find((item) => !this.isExternalCalendar(item)) || null;
            if (!targetCalendar) {
                this.externalOAuthError = '복사본을 저장할 개인 캘린더가 없습니다.';
                await this.service.render();
                return;
            }
            const copy: OwnitEvent = {
                ...event,
                id: this.createId('event'),
                title: `${event.title} 복사본`,
                calendarId: targetCalendar.id,
                color: targetCalendar.color,
                authorId: this.user.id,
                assigneeId: this.user.id,
                visibility: 'private',
                source: 'user',
                externalId: null,
                provider: null,
                externalCalendarId: null,
                externalCalendarName: null,
                externalRecurringEventId: null,
                externalTimezone: null,
                readOnly: false,
                lastSyncedAt: null,
                externalUpdatedAt: null,
                localUpdatedAt: now,
                syncDirection: 'import_only',
                syncStatus: 'idle',
                syncError: null,
                ownerId: this.user.id,
                groupId: null,
                deletedAt: null,
                createdAt: now,
                updatedAt: now
            };
            const saved = this.dataRepository.createEvent(copy);
            this.events = this.sortEvents([...this.events, saved]);
        } else if (resolution === 'skip') {
            nextMapping = { ...mapping, updatedAt: now };
        }

        this.upsertExternalEventMapping(nextMapping);
        this.saveExternalEventMappings();
        this.refreshExternalCalendarData(false);
        this.buildMonth();
        this.externalOAuthNotice = resolution === 'skip' ? '충돌을 나중에 다시 확인하도록 남겨두었습니다.' : '충돌 해결 선택을 저장했습니다.';
        this.externalOAuthError = '';
        await this.service.render();
    }

    public get connectedExternalOAuthConnections() {
        return this.externalOAuthProviderIds
            .map((provider) => this.externalOAuthConnectionFor(provider))
            .filter((connection) => connection.status === 'connected');
    }

    public externalOAuthConfig(provider: ExternalOAuthProviderId): ExternalOAuthProviderConfig | null {
        return getExternalOAuthProviderConfig(provider);
    }

    public externalOAuthProviderLabel(provider: ExternalOAuthProviderId) {
        return this.externalOAuthConfig(provider)?.label || externalSourceLabel(provider);
    }

    public externalOAuthProviderDescription(provider: ExternalOAuthProviderId) {
        return this.externalOAuthConfig(provider)?.note || 'OAuth provider 구조만 준비되어 있습니다.';
    }

    public externalOAuthScopeLabel(provider: ExternalOAuthProviderId) {
        const config = this.externalOAuthConfig(provider);
        if (!config) return '읽기 전용 scope 확인 필요';
        const readScopes = config.scopes.length > 0 ? config.scopes.join(', ') : config.readScope;
        const writeScopes = config.writeScopes.length > 0 ? config.writeScopes.join(', ') : '서버/API 권한 확인 후 별도 구현';
        return `읽기: ${readScopes} / 쓰기 요청 시: ${writeScopes}`;
    }

    public externalOAuthCallbackLabel(provider: ExternalOAuthProviderId) {
        const config = this.externalOAuthConfig(provider);
        return config ? this.externalOAuthRedirectUri(config) : '/dashboard/settings';
    }

    public externalOAuthTokenPolicy(provider: ExternalOAuthProviderId) {
        return this.externalOAuthConfig(provider)?.tokenStoragePolicy || '토큰은 브라우저에 저장하지 않습니다.';
    }

    public externalOAuthStatus(provider: ExternalOAuthProviderId): ExternalOAuthConnectionStatus {
        return this.externalOAuthConnectionFor(provider).status;
    }

    public externalOAuthStatusLabel(provider: ExternalOAuthProviderId) {
        const status = this.externalOAuthStatus(provider);
        if (status === 'connected') return '연결됨';
        if (status === 'connecting') return '연결 중';
        if (status === 'cancelled') return '취소됨';
        if (status === 'error') return '실패';
        return '미연결';
    }

    public externalOAuthStatusMessage(provider: ExternalOAuthProviderId) {
        return this.externalOAuthConnectionFor(provider).lastMessage;
    }

    public externalOAuthLastSyncedLabel(provider: ExternalOAuthProviderId) {
        return this.externalSyncedLabel(this.externalOAuthConnectionFor(provider).lastSyncedAt);
    }

    public externalOAuthConnectButtonLabel(provider: ExternalOAuthProviderId) {
        const status = this.externalOAuthStatus(provider);
        if (status === 'connected') return '다시 연결';
        if (status === 'connecting') return '연결 재시도';
        return `${this.externalOAuthProviderLabel(provider)} 연결`;
    }

    public canDisconnectExternalOAuthProvider(provider: ExternalOAuthProviderId) {
        return this.externalOAuthStatus(provider) !== 'disconnected';
    }

    public async connectExternalOAuthProvider(provider: ExternalOAuthProviderId) {
        this.externalOAuthNotice = '';
        this.externalOAuthError = '';
        const config = this.externalOAuthConfig(provider);
        if (!config) {
            this.externalOAuthError = `${this.externalOAuthProviderLabel(provider)} OAuth 연결은 아직 준비되지 않았습니다.`;
            await this.service.render();
            return;
        }

        const state = this.createExternalOAuthState(provider);
        const redirectUri = this.externalOAuthRedirectUri(config);
        const start = buildExternalOAuthStartUrl({
            provider,
            clientId: this.externalOAuthClientId(config),
            redirectUri,
            state
        });
        const now = new Date().toISOString();
        const pendingConnection = createExternalOAuthConnection(provider, {
            status: 'connecting',
            connectedAt: this.externalOAuthConnectionFor(provider).connectedAt,
            lastSyncedAt: this.externalOAuthConnectionFor(provider).lastSyncedAt,
            updatedAt: now,
            lastMessage: start.isReady ? `${config.label} 동의 화면으로 이동합니다.` : 'OAuth client_id가 없어 mock 연결로 UI와 상태만 검증합니다.'
        }, now);

        this.upsertExternalOAuthConnection(pendingConnection);
        this.saveExternalOAuthPendingState({
            provider,
            state,
            redirectUri,
            scope: start.scopes.join(' '),
            createdAt: now
        });

        if (!start.isReady) {
            await this.completeMockExternalOAuthConnection(provider, `${start.missingConfig.join(', ')} 설정이 없어 ${config.label} OAuth token exchange 없이 mock 연결을 완료했습니다.`);
            return;
        }

        this.saveExternalOAuthConnections();
        this.externalOAuthNotice = `${config.label} OAuth 동의 화면으로 이동합니다. callback에서는 code/state만 확인하고 토큰은 저장하지 않습니다.`;
        await this.service.render();
        window.location.assign(start.url);
    }

    public async disconnectExternalOAuthProvider(provider: ExternalOAuthProviderId) {
        const config = this.externalOAuthConfig(provider);
        if (!config) return;
        const now = new Date().toISOString();
        this.upsertExternalOAuthConnection(createExternalOAuthConnection(provider, {
            status: 'disconnected',
            connectedAt: null,
            lastSyncedAt: null,
            updatedAt: now,
            lastMessage: '연결을 해제했습니다. 서버 토큰 저장소가 도입되면 refresh token revoke/delete를 함께 실행해야 합니다.'
        }, now));
        this.clearExternalOAuthPendingState();
        this.saveExternalOAuthConnections();
        this.externalSyncPolicies = this.externalSyncPolicies.map((policy) => {
            if (policy.source !== provider) return policy;
            const needsWriteScope = policy.direction !== 'import_only';
            return {
                ...policy,
                writePermission: 'not_requested',
                writeScope: '',
                writeRequestedAt: null,
                syncStatus: needsWriteScope ? 'write_scope_required' : 'synced',
                syncError: needsWriteScope ? '외부 캘린더 쓰기 권한을 다시 켜야 합니다.' : null,
                updatedAt: now
            };
        });
        this.saveExternalSyncPolicies();
        this.refreshExternalCalendarData(false);
        this.buildMonth();
        this.externalOAuthNotice = `${config.label} 연결을 해제했습니다.`;
        this.externalOAuthError = '';
        await this.service.render();
    }

    private isExternalSource(source: string | null | undefined) {
        return isExternalCalendarProviderId(source) || source === 'mock';
    }

    public eventMemoLabel(event: OwnitEvent) {
        return (event.description || '').trim() || '없음';
    }

    public memberRoleLabel(role: GroupRole) {
        const labels: Record<GroupRole, string> = {
            owner: '소유자',
            admin: '관리자',
            member: '멤버',
            viewer: '읽기 전용'
        };
        return labels[role];
    }

    public memberStatusLabel(status: GroupMemberStatus) {
        const option = this.groupMemberStatusOptions.find((item) => item.value === status);
        return option?.label || '참여 중';
    }

    public memberDisplayName(member: OwnitGroupMember) {
        return member.nickname || member.name || member.email || member.userId || '구성원';
    }

    public memberNameDetail(member: OwnitGroupMember) {
        if (member.name && member.nickname && member.name !== member.nickname) return `${member.name} / ${member.nickname}`;
        return member.name || member.nickname || '이름 없음';
    }

    public memberEmailLabel(member: OwnitGroupMember) {
        return member.email || (member.userId ? `${member.userId}` : '이메일 없음');
    }

    public memberJoinedLabel(member: OwnitGroupMember) {
        if (!member.joinedAt) return '참여일 없음';
        return this.shortDateLabel(member.joinedAt.slice(0, 10));
    }

    public canChangeMemberRole(member: OwnitGroupMember) {
        return Boolean(this.canManageGroupAction && member.status !== 'left' && member.status !== 'removed');
    }

    public canRemoveMember(member: OwnitGroupMember) {
        return Boolean(this.canManageGroupAction && member.userId !== this.user.id && member.status !== 'removed' && member.status !== 'left');
    }

    public permissionActionLabel(action: CalendarPermissionAction) {
        const labels: Record<CalendarPermissionAction, string> = {
            manage_group: '그룹 관리',
            manage_shared_calendar: '공유 캘린더 관리',
            create_shared_event: '공유 일정 생성',
            edit_own_shared_event: '본인 공유 일정 수정',
            view_shared_event: '공유 일정 보기'
        };
        return labels[action];
    }

    public eventDateLabel(event: OwnitEvent) {
        if (event.startDate === event.endDate) return event.startDate;
        return `${event.startDate} - ${event.endDate}`;
    }

    public eventTimeLabel(event: OwnitEvent) {
        if (event.allDay) return '하루 종일';
        if (event.startTime && event.endTime) return `${event.startTime} - ${event.endTime}`;
        if (event.startTime) return event.startTime;
        if (event.endTime) return event.endTime;
        return '시간 없음';
    }

    public eventReminderLabel(event: OwnitEvent) {
        const reminder = (event.reminders || []).find((item) => item.enabled);
        if (!reminder) return '알림 없음';
        const option = this.eventReminderOptions.find((item) => item.value === String(reminder.offsetMinutes));
        return option?.label || `${reminder.offsetMinutes}분 전`;
    }

    public eventCalendarSelectionLabel(calendar: OwnitCalendar | null | undefined) {
        if (!calendar) return '선택된 캘린더가 없습니다.';
        const groupLabel = calendar.type === 'shared' ? ` · ${this.groupNameForCalendar(calendar)}` : '';
        return `${this.calendarTypeLabel(calendar)} 캘린더 · ${calendar.name}${groupLabel}`;
    }

    public isMultiDayEvent(event: OwnitEvent) {
        return event.startDate !== event.endDate;
    }

    public todoScopeLabel(todo: OwnitTodo) {
        return todo.scope === 'shared' ? '공유' : '개인';
    }

    public mateById(mateId: string) {
        return this.mateProfiles.find((mate) => mate.id === mateId) || null;
    }

    public isFollowingMate(mateId: string) {
        return this.followedMateIds.includes(mateId);
    }

    public async selectTodoSocialView(view: TodoSocialView) {
        this.todoSocialView = view;
        this.closeInlineTodoDraft();
        if (view === 'friends') {
            this.todoFormOpen = false;
            this.todoGoalFormOpen = false;
            this.routineFormOpen = false;
            this.closeTodoCreateFlowState();
            this.todoActionTodoId = null;
            this.unlockTodoActionScroll();
        }
        await this.service.render();
    }

    public async toggleMateFollow(mateId: string) {
        if (!this.mateById(mateId)) return;
        const followed = new Set(this.followedMateIds);
        if (followed.has(mateId)) {
            followed.delete(mateId);
        } else {
            followed.add(mateId);
        }
        this.followedMateIds = Array.from(followed);
        this.saveFollowedMateIds();
        await this.service.render();
    }

    public mateFeedKindLabel(item: MateFeedItem) {
        return item.kind === 'routine' ? '루틴 완료' : '할 일 완료';
    }

    public mateFeedVisibilityLabel(item: MateFeedItem) {
        if (item.visibility === 'private') return 'private';
        if (item.visibility === 'group') return 'group';
        return 'public';
    }

    public mateFeedTimestampLabel(item: MateFeedItem) {
        const time = (item.completedAt.split('T')[1] || '').slice(0, 5);
        return time ? `${this.dateLabel(item.date)} ${time}` : this.dateLabel(item.date);
    }

    public currentMateReaction(item: MateFeedItem) {
        return this.todoReactions.find((reaction) => (
            reaction.userId === this.user.id
            && reaction.targetType === item.targetType
            && reaction.targetId === item.targetId
            && reaction.date === item.date
        )) || null;
    }

    public isMateReactionActive(item: MateFeedItem, option: MateReactionOption) {
        return this.currentMateReaction(item)?.emoji === option.emoji;
    }

    public mateReactionCount(item: MateFeedItem, option: MateReactionOption) {
        const baseCount = option.emoji === '♥'
            ? item.baseHeartCount
            : Number(item.baseEmojiCounts[option.emoji] || 0);
        return baseCount + (this.isMateReactionActive(item, option) ? 1 : 0);
    }

    public async toggleMateReaction(item: MateFeedItem, option: MateReactionOption) {
        const existing = this.currentMateReaction(item);
        if (existing && existing.emoji === option.emoji) {
            await this.clearMateReaction(item);
            return;
        }

        const now = new Date().toISOString();
        const payload: OwnitTodoReaction = {
            id: existing?.id || this.createId('todo-reaction'),
            userId: this.user.id,
            date: item.date,
            authorId: item.mateId,
            targetType: item.targetType,
            targetId: item.targetId,
            kind: 'good',
            emoji: option.emoji,
            message: option.message,
            createdAt: existing?.createdAt || now,
            updatedAt: now
        };
        const saved = existing
            ? this.dataRepository.updateTodoReaction(existing.id, payload)
            : this.dataRepository.createTodoReaction(payload);
        if (saved) {
            this.todoReactions = [
                ...this.todoReactions.filter((reaction) => reaction.id !== saved.id),
                saved
            ];
        }
        await this.service.render();
    }

    public async clearMateReaction(item: MateFeedItem) {
        const existing = this.currentMateReaction(item);
        if (!existing) return;
        this.dataRepository.deleteTodoReaction(existing.id);
        this.todoReactions = this.todoReactions.filter((reaction) => reaction.id !== existing.id);
        await this.service.render();
    }

    public async selectTodoDate(date: string) {
        await this.selectDate(date, false);
    }

    public async openCreateTodoGoal() {
        await this.openTodoCreateFlow('category', this.selectedDate);
    }

    public async editTodoGoal(goal: OwnitTodoGoal) {
        if (!this.canCreateTodoAction || goal.locked) {
            this.todoGoalFormError = '카테고리를 수정할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.editingTodoGoalId = goal.id;
        this.todoGoalFormError = '';
        this.todoGoalForm = {
            name: goal.name,
            color: goal.color,
            marker: this.todoGoalMarkerValue(goal),
            visibility: goal.visibility || 'private',
            order: Number(goal.order) || 0,
            collapsed: Boolean(goal.collapsed),
            setAsDefault: this.isDefaultTodoGoal(goal),
            setAsRecent: this.isRecentTodoGoal(goal)
        };
        this.activeTodoGoalPaletteId = this.paletteIdForColor(goal.color);
        this.todoFormOpen = false;
        this.editingTodoId = null;
        this.todoFormError = '';
        this.routineFormOpen = false;
        this.editingRoutineId = null;
        this.routineFormError = '';
        this.todoGoalFormOpen = true;
        await this.service.render();
    }

    public async editTodoGoalById(goalId: string | null) {
        if (!goalId) return;
        const goal = this.todoGoalById(goalId);
        if (!goal) return;
        await this.editTodoGoal(goal);
    }

    public async closeTodoGoalForm() {
        this.todoGoalFormOpen = false;
        this.editingTodoGoalId = null;
        this.todoGoalFormError = '';
        await this.service.render();
    }

    public async saveTodoGoal() {
        if (!this.canCreateTodoAction) {
            this.todoGoalFormError = '카테고리를 저장할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const form = this.normalizeTodoGoalForm();
        if (!form) {
            await this.service.render();
            return;
        }

        const now = new Date().toISOString();
        const existingGoal = this.editingTodoGoalId ? this.todoGoals.find((goal) => goal.id === this.editingTodoGoalId) || null : null;
        const hadActiveGoals = this.activeTodoGoals.length > 0;
        const category = this.upsertTodoGoalCategory(form, existingGoal);
        let savedGoalId = '';

        if (existingGoal) {
            const updated = this.dataRepository.updateTodoGoal(existingGoal.id, {
                name: form.name,
                color: form.color,
                marker: form.marker,
                visibility: form.visibility,
                order: form.order,
                collapsed: form.collapsed,
                categoryId: category.id,
                updatedAt: now
            });
            if (!updated) {
                this.todoGoalFormError = '수정할 카테고리를 찾지 못했습니다.';
                await this.service.render();
                return;
            }
            savedGoalId = updated.id;
            this.todoGoals = this.sortTodoGoals(this.todoGoals.map((goal) => goal.id === updated.id ? updated : goal));
            this.syncTodoGoalChildren(updated, category.id);
        } else {
            const created: OwnitTodoGoal = {
                id: this.createId('todo-goal'),
                userId: this.user.id,
                name: form.name,
                color: form.color,
                icon: '',
                marker: form.marker,
                visibility: form.visibility,
                order: form.order,
                collapsed: form.collapsed,
                locked: false,
                ownerId: this.user.id,
                categoryId: category.id,
                status: 'active',
                createdAt: now,
                updatedAt: now
            };
            const saved = this.dataRepository.createTodoGoal(created);
            savedGoalId = saved.id;
            this.todoGoals = this.sortTodoGoals([...this.todoGoals, saved]);
        }

        this.refreshTodoRecordsFromRepository();
        this.applyTodoGoalPreferenceForm(savedGoalId, form, hadActiveGoals);
        this.todoGoalFormOpen = false;
        this.editingTodoGoalId = null;
        this.todoGoalFormError = '';
        this.buildMonth();
        await this.service.render();
    }

    public async deleteTodoGoal(goal: OwnitTodoGoal) {
        const target = goal?.id ? this.todoGoalById(goal.id) : null;
        if (!target) {
            this.todoGoalFormError = '삭제할 카테고리를 찾지 못했습니다.';
            await this.service.render();
            return;
        }
        if (!this.canCreateTodoAction || target.locked) {
            this.todoGoalFormError = '카테고리를 삭제할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.todoCategoryDeleteTargetId = target.id;
        this.todoCategoryDeleteMode = 'uncategorized';
        this.todoCategoryDeleteMoveTargetId = this.todoCategoryDeleteMoveOptions[0]?.id || '';
        this.todoCategoryDeleteError = '';
        this.todoCategoryDeleteSubmitting = false;
        this.todoGoalFormOpen = false;
        this.todoCategoryDeleteDialogOpen = true;
        await this.service.render();
    }

    public async closeTodoCategoryDeleteDialog() {
        if (this.todoCategoryDeleteSubmitting) return;
        this.todoCategoryDeleteDialogOpen = false;
        this.todoCategoryDeleteTargetId = null;
        this.todoCategoryDeleteMode = 'uncategorized';
        this.todoCategoryDeleteMoveTargetId = '';
        this.todoCategoryDeleteError = '';
        this.todoCategoryDeleteSubmitting = false;
        await this.service.render();
    }

    public async selectTodoCategoryDeleteMode(mode: TodoCategoryDeleteMode) {
        if (this.todoCategoryDeleteSubmitting) return;
        if (mode === 'move' && this.todoCategoryDeleteMoveOptions.length === 0) {
            this.todoCategoryDeleteError = this.todoCategoryDeleteMoveHelp;
            await this.service.render();
            return;
        }
        this.todoCategoryDeleteMode = mode;
        if (mode === 'move' && !this.todoCategoryDeleteMoveOptions.some((goal) => goal.id === this.todoCategoryDeleteMoveTargetId)) {
            this.todoCategoryDeleteMoveTargetId = this.todoCategoryDeleteMoveOptions[0]?.id || '';
        }
        this.todoCategoryDeleteError = '';
        await this.service.render();
    }

    public async confirmTodoCategoryDelete() {
        if (this.todoCategoryDeleteSubmitting) return;
        const goal = this.todoCategoryDeleteTarget;
        if (!goal) {
            this.todoCategoryDeleteError = '삭제할 카테고리를 찾지 못했습니다.';
            await this.service.render();
            return;
        }
        if (!this.canCreateTodoAction || goal.locked) {
            this.todoCategoryDeleteError = '카테고리를 삭제할 권한이 없습니다.';
            await this.service.render();
            return;
        }
        if (!this.todoCategoryDeleteCanApply) {
            this.todoCategoryDeleteError = '삭제 방식을 확인해 주세요.';
            await this.service.render();
            return;
        }
        if (this.todoCategoryDeleteMode === 'move' && !this.todoCategoryDeleteMoveTargetId) {
            this.todoCategoryDeleteError = '이동할 카테고리를 선택해 주세요.';
            await this.service.render();
            return;
        }

        const moveTarget = this.todoCategoryDeleteMode === 'move'
            ? this.todoGoalById(this.todoCategoryDeleteMoveTargetId)
            : null;
        if (this.todoCategoryDeleteMode === 'move' && !moveTarget) {
            this.todoCategoryDeleteError = '이동할 카테고리를 찾지 못했습니다.';
            await this.service.render();
            return;
        }

        this.todoCategoryDeleteSubmitting = true;
        this.todoCategoryDeleteError = '';
        await this.service.render();
        try {
            const deleted = this.dataRepository.deleteTodoGoalWithResolution({
                goalId: goal.id,
                mode: this.todoCategoryDeleteMode,
                targetGoalId: moveTarget?.id || null,
                uncategorizedLabel: this.uncategorizedTodoLabel,
                uncategorizedColor: this.uncategorizedTodoColor,
                routineFallbackLabel: this.uncategorizedTodoLabel,
                routineFallbackColor: this.uncategorizedTodoColor
            });
            if (!deleted) {
                this.refreshTodoRecordsFromRepository();
                this.todoCategoryDeleteError = '이미 삭제되었거나 찾을 수 없는 카테고리입니다. 목록을 갱신했습니다.';
                return;
            }

            this.clearTodoGoalPreferencesFor(goal.id);
            if (this.editingTodoGoalId === goal.id) {
                this.todoGoalFormOpen = false;
                this.editingTodoGoalId = null;
                this.todoGoalFormError = '';
            }
            this.refreshTodoRecordsFromRepository();
            this.buildMonth();
            this.resetTodoCategoryDeleteDialogState();
        } catch (error) {
            this.todoCategoryDeleteError = repositoryErrorMessage(error, '카테고리 삭제에 실패했습니다. 다시 시도해 주세요.');
        } finally {
            this.todoCategoryDeleteSubmitting = false;
            await this.service.render();
        }
    }

    public async deleteTodoGoalById(goalId: string | null) {
        if (!goalId) return;
        const goal = this.todoGoalById(goalId);
        if (!goal) return;
        await this.deleteTodoGoal(goal);
    }

    private resetTodoCategoryDeleteDialogState() {
        this.todoCategoryDeleteDialogOpen = false;
        this.todoCategoryDeleteTargetId = null;
        this.todoCategoryDeleteMode = 'uncategorized';
        this.todoCategoryDeleteMoveTargetId = '';
        this.todoCategoryDeleteError = '';
    }

    public async toggleTodoGoalCollapsed(goal: OwnitTodoGoal) {
        if (goal.locked) return;
        const collapsed = !goal.collapsed;
        const updated = this.dataRepository.updateTodoGoal(goal.id, {
            collapsed,
            updatedAt: new Date().toISOString()
        });
        if (!updated) return;
        this.todoGoals = this.sortTodoGoals(this.todoGoals.map((item) => item.id === updated.id ? updated : item));
        if (goal.categoryId) {
            const updatedCategory = this.dataRepository.updateTodoCategory(goal.categoryId, {
                collapsed,
                updatedAt: new Date().toISOString()
            });
            if (updatedCategory) this.todoCategories = this.sortTodoCategories(this.todoCategories.map((category) => category.id === updatedCategory.id ? updatedCategory : category));
        }
        await this.service.render();
    }

    public async toggleTodoGoalCollapsedById(goalId: string | null) {
        if (!goalId) return;
        const goal = this.todoGoalById(goalId);
        if (!goal) return;
        await this.toggleTodoGoalCollapsed(goal);
    }

    public async selectTodoGoalPalette(id: TodoGoalPaletteId) {
        this.activeTodoGoalPaletteId = id;
        await this.service.render();
    }

    public async selectTodoGoalColor(color: string) {
        this.todoGoalForm.color = color;
        await this.service.render();
    }

    public async selectTodoGoalMarker(marker: TodoGoalMarkerId) {
        this.todoGoalForm.marker = this.normalizeTodoGoalMarker(marker);
        await this.service.render();
    }

    public async syncTodoFormGoal() {
        this.applyGoalToTodoForm(this.todoForm.goalId ? this.todoGoalById(this.todoForm.goalId) : null);
        await this.service.render();
    }

    public async openCreateTodoForGoal(goalId: string | null, date = this.selectedDate) {
        await this.openTodoCreateFlow('todo', date, goalId);
    }

    public async openCreateTodoForCategory(category: string, color = this.todoColorOptions[0].value, date = this.selectedDate) {
        const goal = this.activeTodoGoals.find((item) => item.name === category);
        if (goal) {
            await this.openCreateTodoForGoal(goal.id, date);
            return;
        }
        await this.openTodoCreateFlow('todo', date, null, '', category || this.todoCategoryOptions[0], color || this.todoColorOptions[0].value);
    }

    public async openCreateRoutineTodo(date = this.selectedDate) {
        await this.openCreateRoutine(date);
    }

    public async openCreateRoutine(date = this.selectedDate) {
        await this.openTodoCreateFlow('routine', date);
    }

    public async editRoutine(routine: OwnitTodoRoutine | null | undefined) {
        if (!routine) return;
        if (!this.canCreateTodoAction) {
            this.routineFormError = '루틴을 수정할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        this.closeInlineTodoDraft();
        this.todoFormOpen = false;
        this.editingTodoId = null;
        this.todoFormError = '';
        this.todoGoalFormOpen = false;
        this.editingTodoGoalId = null;
        this.todoGoalFormError = '';
        this.eventFormOpen = false;
        this.editingEventId = null;
        this.eventFormError = '';
        this.editingRoutineId = routine.id;
        this.routineFormError = '';
        this.routineForm = {
            title: routine.title,
            goalId: routine.goalId || '',
            category: this.routineCategoryLabel(routine),
            color: routine.color,
            repeatType: routine.repeatType,
            repeatDays: [...(routine.repeatDays || [])],
            repeatDates: [...(routine.repeatDates || [])],
            startDate: routine.startDate || this.selectedDate,
            endDate: routine.endDate || '',
            reminderEnabled: Boolean(routine.reminderRule?.enabled) || (routine.reminders || []).some((reminder) => reminder.enabled),
            reminderOption: this.routineReminderPreset(routine),
            reminderTime: this.routineReminderTime(routine),
            hideToday: Boolean(routine.hideToday)
        };
        this.routineFormOpen = true;
        await this.service.render();
    }

    public async closeRoutineForm() {
        this.routineFormOpen = false;
        this.editingRoutineId = null;
        this.routineFormError = '';
        await this.service.render();
    }

    public async saveRoutine() {
        if (!this.canCreateTodoAction) {
            this.routineFormError = '루틴을 저장할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const form = this.normalizeRoutineForm();
        if (!form) {
            await this.service.render();
            return;
        }

        const now = new Date().toISOString();
        const goal = form.goalId ? this.todoGoalById(form.goalId) : null;
        const category = goal?.categoryId ? this.todoCategories.find((item) => item.id === goal.categoryId) || null : null;
        const reminderOffsetMinutes = this.reminderOffsetMinutesForPreset(form.reminderOption) || 0;
        const reminder = form.reminderEnabled
            ? { id: this.editingRoutineId ? `reminder-${this.editingRoutineId}` : this.createId('reminder'), offsetMinutes: reminderOffsetMinutes, channel: 'app' as const, enabled: true }
            : null;
        const repeatRule = this.routineRepeatRuleFromForm(form);
        const reminderRule = form.reminderEnabled ? this.buildRoutineReminderRule(form) : null;
        const reminderAt = reminderRule?.nextAt || null;
        const payload: Partial<OwnitTodoRoutine> = {
            title: form.title,
            goalId: goal?.id || null,
            categoryId: goal?.categoryId || null,
            color: goal?.color || category?.color || form.color,
            repeatType: form.repeatType,
            repeatDays: form.repeatDays,
            repeatDates: form.repeatDates,
            startDate: form.startDate,
            endDate: form.endDate || null,
            reminder,
            reminderAt,
            reminderRule,
            hideToday: form.hideToday,
            visibility: goal?.visibility || 'private',
            scope: goal?.visibility === 'group' ? 'shared' : 'personal',
            repeatRule,
            reminders: reminder ? [reminder] : [],
            memo: form.category,
            updatedAt: now
        };

        if (this.editingRoutineId) {
            const updated = this.dataRepository.updateTodoRoutine(this.editingRoutineId, payload);
            if (!updated) {
                this.routineFormError = '수정할 루틴을 찾지 못했습니다.';
                await this.service.render();
                return;
            }
            this.todoRoutines = this.sortRoutines(this.todoRoutines.map((routine) => routine.id === updated.id ? updated : routine));
        } else {
            const created: OwnitTodoRoutine = {
                id: this.createId('todo-routine'),
                userId: this.user.id,
                title: form.title,
                goalId: goal?.id || null,
                categoryId: goal?.categoryId || null,
                color: goal?.color || category?.color || form.color,
                repeatType: form.repeatType,
                repeatDays: form.repeatDays,
                repeatDates: form.repeatDates,
                startDate: form.startDate,
                endDate: form.endDate || null,
                reminder,
                reminderAt,
                reminderRule,
                hideToday: form.hideToday,
                visibility: goal?.visibility || 'private',
                ownerId: this.user.id,
                assigneeId: this.user.id,
                assignedTo: this.user.id,
                scope: goal?.visibility === 'group' ? 'shared' : 'personal',
                repeatRule,
                reminders: reminder ? [reminder] : [],
                memo: form.category,
                createdAt: now,
                updatedAt: now
            };
            const saved = this.dataRepository.createTodoRoutine(created);
            this.todoRoutines = this.sortRoutines([...this.todoRoutines, saved]);
        }

        this.refreshTodoRecordsFromRepository();
        this.rememberTodoGoalUse(goal?.id || null);
        this.routineFormOpen = false;
        this.editingRoutineId = null;
        this.routineFormError = '';
        this.buildMonth();
        this.refreshReminderSchedules();
        if (reminderRule?.enabled) await this.ensureNotificationPermissionForReminder();
        await this.checkDueReminders(false);
        await this.service.render();
    }

    public async deleteRoutine(routine: OwnitTodoRoutine | null | undefined) {
        if (!routine) return;
        if (!this.canCreateTodoAction) {
            this.routineFormError = '루틴을 삭제할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        if (!window.confirm(`'${routine.title}' 루틴 규칙을 삭제할까요?`)) return;
        const deleted = this.dataRepository.deleteTodoRoutine(routine.id);
        if (!deleted) return;
        this.todoRoutines = this.todoRoutines.filter((item) => item.id !== routine.id);
        this.todoCompletionRecords = this.todoCompletionRecords.filter((record) => record.routineId !== routine.id);
        this.refreshTodoRecordsFromRepository();
        if (this.editingRoutineId === routine.id) {
            this.routineFormOpen = false;
            this.editingRoutineId = null;
            this.routineFormError = '';
        }
        this.buildMonth();
        this.refreshReminderSchedules();
        await this.service.render();
    }

    public async toggleRoutineRepeatDay(day: number) {
        const exists = this.routineForm.repeatDays.includes(day);
        this.routineForm.repeatDays = exists
            ? this.routineForm.repeatDays.filter((item) => item !== day)
            : [...this.routineForm.repeatDays, day].sort((a, b) => this.routineWeekdaySortIndex(a) - this.routineWeekdaySortIndex(b));
        await this.service.render();
    }

    public async toggleRoutineRepeatDate(date: number) {
        const exists = this.routineForm.repeatDates.includes(date);
        this.routineForm.repeatDates = exists
            ? this.routineForm.repeatDates.filter((item) => item !== date)
            : [...this.routineForm.repeatDates, date].sort((a, b) => a - b);
        await this.service.render();
    }

    public async selectRoutineColor(color: string) {
        this.routineForm.color = color;
        await this.service.render();
    }

    public async syncRoutineFormGoal() {
        this.applyGoalToRoutineForm(this.routineForm.goalId ? this.todoGoalById(this.routineForm.goalId) : null);
        await this.service.render();
    }

    public async toggleRoutineSectionCollapsed() {
        this.routineSectionCollapsed = !this.routineSectionCollapsed;
        await this.service.render();
    }

    public async toggleCompletedItemsCollapsed() {
        this.completedItemsCollapsed = !this.completedItemsCollapsed;
        await this.service.render();
    }

    public scrollToDailyRecordSection() {
        if (typeof document === 'undefined') return;
        const target = document.getElementById('todo-daily-record');
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    public focusDailyRecordDiaryInput() {
        if (typeof document === 'undefined') return;
        const target = document.querySelector<HTMLTextAreaElement>('[data-daily-record-diary="true"]');
        if (!target) return;
        this.focusAndRevealInput(target, 'center', 'smooth');
    }

    public todoDayAriaLabel(day: TodoMateCalendarDay) {
        const date = this.fromDateKey(day.date);
        const parts = [`${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`];
        if (day.isToday) parts.push('오늘');
        if (day.date === this.selectedDate) parts.push('선택됨');
        if (day.totalCount > 0) parts.push(`할 일 ${day.completedCount}/${day.totalCount} 완료`);
        const filledSlots = day.slots.filter((slot) => slot.filled).length;
        if (filledSlots > 0) parts.push(`완료 마커 ${filledSlots}개 표시`);
        return parts.join(', ');
    }

    public todoMarkerSlotsAriaLabel(day: TodoMateCalendarDay) {
        const filledSlots = day.slots.filter((slot) => slot.filled);
        if (filledSlots.length === 0) return '완료 마커 슬롯 4개, 완료 없음';
        return `완료 마커 슬롯 4개 중 ${filledSlots.length}개 채움, ${filledSlots.map((slot) => slot.summary).join(', ')}`;
    }

    public todoPointTextColor(color: string) {
        return this.eventBarTextColor(color);
    }

    public todoCountForGoal(goalId: string, date = this.selectedDate) {
        const goal = this.todoGoalById(goalId);
        return this.todosForDay(date).filter((todo) => goal ? this.todoBelongsToGoalCategory(todo, goal) : todo.goalId === goalId).length;
    }

    public isDefaultTodoGoal(goal: OwnitTodoGoal | null | undefined) {
        return Boolean(goal?.id && this.defaultTodoGoalId === goal.id);
    }

    public isRecentTodoGoal(goal: OwnitTodoGoal | null | undefined) {
        return Boolean(goal?.id && this.recentTodoGoalId === goal.id);
    }

    public canMoveTodoGoal(goal: OwnitTodoGoal | null | undefined, direction: -1 | 1) {
        if (!goal || goal.locked) return false;
        const goals = this.activeTodoGoals.filter((item) => !item.locked);
        const index = goals.findIndex((item) => item.id === goal.id);
        const targetIndex = index + direction;
        return index >= 0 && targetIndex >= 0 && targetIndex < goals.length;
    }

    public canMoveTodoGoalById(goalId: string | null | undefined, direction: -1 | 1) {
        return this.canMoveTodoGoal(goalId ? this.todoGoalById(goalId) : null, direction);
    }

    public async moveTodoGoal(goalId: string | null, direction: -1 | 1) {
        const targetGoal = goalId ? this.todoGoalById(goalId) : null;
        if (!targetGoal || !this.canMoveTodoGoal(targetGoal, direction)) return;
        const movableGoals = this.activeTodoGoals.filter((goal) => !goal.locked);
        const currentIndex = movableGoals.findIndex((goal) => goal.id === targetGoal.id);
        const nextIndex = currentIndex + direction;
        if (currentIndex < 0 || nextIndex < 0 || nextIndex >= movableGoals.length) return;

        const reordered = [...movableGoals];
        const [moved] = reordered.splice(currentIndex, 1);
        reordered.splice(nextIndex, 0, moved);
        await this.persistTodoGoalOrder(reordered);
        await this.service.render();
    }

    public async toggleDefaultTodoGoal(goal: OwnitTodoGoal | null | undefined) {
        if (!goal || goal.locked) return;
        this.defaultTodoGoalId = this.isDefaultTodoGoal(goal) ? '' : goal.id;
        this.saveDefaultTodoGoalId();
        await this.service.render();
    }

    public async setRecentTodoGoal(goal: OwnitTodoGoal | null | undefined) {
        if (!goal || goal.locked) return;
        this.rememberTodoGoalUse(goal.id);
        await this.service.render();
    }

    public async archiveTodoGoal(goal: OwnitTodoGoal | null | undefined) {
        if (!goal || goal.locked) return;
        if (!this.canCreateTodoAction) {
            this.todoGoalFormError = '카테고리를 보관할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const now = new Date().toISOString();
        const updated = this.dataRepository.updateTodoGoal(goal.id, { status: 'archived', updatedAt: now });
        if (!updated) return;
        this.todoGoals = this.sortTodoGoals(this.todoGoals.map((item) => item.id === updated.id ? updated : item));
        if (goal.categoryId) {
            const updatedCategory = this.dataRepository.updateTodoCategory(goal.categoryId, {
                archived: true,
                archivedAt: now,
                updatedAt: now
            });
            if (updatedCategory) this.todoCategories = this.sortTodoCategories(this.todoCategories.map((category) => category.id === updatedCategory.id ? updatedCategory : category));
        }
        this.refreshTodoRecordsFromRepository();
        this.clearTodoGoalPreferencesFor(goal.id);
        if (this.editingTodoGoalId === goal.id) {
            this.todoGoalFormOpen = false;
            this.editingTodoGoalId = null;
            this.todoGoalFormError = '';
        }
        this.buildMonth();
        await this.service.render();
    }

    public async archiveTodoGoalById(goalId: string | null) {
        if (!goalId) return;
        const goal = this.todoGoalById(goalId);
        await this.archiveTodoGoal(goal);
    }

    public async restoreTodoGoal(goalId: string | null) {
        if (!goalId || !this.canCreateTodoAction) return;
        const goal = this.todoGoals.find((item) => item.id === goalId) || null;
        if (!goal || goal.locked) return;

        const now = new Date().toISOString();
        const updated = this.dataRepository.updateTodoGoal(goal.id, { status: 'active', updatedAt: now });
        if (!updated) return;
        this.todoGoals = this.sortTodoGoals(this.todoGoals.map((item) => item.id === updated.id ? updated : item));
        if (goal.categoryId) {
            const updatedCategory = this.dataRepository.updateTodoCategory(goal.categoryId, {
                archived: false,
                archivedAt: null,
                updatedAt: now
            });
            if (updatedCategory) this.todoCategories = this.sortTodoCategories(this.todoCategories.map((category) => category.id === updatedCategory.id ? updatedCategory : category));
        }
        this.refreshTodoRecordsFromRepository();
        this.reconcileTodoGoalPreferences();
        this.buildMonth();
        await this.service.render();
    }

    public todoGoalMarkerValue(target: { marker?: string } | null | undefined): TodoGoalMarkerId {
        if (!target) return 'dot';
        const marker = target.marker || this.todoCategoryForGoal(target as OwnitTodoGoal)?.marker || '';
        return this.normalizeTodoGoalMarker(marker);
    }

    public todoGoalMarkerClass(target: { marker?: string } | TodoGoalMarkerId | null | undefined) {
        const marker = typeof target === 'string' ? this.normalizeTodoGoalMarker(target) : this.todoGoalMarkerValue(target);
        return `marker-${marker}`;
    }

    public todoGoalMarkerIconClass(target: { marker?: string } | TodoGoalMarkerId | null | undefined) {
        const marker = typeof target === 'string' ? this.normalizeTodoGoalMarker(target) : this.todoGoalMarkerValue(target);
        if (marker === 'check') return 'fa-solid fa-check';
        if (marker === 'star') return 'fa-solid fa-star';
        return '';
    }

    public todoGoalMarkerFill(target: { marker?: string; color?: string } | TodoGoalMarkerId | null | undefined, color = '') {
        const marker = typeof target === 'string' ? this.normalizeTodoGoalMarker(target) : this.todoGoalMarkerValue(target);
        const markerColor = color || (typeof target === 'string' ? '' : target?.color) || this.uncategorizedTodoColor;
        return marker === 'ring' || marker === 'check' || marker === 'star' ? '#ffffff' : markerColor;
    }

    public todoGoalMarkerBorder(target: { color?: string } | TodoGoalMarkerId | null | undefined, color = '') {
        return color || (typeof target === 'string' ? '' : target?.color) || this.uncategorizedTodoColor;
    }

    public todoGoalById(goalId: string): OwnitTodoGoal | null {
        const goal = this.todoGoals.find((item) => item.id === goalId) || null;
        const resolvedGoal = goal ? this.todoGoalWithCategory(goal) : null;
        return resolvedGoal && resolvedGoal.status !== 'archived' ? resolvedGoal : null;
    }

    private todoGoalWithCategory(goal: OwnitTodoGoal): OwnitTodoGoal {
        const category = goal.categoryId ? this.todoCategories.find((item) => item.id === goal.categoryId) || null : null;
        if (!category) return goal;
        return {
            ...goal,
            name: category.name || goal.name,
            color: category.color || goal.color,
            icon: category.icon || goal.icon || '',
            marker: this.normalizeTodoGoalMarker(category.marker || goal.marker || 'dot'),
            visibility: category.visibility || goal.visibility,
            order: Number.isFinite(Number(category.sortOrder)) ? Number(category.sortOrder) : category.order,
            collapsed: Boolean(category.collapsed),
            locked: Boolean(goal.locked || category.locked),
            status: category.archived ? 'archived' : goal.status
        };
    }

    private todoCategoryForGoal(goal: OwnitTodoGoal | null | undefined): OwnitTodoCategory | null {
        if (!goal?.categoryId) return null;
        return this.todoCategories.find((category) => category.id === goal.categoryId) || null;
    }

    private normalizeTodoGoalMarker(marker: string | null | undefined): TodoGoalMarkerId {
        if (marker === 'ring' || marker === 'square' || marker === 'check' || marker === 'star') return marker;
        return 'dot';
    }

    private async persistTodoGoalOrder(goals: OwnitTodoGoal[]) {
        const now = new Date().toISOString();
        const updatedGoals = new Map<string, OwnitTodoGoal>();
        const updatedCategories = new Map<string, OwnitTodoCategory>();

        goals.forEach((goal, index) => {
            const updatedGoal = this.dataRepository.updateTodoGoal(goal.id, {
                order: index,
                updatedAt: now
            });
            if (updatedGoal) updatedGoals.set(updatedGoal.id, updatedGoal);

            if (!goal.categoryId) return;
            const updatedCategory = this.dataRepository.updateTodoCategory(goal.categoryId, {
                order: index,
                sortOrder: index,
                updatedAt: now
            });
            if (updatedCategory) updatedCategories.set(updatedCategory.id, updatedCategory);
        });

        if (updatedGoals.size > 0) {
            this.todoGoals = this.sortTodoGoals(this.todoGoals.map((goal) => updatedGoals.get(goal.id) || goal));
        }
        if (updatedCategories.size > 0) {
            this.todoCategories = this.sortTodoCategories(this.todoCategories.map((category) => updatedCategories.get(category.id) || category));
        }
        this.buildMonth();
    }

    private clearTodoGoalPreferencesFor(goalId: string) {
        let changed = false;
        if (this.defaultTodoGoalId === goalId) {
            this.defaultTodoGoalId = '';
            changed = true;
        }
        if (this.recentTodoGoalId === goalId) {
            this.recentTodoGoalId = '';
            changed = true;
        }
        if (changed) this.saveTodoGoalPreferences();
    }

    private applyTodoGoalPreferenceForm(goalId: string, form: TodoGoalFormState, hadActiveGoals: boolean) {
        if (!goalId) return;
        if (form.setAsDefault || !hadActiveGoals || !this.defaultTodoGoalId) {
            this.defaultTodoGoalId = goalId;
        } else if (this.defaultTodoGoalId === goalId) {
            this.defaultTodoGoalId = '';
        }

        if (form.setAsRecent) {
            this.recentTodoGoalId = goalId;
        } else if (this.recentTodoGoalId === goalId) {
            this.recentTodoGoalId = '';
        }

        this.saveTodoGoalPreferences();
    }

    private reconcileTodoGoalPreferences() {
        const activeIds = new Set(this.activeTodoGoals.map((goal) => goal.id));
        let changed = false;
        if (this.defaultTodoGoalId && !activeIds.has(this.defaultTodoGoalId)) {
            this.defaultTodoGoalId = '';
            changed = true;
        }
        if (this.recentTodoGoalId && !activeIds.has(this.recentTodoGoalId)) {
            this.recentTodoGoalId = '';
            changed = true;
        }
        if (changed) this.saveTodoGoalPreferences();
    }

    private todoBelongsToGoalCategory(todo: OwnitTodo, goal: OwnitTodoGoal): boolean {
        return todo.goalId === goal.id
            || Boolean(goal.categoryId && todo.categoryId === goal.categoryId)
            || Boolean(!todo.goalId && todo.category && todo.category === goal.name);
    }

    private routineBelongsToGoalCategory(routine: OwnitTodoRoutine, goal: OwnitTodoGoal): boolean {
        return routine.goalId === goal.id
            || Boolean(goal.categoryId && routine.categoryId === goal.categoryId)
            || Boolean(!routine.goalId && routine.memo && routine.memo === goal.name);
    }

    private routineById(routineId: string): OwnitTodoRoutine | null {
        return this.todoRoutines.find((routine) => routine.id === routineId) || null;
    }

    private routineGoalForRoutine(routine: OwnitTodoRoutine): OwnitTodoGoal | null {
        return routine.goalId ? this.todoGoalById(routine.goalId) : null;
    }

    private routineCategoryForRoutine(routine: OwnitTodoRoutine): OwnitTodoCategory | null {
        return routine.categoryId ? this.todoCategories.find((category) => category.id === routine.categoryId) || null : null;
    }

    private routineCategoryLabel(routine: OwnitTodoRoutine): string {
        return this.routineGoalForRoutine(routine)?.name || this.routineCategoryForRoutine(routine)?.name || routine.memo || '루틴';
    }

    private todoGoalForTodo(todo: OwnitTodo): OwnitTodoGoal | null {
        if (todo.goalId) {
            const goal = this.todoGoalById(todo.goalId);
            if (goal) return goal;
        }
        if (todo.categoryId) {
            const goal = this.activeTodoGoals.find((item) => item.categoryId === todo.categoryId);
            if (goal) return goal;
        }
        if (todo.category) {
            const goal = this.activeTodoGoals.find((item) => item.name === todo.category);
            if (goal) return goal;
        }
        return null;
    }

    private sortTodoGoals(goals: OwnitTodoGoal[]): OwnitTodoGoal[] {
        return [...goals].sort((a, b) => {
            const orderDiff = (Number(a.order) || 0) - (Number(b.order) || 0);
            if (orderDiff !== 0) return orderDiff;
            return a.name.localeCompare(b.name);
        });
    }

    private sortRoutines(routines: OwnitTodoRoutine[]): OwnitTodoRoutine[] {
        return [...routines].sort((a, b) => {
            const goalA = a.goalId ? this.todoGoalById(a.goalId) : null;
            const goalB = b.goalId ? this.todoGoalById(b.goalId) : null;
            const orderDiff = (Number(goalA?.order) || 0) - (Number(goalB?.order) || 0);
            if (orderDiff !== 0) return orderDiff;
            if (a.startDate !== b.startDate) return a.startDate.localeCompare(b.startDate);
            return a.title.localeCompare(b.title);
        });
    }

    private nextTodoGoalOrder() {
        const orders = this.todoGoals
            .map((goal) => Number(goal.order))
            .filter((order) => Number.isFinite(order));
        return orders.length === 0 ? 0 : Math.max(...orders) + 1;
    }

    private paletteIdForColor(color: string): TodoGoalPaletteId {
        const normalized = (color || '').toLowerCase();
        return this.todoGoalPalettes.find((palette) => palette.colors.some((item) => item.value.toLowerCase() === normalized))?.id || 'default';
    }

    private normalizeTodoGoalForm(): TodoGoalFormState | null {
        const name = (this.todoGoalForm.name || '').trim();
        if (!name) {
            this.todoGoalFormError = '카테고리 이름을 입력해 주세요.';
            return null;
        }

        const order = Number(this.todoGoalForm.order);
        return {
            name,
            color: this.todoGoalForm.color || this.todoGoalPalettes[0].colors[0].value,
            marker: this.normalizeTodoGoalMarker(this.todoGoalForm.marker),
            visibility: this.todoGoalForm.visibility === 'public' ? 'public' : (this.todoGoalForm.visibility === 'group' ? 'group' : 'private'),
            order: Number.isFinite(order) ? order : this.nextTodoGoalOrder(),
            collapsed: Boolean(this.todoGoalForm.collapsed),
            setAsDefault: Boolean(this.todoGoalForm.setAsDefault),
            setAsRecent: Boolean(this.todoGoalForm.setAsRecent)
        };
    }

    private normalizeRoutineForm(): RoutineFormState | null {
        const title = (this.routineForm.title || '').trim();
        if (!title) {
            this.routineFormError = '루틴 제목을 입력해 주세요.';
            return null;
        }
        if (!this.routineForm.startDate) {
            this.routineFormError = '시작일을 선택해 주세요.';
            return null;
        }
        if (this.routineForm.endDate && this.routineForm.endDate < this.routineForm.startDate) {
            this.routineFormError = '종료일은 시작일보다 빠를 수 없습니다.';
            return null;
        }
        const reminderOption = this.normalizeReminderPreset(this.routineForm.reminderOption);
        const reminderEnabled = reminderOption !== 'none';
        if (reminderEnabled && !this.isValidReminderTime(this.routineForm.reminderTime)) {
            this.routineFormError = '루틴 알림 시간을 선택해 주세요.';
            return null;
        }

        const start = this.fromDateKey(this.routineForm.startDate);
        const repeatDays = this.uniqueNumbers(this.routineForm.repeatDays, 0, 6);
        const repeatDates = this.uniqueNumbers(this.routineForm.repeatDates, 1, 31);
        const normalizedDays = this.routineForm.repeatType === 'weekly' && repeatDays.length === 0 ? [start.getDay()] : repeatDays;
        const normalizedDates = this.routineForm.repeatType === 'monthly' && repeatDates.length === 0 ? [start.getDate()] : repeatDates;

        return {
            ...this.routineForm,
            title,
            category: (this.routineForm.category || '').trim() || '루틴',
            color: this.routineForm.color || this.todoColorOptions[0].value,
            repeatDays: normalizedDays,
            repeatDates: normalizedDates,
            endDate: this.routineForm.endDate || '',
            reminderEnabled,
            reminderOption,
            reminderTime: this.normalizeReminderTime(this.routineForm.reminderTime || '09:00'),
            hideToday: Boolean(this.routineForm.hideToday)
        };
    }

    private routineRepeatRuleFromForm(form: RoutineFormState): OwnitTodoRoutine['repeatRule'] {
        const frequency = form.repeatType === 'daily'
            ? 'daily'
            : form.repeatType === 'monthly' || (form.repeatType === 'custom' && form.repeatDates.length > 0 && form.repeatDays.length === 0)
                ? 'monthly'
                : 'weekly';
        return {
            frequency,
            interval: 1,
            byWeekday: frequency === 'weekly' ? form.repeatDays : [],
            until: form.endDate || null,
            count: null
        };
    }

    private uniqueNumbers(values: number[], min: number, max: number) {
        const numbers = values
            .map((value) => Number(value))
            .filter((value) => Number.isInteger(value) && value >= min && value <= max);
        return Array.from(new Set(numbers)).sort((a, b) => a - b);
    }

    private routineWeekdaySortIndex(day: number) {
        const index = this.routineWeekdayOptions.findIndex((option) => option.value === day);
        return index >= 0 ? index : day;
    }

    private upsertTodoGoalCategory(form: TodoGoalFormState, existingGoal: OwnitTodoGoal | null): OwnitTodoCategory {
        const now = new Date().toISOString();
        const existingCategory = existingGoal?.categoryId
            ? this.todoCategories.find((category) => category.id === existingGoal.categoryId) || null
            : null;

        if (existingCategory) {
            const updated = this.dataRepository.updateTodoCategory(existingCategory.id, {
                name: form.name,
                color: form.color,
                icon: form.marker,
                marker: form.marker,
                visibility: form.visibility,
                order: form.order,
                sortOrder: form.order,
                collapsed: form.collapsed,
                archived: false,
                archivedAt: null,
                updatedAt: now
            }) || {
                ...existingCategory,
                name: form.name,
                color: form.color,
                icon: form.marker,
                marker: form.marker,
                visibility: form.visibility,
                order: form.order,
                sortOrder: form.order,
                collapsed: form.collapsed,
                archived: false,
                archivedAt: null,
                updatedAt: now
            };
            this.todoCategories = this.sortTodoCategories(this.todoCategories.map((category) => category.id === updated.id ? updated : category));
            return updated;
        }

        const created: OwnitTodoCategory = {
            id: this.createId('todo-category'),
            userId: this.user.id,
            name: form.name,
            color: form.color,
            icon: form.marker,
            marker: form.marker,
            visibility: form.visibility,
            order: form.order,
            sortOrder: form.order,
            collapsed: form.collapsed,
            locked: false,
            archived: false,
            archivedAt: null,
            ownerId: this.user.id,
            createdAt: now,
            updatedAt: now
        };
        const saved = this.dataRepository.createTodoCategory(created);
        this.todoCategories = this.sortTodoCategories([...this.todoCategories, saved]);
        return saved;
    }

    private sortTodoCategories(categories: OwnitTodoCategory[]): OwnitTodoCategory[] {
        return [...categories].sort((a, b) => {
            const orderA = Number.isFinite(Number(a.sortOrder)) ? Number(a.sortOrder) : Number(a.order) || 0;
            const orderB = Number.isFinite(Number(b.sortOrder)) ? Number(b.sortOrder) : Number(b.order) || 0;
            const orderDiff = orderA - orderB;
            if (orderDiff !== 0) return orderDiff;
            return a.name.localeCompare(b.name);
        });
    }

    private syncTodoGoalChildren(goal: OwnitTodoGoal, categoryId: string | null) {
        const now = new Date().toISOString();
        const updatedTodos = new Map<string, OwnitTodo>();
        for (const todo of this.todos.filter((item) => item.goalId === goal.id || Boolean(categoryId && item.categoryId === categoryId))) {
            const updated = this.dataRepository.updateTodo(todo.id, {
                goalId: goal.id,
                categoryId,
                category: goal.name,
                color: goal.color,
                updatedAt: now
            });
            if (updated) updatedTodos.set(updated.id, updated);
        }
        if (updatedTodos.size > 0) {
            this.todos = this.sortTodos(this.todos.map((todo) => updatedTodos.get(todo.id) || todo));
        }

        const updatedRoutines = new Map<string, OwnitTodoRoutine>();
        for (const routine of this.todoRoutines.filter((item) => item.goalId === goal.id || Boolean(categoryId && item.categoryId === categoryId))) {
            const updated = this.dataRepository.updateTodoRoutine(routine.id, {
                goalId: goal.id,
                categoryId,
                memo: goal.name,
                color: goal.color,
                updatedAt: now
            });
            if (updated) updatedRoutines.set(updated.id, updated);
        }
        if (updatedRoutines.size > 0) {
            this.todoRoutines = this.todoRoutines.map((routine) => updatedRoutines.get(routine.id) || routine);
        }
    }

    private deleteUnusedTodoCategory(categoryId: string | null) {
        if (!categoryId) return;
        const usedByGoal = this.todoGoals.some((goal) => goal.categoryId === categoryId);
        const usedByTodo = this.todos.some((todo) => todo.categoryId === categoryId);
        const usedByRoutine = this.todoRoutines.some((routine) => routine.categoryId === categoryId);
        if (usedByGoal || usedByTodo || usedByRoutine) return;

        this.dataRepository.deleteTodoCategory(categoryId);
        this.todoCategories = this.todoCategories.filter((category) => category.id !== categoryId);
    }

    private applyGoalToTodoForm(goal: OwnitTodoGoal | null) {
        this.todoForm.goalId = goal?.id || '';
        this.todoForm.category = goal?.name || this.uncategorizedTodoLabel;
        this.todoForm.color = goal?.color || this.uncategorizedTodoColor;
        this.todoForm.scope = goal?.visibility === 'group' ? 'shared' : this.todoForm.scope || 'personal';
    }

    private applyGoalToRoutineForm(goal: OwnitTodoGoal | null) {
        this.routineForm.goalId = goal?.id || '';
        this.routineForm.category = goal?.name || '루틴';
        this.routineForm.color = goal?.color || this.routineForm.color || this.todoColorOptions[0].value;
    }

    private normalizeTodoCreateMode(mode: TodoCreateMode): TodoCreateMode {
        if (mode === 'category' || mode === 'routine') return mode;
        return 'todo';
    }

    private closeTodoCreateFlowState() {
        this.todoCreateFlowOpen = false;
        this.todoCreateMode = 'todo';
        this.todoCreateContext = this.createTodoCreateContext(this.selectedDate);
        this.todoFormError = '';
        this.todoGoalFormError = '';
        this.routineFormError = '';
        if (!this.todoFormOpen) this.editingTodoId = null;
        if (!this.todoGoalFormOpen) this.editingTodoGoalId = null;
        if (!this.routineFormOpen) this.editingRoutineId = null;
    }

    private expandTodoGoalForCreate(goal: OwnitTodoGoal | null): OwnitTodoGoal | null {
        if (!goal?.collapsed || goal.locked) return goal;

        const updated = this.dataRepository.updateTodoGoal(goal.id, {
            collapsed: false,
            updatedAt: new Date().toISOString()
        });
        if (updated) {
            this.todoGoals = this.sortTodoGoals(this.todoGoals.map((item) => item.id === updated.id ? updated : item));
            goal = this.todoGoalWithCategory(updated);
        }

        if (goal?.categoryId) {
            const updatedCategory = this.dataRepository.updateTodoCategory(goal.categoryId, {
                collapsed: false,
                updatedAt: new Date().toISOString()
            });
            if (updatedCategory) {
                this.todoCategories = this.sortTodoCategories(this.todoCategories.map((category) => category.id === updatedCategory.id ? updatedCategory : category));
                goal = this.todoGoalById(goal.id);
            }
        }

        return goal;
    }

    private closeInlineTodoDraft() {
        this.inlineTodoDraft = this.createInlineTodoDraft();
    }

    private focusTodoCreatePrimaryInput() {
        if (!this.todoCreateFlowOpen || typeof window === 'undefined' || typeof document === 'undefined') return;
        const mode = this.todoCreateMode;
        window.setTimeout(() => {
            const input = document.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[data-todo-create-primary="${mode}"]`);
            if (!input) return;
            this.focusAndRevealInput(input);
        }, 40);
    }

    private focusInlineTodoInput(sectionId: string) {
        if (!sectionId || typeof window === 'undefined' || typeof document === 'undefined') return;
        window.setTimeout(() => {
            const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('[data-inline-todo-input]'));
            const input = inputs.find((item) => item.getAttribute('data-inline-todo-input') === sectionId);
            if (!input) return;
            this.focusAndRevealInput(input);
        }, 40);
    }

    private focusAndRevealInput(input: HTMLElement, block: ScrollLogicalPosition = 'center', behavior: ScrollBehavior = 'auto') {
        if (typeof window === 'undefined') return;
        input.focus({ preventScroll: true });
        window.setTimeout(() => {
            input.scrollIntoView({ block, inline: 'nearest', behavior });
        }, 90);
    }

    private todoSectionKeyForGoal(goal: OwnitTodoGoal | null) {
        return goal?.id || 'category-미분류';
    }

    private todoSectionKeyForTodo(todo: OwnitTodo) {
        const goal = this.todoGoalForTodo(todo);
        if (goal) return goal.id;
        return `category-${todo.category || this.uncategorizedTodoLabel}`;
    }

    private todosInSameSection(todo: OwnitTodo) {
        const sectionKey = this.todoSectionKeyForTodo(todo);
        return this.sortTodos(this.selectedTodos.filter((item) => this.todoSectionKeyForTodo(item) === sectionKey));
    }

    private todoMoveTarget(todo: OwnitTodo, direction: -1 | 1): OwnitTodo | null {
        const sectionTodos = this.todosInSameSection(todo);
        const index = sectionTodos.findIndex((item) => item.id === todo.id);
        if (index < 0) return null;
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= sectionTodos.length) return null;
        return sectionTodos[targetIndex];
    }

    private normalizedTodoOrder(todo: OwnitTodo, fallback: number) {
        const order = Number(todo.order);
        return Number.isFinite(order) ? order : fallback;
    }

    private nextTodoOrderForDate(date: string) {
        const orders = this.todos
            .filter((todo) => todo.date === date)
            .map((todo) => Number(todo.order))
            .filter((order) => Number.isFinite(order));
        return orders.length === 0 ? 0 : Math.max(...orders) + 1;
    }

    private routineOccurrencesForDate(date: string): OwnitTodo[] {
        return this.sortRoutines(this.todoRoutines)
            .filter((routine) => this.routineOccursOnDate(routine, date))
            .map((routine, index) => this.createRoutineOccurrenceTodo(routine, date, index));
    }

    private routineOccursOnDate(routine: OwnitTodoRoutine, dateKey: string): boolean {
        if (!dateKey || dateKey < routine.startDate) return false;
        if (routine.endDate && dateKey > routine.endDate) return false;
        if (routine.hideToday && dateKey === this.todayDate) return false;

        const date = this.fromDateKey(dateKey);
        const weekday = date.getDay();
        const monthDate = date.getDate();
        const startDate = this.fromDateKey(routine.startDate);
        const repeatType = routine.repeatType || 'weekly';
        const repeatDays = this.uniqueNumbers(routine.repeatDays || [], 0, 6);
        const repeatDates = this.uniqueNumbers(routine.repeatDates || [], 1, 31);

        if (repeatType === 'daily') return true;
        if (repeatType === 'weekly') return (repeatDays.length > 0 ? repeatDays : [startDate.getDay()]).includes(weekday);
        if (repeatType === 'monthly') return (repeatDates.length > 0 ? repeatDates : [startDate.getDate()]).includes(monthDate);
        if (repeatType === 'custom') {
            if (repeatDays.length === 0 && repeatDates.length === 0) return dateKey === routine.startDate;
            return repeatDays.includes(weekday) || repeatDates.includes(monthDate);
        }

        return false;
    }

    private createRoutineOccurrenceTodo(routine: OwnitTodoRoutine, date: string, index = 0): OwnitTodo {
        const completion = this.routineCompletionRecord(routine.id, date);
        const goal = this.routineGoalForRoutine(routine);
        const category = this.routineCategoryForRoutine(routine);
        const color = goal?.color || category?.color || routine.color || this.todoColorOptions[0].value;
        const ownerId = routine.ownerId || routine.userId || this.user.id;
        const assigneeId = routine.assigneeId || routine.assignedTo || ownerId;

        return {
            id: this.routineOccurrenceId(routine.id, date),
            title: routine.title,
            date,
            completed: Boolean(completion?.completed),
            completedAt: completion?.completedAt || null,
            goalId: routine.goalId || null,
            categoryId: routine.categoryId || null,
            routineId: routine.id,
            color,
            order: 10000 + index,
            category: goal?.name || category?.name || '루틴',
            authorId: ownerId,
            assigneeId,
            createdBy: ownerId,
            assignedTo: assigneeId,
            scope: routine.scope || 'personal',
            visibility: routine.visibility || 'private',
            repeatRule: routine.repeatRule,
            reminders: routine.reminders || [],
            reminderAt: this.routineReminderAtForDate(routine, date),
            reminderRule: routine.reminderRule || null,
            memo: routine.memo || '',
            movedFromDate: null,
            movedToDate: null,
            timerSessionIds: [],
            photoProofUrl: '',
            postponedFromDate: null,
            postponedToDate: null,
            timerSeconds: 0,
            diaryId: null,
            reactionId: null,
            completionRecordId: completion?.id || null,
            createdAt: routine.createdAt,
            updatedAt: completion?.updatedAt || routine.updatedAt
        };
    }

    private routineOccurrenceId(routineId: string, date: string) {
        return `${this.routineOccurrencePrefix}${routineId}::${date}`;
    }

    private parseRoutineOccurrenceId(id: string): { routineId: string; date: string } | null {
        if (!id.startsWith(this.routineOccurrencePrefix)) return null;
        const rest = id.slice(this.routineOccurrencePrefix.length);
        const separator = rest.lastIndexOf('::');
        if (separator <= 0) return null;
        return {
            routineId: rest.slice(0, separator),
            date: rest.slice(separator + 2)
        };
    }

    private routineOccurrenceTodoById(id: string): OwnitTodo | null {
        const parsed = this.parseRoutineOccurrenceId(id);
        if (!parsed) return null;
        const routine = this.routineById(parsed.routineId);
        if (!routine || !this.routineOccursOnDate(routine, parsed.date)) return null;
        return this.createRoutineOccurrenceTodo(routine, parsed.date);
    }

    public isRoutineOccurrenceTodo(todo: OwnitTodo) {
        return Boolean(todo.routineId && this.parseRoutineOccurrenceId(todo.id));
    }

    private routineCompletionRecord(routineId: string, date: string): OwnitTodoCompletionRecord | null {
        return this.todoCompletionRecords.find((record) => record.routineId === routineId && record.date === date) || null;
    }

    private async toggleRoutineCompletion(todo: OwnitTodo) {
        if (!todo.routineId) return;
        if (!this.canEditTodo(todo)) {
            this.todoFormError = '루틴 완료 상태를 바꿀 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const routine = this.routineById(todo.routineId);
        if (!routine) return;

        const existing = this.routineCompletionRecord(routine.id, todo.date);
        const completed = !Boolean(existing?.completed);
        const now = new Date().toISOString();
        const record: OwnitTodoCompletionRecord = {
            id: existing?.id || this.createId('todo-completion'),
            todoId: existing?.todoId || null,
            routineId: routine.id,
            date: todo.date,
            completed,
            completedAt: completed ? now : null,
            authorId: existing?.authorId || this.user.id,
            reactionId: existing?.reactionId || null,
            diaryId: existing?.diaryId || null,
            createdAt: existing?.createdAt || now,
            updatedAt: now
        };
        const saved = this.dataRepository.upsertTodoCompletionRecord(record);
        this.todoCompletionRecords = [
            ...this.todoCompletionRecords.filter((item) => item.id !== saved.id),
            saved
        ];
        this.refreshTodoRecordsFromRepository();
        this.buildMonth();
        this.refreshReminderSchedules();
        await this.service.render();
    }

    private createMainCalendarTodoRecord(todos: OwnitTodo[]): MainCalendarTodoRecord | null {
        const options = this.todoCalendarDisplayOptions;
        if (!options.enabled || todos.length === 0) return null;

        const regularTodos = todos.filter((todo) => !this.isRoutineOccurrenceTodo(todo));
        const routineTodos = todos.filter((todo) => this.isRoutineOccurrenceTodo(todo));
        const completedTodos = regularTodos.filter((todo) => todo.completed);
        const completedRoutines = routineTodos.filter((todo) => todo.completed);
        const markerTodos = [
            ...(options.showTodoCompleted ? completedTodos : []),
            ...(options.showRoutineCompleted ? completedRoutines : [])
        ];
        const allMarkers = this.todoCompletionMarkers(markerTodos);
        const markers = allMarkers.slice(0, 3);
        const completedCount = todos.filter((todo) => todo.completed).length;
        const completionRate = this.todoCompletionPercent(todos);
        const showCompletionRate = options.showCompletionRate && completedCount > 0;

        if (markers.length === 0 && !showCompletionRate) return null;

        const labelParts: string[] = [];
        if (options.showTodoCompleted && completedTodos.length > 0) labelParts.push(`할 일 ${completedTodos.length}개 완료`);
        if (options.showRoutineCompleted && completedRoutines.length > 0) labelParts.push(`루틴 ${completedRoutines.length}개 완료`);
        if (showCompletionRate) labelParts.push(`완료율 ${completionRate}%`);

        return {
            totalCount: todos.length,
            completedCount,
            completedTodoCount: completedTodos.length,
            completedRoutineCount: completedRoutines.length,
            completionRate,
            showCompletionRate,
            markers,
            markerOverflowCount: Math.max(0, allMarkers.length - markers.length),
            label: labelParts.join(', ') || '할 일 기록'
        };
    }

    private createTodoMateCalendarDay(day: CalendarDay): TodoMateCalendarDay {
        const todos = this.todosForDay(day.date);
        const completedCount = todos.filter((todo) => todo.completed).length;
        return {
            ...day,
            totalCount: todos.length,
            completedCount,
            openCount: todos.length - completedCount,
            progress: this.todoCompletionPercent(todos),
            hasBaseMarker: day.isCurrentMonth,
            slots: this.buildTodoDayMarkerSlots(day.date)
        };
    }

    private buildTodoDayMarkerSlots(date: string): TodoDayMarkerSlot[] {
        const completedTodosForMarker = this.todosForDay(date)
            .filter((todo) => todo.completed)
            .map((todo) => this.todoDayMarkerCompletedItemForTodo(todo));
        return buildTodoDayMarkerSlotData(date, completedTodosForMarker, this.todoCategories, this.uncategorizedTodoColor);
    }

    private todoDayMarkerCompletedItemForTodo(todo: OwnitTodo): TodoDayMarkerCompletedItem {
        const isRoutine = this.isRoutineOccurrenceTodo(todo);
        const routine = isRoutine && todo.routineId ? this.routineById(todo.routineId) : null;
        const goal = isRoutine && routine ? this.routineGoalForRoutine(routine) : this.todoGoalForTodo(todo);
        const category = isRoutine && routine ? this.routineCategoryForRoutine(routine) : this.todoCategoryForTodo(todo);
        const color = goal?.color || category?.color || todo.color || routine?.color || this.uncategorizedTodoColor;
        const label = goal?.name || category?.name || todo.category || (isRoutine ? '루틴' : this.uncategorizedTodoLabel);
        const kindLabel = isRoutine ? '루틴' : '할 일';

        return {
            id: todo.id,
            color,
            categoryId: category?.id || todo.categoryId || goal?.categoryId || null,
            routineId: isRoutine ? (todo.routineId || null) : null,
            category: category?.name || todo.category || null,
            label,
            kindLabel
        };
    }

    private todoCompletionMarkers(todos: OwnitTodo[]): CalendarTodoMarker[] {
        const markers = new Map<string, CalendarTodoMarker>();
        for (const todo of todos) {
            if (!todo.completed) continue;
            const marker = this.todoCalendarMarker(todo);
            const key = `${marker.color}:${marker.marker}`;
            if (!markers.has(key)) markers.set(key, marker);
        }
        return Array.from(markers.values());
    }

    private todoCalendarMarker(todo: OwnitTodo): CalendarTodoMarker {
        const goal = todo.goalId ? this.todoGoals.find((item) => item.id === todo.goalId) || null : null;
        const categoryById = todo.categoryId ? this.todoCategories.find((item) => item.id === todo.categoryId) || null : null;
        const categoryByName = todo.category ? this.todoCategories.find((item) => item.name === todo.category) || null : null;
        const category = categoryById || categoryByName;
        const color = goal?.color || category?.color || todo.color || this.todoColorOptions[0].value;
        const label = goal?.name || category?.name || todo.category || '할 일';
        const marker = this.normalizeTodoGoalMarker(category?.marker || goal?.marker || 'dot');
        return { color, label, marker };
    }

    private buildDailyCategoryStats(todos: OwnitTodo[]): OwnitTodoDailyCategoryStat[] {
        const statsByKey = new Map<string, OwnitTodoDailyCategoryStat>();

        for (const todo of todos) {
            const isRoutine = this.isRoutineOccurrenceTodo(todo);
            const routine = todo.routineId ? this.routineById(todo.routineId) : null;
            const goal = isRoutine && routine ? this.routineGoalForRoutine(routine) : this.todoGoalForTodo(todo);
            const category = isRoutine && routine ? this.routineCategoryForRoutine(routine) : this.todoCategoryForTodo(todo);
            const categoryId = category?.id || todo.categoryId || routine?.categoryId || goal?.categoryId || null;
            const goalId = goal?.id || todo.goalId || routine?.goalId || null;
            const name = isRoutine && routine
                ? this.routineCategoryLabel(routine)
                : (goal?.name || category?.name || todo.category || this.uncategorizedTodoLabel);
            const color = isRoutine && routine
                ? (goal?.color || category?.color || routine.color || this.uncategorizedTodoColor)
                : (goal?.color || category?.color || todo.color || this.uncategorizedTodoColor);
            const key = `${categoryId || 'none'}::${goalId || 'none'}::${name}`;
            const stat = statsByKey.get(key) || {
                categoryId,
                goalId,
                name,
                color,
                completedCount: 0,
                totalCount: 0,
                timerSeconds: 0
            };

            stat.totalCount += 1;
            if (todo.completed) stat.completedCount += 1;
            stat.timerSeconds += isRoutine ? 0 : this.todoTimerTotalSeconds(todo);
            statsByKey.set(key, stat);
        }

        return Array.from(statsByKey.values());
    }

    private todoCategoryForTodo(todo: OwnitTodo): OwnitTodoCategory | null {
        if (todo.categoryId) {
            const category = this.todoCategories.find((item) => item.id === todo.categoryId) || null;
            if (category) return category;
        }
        if (todo.category) {
            const category = this.todoCategories.find((item) => item.name === todo.category) || null;
            if (category) return category;
        }
        return null;
    }

    private buildTodoMateCalendarWeeks(): TodoMateCalendarDay[][] {
        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const mondayStartOffset = (firstDay.getDay() + 6) % 7;
        const start = new Date(year, month, 1 - mondayStartOffset);
        const weekCount = Math.max(5, Math.ceil((mondayStartOffset + this.daysInMonth(firstDay)) / 7));
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + (weekCount * 7) - 1);
        const holidaysByDate = this.holidayLayer.enabled
            ? this.holidayService.groupByDate(this.holidayService.getHolidaysForRange(this.toDateKey(start), this.toDateKey(end)))
            : {};
        const todayKey = this.toDateKey(new Date());
        const weeks: TodoMateCalendarDay[][] = [];

        for (let weekIndex = 0; weekIndex < weekCount; weekIndex++) {
            const week: TodoMateCalendarDay[] = [];
            for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
                const offset = weekIndex * 7 + dayIndex;
                const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + offset);
                const dateKey = this.toDateKey(date);
                const weekday = date.getDay();
                const weekendTone = this.weekendToneForWeekday(weekday);
                const holidays = holidaysByDate[dateKey] || [];
                const isPublicHoliday = holidays.some((holiday) => holiday.isPublicHoliday);
                week.push(this.createTodoMateCalendarDay({
                    date: dateKey,
                    day: date.getDate(),
                    weekday,
                    weekendTone,
                    holidays,
                    holidayLabel: this.summarizeHolidays(holidays),
                    isCurrentMonth: date.getMonth() === month,
                    isToday: dateKey === todayKey,
                    isSunday: weekendTone === 'sunday',
                    isSaturday: weekendTone === 'saturday',
                    isHoliday: holidays.length > 0,
                    isPublicHoliday,
                    eventCount: this.eventsForDay(dateKey).length,
                    eventOverflowCount: 0,
                    todoCount: this.todosForDay(dateKey).length
                }));
            }
            weeks.push(week);
        }

        return weeks;
    }

    private todoItemRows(todos: OwnitTodo[]): TodoItemRow[] {
        return this.sortTodos(todos).map((todo) => {
            const goal = this.todoGoalForTodo(todo);
            const routine = todo.routineId ? this.routineById(todo.routineId) : null;
            const isRoutine = this.isRoutineOccurrenceTodo(todo);
            const category = isRoutine && routine ? this.routineCategoryLabel(routine) : (goal?.name || todo.category || this.uncategorizedTodoLabel);
            const color = isRoutine && routine
                ? (this.routineGoalForRoutine(routine)?.color || this.routineCategoryForRoutine(routine)?.color || routine.color)
                : (goal?.color || todo.color || this.uncategorizedTodoColor);
            const timerSeconds = this.todoTimerDisplaySeconds(todo);
            const timerStatus = this.todoTimerStatus(todo);
            return {
                id: todo.id,
                kind: isRoutine ? 'routine' : 'todo',
                todo,
                routine: isRoutine ? routine : null,
                title: todo.title,
                memo: todo.memo,
                category,
                color,
                completed: todo.completed,
                scopeLabel: this.todoScopeLabel(todo),
                repeatLabel: isRoutine && routine ? this.routineRepeatLabel(routine) : this.todoRepeatLabel(todo),
                reminderLabel: this.todoReminderLabel(todo, routine),
                completedAtLabel: this.todoCompletedAtLabel(todo),
                timerSeconds,
                timerLabel: timerSeconds > 0 || timerStatus === 'running' || timerStatus === 'paused' ? this.formatTimerDuration(timerSeconds) : '',
                timerStatus,
                canUseTimer: !isRoutine,
                canEdit: this.canEditTodo(todo)
            };
        });
    }

    private buildTodoGoalSections(rows: TodoItemRow[]): TodoGoalSection[] {
        const sections = new Map<string, TodoGoalSection>();
        const activeGoals = this.activeTodoGoals;

        for (const goal of activeGoals) {
            sections.set(goal.id, this.createTodoGoalSection(goal.id, goal.id, goal.name, goal.color, this.todoGoalMarkerValue(goal), [], goal.collapsed, goal.locked));
        }

        for (const row of rows) {
            const goal = this.todoGoalForTodo(row.todo);
            const key = goal?.id || `category-${row.category}`;
            const label = goal?.name || row.category || '카테고리';
            const color = goal?.color || row.color;
            const marker = goal ? this.todoGoalMarkerValue(goal) : 'dot';
            const section = sections.get(key) || this.createTodoGoalSection(key, goal?.id || null, label, color, marker, [], goal?.collapsed || false, goal?.locked || false);
            section.items.push(row);
            section.totalCount += 1;
            if (row.completed) section.completedCount += 1;
            sections.set(key, section);
        }

        if (sections.size === 0) {
            const fallback = activeGoals[0] || null;
            sections.set('empty-goal', this.createTodoGoalSection('empty-goal', fallback?.id || null, fallback?.name || '카테고리', fallback?.color || this.uncategorizedTodoColor, fallback ? this.todoGoalMarkerValue(fallback) : 'dot', [], false, false));
        }

        return Array.from(sections.values()).map((section) => ({
            ...section,
            openCount: section.totalCount - section.completedCount,
            progress: section.totalCount === 0 ? 0 : Math.round((section.completedCount / section.totalCount) * 100)
        }));
    }

    private createTodoGoalSection(id: string, goalId: string | null, label: string, color: string, marker: TodoGoalMarkerId, items: TodoItemRow[], collapsed: boolean, locked: boolean): TodoGoalSection {
        return {
            id,
            goalId,
            label,
            color,
            marker,
            collapsed,
            locked,
            totalCount: items.length,
            completedCount: items.filter((item) => item.completed).length,
            openCount: items.filter((item) => !item.completed).length,
            progress: this.todoCompletionPercent(items.map((item) => item.todo)),
            items
        };
    }

    private buildTodoRoutineSection(rows: TodoItemRow[]): TodoRoutineSection {
        const routineRows = rows.filter((row) => row.kind === 'routine');
        return {
            id: 'routine',
            label: '루틴',
            collapsed: this.routineSectionCollapsed,
            totalCount: routineRows.length,
            completedCount: routineRows.filter((row) => row.completed).length,
            openCount: routineRows.filter((row) => !row.completed).length,
            progress: this.todoCompletionPercent(routineRows.map((row) => row.todo)),
            items: routineRows
        };
    }

    private isRoutineTodo(todo: OwnitTodo) {
        return Boolean(this.isRoutineOccurrenceTodo(todo) || todo.routineId || todo.repeatRule || todo.category === '루틴');
    }

    private todoRepeatLabel(todo: OwnitTodo) {
        if (!todo.repeatRule) return '';
        const labels: Record<string, string> = {
            daily: '매일',
            weekly: '매주',
            monthly: '매월',
            yearly: '매년'
        };
        return labels[todo.repeatRule.frequency] || '반복';
    }

    private routineRepeatLabel(routine: OwnitTodoRoutine) {
        if (routine.repeatType === 'daily') return '매일';
        if (routine.repeatType === 'weekly') {
            const days = (routine.repeatDays || [])
                .map((day) => this.routineWeekdayOptions.find((option) => option.value === day)?.label || '')
                .filter(Boolean);
            return days.length > 0 ? `매주 ${days.join(', ')}` : '매주';
        }
        if (routine.repeatType === 'monthly') {
            const dates = (routine.repeatDates || []).filter((date) => date >= 1 && date <= 31);
            return dates.length > 0 ? `매월 ${dates.join(', ')}일` : '매월';
        }
        const customParts: string[] = [];
        if ((routine.repeatDays || []).length > 0) {
            customParts.push((routine.repeatDays || [])
                .map((day) => this.routineWeekdayOptions.find((option) => option.value === day)?.label || '')
                .filter(Boolean)
                .join(', '));
        }
        if ((routine.repeatDates || []).length > 0) customParts.push(`${(routine.repeatDates || []).join(', ')}일`);
        return customParts.length > 0 ? `사용자 지정 ${customParts.join(' · ')}` : '사용자 지정';
    }

    private todoCompletedAtLabel(todo: OwnitTodo) {
        if (!todo.completed || !todo.completedAt) return '';
        const date = new Date(todo.completedAt);
        if (Number.isNaN(date.getTime())) return '완료';
        return `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
    }

    private todoReminderLabel(todo: OwnitTodo, routine: OwnitTodoRoutine | null = null) {
        const rule = todo.reminderRule || routine?.reminderRule || null;
        const reminderAt = todo.reminderAt || (routine ? this.routineReminderAtForDate(routine, todo.date) : null);
        if (!rule && !reminderAt) return '';
        if (rule?.kind === 'routine') return `알림 ${rule.time}`;
        if (reminderAt) return `알림 ${this.reminderDateTimeLabel(reminderAt)}`;
        if (rule?.time) return `알림 ${rule.time}`;
        return '';
    }

    private todoReminderTime(todo: OwnitTodo) {
        return this.normalizeReminderTime(todo.reminderRule?.time || this.reminderBaseTimeFromActual(todo.reminderAt, this.todoReminderOffsetMinutes(todo)) || '09:00');
    }

    private routineReminderTime(routine: OwnitTodoRoutine | null | undefined) {
        if (!routine) return '09:00';
        return this.normalizeReminderTime(routine.reminderRule?.time || this.reminderBaseTimeFromActual(routine.reminderAt, this.routineReminderOffsetMinutes(routine)) || '09:00');
    }

    private routineReminderAtForDate(routine: OwnitTodoRoutine, date: string): string | null {
        if (!routine.reminderRule?.enabled && !(routine.reminders || []).some((reminder) => reminder.enabled)) return null;
        if (!this.routineOccursOnDate(routine, date)) return null;
        return this.reminderAtFromBase(date, this.routineReminderTime(routine), this.routineReminderOffsetMinutes(routine));
    }

    private routineForReminderTarget(todo: OwnitTodo | null) {
        if (!todo?.routineId) return null;
        return this.routineById(todo.routineId);
    }

    private previewReminderAtFromDraft(todo: OwnitTodo): string | null {
        const preset = this.normalizeReminderPreset(this.todoActionDraft.reminderOption);
        if (preset === 'none') return null;
        const time = this.normalizeReminderTime(this.todoActionDraft.reminderTime || this.todoReminderTime(todo) || this.defaultReminderTime());
        const date = preset === 'custom' && !this.isRoutineOccurrenceTodo(todo)
            ? (this.todoActionDraft.reminderDate || todo.date || this.selectedDate)
            : (todo.date || this.selectedDate);
        const offsetMinutes = this.reminderOffsetMinutesForPreset(preset) || 0;

        if (!date || !this.isValidReminderTime(time)) return null;
        return this.reminderAtFromBase(date, time, offsetMinutes);
    }

    private buildReminderPayloadFromDraft(todo: OwnitTodo): ReminderPayload | null {
        const preset = this.normalizeReminderPreset(this.todoActionDraft.reminderOption);
        if (preset === 'none') {
            this.todoActionError = '알림 옵션을 선택해 주세요.';
            return null;
        }

        const time = this.normalizeReminderTime(this.todoActionDraft.reminderTime || this.todoReminderTime(todo) || this.defaultReminderTime());
        const date = preset === 'custom' && !this.isRoutineOccurrenceTodo(todo)
            ? (this.todoActionDraft.reminderDate || todo.date || this.selectedDate)
            : (todo.date || this.selectedDate);
        const offsetMinutes = this.reminderOffsetMinutesForPreset(preset) || 0;

        if (!this.isValidReminderTime(time)) {
            this.todoActionError = '알림 시간을 선택해 주세요.';
            return null;
        }

        const reminderAt = this.reminderAtFromBase(date, time, offsetMinutes);
        if (!reminderAt) {
            this.todoActionError = '알림 날짜와 시간을 확인해 주세요.';
            return null;
        }
        if (!this.isRoutineOccurrenceTodo(todo) && Date.parse(reminderAt) < Date.now() - 60000) {
            this.todoActionError = '현재 이후의 알림 시간을 선택해 주세요.';
            return null;
        }

        return {
            reminderAt,
            reminderRule: {
                kind: this.isRoutineOccurrenceTodo(todo) ? 'routine' : 'custom',
                date: this.isRoutineOccurrenceTodo(todo) ? null : date,
                time,
                nextAt: reminderAt,
                timezone: this.localTimezone(),
                userId: this.user.id,
                enabled: true,
                reminderPreset: preset,
                offsetMinutes
            },
            reminder: {
                id: `reminder-${todo.id}`,
                offsetMinutes,
                channel: 'app',
                enabled: true
            }
        };
    }

    private updateRoutineReminderFromAction(todo: OwnitTodo, payload: ReminderPayload | null): OwnitTodoRoutine | null {
        const routine = this.routineForReminderTarget(todo);
        if (!routine) {
            this.todoActionError = '수정할 루틴을 찾지 못했습니다.';
            return null;
        }
        if (!this.canEditTodo(todo)) {
            this.todoActionError = '루틴 알림을 수정할 권한이 없습니다.';
            return null;
        }

        const nextAt = payload ? this.nextRoutineReminderAt(routine, payload.reminderRule.time, payload.reminderRule.offsetMinutes || 0, todo.date) : null;
        const patch: Partial<OwnitTodoRoutine> = payload ? {
            reminder: { ...payload.reminder, id: `reminder-${routine.id}` },
            reminderAt: nextAt,
            reminderRule: {
                ...payload.reminderRule,
                kind: 'routine',
                date: null,
                nextAt
            },
            reminders: [{ ...payload.reminder, id: `reminder-${routine.id}` }],
            updatedAt: new Date().toISOString()
        } : {
            reminder: null,
            reminderAt: null,
            reminderRule: null,
            reminders: [],
            updatedAt: new Date().toISOString()
        };

        const updated = this.dataRepository.updateTodoRoutine(routine.id, patch);
        if (!updated) {
            this.todoActionError = '수정할 루틴을 찾지 못했습니다.';
            return null;
        }

        this.todoRoutines = this.sortRoutines(this.todoRoutines.map((item) => item.id === updated.id ? updated : item));
        this.buildMonth();
        this.refreshReminderSchedules();
        return updated;
    }

    private buildRoutineReminderRule(form: RoutineFormState): ReminderRule {
        const time = this.normalizeReminderTime(form.reminderTime || '09:00');
        const preset = this.normalizeReminderPreset(form.reminderOption);
        const offsetMinutes = this.reminderOffsetMinutesForPreset(preset) || 0;
        return {
            kind: 'routine',
            date: null,
            time,
            nextAt: this.nextRoutineReminderAtForForm(form, time, offsetMinutes),
            timezone: this.localTimezone(),
            userId: this.user.id,
            enabled: true,
            reminderPreset: preset,
            offsetMinutes
        };
    }

    private nextRoutineReminderAt(routine: OwnitTodoRoutine, time: string, offsetMinutes = 0, preferredDate?: string): string | null {
        if (preferredDate && this.routineOccursOnDate(routine, preferredDate)) {
            const preferredAt = this.reminderAtFromBase(preferredDate, time, offsetMinutes);
            if (preferredAt && Date.parse(preferredAt) >= Date.now() - 60000) return preferredAt;
        }
        const date = this.nextRoutineReminderDate(routine, this.todayDate, time, offsetMinutes);
        return this.reminderAtFromBase(date, time, offsetMinutes);
    }

    private nextRoutineReminderDate(routine: OwnitTodoRoutine, fromDate: string, time: string, offsetMinutes = 0): string {
        const start = this.fromDateKey(fromDate || this.todayDate);
        for (let offset = 0; offset <= 370; offset += 1) {
            const date = this.toDateKey(new Date(start.getFullYear(), start.getMonth(), start.getDate() + offset));
            if (!this.routineOccursOnDate(routine, date)) continue;
            const reminderAt = this.reminderAtFromBase(date, time, offsetMinutes);
            if (reminderAt && Date.parse(reminderAt) >= Date.now() - 60000) return date;
        }
        return routine.startDate || fromDate || this.todayDate;
    }

    private nextRoutineReminderAtForForm(form: RoutineFormState, time: string, offsetMinutes = 0): string | null {
        const fromDate = form.startDate > this.todayDate ? form.startDate : this.todayDate;
        const start = this.fromDateKey(fromDate);
        for (let offset = 0; offset <= 370; offset += 1) {
            const date = this.toDateKey(new Date(start.getFullYear(), start.getMonth(), start.getDate() + offset));
            if (!this.routineFormOccursOnDate(form, date)) continue;
            const reminderAt = this.reminderAtFromBase(date, time, offsetMinutes);
            if (reminderAt && Date.parse(reminderAt) >= Date.now() - 60000) return reminderAt;
        }
        return this.reminderAtFromBase(form.startDate, time, offsetMinutes);
    }

    private routineFormOccursOnDate(form: RoutineFormState, dateKey: string): boolean {
        if (!dateKey || dateKey < form.startDate) return false;
        if (form.endDate && dateKey > form.endDate) return false;

        const date = this.fromDateKey(dateKey);
        const weekday = date.getDay();
        const monthDate = date.getDate();
        const startDate = this.fromDateKey(form.startDate);

        if (form.repeatType === 'daily') return true;
        if (form.repeatType === 'weekly') return (form.repeatDays.length > 0 ? form.repeatDays : [startDate.getDay()]).includes(weekday);
        if (form.repeatType === 'monthly') return (form.repeatDates.length > 0 ? form.repeatDates : [startDate.getDate()]).includes(monthDate);
        if (form.repeatDays.length === 0 && form.repeatDates.length === 0) return dateKey === form.startDate;
        return form.repeatDays.includes(weekday) || form.repeatDates.includes(monthDate);
    }

    private normalizeReminderTime(value: string) {
        const match = /^(\d{1,2}):(\d{2})/.exec(value || '');
        if (!match) return '09:00';
        const hour = this.clamp(Number(match[1]) || 0, 0, 23);
        const minute = this.clamp(Number(match[2]) || 0, 0, 59);
        return `${`${hour}`.padStart(2, '0')}:${`${minute}`.padStart(2, '0')}`;
    }

    private isValidReminderTime(value: string) {
        return /^([01]\d|2[0-3]):[0-5]\d$/.test(value || '');
    }

    private localDateTimeToIso(dateKey: string, time: string): string | null {
        if (!dateKey || !this.isValidReminderTime(time)) return null;
        const [year, month, day] = dateKey.split('-').map((value) => Number(value));
        const [hour, minute] = time.split(':').map((value) => Number(value));
        if (!year || !month || !day) return null;
        return new Date(year, month - 1, day, hour, minute, 0, 0).toISOString();
    }

    private localTimeFromIso(value: string | null | undefined): string {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value.slice(11, 16);
        return `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
    }

    private localTimezone() {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
        } catch (error) {
            return 'local';
        }
    }

    private normalizeReminderPreset(value: ReminderPreset | string | null | undefined): ReminderPreset {
        const normalized = String(value || 'none') as ReminderPreset;
        return this.reminderPresetOptions.some((option) => option.value === normalized) ? normalized : 'none';
    }

    private reminderOffsetMinutesForPreset(preset: ReminderPreset | string | null | undefined): number | null {
        switch (this.normalizeReminderPreset(preset)) {
            case 'none':
                return null;
            case 'before_5':
                return 5;
            case 'before_10':
                return 10;
            case 'before_30':
                return 30;
            case 'before_60':
                return 60;
            case 'before_1440':
                return 1440;
            case 'at_time':
            case 'custom':
            default:
                return 0;
        }
    }

    private reminderPresetFromOffset(value: unknown): ReminderPreset {
        const offsetMinutes = this.normalizeReminderOffset(value);
        if (offsetMinutes === 0) return 'at_time';
        if (offsetMinutes === 5) return 'before_5';
        if (offsetMinutes === 10) return 'before_10';
        if (offsetMinutes === 30) return 'before_30';
        if (offsetMinutes === 60) return 'before_60';
        if (offsetMinutes === 1440) return 'before_1440';
        return 'custom';
    }

    private normalizeReminderOffset(value: unknown): number {
        const offsetMinutes = Number(value);
        if (!Number.isFinite(offsetMinutes) || offsetMinutes < 0) return 0;
        return Math.round(offsetMinutes);
    }

    private reminderAtFromBase(dateKey: string, time: string, offsetMinutes: number): string | null {
        const baseAt = this.localDateTimeToIso(dateKey, this.normalizeReminderTime(time));
        if (!baseAt) return null;
        const baseMs = Date.parse(baseAt);
        if (Number.isNaN(baseMs)) return null;
        return new Date(baseMs - this.normalizeReminderOffset(offsetMinutes) * 60000).toISOString();
    }

    private reminderBaseDateFromActual(actualAt: string | null | undefined, offsetMinutes: number): string | null {
        const base = this.reminderBaseDate(actualAt, offsetMinutes);
        return base ? this.toDateKey(base) : null;
    }

    private reminderBaseTimeFromActual(actualAt: string | null | undefined, offsetMinutes: number): string | null {
        const base = this.reminderBaseDate(actualAt, offsetMinutes);
        if (!base) return null;
        return `${`${base.getHours()}`.padStart(2, '0')}:${`${base.getMinutes()}`.padStart(2, '0')}`;
    }

    private reminderBaseDate(actualAt: string | null | undefined, offsetMinutes: number): Date | null {
        if (!actualAt) return null;
        const actualMs = Date.parse(actualAt);
        if (Number.isNaN(actualMs)) return null;
        return new Date(actualMs + this.normalizeReminderOffset(offsetMinutes) * 60000);
    }

    private todoReminderOffsetMinutes(todo: OwnitTodo | null | undefined): number {
        return this.normalizeReminderOffset(todo?.reminderRule?.offsetMinutes ?? (todo?.reminders || []).find((reminder) => reminder.enabled)?.offsetMinutes ?? 0);
    }

    private routineReminderOffsetMinutes(routine: OwnitTodoRoutine | null | undefined): number {
        return this.normalizeReminderOffset(routine?.reminderRule?.offsetMinutes ?? (routine?.reminders || []).find((reminder) => reminder.enabled)?.offsetMinutes ?? routine?.reminder?.offsetMinutes ?? 0);
    }

    private defaultReminderTime() {
        const now = new Date();
        const date = new Date(Date.now() + 60 * 60 * 1000);
        if (date.getDate() !== now.getDate()) return '23:59';
        date.setMinutes(0, 0, 0);
        return `${`${date.getHours()}`.padStart(2, '0')}:00`;
    }

    private createEventReminderFormState(event: OwnitEvent): Pick<EventFormState, 'reminderPreset' | 'reminderCustomDate' | 'reminderCustomTime'> {
        const reminder = (event.reminders || []).find((item) => item.enabled) || null;
        const defaultTime = event.allDay ? '09:00' : (event.startTime || '09:00');
        if (!reminder) {
            return {
                reminderPreset: 'none',
                reminderCustomDate: event.startDate,
                reminderCustomTime: this.normalizeReminderTime(defaultTime)
            };
        }

        const reminderAt = this.eventReminderAt(event, reminder);
        return {
            reminderPreset: this.reminderPresetFromOffset(reminder.offsetMinutes),
            reminderCustomDate: reminderAt ? this.toDateKey(new Date(reminderAt)) : event.startDate,
            reminderCustomTime: this.normalizeReminderTime(this.localTimeFromIso(reminderAt) || defaultTime)
        };
    }

    private createTodoReminderFormState(todo: OwnitTodo | null): Pick<TodoFormState, 'reminderPreset' | 'reminderDate' | 'reminderTime'> {
        const fallbackDate = todo?.date || this.selectedDate;
        const offsetMinutes = this.todoReminderOffsetMinutes(todo);
        const actualAt = todo?.reminderRule?.nextAt || todo?.reminderAt || null;
        const baseAt = this.reminderBaseDateFromActual(actualAt, offsetMinutes);
        const baseDate = todo?.reminderRule?.date || baseAt || fallbackDate;
        const baseTime = todo?.reminderRule?.time || this.reminderBaseTimeFromActual(actualAt, offsetMinutes) || this.defaultReminderTime();
        return {
            reminderPreset: this.todoReminderPreset(todo),
            reminderDate: baseDate,
            reminderTime: this.normalizeReminderTime(baseTime)
        };
    }

    private todoReminderPreset(todo: OwnitTodo | null | undefined): ReminderPreset {
        if (!todo) return 'none';
        const hasReminder = Boolean(todo.reminderRule?.enabled || todo.reminderAt || (todo.reminders || []).some((reminder) => reminder.enabled));
        if (!hasReminder) return 'none';
        const preset = this.normalizeReminderPreset(todo.reminderRule?.reminderPreset);
        if (preset !== 'none') return preset;
        return this.reminderPresetFromOffset(this.todoReminderOffsetMinutes(todo));
    }

    private routineReminderPreset(routine: OwnitTodoRoutine | null | undefined): ReminderPreset {
        if (!routine) return 'none';
        const hasReminder = Boolean(routine.reminderRule?.enabled || routine.reminderAt || (routine.reminders || []).some((reminder) => reminder.enabled));
        if (!hasReminder) return 'none';
        const preset = this.normalizeReminderPreset(routine.reminderRule?.reminderPreset);
        if (preset !== 'none') return preset;
        return this.reminderPresetFromOffset(this.routineReminderOffsetMinutes(routine));
    }

    private preferredEventCreateCalendar(preferredScope: OwnitCalendar['type'] = this.activeTab === 'share' ? 'shared' : 'personal') {
        const fallbackScope: OwnitCalendar['type'] = preferredScope === 'shared' ? 'personal' : 'shared';
        return this.defaultWritableEventCalendar(preferredScope) || this.defaultWritableEventCalendar(fallbackScope);
    }

    private defaultWritableEventCalendar(scope: OwnitCalendar['type']) {
        const calendars = scope === 'shared' ? this.writableSharedEventCalendars : this.writablePersonalEventCalendars;
        if (scope === 'shared') {
            const selected = this.selectedSharedCalendar;
            return (selected && calendars.some((calendar) => calendar.id === selected.id) ? selected : null)
                || calendars[0]
                || null;
        }
        return calendars.find((calendar) => calendar.isDefault)
            || calendars[0]
            || null;
    }

    private createEventForm(date: string, calendarId?: string, preferredScope?: OwnitCalendar['type']): EventFormState {
        const requestedCalendar = this.calendarById(calendarId || '');
        const requestedScope = preferredScope || requestedCalendar?.type || 'personal';
        const writableCalendars = requestedScope === 'shared' ? this.writableSharedEventCalendars : this.writablePersonalEventCalendars;
        const fallbackCalendars = requestedScope === 'shared' ? this.writablePersonalEventCalendars : this.writableSharedEventCalendars;
        const calendar = (requestedCalendar && writableCalendars.some((item) => item.id === requestedCalendar.id) ? requestedCalendar : null)
            || this.defaultWritableEventCalendar(requestedScope)
            || writableCalendars[0]
            || this.defaultWritableEventCalendar(requestedScope === 'shared' ? 'personal' : 'shared')
            || fallbackCalendars[0]
            || null;
        return {
            title: '',
            description: '',
            scope: calendar?.type || requestedScope,
            startDate: date,
            endDate: date,
            startTime: '09:00',
            endTime: '10:00',
            allDay: true,
            color: calendar?.color || this.eventColorOptions[0].value,
            calendarId: calendar?.id || '',
            assigneeId: calendar?.type === 'shared' ? this.defaultSharedAssigneeId('', calendar) : this.user.id,
            completed: false,
            reminderPreset: 'none',
            reminderCustomDate: date,
            reminderCustomTime: calendar?.type === 'shared' ? '09:00' : '09:00'
        };
    }

    private createCalendarForm(type: OwnitCalendar['type'], calendar: OwnitCalendar | null = null): CalendarFormState {
        const group = calendar?.type === 'shared'
            ? this.groupForCalendar(calendar) || this.selectedSharedGroup
            : this.selectedSharedGroup;
        return {
            type: calendar?.type || type,
            groupId: calendar?.type === 'shared' ? (calendar.groupId || '') : (type === 'shared' ? group.id : ''),
            groupName: calendar?.type === 'shared' ? (group.name || '') : (type === 'shared' ? (group.id ? group.name : '') : ''),
            name: calendar?.name || '',
            description: calendar?.description || '',
            color: calendar?.color || (type === 'shared' ? OWNIT_NEUTRAL_COLORS.shared : OWNIT_NEUTRAL_COLORS.personal),
            visibility: calendar?.visibility || (type === 'shared' ? 'group' : 'private'),
            isVisible: calendar?.isVisible !== false
        };
    }

    private createMemberInviteDraft(): MemberInviteDraftState {
        return {
            name: '',
            email: '',
            role: 'member'
        };
    }

    private createTodoForm(date: string): TodoFormState {
        const goal = this.preferredTodoGoal;
        return {
            title: '',
            date,
            memo: '',
            goalId: goal?.id || '',
            category: goal?.name || this.uncategorizedTodoLabel,
            color: goal?.color || this.uncategorizedTodoColor,
            scope: goal?.visibility === 'group' ? 'shared' : 'personal',
            reminderPreset: 'none',
            reminderDate: date,
            reminderTime: this.defaultReminderTime()
        };
    }

    private buildCalendarPayload(): CalendarPayload | null {
        const name = (this.calendarForm.name || '').trim();
        const groupName = (this.calendarForm.groupName || '').trim();
        if (!name) {
            this.calendarFormError = '캘린더 이름을 입력해 주세요.';
            return null;
        }
        if (this.calendarForm.type === 'shared' && !groupName) {
            this.calendarFormError = '센터/그룹 이름을 입력해 주세요.';
            return null;
        }

        return {
            name,
            type: this.calendarForm.type,
            color: this.calendarForm.color || (this.calendarForm.type === 'shared' ? OWNIT_NEUTRAL_COLORS.shared : OWNIT_NEUTRAL_COLORS.personal),
            description: (this.calendarForm.description || '').trim(),
            groupId: this.calendarForm.type === 'shared' ? this.calendarForm.groupId : '',
            groupName,
            visibility: this.calendarForm.type === 'shared' ? (this.calendarForm.visibility || 'group') : 'private',
            isVisible: this.calendarForm.isVisible !== false,
            updatedAt: new Date().toISOString()
        };
    }

    private async removeCalendar(calendar: OwnitCalendar, mode: 'archive' | 'delete') {
        if (!this.canArchiveCalendar(calendar)) {
            this.calendarFormError = calendar.isDefault ? '기본 개인 캘린더는 삭제하거나 보관할 수 없습니다.' : '캘린더를 삭제하거나 보관할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const actionSheetWasOpen = this.calendarActionCalendarId === calendar.id;
        this.calendarActionCalendarId = null;
        if (actionSheetWasOpen) await this.service.render();

        const confirmed = await this.confirmCalendarRemoval(calendar, mode);
        if (!confirmed) return;

        try {
            const removed = mode === 'delete'
                ? this.dataRepository.deleteCalendar(calendar.id)
                : Boolean(this.dataRepository.archiveCalendar(calendar.id));
            if (!removed) return;

            this.calendars = this.sortCalendars(this.calendars.filter((item) => item.id !== calendar.id));
            this.calendarActionCalendarId = null;
            if (this.eventForm.calendarId === calendar.id) this.eventForm = this.createEventForm(this.selectedDate);
            if (this.editingCalendarId === calendar.id) {
                this.calendarFormOpen = false;
                this.editingCalendarId = null;
            }
            this.calendarFormError = '';
            this.buildMonth();
        } catch (error) {
            this.calendarFormError = this.dataErrorMessage(error, '캘린더를 삭제하거나 보관하지 못했습니다.');
        }
        await this.service.render();
    }

    private async confirmCalendarRemoval(calendar: OwnitCalendar, mode: 'archive' | 'delete'): Promise<boolean> {
        const eventCount = this.eventCountForCalendar(calendar.id);
        const actionLabel = mode === 'delete' ? '삭제' : '보관';
        const eventWarning = eventCount > 0
            ? ` 연결된 일정 ${eventCount}개가 메인 캘린더에서 보이지 않게 됩니다.`
            : '';
        const message = `'${calendar.name}' 캘린더를 ${actionLabel}할까요?${eventWarning}`;

        if (this.service?.modal?.show) {
            const confirmed = await this.service.modal.show({
                title: `캘린더 ${actionLabel}`,
                message,
                action: actionLabel,
                cancel: '취소',
                status: mode === 'delete' ? 'error' : 'warning',
                actionBtn: mode === 'delete' ? 'error' : 'warning'
            });
            return Boolean(confirmed);
        }

        return window.confirm(message);
    }

    private async confirmEventRemoval(event: OwnitEvent): Promise<boolean> {
        const calendar = this.calendarById(event.calendarId);
        const message = `'${event.title}' 일정을 삭제할까요? ${this.eventCalendarSelectionLabel(calendar)}에서 삭제됩니다.`;

        if (this.service?.modal?.show) {
            const confirmed = await this.service.modal.show({
                title: '일정 삭제',
                message,
                action: '삭제',
                cancel: '취소',
                status: 'error',
                actionBtn: 'error'
            });
            return Boolean(confirmed);
        }

        return window.confirm(message);
    }

    private async removeSharedGroup(group: OwnitGroup, mode: 'archive' | 'delete') {
        if (!this.canManageSharedGroup(group)) {
            this.calendarFormError = '센터/그룹을 삭제하거나 보관할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const calendars = this.sharedCalendars.filter((calendar) => calendar.groupId === group.id);
        const eventCount = calendars.reduce((sum, calendar) => sum + this.eventCountForCalendar(calendar.id), 0);
        const actionLabel = mode === 'delete' ? '삭제' : '보관';
        const eventWarning = eventCount > 0 ? ` 연결된 공유 일정 ${eventCount}개가 캘린더에서 보이지 않게 됩니다.` : '';
        const message = `'${group.name}' 센터/그룹을 ${actionLabel}할까요?${eventWarning}`;
        const confirmed = this.service?.modal?.show
            ? await this.service.modal.show({
                title: `센터/그룹 ${actionLabel}`,
                message,
                action: actionLabel,
                cancel: '취소',
                status: mode === 'delete' ? 'error' : 'warning',
                actionBtn: mode === 'delete' ? 'error' : 'warning'
            })
            : window.confirm(message);
        if (!confirmed) return;

        let updated: OwnitGroup | null = null;
        try {
            const now = new Date().toISOString();
            updated = this.dataRepository.updateGroup(group.id, {
                archivedAt: mode === 'archive' ? now : null,
                deletedAt: mode === 'delete' ? now : null,
                updatedAt: now
            });
            for (const calendar of calendars) {
                if (mode === 'delete') this.dataRepository.deleteCalendar(calendar.id);
                else this.dataRepository.archiveCalendar(calendar.id);
            }
        } catch (error) {
            this.calendarFormError = this.dataErrorMessage(error, '센터/그룹을 삭제하거나 보관하지 못했습니다.');
            await this.service.render();
            return;
        }

        this.groups = this.sortGroups(this.groups.map((item) => item.id === group.id && updated ? updated : item));
        this.calendars = this.sortCalendars(this.calendars.filter((calendar) => calendar.groupId !== group.id));
        this.selectedSharedGroupId = '';
        this.selectedSharedCalendarId = '';
        this.syncSharedSelection();
        this.buildMonth();
        await this.service.render();
    }

    private createTodoActionDraft(todo: OwnitTodo | null = null): TodoActionDraftState {
        const baseDate = todo?.date || this.selectedDate;
        const offsetMinutes = this.todoReminderOffsetMinutes(todo);
        const actualAt = todo?.reminderRule?.nextAt || todo?.reminderAt || null;
        const reminderDate = todo?.reminderRule?.date || this.reminderBaseDateFromActual(actualAt, offsetMinutes) || baseDate;
        const reminderTime = this.normalizeReminderTime(todo?.reminderRule?.time || this.reminderBaseTimeFromActual(actualAt, offsetMinutes) || this.defaultReminderTime());
        return {
            memo: todo?.memo || '',
            reminderAt: this.toDateTimeLocalValue(todo?.reminderAt || null),
            reminderOption: this.todoReminderPreset(todo),
            reminderDate,
            reminderTime,
            photoProofUrl: todo?.photoProofUrl || '',
            repeatDate: this.nextDateKey(baseDate),
            changeDate: baseDate
        };
    }

    private createInlineTodoDraft(): InlineTodoDraftState {
        return {
            sectionId: '',
            goalId: null,
            category: this.uncategorizedTodoLabel,
            color: this.uncategorizedTodoColor,
            title: ''
        };
    }

    private createTodoCreateContext(date: string, goal: OwnitTodoGoal | null = null, sectionId = '', category = '', color = ''): TodoCreateFlowContext {
        return {
            date: date || this.selectedDate,
            goalId: goal?.id || null,
            sectionId: sectionId || this.todoSectionKeyForGoal(goal),
            category: goal?.name || category || this.uncategorizedTodoLabel,
            color: goal?.color || color || this.uncategorizedTodoColor
        };
    }

    private createTodoGoalForm(): TodoGoalFormState {
        return {
            name: '',
            color: this.todoGoalPalettes[0].colors[0].value,
            marker: 'dot',
            visibility: 'private',
            order: this.nextTodoGoalOrder(),
            collapsed: false,
            setAsDefault: false,
            setAsRecent: false
        };
    }

    private createRoutineForm(date: string): RoutineFormState {
        const selected = this.fromDateKey(date);
        const routineColor = this.todoColorOptions.find((option) => option.label === '루틴')?.value || this.todoColorOptions[0].value;
        return {
            title: '',
            goalId: '',
            category: '루틴',
            color: routineColor,
            repeatType: 'daily',
            repeatDays: [selected.getDay()],
            repeatDates: [selected.getDate()],
            startDate: date,
            endDate: '',
            reminderEnabled: false,
            reminderOption: 'none',
            reminderTime: '09:00',
            hideToday: false
        };
    }

    private prepareTodoActionEditForm(todo: OwnitTodo) {
        if (this.isRoutineOccurrenceTodo(todo)) return;
        this.focusDate(todo.date);
        this.editingTodoId = todo.id;
        this.todoFormError = '';
        const goal = this.todoGoalForTodo(todo);
        this.todoForm = {
            title: todo.title,
            date: todo.date,
            memo: todo.memo,
            goalId: goal?.id || '',
            category: goal?.name || todo.category || this.uncategorizedTodoLabel,
            color: goal?.color || todo.color || this.uncategorizedTodoColor,
            scope: todo.scope,
            ...this.createTodoReminderFormState(todo)
        };
    }

    private prepareTodoActionRoutineForm(todo: OwnitTodo) {
        const date = todo.date || this.selectedDate;
        const goal = this.todoGoalForTodo(todo);
        const selected = this.fromDateKey(date);
        this.editingRoutineId = null;
        this.routineFormError = '';
        this.routineForm = {
            ...this.createRoutineForm(date),
            title: todo.title,
            goalId: goal?.id || '',
            category: goal?.name || todo.category || '루틴',
            color: goal?.color || todo.color || this.todoColorOptions[0].value,
            repeatType: 'weekly',
            repeatDays: [selected.getDay()],
            repeatDates: [selected.getDate()],
            startDate: date,
            reminderEnabled: Boolean(todo.reminderAt || todo.reminderRule?.enabled),
            reminderOption: this.todoReminderPreset(todo),
            reminderTime: this.todoReminderTime(todo) || '09:00'
        };
    }

    private createReceiptDraft(record: OwnitTodoDailyRecord | null = null): ReceiptDraft {
        return {
            emoji: record?.representativeEmoji || '☁',
            mood: '차분함',
            note: record?.diary || ''
        };
    }

    private createDefaultReceiptTemplate(): DailyReceiptTemplateState {
        return {
            title: '하루 영수증',
            footerText: 'THANK YOU FOR YOUR DAY',
            dateFormat: 'korean-full',
            includeSharedSchedules: true,
            styleId: 'basic-receipt',
            sections: this.defaultReceiptTemplateSections()
        };
    }

    private defaultReceiptTemplateSections(): DailyReceiptTemplateSectionState[] {
        return [
            { id: 'personalSchedules', label: '개인 일정 요약', enabled: true, order: 0 },
            { id: 'sharedSchedules', label: '공유 일정 요약', enabled: true, order: 1 },
            { id: 'completedTodos', label: '완료한 Todo', enabled: true, order: 2 },
            { id: 'unfinishedTodoCount', label: '미완료 Todo 수', enabled: true, order: 3 },
            { id: 'routines', label: '루틴 기록', enabled: true, order: 4 },
            { id: 'dailyMemo', label: '하루 메모/일기', enabled: true, order: 5 },
            { id: 'completionRate', label: '완료율', enabled: true, order: 6 }
        ];
    }

    private normalizeReceiptTemplate(template: Partial<DailyReceiptTemplateState> | null | undefined): DailyReceiptTemplateState {
        const defaults = this.createDefaultReceiptTemplate();
        const allowedDateFormats = new Set(this.receiptDateFormatOptions.map((option) => option.value));
        const allowedStyleIds = new Set(this.receiptTemplateStyles.map((style) => style.id));
        const incomingSections = Array.isArray(template?.sections) ? template.sections : [];
        const incomingById = new Map<DailyReceiptSectionId, Partial<DailyReceiptTemplateSectionState>>();
        incomingSections.forEach((section) => {
            if (this.isReceiptSectionId(section?.id)) incomingById.set(section.id, section);
        });

        const sections = this.sortedReceiptTemplateSections(defaults.sections.map((section) => {
            const incoming = incomingById.get(section.id);
            return {
                ...section,
                enabled: typeof incoming?.enabled === 'boolean' ? incoming.enabled : section.enabled,
                order: Number.isFinite(Number(incoming?.order)) ? Number(incoming?.order) : section.order
            };
        })).map((section, order) => ({ ...section, order }));

        const title = (template?.title || '').trim() || defaults.title;
        const footerText = (template?.footerText || '').trim() || defaults.footerText;
        const dateFormat = allowedDateFormats.has(template?.dateFormat as DailyReceiptDateFormat)
            ? template?.dateFormat as DailyReceiptDateFormat
            : defaults.dateFormat;
        const styleId = allowedStyleIds.has(template?.styleId || '')
            ? template?.styleId || defaults.styleId
            : defaults.styleId;

        return {
            title: title.slice(0, 40),
            footerText: footerText.slice(0, 80),
            dateFormat,
            includeSharedSchedules: template?.includeSharedSchedules !== false,
            styleId,
            sections
        };
    }

    private sortedReceiptTemplateSections(sections: DailyReceiptTemplateSectionState[]): DailyReceiptTemplateSectionState[] {
        return [...sections].sort((a, b) => {
            if (a.order !== b.order) return a.order - b.order;
            return a.label.localeCompare(b.label);
        });
    }

    private isReceiptSectionId(value: unknown): value is DailyReceiptSectionId {
        return value === 'personalSchedules'
            || value === 'sharedSchedules'
            || value === 'completedTodos'
            || value === 'unfinishedTodoCount'
            || value === 'routines'
            || value === 'dailyMemo'
            || value === 'completionRate';
    }

    private selectedReceiptTemplateStyle(): DailyReceiptTemplateStyle {
        return this.receiptTemplateStyles.find((style) => style.id === this.receiptTemplate.styleId)
            || this.receiptTemplateStyles[0];
    }

    private createDailyRecordDraft(record: OwnitTodoDailyRecord | null = null): DailyRecordDraft {
        return {
            representativeEmoji: record?.representativeEmoji || '☁',
            diary: record?.diary || ''
        };
    }

    private buildDailyReceiptViewModel(date: string): DailyReceiptViewModel {
        const events = this.sortEvents(this.visibleEvents.filter((event) => this.isDateWithin(date, event.startDate, event.endDate)));
        const personalEvents = events.filter((event) => !this.isSharedEvent(event));
        const sharedEvents = events.filter((event) => this.isSharedEvent(event));
        const todos = this.todosForDay(date);
        const rows = this.todoItemRows(todos);
        const routineRows = rows.filter((row) => row.kind === 'routine');
        const todoRows = rows.filter((row) => row.kind === 'todo');
        const completedTodoRows = todoRows.filter((row) => row.completed);
        const openTodoRows = todoRows.filter((row) => !row.completed);
        const dailyRecord = this.todoDailyRecordForDate(date);
        const draft = date === this.selectedDate
            ? this.receiptDraft
            : (this.receiptDrafts[date] || this.createReceiptDraft(dailyRecord));
        const representativeEmoji = (draft.emoji || dailyRecord?.representativeEmoji || '☁').trim() || '☁';
        const mood = (draft.mood || this.receiptMoodLabel(todos)).trim() || this.receiptMoodLabel(todos);
        const memo = (draft.note || dailyRecord?.diary || '').trim();
        const completionRate = this.todoCompletionPercent(todos);
        const openCount = todos.filter((todo) => !todo.completed).length;
        const completedTodoCount = completedTodoRows.length;
        const todoTotalCount = todoRows.length;
        const routineCompletedCount = routineRows.filter((row) => row.completed).length;
        const routineTotalCount = routineRows.length;
        const savedAt = this.receiptModalOpen ? this.receiptPreviewSavedAt : new Date();
        const dateValue = this.fromDateKey(date);
        const weekday = this.weekLabels[dateValue.getDay()];
        const dateFullLabel = `${dateValue.getFullYear()}년 ${dateValue.getMonth() + 1}월 ${dateValue.getDate()}일 ${weekday}요일`;
        const hasTypedMood = Boolean((draft.mood || '').trim()) && (draft.mood || '').trim() !== '차분함';
        const hasTypedEmoji = Boolean((draft.emoji || '').trim()) && (draft.emoji || '').trim() !== '☁';
        const hasSavedEmoji = Boolean(dailyRecord?.representativeEmoji && dailyRecord.representativeEmoji !== '☁');
        const hasData = events.length > 0
            || todos.length > 0
            || Boolean(memo)
            || hasTypedMood
            || hasTypedEmoji
            || hasSavedEmoji;
        const personalScheduleSummary = this.receiptScheduleSummaryFromEvents('개인 일정', personalEvents);
        const sharedScheduleSummary = this.receiptScheduleSummaryFromEvents('공유 일정', sharedEvents);
        const completedTodos = completedTodoRows.map((row) => this.receiptLineFromTodoRow(row, row.completedAtLabel || 'DONE'));
        const openTodos = openTodoRows.map((row) => this.receiptLineFromTodoRow(row, 'WAIT'));
        const routineRecords = routineRows.map((row) => this.receiptLineFromTodoRow(row, row.completed ? 'DONE' : 'WAIT'));
        const data: DailyReceiptDataViewModel = {
            date,
            dateLabel: this.dateLabel(date),
            dateFullLabel,
            formattedDate: this.formatReceiptDate(date, this.receiptTemplate.dateFormat),
            ownerLabel: this.user.nickname || this.user.name || 'Ownit',
            savedAtIso: savedAt.toISOString(),
            savedAtLabel: this.formatReceiptSavedAt(savedAt),
            generatedAtLabel: `${this.formatReceiptDate(date, this.receiptTemplate.dateFormat)} · ${this.formatReceiptSavedAt(savedAt)} 저장`,
            moodEmoji: representativeEmoji,
            moodLabel: mood,
            completedTodoCount,
            todoTotalCount,
            completedTodos,
            unfinishedTodoCount: openTodoRows.length,
            routineCompletedCount,
            routineTotalCount,
            routineRecords,
            routineOpenCount: routineRows.filter((row) => !row.completed).length,
            personalScheduleSummary,
            sharedScheduleSummary,
            includeSharedScheduleSummary: this.receiptTemplate.includeSharedSchedules,
            dailyMemo: memo,
            completionRate,
            completionLabel: todos.length === 0 ? '할 일 없음' : `${completionRate}% 완료`,
            balanceLabel: hasData ? (openCount === 0 ? '정산 완료' : `${openCount}개 이월`) : '기록 대기',
            timerLabel: this.formatTimerDuration(todos.reduce((total, todo) => total + this.todoTimerTotalSeconds(todo), 0)),
            scheduleSummaryLabel: events.length === 0 ? '0건' : `${events.length}건`,
            todoSummaryLabel: todoTotalCount === 0 ? '0개' : `${completedTodoCount}/${todoTotalCount} 완료`,
            routineSummaryLabel: routineTotalCount === 0 ? '0개' : `${routineCompletedCount}/${routineTotalCount} 완료`,
            hasData
        };
        const scheduleSummary: DailyReceiptScheduleSummary = {
            label: '전체 일정',
            totalCount: events.length,
            personalCount: personalScheduleSummary.totalCount,
            sharedCount: sharedScheduleSummary.totalCount,
            completedCount: personalScheduleSummary.completedCount + sharedScheduleSummary.completedCount,
            items: [...personalScheduleSummary.items, ...sharedScheduleSummary.items]
        };

        return {
            ...data,
            data,
            template: this.receiptTemplate,
            style: this.selectedReceiptTemplateStyle(),
            sections: this.buildDailyReceiptSections(data, openTodos),
            scheduleSummary,
            openTodos,
            openTodoCount: data.unfinishedTodoCount,
            routines: data.routineRecords,
            representativeEmoji: data.moodEmoji,
            mood: data.moodLabel,
            memo: data.dailyMemo
        };
    }

    private receiptScheduleSummaryFromEvents(label: string, events: OwnitEvent[]): DailyReceiptScheduleSummary {
        return {
            label,
            totalCount: events.length,
            personalCount: events.filter((event) => !this.isSharedEvent(event)).length,
            sharedCount: events.filter((event) => this.isSharedEvent(event)).length,
            completedCount: events.filter((event) => event.completed).length,
            items: events.map((event) => this.receiptLineFromEvent(event))
        };
    }

    private buildDailyReceiptSections(data: DailyReceiptDataViewModel, openTodos: DailyReceiptLineItem[]): DailyReceiptSection[] {
        const sectionMap: Record<DailyReceiptSectionId, DailyReceiptSection> = {
            personalSchedules: {
                id: 'personalSchedules',
                kind: 'lines',
                label: 'PERSONAL SCHEDULE',
                countLabel: `${data.personalScheduleSummary.totalCount}건`,
                emptyLabel: '개인 일정 없음',
                items: data.personalScheduleSummary.items,
                metrics: [],
                stats: data.personalScheduleSummary.totalCount > 0 ? [
                    { id: 'done', label: '완료', value: `${data.personalScheduleSummary.completedCount}` }
                ] : [],
                memo: ''
            },
            sharedSchedules: {
                id: 'sharedSchedules',
                kind: 'lines',
                label: 'SHARED SCHEDULE',
                countLabel: `${data.sharedScheduleSummary.totalCount}건`,
                emptyLabel: '공유 일정 없음',
                items: data.sharedScheduleSummary.items,
                metrics: [],
                stats: data.sharedScheduleSummary.totalCount > 0 ? [
                    { id: 'done', label: '완료', value: `${data.sharedScheduleSummary.completedCount}` }
                ] : [],
                memo: ''
            },
            completedTodos: {
                id: 'completedTodos',
                kind: 'lines',
                label: 'COMPLETED TODO',
                countLabel: `${data.completedTodos.length}개`,
                emptyLabel: '완료한 Todo 없음',
                items: data.completedTodos,
                metrics: [],
                stats: [],
                memo: ''
            },
            unfinishedTodoCount: {
                id: 'unfinishedTodoCount',
                kind: 'metrics',
                label: 'UNFINISHED TODO',
                countLabel: `${data.unfinishedTodoCount}개`,
                emptyLabel: '',
                items: openTodos,
                metrics: [
                    { id: 'openTodoCount', label: '남은 Todo', value: `${data.unfinishedTodoCount}개`, important: data.unfinishedTodoCount > 0 }
                ],
                stats: [],
                memo: ''
            },
            routines: {
                id: 'routines',
                kind: 'lines',
                label: 'ROUTINE RECORD',
                countLabel: data.routineTotalCount === 0 ? '0개' : `${data.routineCompletedCount}/${data.routineTotalCount} 완료`,
                emptyLabel: '기록된 루틴 없음',
                items: data.routineRecords,
                metrics: [],
                stats: data.routineRecords.length > 0 ? [
                    { id: 'done', label: '완료', value: `${data.routineCompletedCount}` },
                    { id: 'wait', label: '대기', value: `${data.routineOpenCount}` }
                ] : [],
                memo: ''
            },
            dailyMemo: {
                id: 'dailyMemo',
                kind: 'memo',
                label: 'DAY MEMO',
                countLabel: data.dailyMemo ? '기록됨' : '비어 있음',
                emptyLabel: '',
                items: [],
                metrics: [],
                stats: [],
                memo: data.dailyMemo || '오늘의 메모가 비어 있습니다.'
            },
            completionRate: {
                id: 'completionRate',
                kind: 'metrics',
                label: 'TODAY BALANCE',
                countLabel: data.balanceLabel,
                emptyLabel: '',
                items: [],
                metrics: [
                    { id: 'completionRate', label: '완료율', value: `${data.completionRate}%` },
                    { id: 'unfinishedTodoCount', label: '미완료 Todo', value: `${data.unfinishedTodoCount}개` },
                    { id: 'routineOpenCount', label: '루틴 대기', value: `${data.routineOpenCount}개` },
                    { id: 'savedAt', label: '저장 시간', value: data.savedAtLabel },
                    { id: 'balance', label: '정산', value: data.balanceLabel, important: true }
                ],
                stats: [],
                memo: ''
            }
        };

        return this.receiptTemplateSections
            .filter((section) => section.enabled)
            .filter((section) => section.id !== 'sharedSchedules' || data.includeSharedScheduleSummary)
            .map((section) => sectionMap[section.id])
            .filter(Boolean);
    }

    private formatReceiptDate(date: string, format: DailyReceiptDateFormat): string {
        const value = this.fromDateKey(date);
        const weekday = this.weekLabels[value.getDay()];
        if (format === 'iso') return date;
        if (format === 'dot') return date.replace(/-/g, '.');
        if (format === 'korean-short') return `${value.getMonth() + 1}월 ${value.getDate()}일 ${weekday}`;
        return `${value.getFullYear()}년 ${value.getMonth() + 1}월 ${value.getDate()}일 ${weekday}요일`;
    }

    private formatReceiptSavedAt(date: Date): string {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        const hour = `${date.getHours()}`.padStart(2, '0');
        const minute = `${date.getMinutes()}`.padStart(2, '0');
        return `${year}.${month}.${day} ${hour}:${minute}`;
    }

    private receiptLineFromEvent(event: OwnitEvent): DailyReceiptLineItem {
        const calendar = this.calendarById(event.calendarId);
        return {
            id: event.id,
            title: event.title,
            meta: this.compactReceiptParts([
                this.eventScopeLabel(event),
                calendar?.name || '캘린더 없음',
                event.completed ? '완료' : ''
            ]),
            amount: this.eventTimeLabel(event),
            accentColor: event.color || calendar?.color || '#d8d0c2',
            tone: event.completed ? 'done' : 'default'
        };
    }

    private receiptLineFromTodoRow(row: TodoItemRow, amount: string): DailyReceiptLineItem {
        return {
            id: row.id,
            title: row.title,
            meta: this.compactReceiptParts([
                row.category,
                row.repeatLabel,
                row.timerLabel && row.timerLabel !== '0초' ? row.timerLabel : '',
                row.memo
            ]),
            amount,
            accentColor: row.color || '#d8d0c2',
            tone: row.completed ? 'done' : 'open'
        };
    }

    private compactReceiptParts(parts: Array<string | null | undefined>): string {
        return parts
            .map((part) => (part || '').trim())
            .filter(Boolean)
            .join(' · ');
    }

    private receiptMoodLabel(todos: OwnitTodo[]): string {
        if (todos.length === 0) return '차분함';
        const progress = this.todoCompletionPercent(todos);
        if (progress >= 100) return '가뿐함';
        if (progress >= 60) return '꾸준함';
        if (progress > 0) return '진행 중';
        return '준비 중';
    }

    private createTodoAiSuggestionDraftState(): TodoAiSuggestionDraftState {
        return {
            open: false,
            loading: false,
            activeTab: 'diary',
            error: '',
            notice: '',
            providerLabel: '',
            generatedAt: '',
            diaryDraft: '',
            tomorrowTodos: []
        };
    }

    private collectTodoAiSuggestionInput(): TodoAiSuggestionContext {
        const rows = this.todoItemRows(this.selectedTodos);
        return {
            date: this.selectedDate,
            dateLabel: this.selectedDateLabel,
            userName: this.user.nickname || this.user.name || this.user.email || '',
            representativeEmoji: this.selectedRepresentativeEmoji,
            diaryMemo: this.dailyRecordDraft.diary || this.selectedTodoDailyRecord?.diary || '',
            completedCount: this.selectedCompletedCount,
            openCount: this.selectedOpenCount,
            totalCount: this.selectedTodos.length,
            completionRate: this.selectedTodoProgress,
            totalTimerLabel: this.selectedTodoTimerLabel,
            todos: rows.map((row) => ({
                title: row.title,
                completed: row.completed,
                kind: row.kind,
                category: row.category,
                memo: row.memo,
                repeatLabel: row.repeatLabel,
                reminderLabel: row.reminderLabel,
                timerLabel: row.timerLabel
            }))
        };
    }

    private applyTodoAiSuggestionResult(result: TodoAiSuggestionResult) {
        const tomorrowTodos = this.uniqueTodoAiTitles(result.tomorrowTodos).slice(0, 6);
        this.todoAiSuggestionDraft = {
            ...this.todoAiSuggestionDraft,
            open: true,
            loading: false,
            activeTab: 'diary',
            error: '',
            notice: result.warning || '',
            providerLabel: result.providerLabel || (result.provider === 'openai' ? 'OpenAI' : 'Mock provider'),
            generatedAt: result.createdAt || new Date().toISOString(),
            diaryDraft: (result.diaryDraft || '').trim(),
            tomorrowTodos
        };
    }

    private normalizedTodoAiTomorrowTitles(): string[] {
        return this.uniqueTodoAiTitles(this.todoAiSuggestionDraft.tomorrowTodos).slice(0, 6);
    }

    private uniqueTodoAiTitles(values: string[]): string[] {
        const seen = new Set<string>();
        const titles: string[] = [];
        for (const value of values || []) {
            const title = (value || '').replace(/^[-*]\s+/, '').trim();
            if (!title || seen.has(title)) continue;
            seen.add(title);
            titles.push(title.slice(0, 80));
        }
        return titles;
    }

    private ensureReceiptDraftForDate(date: string) {
        if (!this.receiptDrafts[date]) {
            this.receiptDrafts[date] = this.createReceiptDraft(this.todoDailyRecordForDate(date));
        }
        this.receiptDraft = this.receiptDrafts[date];
    }

    private syncReceiptDraftFromDailyRecord(record: OwnitTodoDailyRecord) {
        if (!this.receiptDrafts[record.date]) this.receiptDrafts[record.date] = this.createReceiptDraft(record);
        this.receiptDrafts[record.date] = {
            ...this.receiptDrafts[record.date],
            emoji: record.representativeEmoji || this.receiptDrafts[record.date].emoji || '☁',
            note: record.diary
        };
        if (record.date === this.selectedDate) this.receiptDraft = this.receiptDrafts[record.date];
    }

    private syncDailyRecordDraftForDate(date: string, clearMessages = true) {
        this.dailyRecordDraft = this.createDailyRecordDraft(this.todoDailyRecordForDate(date));
        if (clearMessages) {
            this.dailyRecordError = '';
            this.dailyRecordNotice = '';
        }
    }

    private buildDailyRecordPayload(): OwnitTodoDailyRecord {
        const now = new Date().toISOString();
        const existing = this.selectedTodoDailyRecord;
        const id = existing?.id || `todo-daily-${this.user.id}-${this.selectedDate}`;
        const representativeEmoji = (this.dailyRecordDraft.representativeEmoji || existing?.representativeEmoji || '☁').trim() || '☁';
        const diary = (this.dailyRecordDraft.diary || '').trim();
        const totalTimerSeconds = this.selectedTodoTimerSeconds;
        const receipt = this.buildDailyReceiptViewModel(this.selectedDate);
        const routineIds = receipt.data.routineRecords.map((item) => item.id);

        return {
            id,
            userId: this.user.id,
            date: this.selectedDate,
            completedCount: this.selectedCompletedCount,
            totalCount: this.selectedTodos.length,
            categoryStats: this.selectedDailyCategoryStats,
            representativeEmoji,
            diary,
            totalTimerSeconds,
            receiptId: existing?.receiptId || null,
            receiptSnapshot: {
                date: this.selectedDate,
                eventIds: this.receiptEvents.map((event) => event.id),
                personalScheduleEventIds: receipt.data.personalScheduleSummary.items.map((item) => item.id),
                sharedScheduleEventIds: receipt.data.sharedScheduleSummary.items.map((item) => item.id),
                completedTodoIds: this.selectedCompletedTodos.map((todo) => todo.id),
                openTodoIds: this.selectedOpenTodos.map((todo) => todo.id),
                completedTodoTitles: receipt.data.completedTodos.map((item) => item.title),
                unfinishedTodoCount: this.selectedOpenTodos.length,
                routineRecordIds: routineIds,
                routineTitles: receipt.data.routineRecords.map((item) => item.title),
                includeSharedScheduleSummary: receipt.data.includeSharedScheduleSummary,
                dailyMemo: diary,
                completedCount: this.selectedCompletedCount,
                totalCount: this.selectedTodos.length,
                completionRate: this.selectedTodoProgress,
                totalTimerSeconds,
                representativeEmoji,
                moodEmoji: representativeEmoji,
                moodLabel: receipt.data.moodLabel,
                updatedAt: now
            },
            createdAt: existing?.createdAt || now,
            updatedAt: now
        };
    }

    private todoDailyRecordForDate(date: string): OwnitTodoDailyRecord | null {
        return this.todoDailyRecords.find((record) => record.userId === this.user.id && record.date === date) || null;
    }

    private replaceTodoDailyRecord(record: OwnitTodoDailyRecord): OwnitTodoDailyRecord[] {
        return [
            ...this.todoDailyRecords.filter((item) => item.id !== record.id),
            record
        ].sort((a, b) => {
            if (a.userId !== b.userId) return a.userId.localeCompare(b.userId);
            return a.date.localeCompare(b.date);
        });
    }

    private createStickerUploadDraft(): StickerUploadDraftState {
        return {
            name: '',
            dataUrl: '',
            originalDataUrl: '',
            processedDataUrl: '',
            fileName: '',
            fileType: '',
            fileSize: 0,
            originalFileSize: 0,
            width: 0,
            height: 0,
            processedWidth: 0,
            processedHeight: 0,
            backgroundRemovalStatus: 'idle',
            backgroundRemovalError: '',
            borderEnabled: true,
            shadowEnabled: true,
            error: '',
            notice: ''
        };
    }

    private validateStickerUploadFile(file: File): string {
        if (!this.stickerUploadAllowedTypes.includes(file.type)) {
            return 'PNG, JPG, WebP, GIF 이미지만 업로드할 수 있습니다.';
        }
        if (file.size > this.stickerUploadMaxBytes) {
            return `파일 용량은 ${this.stickerUploadLimitLabel}로 제한됩니다.`;
        }
        return '';
    }

    private async prepareStickerUploadImage(file: File): Promise<StickerPreparedImage> {
        const sourceDataUrl = await this.readFileAsDataUrl(file);
        const image = await this.loadImageFromDataUrl(sourceDataUrl);
        const sourceWidth = image.naturalWidth || image.width;
        const sourceHeight = image.naturalHeight || image.height;
        if (!sourceWidth || !sourceHeight) throw new Error('Invalid image size');

        const longestSide = Math.max(sourceWidth, sourceHeight);
        const scale = Math.min(1, this.stickerUploadMaxDimension / longestSide);
        const shouldCompress = scale < 1 || file.size > this.stickerUploadTargetBytes;
        if (!shouldCompress) {
            return {
                dataUrl: sourceDataUrl,
                fileType: file.type || this.inferDataUrlType(sourceDataUrl),
                fileSize: file.size,
                width: sourceWidth,
                height: sourceHeight,
                notice: ''
            };
        }

        const width = Math.max(1, Math.round(sourceWidth * scale));
        const height = Math.max(1, Math.round(sourceHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Canvas is not available');
        context.drawImage(image, 0, 0, width, height);

        const keepsTransparency = file.type === 'image/png' && this.canvasHasTransparency(canvas);
        const outputType = keepsTransparency ? 'image/png' : 'image/jpeg';
        const dataUrl = canvas.toDataURL(outputType, 0.86);
        const notice = `큰 이미지를 ${width}x${height}px로 줄여 처리합니다.`;
        return {
            dataUrl,
            fileType: outputType,
            fileSize: this.dataUrlByteSize(dataUrl),
            width,
            height,
            notice
        };
    }

    private readFileAsDataUrl(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(reader.error || new Error('File read failed'));
            reader.readAsDataURL(file);
        });
    }

    private loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('Image decode failed'));
            image.src = dataUrl;
        });
    }

    private async createTransparentStickerPng(dataUrl: string): Promise<StickerBackgroundRemovalResult> {
        const image = await this.loadImageFromDataUrl(dataUrl);
        const sourceWidth = image.naturalWidth || image.width;
        const sourceHeight = image.naturalHeight || image.height;
        if (!sourceWidth || !sourceHeight) throw new Error('이미지 크기를 확인하지 못했습니다.');

        const scale = Math.min(1, this.stickerBackgroundRemovalMaxDimension / Math.max(sourceWidth, sourceHeight));
        const width = Math.max(1, Math.round(sourceWidth * scale));
        const height = Math.max(1, Math.round(sourceHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('브라우저 Canvas를 사용할 수 없습니다.');

        context.clearRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);
        const imageData = context.getImageData(0, 0, width, height);
        const pixels = imageData.data;
        const edge = this.estimateStickerEdgeBackground(pixels, width, height);
        const totalPixels = width * height;

        if (edge.transparentRatio > 0.08) {
            const output = canvas.toDataURL('image/png');
            return {
                dataUrl: output,
                fileSize: this.dataUrlByteSize(output),
                width,
                height,
                removedRatio: edge.transparentRatio,
                alreadyTransparent: true
            };
        }

        const backgroundMask = this.createStickerBackgroundMask(pixels, width, height, edge);
        let removedCount = 0;
        let opaqueCount = 0;
        for (let index = 0; index < totalPixels; index += 1) {
            const alphaIndex = index * 4 + 3;
            if (pixels[alphaIndex] > 10) opaqueCount += 1;
            if (backgroundMask[index]) {
                pixels[alphaIndex] = 0;
                removedCount += 1;
            }
        }

        const removedRatio = opaqueCount > 0 ? removedCount / opaqueCount : 0;
        if (removedRatio < 0.015) {
            throw new Error('배경과 피사체 색이 너무 비슷해 자동 제거가 어렵습니다.');
        }
        if (removedRatio > 0.92) {
            throw new Error('이미지 대부분이 배경으로 감지되어 원본을 유지했습니다.');
        }

        this.featherStickerCutoutEdge(pixels, backgroundMask, width, height, edge);
        context.putImageData(imageData, 0, 0);
        const output = canvas.toDataURL('image/png');
        return {
            dataUrl: output,
            fileSize: this.dataUrlByteSize(output),
            width,
            height,
            removedRatio,
            alreadyTransparent: false
        };
    }

    private estimateStickerEdgeBackground(pixels: Uint8ClampedArray, width: number, height: number): StickerEdgeBackground {
        const stride = Math.max(1, Math.floor(Math.min(width, height) / 80));
        let count = 0;
        let transparentCount = 0;
        let red = 0;
        let green = 0;
        let blue = 0;
        let redSquare = 0;
        let greenSquare = 0;
        let blueSquare = 0;
        const samplePixel = (x: number, y: number) => {
            const offset = (y * width + x) * 4;
            const alpha = pixels[offset + 3];
            if (alpha < 24) {
                transparentCount += 1;
                return;
            }
            const r = pixels[offset];
            const g = pixels[offset + 1];
            const b = pixels[offset + 2];
            red += r;
            green += g;
            blue += b;
            redSquare += r * r;
            greenSquare += g * g;
            blueSquare += b * b;
            count += 1;
        };

        for (let x = 0; x < width; x += stride) {
            samplePixel(x, 0);
            samplePixel(x, height - 1);
        }
        for (let y = 0; y < height; y += stride) {
            samplePixel(0, y);
            samplePixel(width - 1, y);
        }

        if (count <= 0) {
            return { r: 255, g: 255, b: 255, threshold: 36, transparentRatio: 1 };
        }

        const r = red / count;
        const g = green / count;
        const b = blue / count;
        const variance = (
            Math.sqrt(Math.max(0, redSquare / count - r * r)) +
            Math.sqrt(Math.max(0, greenSquare / count - g * g)) +
            Math.sqrt(Math.max(0, blueSquare / count - b * b))
        ) / 3;
        const sampleCount = count + transparentCount;
        return {
            r,
            g,
            b,
            threshold: this.clamp(30 + variance * 1.25, 34, 92),
            transparentRatio: sampleCount > 0 ? transparentCount / sampleCount : 0
        };
    }

    private createStickerBackgroundMask(pixels: Uint8ClampedArray, width: number, height: number, edge: StickerEdgeBackground): Uint8Array {
        const totalPixels = width * height;
        const mask = new Uint8Array(totalPixels);
        const queue = new Int32Array(totalPixels);
        let head = 0;
        let tail = 0;
        const push = (index: number) => {
            if (mask[index]) return;
            mask[index] = 1;
            queue[tail] = index;
            tail += 1;
        };
        const canRemove = (index: number, thresholdMultiplier = 1) => {
            const offset = index * 4;
            if (pixels[offset + 3] < 24) return true;
            return this.pixelColorDistance(pixels[offset], pixels[offset + 1], pixels[offset + 2], edge.r, edge.g, edge.b) <= edge.threshold * thresholdMultiplier;
        };

        for (let x = 0; x < width; x += 1) {
            const top = x;
            const bottom = (height - 1) * width + x;
            if (canRemove(top, 1.12)) push(top);
            if (canRemove(bottom, 1.12)) push(bottom);
        }
        for (let y = 0; y < height; y += 1) {
            const left = y * width;
            const right = y * width + width - 1;
            if (canRemove(left, 1.12)) push(left);
            if (canRemove(right, 1.12)) push(right);
        }

        while (head < tail) {
            const index = queue[head];
            head += 1;
            const x = index % width;
            const y = Math.floor(index / width);
            if (x > 0 && canRemove(index - 1)) push(index - 1);
            if (x < width - 1 && canRemove(index + 1)) push(index + 1);
            if (y > 0 && canRemove(index - width)) push(index - width);
            if (y < height - 1 && canRemove(index + width)) push(index + width);
        }

        return mask;
    }

    private featherStickerCutoutEdge(pixels: Uint8ClampedArray, mask: Uint8Array, width: number, height: number, edge: StickerEdgeBackground): void {
        for (let y = 1; y < height - 1; y += 1) {
            for (let x = 1; x < width - 1; x += 1) {
                const index = y * width + x;
                if (mask[index]) continue;
                const touchesBackground = mask[index - 1] || mask[index + 1] || mask[index - width] || mask[index + width];
                if (!touchesBackground) continue;
                const offset = index * 4;
                const distance = this.pixelColorDistance(pixels[offset], pixels[offset + 1], pixels[offset + 2], edge.r, edge.g, edge.b);
                if (distance <= edge.threshold * 1.35) {
                    pixels[offset + 3] = Math.round(pixels[offset + 3] * 0.48);
                }
            }
        }
    }

    private pixelColorDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
        const dr = r1 - r2;
        const dg = g1 - g2;
        const db = b1 - b2;
        return Math.sqrt(dr * dr + dg * dg + db * db);
    }

    private canvasHasTransparency(canvas: HTMLCanvasElement): boolean {
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return false;
        const width = canvas.width;
        const height = canvas.height;
        const pixels = context.getImageData(0, 0, width, height).data;
        const stride = Math.max(1, Math.floor((width * height) / 5000));
        for (let index = 0; index < width * height; index += stride) {
            if (pixels[index * 4 + 3] < 250) return true;
        }
        return false;
    }

    private dataUrlByteSize(dataUrl: string): number {
        const base64 = (dataUrl || '').split(',')[1] || '';
        if (!base64) return 0;
        const padding = base64.endsWith('==') ? 2 : (base64.endsWith('=') ? 1 : 0);
        return Math.max(0, Math.floor(base64.length * 3 / 4) - padding);
    }

    private inferDataUrlType(dataUrl: string): string {
        const match = /^data:([^;,]+)/.exec(dataUrl || '');
        return match?.[1] || 'image/png';
    }

    private nextAnimationFrame(): Promise<void> {
        return new Promise((resolve) => {
            if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(() => resolve());
                return;
            }
            setTimeout(resolve, 0);
        });
    }

    private defaultStickerName(fileName: string): string {
        const raw = (fileName || '').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
        return (raw || '내 스티커').slice(0, 24);
    }

    public formatFileSize(bytes: number): string {
        const size = Math.max(0, Number(bytes) || 0);
        if (size >= 1024 * 1024) {
            const value = size / (1024 * 1024);
            return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}MB`;
        }
        if (size >= 1024) return `${Math.round(size / 1024)}KB`;
        return `${Math.round(size)}B`;
    }

    private sortStickerAssets(assets: OwnitStickerAsset[]): OwnitStickerAsset[] {
        return [...assets]
            .filter((asset) => !asset.deletedAt)
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.name.localeCompare(b.name));
    }

    private async renameStickerPlacements(stickerId: string, label: string): Promise<void> {
        const now = new Date().toISOString();
        const nextDecorations = this.decorations.map((decoration) => {
            const hasPlacement = decoration.stickers.some((sticker) => sticker.stickerId === stickerId);
            if (!hasPlacement) return decoration;
            return {
                ...decoration,
                stickers: decoration.stickers.map((sticker) => sticker.stickerId === stickerId ? { ...sticker, label } : sticker),
                updatedAt: now
            };
        });
        nextDecorations
            .filter((decoration, index) => decoration !== this.decorations[index])
            .forEach((decoration) => this.dataRepository.updateDecoration(decoration.id, decoration));
        this.decorations = nextDecorations;
        this.syncDecorationForCurrentMonth();
    }

    private async removeStickerPlacementsByAsset(stickerId: string): Promise<void> {
        const now = new Date().toISOString();
        const nextDecorations = this.decorations.map((decoration) => {
            const stickers = decoration.stickers.filter((sticker) => sticker.stickerId !== stickerId);
            if (stickers.length === decoration.stickers.length) return decoration;
            return { ...decoration, stickers, updatedAt: now };
        });
        nextDecorations
            .filter((decoration, index) => decoration !== this.decorations[index])
            .forEach((decoration) => this.dataRepository.updateDecoration(decoration.id, decoration));
        this.decorations = nextDecorations;
        if (this.selectedSticker?.stickerId === stickerId) this.selectedStickerId = '';
        this.syncDecorationForCurrentMonth();
    }

    private resolveStickerDragContainer(target: HTMLElement | null): HTMLElement | null {
        return target?.closest('.theme-preview, .calendar-sticker-layer') as HTMLElement | null;
    }

    private async updateDraggedStickerPosition(event: PointerEvent, markMoved: boolean): Promise<void> {
        if (!this.stickerDragState) return;
        const rect = this.stickerDragState.container.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return;

        const x = this.clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
        const y = this.clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
        this.stickerDragState.moved = this.stickerDragState.moved || markMoved;
        this.decoration = {
            ...this.decoration,
            stickers: this.decoration.stickers.map((sticker) => sticker.id === this.stickerDragState?.id ? {
                ...sticker,
                x: Math.round(x),
                y: Math.round(y)
            } : sticker)
        };
        await this.service.render();
    }

    private async saveDecoration(patch: Partial<DecorationState>) {
        const now = new Date().toISOString();
        const next = this.normalizeDecoration({
            ...this.decoration,
            ...patch,
            updatedAt: now
        });

        try {
            const updated = this.dataRepository.updateDecoration(next.id, next) || this.dataRepository.createDecoration(next);
            this.decoration = this.normalizeDecoration(updated);
            this.decorations = [
                ...this.decorations.filter((item) => item.id !== this.decoration.id),
                this.decoration
            ];
            if (!this.selectedStickerId && this.decoration.stickers.length > 0) {
                this.selectedStickerId = this.decoration.stickers[0].id;
            }
            this.stickerUploadDraft.error = '';
        } catch (error) {
            this.stickerUploadDraft.error = this.dataErrorMessage(error, '꾸미기 설정을 저장하지 못했습니다.');
        }
        await this.service.render();
    }

    private syncDecorationForCurrentMonth() {
        const target = this.currentMonthKey;
        const existing = this.decorations.find((item) => item.targetType === 'month' && item.target === target);
        if (existing) {
            this.decoration = this.normalizeDecoration(existing);
        } else {
            this.decoration = this.normalizeDecoration(createEmptyDecoration(this.user, this.currentMonth));
        }
        if (this.selectedStickerId && !this.decoration.stickers.some((sticker) => sticker.id === this.selectedStickerId)) {
            this.selectedStickerId = '';
        }
        if (!this.selectedStickerId && this.decoration.stickers.length > 0) {
            this.selectedStickerId = this.decoration.stickers[0].id;
        }
    }

    private normalizeDecoration(decoration: DecorationState): DecorationState {
        const validTheme = this.themeTokens.some((theme) => theme.id === decoration.themeId);
        const validBackground = this.backgroundOptions.some((option) => option.id === decoration.background);
        const normalizedFont = normalizeOwnitFontId(decoration.fontId);
        const themeId = validTheme ? decoration.themeId : OWNIT_DEFAULT_THEME_ID;
        return {
            ...decoration,
            targetType: decoration.targetType || 'month',
            target: decoration.target || this.currentMonthKey,
            background: themeId === OWNIT_CLOUD_DANCER_THEME_ID && validBackground ? decoration.background : 'white',
            themeId,
            fontId: normalizedFont,
            stickers: this.sortStickerPlacements((decoration.stickers || []).map((sticker, index) => ({
                ...sticker,
                label: sticker.label || this.stickerAsset(sticker.stickerId).label,
                target: sticker.target || 'calendar',
                date: (sticker.target || 'calendar') === 'date' ? (sticker.date || this.selectedDate) : null,
                x: this.clamp(Number(sticker.x), 0, 100),
                y: this.clamp(Number(sticker.y), 0, 100),
                size: this.clamp(Number(sticker.size), 18, 72),
                rotation: this.clamp(Number(sticker.rotation), -45, 45),
                zIndex: this.clamp(Number(sticker.zIndex) || index + 1, 1, 99)
            })))
        };
    }

    private stickerAsset(stickerId: string): StickerDisplayAsset {
        const customEmoji = this.decodeCustomEmojiStickerId(stickerId);
        if (customEmoji) {
            return {
                id: stickerId,
                label: `${customEmoji} 직접 입력`,
                emoji: customEmoji,
                categoryId: 'custom',
                iconClass: '',
                color: OWNIT_NEUTRAL_COLORS.text,
                background: 'rgba(255, 255, 255, 0.72)',
                dataUrl: '',
                sourceType: 'emoji'
            };
        }

        const uploaded = this.stickerAssets.find((asset) => asset.id === stickerId && !asset.deletedAt);
        if (uploaded) {
            return {
                id: uploaded.id,
                label: uploaded.name,
                emoji: '',
                categoryId: 'upload',
                iconClass: '',
                color: OWNIT_NEUTRAL_COLORS.text,
                background: 'rgba(255, 255, 255, 0.72)',
                dataUrl: uploaded.dataUrl || uploaded.imageUrl || '',
                sourceType: 'upload',
                renderOptions: uploaded.renderOptions
            };
        }
        const basic = this.basicStickerAssets.find((asset) => asset.id === stickerId) || this.basicStickerAssets[0];
        return {
            ...basic,
            dataUrl: '',
            sourceType: 'basic'
        };
    }

    private sortStickerPlacements(stickers: StickerPlacement[]): StickerPlacement[] {
        return [...stickers].sort((a, b) => {
            const zOrder = (Number(a.zIndex) || 0) - (Number(b.zIndex) || 0);
            if (zOrder !== 0) return zOrder;
            return a.id.localeCompare(b.id);
        });
    }

    private normalizeCustomEmojiInput(value: string): string {
        const input = (value || '').trim();
        if (!input) return '';

        const Segmenter = (Intl as any).Segmenter;
        if (typeof Segmenter === 'function') {
            const segmenter = new Segmenter(undefined, { granularity: 'grapheme' });
            const first = Array.from(segmenter.segment(input), (segment: any) => segment.segment)
                .find((segment) => segment.trim().length > 0);
            return first || '';
        }

        return Array.from(input)[0] || '';
    }

    private isLikelyEmoji(value: string): boolean {
        return /[\u{1F1E6}-\u{1FAFF}\u2600-\u27BF]/u.test(value);
    }

    private encodeCustomEmojiStickerId(emoji: string): string {
        return `${this.customEmojiStickerPrefix}${encodeURIComponent(emoji)}`;
    }

    private decodeCustomEmojiStickerId(stickerId: string): string {
        if (!stickerId.startsWith(this.customEmojiStickerPrefix)) return '';
        try {
            return decodeURIComponent(stickerId.slice(this.customEmojiStickerPrefix.length));
        } catch (error) {
            return '';
        }
    }

    private stickerImageFilterFromOptions(options?: OwnitStickerAsset['renderOptions'] | null): string {
        const filters: string[] = [];
        if (options?.border) {
            filters.push(
                'drop-shadow(0 1px 0 #ffffff)',
                'drop-shadow(1px 0 0 #ffffff)',
                'drop-shadow(0 -1px 0 #ffffff)',
                'drop-shadow(-1px 0 0 #ffffff)'
            );
        }
        if (options?.shadow) {
            filters.push('drop-shadow(0 8px 10px rgba(36, 48, 43, 0.22))');
        }
        return filters.join(' ') || 'none';
    }

    private clamp(value: number, min: number, max: number) {
        if (Number.isNaN(value)) return min;
        return Math.min(max, Math.max(min, value));
    }

    private renderReceiptCanvasToPng(receipt: DailyReceiptViewModel) {
        const styleVars = receipt.style.cssVars || {};
        const ink = styleVars['--receipt-ink'] || '#292622';
        const muted = styleVars['--receipt-muted'] || '#6f6759';
        const accent = styleVars['--receipt-accent'] || '#4d463d';
        const line = styleVars['--receipt-line'] || 'rgba(43, 41, 37, 0.42)';
        const paper = styleVars['--receipt-paper'] || '#fffdf8';
        const scale = 2;
        const width = 420;
        const padding = 30;
        const contentWidth = width - padding * 2;
        const sectionRows = receipt.sections.reduce((total, section) => {
            return total + Math.max(1, section.items.length + section.metrics.length + (section.memo ? 1 : 0));
        }, 0);
        const estimatedHeight = Math.max(930, 520 + receipt.sections.length * 84 + sectionRows * 44 + (receipt.hasData ? 0 : 86));

        const canvas = document.createElement('canvas');
        canvas.width = width * scale;
        canvas.height = estimatedHeight * scale;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('브라우저 canvas를 초기화하지 못했습니다.');
        context.scale(scale, scale);
        context.fillStyle = paper;
        context.fillRect(0, 0, width, estimatedHeight);
        this.drawReceiptPaperTexture(context, width, estimatedHeight, line);

        let y = padding;
        context.textBaseline = 'top';
        context.fillStyle = accent;
        context.font = '700 13px "OwnitNanumGothic", Arial, sans-serif';
        context.fillText('Ownit', padding, y);
        context.fillStyle = muted;
        context.font = '11px "OwnitNanumGothic", Arial, sans-serif';
        context.fillText('DAILY RECEIPT', width - padding - 86, y + 2);
        y += 26;

        context.fillStyle = ink;
        context.font = '700 24px "OwnitNanumGothic", Arial, sans-serif';
        y = this.drawReceiptWrappedText(context, receipt.template.title || 'Daily Receipt', padding, y, contentWidth, 29, 2);
        context.fillStyle = muted;
        context.font = '12px "OwnitNanumGothic", Arial, sans-serif';
        y = this.drawReceiptWrappedText(context, receipt.generatedAtLabel, padding, y + 4, contentWidth, 17, 1) + 16;
        this.drawReceiptDash(context, padding, y, width - padding, line);
        y += 17;

        const metaRows = [
            ['DATE', receipt.formattedDate],
            ['DAY', receipt.dateFullLabel],
            ['OWNER', receipt.ownerLabel],
            ['SAVED', receipt.savedAtLabel]
        ];
        for (const [label, value] of metaRows) {
            context.fillStyle = muted;
            context.font = '11px "OwnitNanumGothic", Arial, sans-serif';
            context.fillText(label, padding, y);
            context.fillStyle = ink;
            context.font = '700 12px "OwnitNanumGothic", Arial, sans-serif';
            this.drawReceiptRightText(context, value, width - padding, y, 220);
            y += 20;
        }

        y += 8;
        context.fillStyle = accent;
        context.font = '28px "Apple Color Emoji", "Noto Color Emoji", sans-serif';
        context.fillText(receipt.data.moodEmoji || receipt.representativeEmoji || '☁', padding, y);
        context.fillStyle = ink;
        context.font = '700 16px "OwnitNanumGothic", Arial, sans-serif';
        context.fillText(receipt.data.moodLabel || receipt.mood || '차분함', padding + 42, y + 2);
        context.fillStyle = muted;
        context.font = '11px "OwnitNanumGothic", Arial, sans-serif';
        context.fillText(`${receipt.completionLabel} · ${receipt.timerLabel}`, padding + 42, y + 23);
        y += 58;

        this.drawReceiptDash(context, padding, y, width - padding, line);
        y += 14;
        const summaryTiles = [
            ['일정', receipt.scheduleSummaryLabel],
            ['Todo', receipt.todoSummaryLabel],
            ['루틴', receipt.routineSummaryLabel]
        ];
        const tileGap = 6;
        const tileWidth = (contentWidth - tileGap * 2) / 3;
        summaryTiles.forEach(([label, value], index) => {
            const x = padding + index * (tileWidth + tileGap);
            context.strokeStyle = line;
            context.lineWidth = 1;
            context.strokeRect(x, y, tileWidth, 44);
            context.fillStyle = muted;
            context.font = '700 10px "OwnitNanumGothic", Arial, sans-serif';
            this.drawReceiptCenteredText(context, label, x + tileWidth / 2, y + 7, tileWidth - 8, 13, 1);
            context.fillStyle = ink;
            context.font = '700 11px "OwnitNanumGothic", Arial, sans-serif';
            this.drawReceiptCenteredText(context, value, x + tileWidth / 2, y + 24, tileWidth - 8, 13, 1);
        });
        y += 58;

        if (!receipt.hasData) {
            this.drawReceiptDash(context, padding, y, width - padding, line);
            y += 16;
            context.fillStyle = accent;
            context.font = '700 11px "OwnitNanumGothic", Arial, sans-serif';
            y = this.drawReceiptCenteredText(context, 'BLANK DAY', width / 2, y, contentWidth, 15, 1);
            context.fillStyle = ink;
            context.font = '700 14px "OwnitNanumGothic", Arial, sans-serif';
            y = this.drawReceiptCenteredText(context, '아직 비어 있는 하루입니다.', width / 2, y + 4, contentWidth, 19, 1);
            context.fillStyle = muted;
            context.font = '11px "OwnitNanumGothic", Arial, sans-serif';
            y = this.drawReceiptCenteredText(context, '날짜와 저장 시간이 담긴 빈 영수증으로 보관합니다.', width / 2, y + 4, contentWidth, 16, 2) + 8;
        }

        for (const section of receipt.sections) {
            this.drawReceiptDash(context, padding, y, width - padding, line);
            y += 13;
            context.fillStyle = muted;
            context.font = '700 11px "OwnitNanumGothic", Arial, sans-serif';
            context.fillText(section.label, padding, y);
            context.fillStyle = ink;
            this.drawReceiptRightText(context, section.countLabel, width - padding, y, 100);
            y += 22;

            if (section.stats.length > 0) {
                context.fillStyle = muted;
                context.font = '10px "OwnitNanumGothic", Arial, sans-serif';
                const statText = section.stats.map((stat) => `${stat.label} ${stat.value}`).join(' · ');
                y = this.drawReceiptWrappedText(context, statText, padding, y, contentWidth, 15, 2) + 4;
            }

            if (section.kind === 'lines') {
                if (section.items.length === 0) {
                    context.fillStyle = muted;
                    context.font = '12px "OwnitNanumGothic", Arial, sans-serif';
                    y = this.drawReceiptWrappedText(context, section.emptyLabel, padding + 14, y, contentWidth - 28, 18, 1) + 8;
                } else {
                    for (const item of section.items) {
                        context.fillStyle = item.accentColor || accent;
                        context.fillRect(padding, y + 3, 3, 29);
                        context.fillStyle = ink;
                        context.font = '700 12px "OwnitNanumGothic", Arial, sans-serif';
                        const titleY = this.drawReceiptWrappedText(context, item.title, padding + 10, y, contentWidth - 92, 17, 2);
                        context.fillStyle = muted;
                        context.font = '10px "OwnitNanumGothic", Arial, sans-serif';
                        const metaY = item.meta ? this.drawReceiptWrappedText(context, item.meta, padding + 10, titleY, contentWidth - 92, 14, 2) : titleY;
                        context.fillStyle = item.tone === 'open' ? accent : ink;
                        context.font = '700 11px "OwnitNanumGothic", Arial, sans-serif';
                        this.drawReceiptRightText(context, item.amount, width - padding, y, 78);
                        y = Math.max(metaY, y + 34) + 8;
                    }
                }
            } else if (section.kind === 'memo') {
                context.fillStyle = ink;
                context.font = '12px "OwnitNanumGothic", Arial, sans-serif';
                y = this.drawReceiptWrappedText(context, section.memo, padding, y, contentWidth, 18, 5) + 8;
            } else {
                for (const metric of section.metrics) {
                    context.fillStyle = metric.important ? ink : muted;
                    context.font = metric.important ? '700 13px "OwnitNanumGothic", Arial, sans-serif' : '12px "OwnitNanumGothic", Arial, sans-serif';
                    context.fillText(metric.label, padding, y);
                    this.drawReceiptRightText(context, metric.value, width - padding, y, 150);
                    y += metric.important ? 22 : 19;
                }
                y += 4;
            }
        }

        y += 8;
        this.drawReceiptDash(context, padding, y, width - padding, ink);
        y += 18;
        this.drawReceiptBarcode(context, width / 2 - 62, y, 124, 34, ink);
        y += 45;
        context.fillStyle = muted;
        context.font = '11px "OwnitNanumGothic", Arial, sans-serif';
        y = this.drawReceiptCenteredText(context, receipt.template.footerText || 'THANK YOU FOR YOUR DAY', width / 2, y, contentWidth, 16, 2);
        y = this.drawReceiptCenteredText(context, `${receipt.savedAtLabel} · Ownit`, width / 2, y + 4, contentWidth, 15, 1) + padding;

        const finalHeight = Math.min(Math.ceil(y), estimatedHeight);
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = width * scale;
        finalCanvas.height = finalHeight * scale;
        const finalContext = finalCanvas.getContext('2d');
        if (!finalContext) throw new Error('영수증 이미지를 생성하지 못했습니다.');
        finalContext.drawImage(canvas, 0, 0, finalCanvas.width, finalCanvas.height, 0, 0, finalCanvas.width, finalCanvas.height);
        return finalCanvas.toDataURL('image/png');
    }

    private drawReceiptPaperTexture(context: CanvasRenderingContext2D, width: number, height: number, color: string) {
        context.save();
        context.globalAlpha = 0.22;
        context.fillStyle = color;
        for (let y = 8; y < height; y += 14) {
            for (let x = 8 + ((y / 14) % 2) * 7; x < width; x += 14) {
                context.fillRect(x, y, 1, 1);
            }
        }
        context.restore();
    }

    private drawReceiptDash(context: CanvasRenderingContext2D, x1: number, y: number, x2: number, color: string) {
        context.save();
        context.strokeStyle = color;
        context.setLineDash([4, 4]);
        context.lineWidth = 1;
        context.beginPath();
        context.moveTo(x1, y);
        context.lineTo(x2, y);
        context.stroke();
        context.restore();
    }

    private drawReceiptBarcode(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, color: string) {
        context.save();
        context.fillStyle = color;
        for (let offset = 0; offset < width; offset += 5) {
            const barWidth = offset % 15 === 0 ? 3 : 1;
            context.fillRect(x + offset, y, barWidth, height);
        }
        context.restore();
    }

    private drawReceiptWrappedText(context: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number, maxLines = 4) {
        const lines = this.receiptTextLines(context, text, maxWidth);
        const visible = lines.slice(0, maxLines);
        visible.forEach((line, index) => {
            const suffix = lines.length > maxLines && index === maxLines - 1 ? '...' : '';
            context.fillText(`${line}${suffix}`, x, y + index * lineHeight);
        });
        return y + Math.max(visible.length, 1) * lineHeight;
    }

    private drawReceiptCenteredText(context: CanvasRenderingContext2D, text: string, centerX: number, y: number, maxWidth: number, lineHeight: number, maxLines = 2) {
        const lines = this.receiptTextLines(context, text, maxWidth).slice(0, maxLines);
        lines.forEach((line, index) => {
            context.fillText(line, centerX - context.measureText(line).width / 2, y + index * lineHeight);
        });
        return y + Math.max(lines.length, 1) * lineHeight;
    }

    private drawReceiptRightText(context: CanvasRenderingContext2D, text: string, rightX: number, y: number, maxWidth: number) {
        const lines = this.receiptTextLines(context, text, maxWidth);
        const line = lines[0] || '';
        context.fillText(line, rightX - context.measureText(line).width, y);
    }

    private receiptTextLines(context: CanvasRenderingContext2D, value: string, maxWidth: number) {
        const text = (value || '').trim();
        if (!text) return [''];
        const words = text.split(/\s+/);
        const lines: string[] = [];
        let line = '';
        const pushLongWord = (word: string) => {
            let segment = '';
            for (const char of Array.from(word)) {
                const next = `${segment}${char}`;
                if (context.measureText(next).width <= maxWidth || !segment) {
                    segment = next;
                } else {
                    lines.push(segment);
                    segment = char;
                }
            }
            line = segment;
        };
        for (const word of words) {
            const next = line ? `${line} ${word}` : word;
            if (context.measureText(next).width <= maxWidth || !line) {
                if (!line && context.measureText(word).width > maxWidth) {
                    pushLongWord(word);
                } else {
                    line = next;
                }
            } else {
                lines.push(line);
                line = '';
                if (context.measureText(word).width > maxWidth) {
                    pushLongWord(word);
                } else {
                    line = word;
                }
            }
        }
        if (line) lines.push(line);
        return lines;
    }

    private async renderElementToPng(element: HTMLElement) {
        const rect = element.getBoundingClientRect();
        const width = Math.ceil(rect.width);
        const height = Math.ceil(rect.height);
        if (width <= 0 || height <= 0) throw new Error('영수증 미리보기 크기를 계산하지 못했습니다.');

        const clone = element.cloneNode(true) as HTMLElement;
        this.copyComputedStyles(element, clone);
        clone.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
        clone.style.width = `${width}px`;
        clone.style.height = `${height}px`;
        clone.style.margin = '0';
        clone.style.transform = 'none';

        const wrapper = document.createElement('div');
        wrapper.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
        wrapper.style.width = `${width}px`;
        wrapper.style.height = `${height}px`;
        wrapper.appendChild(clone);

        const serialized = new XMLSerializer().serializeToString(wrapper);
        const svg = [
            `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
            `<foreignObject width="100%" height="100%">${serialized}</foreignObject>`,
            '</svg>'
        ].join('');

        return this.svgToPngDataUrl(svg, width, height, 2);
    }

    private renderReceiptPreviewToPng(element: HTMLElement): Promise<string> {
        return Promise.race([
            this.renderElementToPng(element),
            new Promise<string>((resolve) => window.setTimeout(() => resolve(''), 1800))
        ]);
    }

    private copyComputedStyles(source: Element, target: Element) {
        const computed = window.getComputedStyle(source);
        const targetElement = target as HTMLElement;
        Array.from(computed).forEach((property) => {
            targetElement.style.setProperty(property, computed.getPropertyValue(property), computed.getPropertyPriority(property));
        });

        Array.from(source.children).forEach((child, index) => {
            const targetChild = target.children.item(index);
            if (targetChild) this.copyComputedStyles(child, targetChild);
        });
    }

    private svgToPngDataUrl(svg: string, width: number, height: number, scale: number): Promise<string> {
        return new Promise((resolve, reject) => {
            const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const image = new Image();

            image.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = width * scale;
                    canvas.height = height * scale;
                    const context = canvas.getContext('2d');
                    if (!context) {
                        reject(new Error('브라우저 canvas를 초기화하지 못했습니다.'));
                        return;
                    }
                    context.scale(scale, scale);
                    context.drawImage(image, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/png'));
                } catch (error) {
                    reject(error instanceof Error ? error : new Error('영수증 이미지를 생성하지 못했습니다.'));
                } finally {
                    URL.revokeObjectURL(url);
                }
            };

            image.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('영수증 이미지를 생성하지 못했습니다.'));
            };

            image.src = url;
        });
    }

    private triggerReceiptDownload(dataUrl: string, filename: string) {
        let href = dataUrl;
        let objectUrl = '';
        try {
            const blob = this.receiptDataUrlToBlob(dataUrl);
            objectUrl = URL.createObjectURL(blob);
            href = objectUrl;
        } catch (error) {
            href = dataUrl;
        }
        const link = document.createElement('a');
        link.href = href;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        if (objectUrl) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }

    private receiptDataUrlToBlob(dataUrl: string): Blob {
        const [header, payload] = dataUrl.split(',');
        if (!header || !payload) throw new Error('PNG 데이터 형식이 올바르지 않습니다.');
        const mime = /data:([^;]+)/.exec(header)?.[1] || 'image/png';
        const binary = header.includes(';base64') ? atob(payload) : decodeURIComponent(payload);
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index++) {
            bytes[index] = binary.charCodeAt(index);
        }
        return new Blob([bytes], { type: mime });
    }

    private buildInlineTodoPayload(): Pick<OwnitTodo, 'title' | 'date' | 'memo' | 'goalId' | 'categoryId' | 'category' | 'color' | 'scope' | 'visibility' | 'updatedAt'> | null {
        const title = (this.inlineTodoDraft.title || '').trim();
        const goal = this.inlineTodoDraft.goalId
            ? this.todoGoalById(this.inlineTodoDraft.goalId)
            : this.activeTodoGoals.find((item) => item.name === this.inlineTodoDraft.category) || null;

        if (!title) {
            this.todoFormError = '할 일 제목을 입력해 주세요.';
            return null;
        }

        return {
            title,
            date: this.selectedDate,
            memo: '',
            goalId: goal?.id || null,
            categoryId: goal?.categoryId || null,
            category: goal?.name || this.inlineTodoDraft.category || this.uncategorizedTodoLabel,
            color: goal?.color || this.inlineTodoDraft.color || this.uncategorizedTodoColor,
            scope: goal?.visibility === 'group' ? 'shared' : 'personal',
            visibility: goal?.visibility === 'public' ? 'public' : (goal?.visibility === 'group' ? 'group' : 'private'),
            updatedAt: new Date().toISOString()
        };
    }

    private buildTodoReminderPayloadFromForm(todoDate: string): ReminderPayload | null {
        const preset = this.normalizeReminderPreset(this.todoForm.reminderPreset);
        if (preset === 'none') return null;

        const reminderDate = preset === 'custom' ? this.todoForm.reminderDate : todoDate;
        const reminderTime = this.normalizeReminderTime(this.todoForm.reminderTime || this.defaultReminderTime());
        const offsetMinutes = this.reminderOffsetMinutesForPreset(preset) || 0;
        if (!reminderDate) {
            this.todoFormError = '알림 날짜를 선택해 주세요.';
            return null;
        }
        if (!this.isValidReminderTime(reminderTime)) {
            this.todoFormError = '알림 시간을 선택해 주세요.';
            return null;
        }

        const reminderAt = this.reminderAtFromBase(reminderDate, reminderTime, offsetMinutes);
        if (!reminderAt) {
            this.todoFormError = '알림 날짜와 시간을 확인해 주세요.';
            return null;
        }
        if (Date.parse(reminderAt) < Date.now() - 60000) {
            this.todoFormError = '현재 이후의 알림 시간을 선택해 주세요.';
            return null;
        }

        const reminderId = this.editingTodoId ? `reminder-${this.editingTodoId}` : this.createId('reminder');
        return {
            reminderAt,
            reminderRule: {
                kind: 'custom',
                date: reminderDate,
                time: reminderTime,
                nextAt: reminderAt,
                timezone: this.localTimezone(),
                userId: this.user.id,
                enabled: true,
                reminderPreset: preset,
                offsetMinutes
            },
            reminder: {
                id: reminderId,
                offsetMinutes,
                channel: 'app',
                enabled: true
            }
        };
    }

    private buildTodoPayload(): TodoPayload | null {
        this.todoFormError = '';
        const title = (this.todoForm.title || '').trim();
        const date = this.todoForm.date;
        const goal = this.todoForm.goalId ? this.todoGoalById(this.todoForm.goalId) : null;
        const category = goal?.name || (this.todoForm.category || '').trim() || this.uncategorizedTodoLabel;
        const color = goal?.color || this.todoForm.color || this.uncategorizedTodoColor;

        if (!title) {
            this.todoFormError = '할 일 제목을 입력해 주세요.';
            return null;
        }
        if (!date) {
            this.todoFormError = '할 일 날짜를 선택해 주세요.';
            return null;
        }

        const reminderPayload = this.buildTodoReminderPayloadFromForm(date);
        if (this.todoFormError) return null;

        return {
            title,
            date,
            memo: (this.todoForm.memo || '').trim(),
            goalId: goal?.id || null,
            categoryId: goal?.categoryId || null,
            category,
            color,
            scope: this.todoForm.scope || 'personal',
            visibility: goal?.visibility === 'public' ? 'public' : ((this.todoForm.scope || 'personal') === 'shared' || goal?.visibility === 'group' ? 'group' : 'private'),
            reminders: reminderPayload ? [reminderPayload.reminder] : [],
            reminderAt: reminderPayload?.reminderAt || null,
            reminderRule: reminderPayload?.reminderRule || null,
            updatedAt: new Date().toISOString()
        };
    }

    private todoReminderPatchForDate(todo: OwnitTodo, date: string): Partial<Pick<OwnitTodo, 'reminderAt' | 'reminderRule' | 'reminders'>> {
        const hasReminder = Boolean(todo.reminderAt || todo.reminderRule?.enabled || (todo.reminders || []).some((reminder) => reminder.enabled));
        if (!hasReminder) return {};

        const offsetMinutes = this.todoReminderOffsetMinutes(todo);
        const time = this.todoReminderTime(todo);
        const reminderAt = this.reminderAtFromBase(date, time, offsetMinutes);
        if (!reminderAt) {
            return {
                reminderAt: null,
                reminderRule: null,
                reminders: []
            };
        }

        const reminder = (todo.reminders || []).find((item) => item.enabled) || null;
        return {
            reminderAt,
            reminderRule: {
                kind: 'custom',
                date,
                time,
                nextAt: reminderAt,
                timezone: this.localTimezone(),
                userId: todo.reminderRule?.userId || this.user.id,
                enabled: true,
                reminderPreset: this.todoReminderPreset(todo),
                offsetMinutes
            },
            reminders: [{
                id: reminder?.id || `reminder-${todo.id}`,
                offsetMinutes,
                channel: 'app' as const,
                enabled: true
            }]
        };
    }

    private createTodoFromPayload(payload: TodoPayload): OwnitTodo {
        const now = new Date().toISOString();
        const created: OwnitTodo = {
            id: this.createId('todo'),
            completed: false,
            completedAt: null,
            routineId: null,
            order: this.nextTodoOrderForDate(payload.date),
            authorId: this.user.id,
            assigneeId: this.user.id,
            createdBy: this.user.id,
            assignedTo: this.user.id,
            repeatRule: null,
            reminders: [],
            reminderAt: null,
            reminderRule: null,
            movedFromDate: null,
            movedToDate: null,
            timerSessionIds: [],
            photoProofUrl: '',
            timerSeconds: 0,
            diaryId: null,
            reactionId: null,
            completionRecordId: null,
            createdAt: now,
            ...payload
        };
        return this.dataRepository.createTodo(created);
    }

    private updateTodoFromAction(patch: Partial<OwnitTodo>): OwnitTodo | null {
        const todo = this.todoActionTodo;
        if (!todo) return null;
        if (this.isRoutineOccurrenceTodo(todo)) {
            this.todoActionError = '루틴 발생 항목은 루틴 등록 화면에서 수정해 주세요.';
            return null;
        }
        if (!this.canEditTodo(todo)) {
            this.todoActionError = '할 일을 수정할 권한이 없습니다.';
            return null;
        }

        const updated = this.dataRepository.updateTodo(todo.id, {
            ...patch,
            updatedAt: new Date().toISOString()
        });
        if (!updated) {
            this.todoActionError = '수정할 할 일을 찾지 못했습니다.';
            return null;
        }

        this.todos = this.sortTodos(this.todos.map((item) => item.id === updated.id ? updated : item));
        this.todoActionTodoId = updated.id;
        this.refreshTodoRecordsFromRepository();
        this.buildMonth();
        this.refreshReminderSchedules();
        return updated;
    }

    private async duplicateTodoFromAction(todo: OwnitTodo, targetDate: string) {
        if (this.isRoutineOccurrenceTodo(todo)) {
            this.todoActionError = '루틴 발생 항목은 같은 날짜로 복사할 수 없습니다.';
            return;
        }
        if (!this.canCreateTodoAction) {
            this.todoActionError = '할 일을 추가할 권한이 없습니다.';
            return;
        }
        if (!targetDate) {
            this.todoActionError = '복사할 날짜를 선택해 주세요.';
            return;
        }

        const saved = this.createTodoFromPayload({
            title: todo.title,
            date: targetDate,
            memo: todo.memo,
            goalId: todo.goalId || null,
            categoryId: todo.categoryId || null,
            category: todo.category || this.uncategorizedTodoLabel,
            color: todo.color || this.uncategorizedTodoColor,
            scope: todo.scope || 'personal',
            visibility: todo.visibility || ((todo.scope || 'personal') === 'shared' ? 'group' : 'private'),
            updatedAt: new Date().toISOString()
        });
        this.todos = this.sortTodos([...this.todos, saved]);
        this.refreshTodoRecordsFromRepository();
        this.todoActionDraft = this.createTodoActionDraft(todo);
        this.todoActionMode = 'menu';
        this.todoActionNotice = `${this.dateLabel(targetDate)}에 같은 할 일을 만들었습니다.`;
        this.buildMonth();
    }

    private toDateTimeLocalValue(value: string | null) {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value.slice(0, 16);
        const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
        return local.toISOString().slice(0, 16);
    }

    private dateTimeLocalToIso(value: string) {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return date.toISOString();
    }

    private lockTodoActionScroll() {
        if (typeof document === 'undefined') return;
        if (this.previousBodyOverflow === null) {
            this.previousBodyOverflow = document.body.style.overflow || '';
        }
        document.body.style.overflow = 'hidden';
    }

    private unlockTodoActionScroll() {
        if (typeof document === 'undefined') return;
        if (this.previousBodyOverflow === null) return;
        document.body.style.overflow = this.previousBodyOverflow;
        this.previousBodyOverflow = null;
    }

    private buildEventPayload(): EventPayload | null {
        this.eventFormError = '';
        const title = (this.eventForm.title || '').trim();
        const description = (this.eventForm.description || '').trim();
        const startDate = this.eventForm.startDate;
        const endDate = this.eventForm.endDate;
        const calendar = this.calendarById(this.eventForm.calendarId);

        if (!title) {
            this.eventFormError = '일정 제목을 입력해 주세요.';
            return null;
        }
        if (!startDate || !endDate) {
            this.eventFormError = '시작일과 종료일을 선택해 주세요.';
            return null;
        }
        if (endDate < startDate) {
            this.eventFormError = '종료일은 시작일보다 빠를 수 없습니다.';
            return null;
        }
        if (!calendar) {
            this.eventFormError = '캘린더를 선택해 주세요.';
            return null;
        }
        if (calendar.type !== this.eventForm.scope) {
            this.eventFormError = '개인/공유 구분에 맞는 캘린더를 선택해 주세요.';
            return null;
        }
        if (!this.eventFormCalendarOptions.some((item) => item.id === calendar.id)) {
            this.eventFormError = calendar.type === 'shared'
                ? '권한이 있는 공유 캘린더에만 일정을 만들 수 있습니다.'
                : '개인 캘린더에만 개인 일정을 만들 수 있습니다.';
            return null;
        }
        const assigneeId = calendar.type === 'shared' ? this.defaultSharedAssigneeId(this.eventForm.assigneeId, calendar) : this.user.id;
        if (calendar.type === 'shared' && !assigneeId) {
            this.eventFormError = '공유 일정 담당자를 선택해 주세요.';
            return null;
        }

        const startTime = this.eventForm.allDay ? null : (this.eventForm.startTime || null);
        const endTime = this.eventForm.allDay ? null : (this.eventForm.endTime || null);
        if (!this.eventForm.allDay && startDate === endDate && startTime && endTime && endTime <= startTime) {
            this.eventFormError = '종료 시간은 시작 시간보다 늦어야 합니다.';
            return null;
        }

        const reminders = this.buildEventReminderSettings();
        if (!reminders) return null;

        return {
            title,
            description,
            startDate,
            endDate,
            startTime,
            endTime,
            allDay: this.eventForm.allDay,
            color: this.eventForm.color || calendar.color,
            calendarId: calendar.id,
            assigneeId,
            category: calendar.type === 'shared' ? '공유' : '개인',
            visibility: calendar.type === 'shared' ? (calendar.visibility || 'group') : 'private',
            reminders,
            completed: this.eventForm.completed === true,
            updatedAt: new Date().toISOString()
        };
    }

    private buildEventReminderSettings(): ReminderSetting[] | null {
        const preset = this.normalizeReminderPreset(this.eventForm.reminderPreset);
        const offsetMinutes = this.eventReminderOffsetMinutesFromForm(preset);
        if (offsetMinutes === null) {
            return preset === 'none' ? [] : null;
        }
        return [{
            id: this.editingEventId ? `reminder-${this.editingEventId}` : this.createId('reminder'),
            offsetMinutes,
            channel: 'app',
            enabled: true
        }];
    }

    private eventReminderOffsetMinutesFromForm(preset: ReminderPreset): number | null {
        if (preset === 'none') return null;
        if (preset !== 'custom') return this.reminderOffsetMinutesForPreset(preset);

        const eventStartAt = this.localDateTimeToIso(this.eventForm.startDate, this.eventForm.allDay ? '09:00' : (this.eventForm.startTime || '09:00'));
        const customAt = this.localDateTimeToIso(this.eventForm.reminderCustomDate, this.eventForm.reminderCustomTime);
        if (!eventStartAt || !customAt) {
            this.eventFormError = '사용자 지정 알림 날짜와 시간을 선택해 주세요.';
            return null;
        }

        const startMs = Date.parse(eventStartAt);
        const customMs = Date.parse(customAt);
        if (Number.isNaN(startMs) || Number.isNaN(customMs)) {
            this.eventFormError = '사용자 지정 알림 날짜와 시간을 확인해 주세요.';
            return null;
        }
        if (customMs > startMs) {
            this.eventFormError = '알림 시간은 일정 시작 이후로 설정할 수 없습니다.';
            return null;
        }
        if (customMs < Date.now() - 60000) {
            this.eventFormError = '현재 이후의 알림 시간을 선택해 주세요.';
            return null;
        }

        return Math.round((startMs - customMs) / 60000);
    }

    private defaultSharedAssigneeId(preferredId = '', calendar: OwnitCalendar | null = this.eventFormCalendar) {
        const group = this.groupForCalendar(calendar) || this.selectedSharedGroup;
        const activeMembers = this.sortGroupMembers(group.members || []).filter((member) => member.status === 'active');
        const memberIds = activeMembers.map((member) => member.userId);
        if (preferredId && memberIds.includes(preferredId)) return preferredId;
        if (memberIds.includes(this.user.id)) return this.user.id;
        return activeMembers[0]?.userId || this.user.id;
    }

    private memberNameByUserId(userId: string, group: OwnitGroup | null = null) {
        const member = (group?.members || this.groups.flatMap((item) => item.members)).find((item) => item.userId === userId);
        if (member) return member.name;
        if (userId === this.user.id) return this.user.nickname || this.user.name || '나';
        return userId || '미지정';
    }

    private sharedEventsForRange(start: string, end: string) {
        return this.sortEvents(this.sharedEvents.filter((event) => event.startDate <= end && event.endDate >= start));
    }

    private sharedEventMatchesAssigneeFilter(event: OwnitEvent) {
        if (this.sharedAssigneeFilter === 'all') return true;
        if (this.sharedAssigneeFilter === 'unassigned') return !event.assigneeId;
        return event.assigneeId === this.sharedAssigneeFilter;
    }

    private sharedEventMatchesCompletionFilter(event: OwnitEvent) {
        if (this.sharedCompletionFilter === 'completed') return event.completed === true;
        if (this.sharedCompletionFilter === 'open') return event.completed !== true;
        return true;
    }

    private resetSharedFiltersForSelection() {
        this.sharedAssigneeFilter = 'all';
        this.sharedCompletionFilter = 'all';
    }

    private weekRangeFor(dateKey: string) {
        const date = this.fromDateKey(dateKey);
        const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
        const endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 6);
        return {
            start: this.toDateKey(startDate),
            end: this.toDateKey(endDate)
        };
    }

    private shortDateLabel(dateKey: string) {
        const date = this.fromDateKey(dateKey);
        return `${date.getMonth() + 1}/${date.getDate()}`;
    }

    private buildMateFeedItems(): MateFeedItem[] {
        return [];
    }

    private loadFollowedMateIds(): string[] {
        const defaults = this.defaultFollowedMateIds();
        const parsed = this.dataRepository.loadLocalState<string[]>(this.mateFollowStorageKeyForUser(), defaults);
        if (!Array.isArray(parsed)) return defaults;
        const visibleMateIds = new Set(this.visibleMates.map((mate) => mate.id));
        return parsed.filter((id) => typeof id === 'string' && visibleMateIds.has(id));
    }

    private saveFollowedMateIds(): void {
        this.dataRepository.saveLocalState(this.mateFollowStorageKeyForUser(), this.followedMateIds);
    }

    private loadDefaultTodoGoalId(): string {
        return this.normalizeStoredTodoGoalId(this.dataRepository.loadLocalState<string>(this.defaultTodoGoalStorageKeyForUser(), ''));
    }

    private saveDefaultTodoGoalId(): void {
        this.defaultTodoGoalId = this.normalizeStoredTodoGoalId(this.defaultTodoGoalId);
        this.dataRepository.saveLocalState(this.defaultTodoGoalStorageKeyForUser(), this.defaultTodoGoalId);
    }

    private loadRecentTodoGoalId(): string {
        return this.normalizeStoredTodoGoalId(this.dataRepository.loadLocalState<string>(this.recentTodoGoalStorageKeyForUser(), ''));
    }

    private saveRecentTodoGoalId(): void {
        this.recentTodoGoalId = this.normalizeStoredTodoGoalId(this.recentTodoGoalId);
        this.dataRepository.saveLocalState(this.recentTodoGoalStorageKeyForUser(), this.recentTodoGoalId);
    }

    private saveTodoGoalPreferences(): void {
        this.saveDefaultTodoGoalId();
        this.saveRecentTodoGoalId();
    }

    private normalizeStoredTodoGoalId(goalId: string | null | undefined): string {
        if (!goalId || typeof goalId !== 'string') return '';
        const goal = this.todoGoals.find((item) => item.id === goalId) || null;
        const resolved = goal ? this.todoGoalWithCategory(goal) : null;
        return resolved && resolved.status !== 'archived' && !resolved.locked ? resolved.id : '';
    }

    private rememberTodoGoalUse(goalId: string | null | undefined): void {
        const normalized = this.normalizeStoredTodoGoalId(goalId);
        if (!normalized) return;
        this.recentTodoGoalId = normalized;
        this.saveRecentTodoGoalId();
    }

    private loadTodoCalendarDisplayOptions(): TodoCalendarDisplayOptions {
        const defaults = this.createDefaultTodoCalendarDisplayOptions();
        const parsed = this.dataRepository.loadLocalState<Partial<TodoCalendarDisplayOptions>>(this.todoCalendarDisplayStorageKeyForUser(), defaults);
        return this.normalizeTodoCalendarDisplayOptions(parsed);
    }

    private saveTodoCalendarDisplayOptions(): void {
        this.dataRepository.saveLocalState(this.todoCalendarDisplayStorageKeyForUser(), this.todoCalendarDisplayOptions);
    }

    private loadReceiptTemplate(): DailyReceiptTemplateState {
        const defaults = this.createDefaultReceiptTemplate();
        const parsed = this.dataRepository.loadLocalState<Partial<DailyReceiptTemplateState>>(this.receiptTemplateStorageKeyForUser(), defaults);
        return this.normalizeReceiptTemplate(parsed);
    }

    private saveReceiptTemplateState(): void {
        this.receiptTemplate = this.normalizeReceiptTemplate(this.receiptTemplate);
        this.dataRepository.saveLocalState(this.receiptTemplateStorageKeyForUser(), this.receiptTemplate);
    }

    private loadExternalOAuthConnections(): ExternalOAuthConnection[] {
        const parsed = this.dataRepository.loadLocalState<Partial<ExternalOAuthConnection>[]>(this.externalOAuthStorageKeyForUser(), []);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((item) => {
                const provider = item?.provider;
                return isExternalOAuthProviderId(provider) && Boolean(this.externalOAuthConfig(provider));
            })
            .map((item) => this.normalizeExternalOAuthConnection(item));
    }

    private saveExternalOAuthConnections(): void {
        this.dataRepository.saveLocalState(this.externalOAuthStorageKeyForUser(), this.externalOAuthConnections);
    }

    private loadExternalSyncPolicies(): ExternalCalendarSyncPolicy[] {
        const parsed = this.dataRepository.loadLocalState<Partial<ExternalCalendarSyncPolicy>[]>(this.externalSyncPolicyStorageKeyForUser(), []);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((policy) => this.normalizeExternalSyncPolicy(policy))
            .filter((policy): policy is ExternalCalendarSyncPolicy => Boolean(policy));
    }

    private saveExternalSyncPolicies(): void {
        this.dataRepository.saveLocalState(this.externalSyncPolicyStorageKeyForUser(), this.externalSyncPolicies);
    }

    private loadExternalEventMappings(): ExternalEventMapping[] {
        const parsed = this.dataRepository.loadLocalState<Partial<ExternalEventMapping>[]>(this.externalMappingStorageKeyForUser(), []);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((mapping) => this.normalizeExternalEventMapping(mapping))
            .filter((mapping): mapping is ExternalEventMapping => Boolean(mapping));
    }

    private saveExternalEventMappings(): void {
        this.dataRepository.saveLocalState(this.externalMappingStorageKeyForUser(), this.externalEventMappings);
    }

    private normalizeExternalSyncPolicy(policy: Partial<ExternalCalendarSyncPolicy> | null | undefined): ExternalCalendarSyncPolicy | null {
        const calendarId = typeof policy?.calendarId === 'string' ? policy.calendarId : '';
        const externalCalendarId = typeof policy?.externalCalendarId === 'string' ? policy.externalCalendarId : '';
        const source = isExternalCalendarProviderId(policy?.source)
            ? policy.source
            : (isExternalCalendarProviderId(policy?.provider) ? policy.provider : null);
        if (!calendarId || !source) return null;
        const direction = normalizeExternalSyncDirection(policy?.direction);
        const writePermission = normalizeExternalWritePermission(policy?.writePermission);
        const now = new Date().toISOString();
        const needsWriteScope = direction !== 'import_only' && writePermission !== 'granted';
        return {
            calendarId,
            externalCalendarId,
            source,
            provider: source,
            direction,
            deletionPolicy: normalizeExternalDeletionPolicy(policy?.deletionPolicy),
            writePermission,
            writeScope: typeof policy?.writeScope === 'string' ? policy.writeScope : '',
            writeRequestedAt: typeof policy?.writeRequestedAt === 'string' ? policy.writeRequestedAt : null,
            lastSyncedAt: typeof policy?.lastSyncedAt === 'string' ? policy.lastSyncedAt : null,
            syncStatus: needsWriteScope ? 'write_scope_required' : normalizeExternalSyncStatus(policy?.syncStatus || 'synced'),
            syncError: needsWriteScope ? '외부 캘린더 쓰기 권한을 명시적으로 켜야 합니다.' : (typeof policy?.syncError === 'string' ? policy.syncError : null),
            updatedAt: typeof policy?.updatedAt === 'string' ? policy.updatedAt : now
        };
    }

    private normalizeExternalEventMapping(mapping: Partial<ExternalEventMapping> | null | undefined): ExternalEventMapping | null {
        const source = isExternalCalendarProviderId(mapping?.provider)
            ? mapping.provider
            : (isExternalCalendarProviderId(mapping?.source) ? mapping.source : null);
        if (!mapping?.id || !mapping.eventId || !mapping.calendarId || !mapping.externalEventId || !mapping.externalCalendarId || !source) return null;
        const now = new Date().toISOString();
        return {
            id: mapping.id,
            eventId: mapping.eventId,
            calendarId: mapping.calendarId,
            source,
            provider: source,
            calendarName: typeof mapping.calendarName === 'string' && mapping.calendarName.trim() ? mapping.calendarName : this.calendarById(mapping.calendarId)?.name || '외부 캘린더',
            externalEventId: mapping.externalEventId,
            externalCalendarId: mapping.externalCalendarId,
            externalRecurringEventId: mapping.externalRecurringEventId || null,
            syncDirection: normalizeExternalSyncDirection(mapping.syncDirection),
            lastSyncedAt: typeof mapping.lastSyncedAt === 'string' ? mapping.lastSyncedAt : null,
            externalUpdatedAt: typeof mapping.externalUpdatedAt === 'string' ? mapping.externalUpdatedAt : null,
            localUpdatedAt: typeof mapping.localUpdatedAt === 'string' ? mapping.localUpdatedAt : null,
            syncStatus: normalizeExternalSyncStatus(mapping.syncStatus),
            syncError: typeof mapping.syncError === 'string' ? mapping.syncError : null,
            allDay: mapping.allDay === true,
            timezone: typeof mapping.timezone === 'string' && mapping.timezone ? mapping.timezone : this.localTimezone(),
            repeatRule: mapping.repeatRule || null,
            localDeletedAt: typeof mapping.localDeletedAt === 'string' ? mapping.localDeletedAt : null,
            externalDeletedAt: typeof mapping.externalDeletedAt === 'string' ? mapping.externalDeletedAt : null,
            ownerId: mapping.ownerId || this.user.id,
            groupId: mapping.groupId || null,
            deletedAt: mapping.deletedAt || null,
            syncVersion: Math.max(1, Number(mapping.syncVersion) || 1),
            createdAt: mapping.createdAt || now,
            updatedAt: mapping.updatedAt || now
        };
    }

    private ensureExternalSyncPolicy(calendar: OwnitCalendar): ExternalCalendarSyncPolicy {
        const source = isExternalCalendarProviderId(calendar.provider)
            ? calendar.provider
            : (isExternalCalendarProviderId(calendar.source) ? calendar.source : 'ics_url');
        const existing = this.externalSyncPolicies.find((policy) => policy.calendarId === calendar.id);
        if (existing) {
            const normalized = this.normalizeExternalSyncPolicy(existing);
            if (normalized) return normalized;
        }
        const now = new Date().toISOString();
        const policy: ExternalCalendarSyncPolicy = {
            calendarId: calendar.id,
            externalCalendarId: calendar.externalId || calendar.id,
            source,
            provider: source,
            direction: normalizeExternalSyncDirection(calendar.syncDirection || 'import_only'),
            deletionPolicy: normalizeExternalDeletionPolicy(calendar.syncDeletionPolicy || 'mark_deleted_only'),
            writePermission: normalizeExternalWritePermission(calendar.externalWritePermission || 'not_requested'),
            writeScope: calendar.externalWriteScope || '',
            writeRequestedAt: null,
            lastSyncedAt: calendar.lastSyncedAt || null,
            syncStatus: normalizeExternalSyncStatus(calendar.syncStatus || 'synced'),
            syncError: calendar.syncError || null,
            updatedAt: now
        };
        this.upsertExternalSyncPolicy(policy);
        return policy;
    }

    private upsertExternalSyncPolicy(policy: ExternalCalendarSyncPolicy): void {
        const normalized = this.normalizeExternalSyncPolicy(policy);
        if (!normalized) return;
        const index = this.externalSyncPolicies.findIndex((item) => item.calendarId === normalized.calendarId);
        if (index >= 0) {
            this.externalSyncPolicies[index] = normalized;
            return;
        }
        this.externalSyncPolicies.push(normalized);
    }

    private upsertExternalEventMapping(mapping: ExternalEventMapping): void {
        const normalized = this.normalizeExternalEventMapping(mapping);
        if (!normalized) return;
        const index = this.externalEventMappings.findIndex((item) => item.id === normalized.id);
        if (index >= 0) {
            this.externalEventMappings[index] = normalized;
            return;
        }
        this.externalEventMappings.push(normalized);
    }

    private touchExternalMappingForLocalEvent(event: OwnitEvent, deletedAt: string | null = null): void {
        const mapping = this.externalEventMappings.find((item) => item.eventId === event.id);
        if (!mapping) return;
        const calendar = this.calendarById(mapping.calendarId);
        const policy = calendar ? this.ensureExternalSyncPolicy(calendar) : null;
        const now = new Date().toISOString();
        const canExport = policy?.direction !== 'import_only' && policy?.writePermission === 'granted';
        const needsWriteScope = policy?.direction !== 'import_only' && policy?.writePermission !== 'granted';
        const nextStatus: CalendarSyncStatus = deletedAt
            ? 'deleted_local'
            : needsWriteScope
                ? 'write_scope_required'
                : canExport
                    ? 'pending_export'
                    : 'synced';
        this.upsertExternalEventMapping({
            ...mapping,
            localUpdatedAt: event.updatedAt || now,
            localDeletedAt: deletedAt,
            syncStatus: nextStatus,
            syncError: needsWriteScope ? '외부 캘린더 쓰기 권한 승인 전까지 내보내기를 중단합니다.' : null,
            updatedAt: now
        });
        if (policy && policy.direction !== 'import_only') {
            this.upsertExternalSyncPolicy({
                ...policy,
                syncStatus: needsWriteScope ? 'write_scope_required' : 'pending_export',
                syncError: needsWriteScope ? '외부 캘린더 쓰기 권한 승인 전까지 내보내기를 중단합니다.' : null,
                updatedAt: now
            });
            this.saveExternalSyncPolicies();
        }
        this.saveExternalEventMappings();
    }

    private loadExternalOAuthPendingState(): ExternalOAuthPendingState | null {
        const parsed = this.dataRepository.loadLocalState<Partial<ExternalOAuthPendingState> | null>(this.externalOAuthPendingStorageKeyForUser(), null);
        if (!parsed || !isExternalOAuthProviderId(parsed.provider) || typeof parsed.state !== 'string') return null;
        return {
            provider: parsed.provider,
            state: parsed.state,
            redirectUri: typeof parsed.redirectUri === 'string' ? parsed.redirectUri : '',
            scope: typeof parsed.scope === 'string' ? parsed.scope : '',
            createdAt: typeof parsed.createdAt === 'string' ? parsed.createdAt : new Date().toISOString()
        };
    }

    private saveExternalOAuthPendingState(state: ExternalOAuthPendingState): void {
        this.dataRepository.saveLocalState(this.externalOAuthPendingStorageKeyForUser(), state);
    }

    private clearExternalOAuthPendingState(): void {
        this.dataRepository.saveLocalState(this.externalOAuthPendingStorageKeyForUser(), null);
    }

    private normalizeExternalOAuthConnection(connection: Partial<ExternalOAuthConnection>): ExternalOAuthConnection {
        const provider = isExternalOAuthProviderId(connection.provider) ? connection.provider : 'google';
        const status = this.normalizeExternalOAuthConnectionStatus(connection.status);
        const config = this.externalOAuthConfig(provider);
        return createExternalOAuthConnection(provider, {
            status,
            readOnly: connection.readOnly === undefined ? true : connection.readOnly !== false,
            scope: typeof connection.scope === 'string' && connection.scope.trim() ? connection.scope : config?.scopes.join(' '),
            writeEnabled: connection.writeEnabled === true,
            writeScope: typeof connection.writeScope === 'string' ? connection.writeScope : '',
            writeScopeRequestedAt: typeof connection.writeScopeRequestedAt === 'string' ? connection.writeScopeRequestedAt : null,
            connectedAt: typeof connection.connectedAt === 'string' ? connection.connectedAt : null,
            lastSyncedAt: typeof connection.lastSyncedAt === 'string' ? connection.lastSyncedAt : null,
            updatedAt: typeof connection.updatedAt === 'string' ? connection.updatedAt : new Date().toISOString(),
            lastMessage: typeof connection.lastMessage === 'string' && connection.lastMessage.trim() ? connection.lastMessage : '연결 대기 중입니다.'
        });
    }

    private normalizeExternalOAuthConnectionStatus(status: any): ExternalOAuthConnectionStatus {
        if (status === 'connecting' || status === 'connected' || status === 'cancelled' || status === 'error') return status;
        return 'disconnected';
    }

    private externalOAuthConnectionFor(provider: ExternalOAuthProviderId): ExternalOAuthConnection {
        return this.externalOAuthConnections.find((connection) => connection.provider === provider)
            || createExternalOAuthConnection(provider);
    }

    private upsertExternalOAuthConnection(connection: ExternalOAuthConnection): void {
        const next = this.externalOAuthConnections.filter((item) => item.provider !== connection.provider);
        next.push(connection);
        this.externalOAuthConnections = this.externalOAuthProviderIds
            .map((provider) => next.find((item) => item.provider === provider) || createExternalOAuthConnection(provider))
            .filter((item) => Boolean(this.externalOAuthConfig(item.provider)));
    }

    private hasExternalWriteScope(provider: ExternalOAuthProviderId, scope: string | null | undefined): boolean {
        return externalProviderHasWriteScope(provider, scope);
    }

    private markExternalProviderWriteAccess(provider: ExternalOAuthProviderId, scope: string, now = new Date().toISOString()): void {
        this.externalSyncPolicies = this.externalSyncPolicies.map((policy) => {
            if (policy.source !== provider || policy.direction === 'import_only') return policy;
            return {
                ...policy,
                writePermission: 'granted',
                writeScope: scope,
                writeRequestedAt: policy.writeRequestedAt || now,
                syncStatus: policy.syncStatus === 'write_scope_required' ? 'synced' : policy.syncStatus,
                syncError: policy.syncStatus === 'write_scope_required' ? null : policy.syncError,
                updatedAt: now
            };
        });
        this.saveExternalSyncPolicies();
    }

    private markExternalProviderWriteAccessDenied(provider: ExternalOAuthProviderId, now = new Date().toISOString()): void {
        this.externalSyncPolicies = this.externalSyncPolicies.map((policy) => {
            if (policy.source !== provider || policy.direction === 'import_only') return policy;
            return {
                ...policy,
                writePermission: 'denied',
                syncStatus: 'write_scope_required',
                syncError: '사용자가 외부 캘린더 쓰기 권한 요청을 취소했습니다.',
                updatedAt: now
            };
        });
        this.saveExternalSyncPolicies();
    }

    private externalOAuthRedirectUri(config: ExternalOAuthProviderConfig): string {
        try {
            return new URL(config.redirectPath, window.location.origin).toString();
        } catch (error) {
            return config.redirectPath;
        }
    }

    private externalOAuthClientId(config: ExternalOAuthProviderConfig): string {
        const env = this.externalOAuthRuntimeEnv();
        for (const key of config.clientIdEnvKeys) {
            const value = (env[key] || '').trim();
            if (value) return value;
        }
        return '';
    }

    private externalOAuthRuntimeEnv(): Record<string, string | undefined> {
        const globalScope = globalThis as any;
        return {
            ...(globalScope.process?.env || {}),
            ...(globalScope.OWNIT_ENV || {}),
            ...(globalScope.__OWNIT_ENV__ || {}),
            ...this.readExternalOAuthLocalEnv('ownit.cloud.env.v1'),
            ...this.readExternalOAuthLocalEnv('ownit.external.oauth.env.v1')
        };
    }

    private readExternalOAuthLocalEnv(key: string): Record<string, string | undefined> {
        try {
            const raw = globalThis.localStorage?.getItem(key);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object') return {};
            return parsed;
        } catch (error) {
            return {};
        }
    }

    private createExternalOAuthState(provider: ExternalOAuthProviderId): string {
        const cryptoObject = (globalThis as any).crypto;
        const nonce = typeof cryptoObject?.randomUUID === 'function'
            ? cryptoObject.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        return `${provider}.${nonce}`;
    }

    private createDefaultTodoCalendarDisplayOptions(): TodoCalendarDisplayOptions {
        return {
            enabled: false,
            showTodoCompleted: true,
            showRoutineCompleted: true,
            showCompletionRate: false
        };
    }

    private normalizeTodoCalendarDisplayOptions(options: Partial<TodoCalendarDisplayOptions> | null | undefined): TodoCalendarDisplayOptions {
        const defaults = this.createDefaultTodoCalendarDisplayOptions();
        return {
            enabled: Boolean(options?.enabled ?? defaults.enabled),
            showTodoCompleted: Boolean(options?.showTodoCompleted ?? defaults.showTodoCompleted),
            showRoutineCompleted: Boolean(options?.showRoutineCompleted ?? defaults.showRoutineCompleted),
            showCompletionRate: Boolean(options?.showCompletionRate ?? defaults.showCompletionRate)
        };
    }

    private defaultFollowedMateIds(): string[] {
        return [];
    }

    private mateFollowStorageKeyForUser() {
        return `${this.mateFollowStorageKey}.${this.user.id || 'guest'}`;
    }

    private defaultTodoGoalStorageKeyForUser() {
        return `${this.defaultTodoGoalStorageKey}.${this.user.id || 'guest'}`;
    }

    private recentTodoGoalStorageKeyForUser() {
        return `${this.recentTodoGoalStorageKey}.${this.user.id || 'guest'}`;
    }

    private todoCalendarDisplayStorageKeyForUser() {
        return `${this.todoCalendarDisplayStorageKey}.${this.user.id || 'guest'}`;
    }

    private receiptTemplateStorageKeyForUser() {
        return `${this.receiptTemplateStorageKey}.${this.user.id || 'guest'}`;
    }

    private externalOAuthStorageKeyForUser() {
        return `${this.externalOAuthStorageKey}.${this.user.id || 'guest'}`;
    }

    private externalOAuthPendingStorageKeyForUser() {
        return `${this.externalOAuthPendingStorageKey}.${this.user.id || 'guest'}`;
    }

    private externalSyncPolicyStorageKeyForUser() {
        return `${this.externalSyncPolicyStorageKey}.${this.user.id || 'guest'}`;
    }

    private externalMappingStorageKeyForUser() {
        return `${this.externalMappingStorageKey}.${this.user.id || 'guest'}`;
    }

    private segmentLabel(event: OwnitEvent) {
        if (event.startDate === event.endDate && !event.allDay && event.startTime) {
            return `${event.startTime} ${event.title}`;
        }
        return event.title;
    }

    private sortCalendars(calendars: OwnitCalendar[]) {
        return [...calendars].sort((a, b) => {
            if (a.type !== b.type) return a.type === 'personal' ? -1 : 1;
            if (a.order !== b.order) return a.order - b.order;
            return a.name.localeCompare(b.name);
        });
    }

    private sortGroups(groups: OwnitGroup[]) {
        return [...groups].sort((a, b) => {
            if (a.createdAt !== b.createdAt) return a.createdAt.localeCompare(b.createdAt);
            return a.name.localeCompare(b.name);
        });
    }

    private sortGroupMembers(members: OwnitGroupMember[]) {
        const roleOrder: Record<GroupRole, number> = { owner: 0, admin: 1, member: 2, viewer: 3 };
        const statusOrder: Record<GroupMemberStatus, number> = { active: 0, invited: 1, left: 2, removed: 3 };
        return [...members].sort((a, b) => {
            const statusDiff = (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9);
            if (statusDiff !== 0) return statusDiff;
            const roleDiff = (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9);
            if (roleDiff !== 0) return roleDiff;
            return this.memberDisplayName(a).localeCompare(this.memberDisplayName(b));
        });
    }

    private groupById(groupId: string | null | undefined) {
        if (!groupId) return null;
        return this.groups.find((group) => group.id === groupId && !group.archivedAt && !group.deletedAt) || null;
    }

    private groupForCalendar(calendar: OwnitCalendar | null | undefined) {
        if (!calendar || calendar.type !== 'shared') return null;
        return this.groupById(calendar.groupId);
    }

    private canManageSharedGroup(group: OwnitGroup | null | undefined) {
        return Boolean(group?.id && canManageGroupForUser(this.user, group));
    }

    private applyUpdatedGroup(updated: OwnitGroup) {
        this.groups = this.sortGroups(this.groups.map((group) => group.id === updated.id ? updated : group));
        this.group = updated;
        this.selectedSharedGroupId = updated.id;
        this.syncSharedSelection(updated.id, this.selectedSharedCalendarId);
    }

    private updateGroupMembers(group: OwnitGroup, members: OwnitGroupMember[]): OwnitGroup | null {
        try {
            const updated = this.dataRepository.updateGroup(group.id, {
                members,
                updatedAt: new Date().toISOString()
            });
            if (!updated) {
                this.memberManagementError = '구성원 정보를 저장하지 못했습니다.';
                return null;
            }

            this.applyUpdatedGroup(updated);
            return updated;
        } catch (error) {
            this.memberManagementError = this.dataErrorMessage(error, '구성원 정보를 저장하지 못했습니다.');
            return null;
        }
    }

    private async confirmSharedGroupRename() {
        const group = this.selectedSharedGroup;
        if (!this.canManageSharedGroup(group)) {
            this.sharedGuardError = '센터/그룹을 수정할 권한이 없습니다.';
            await this.service.render();
            return;
        }

        const nextName = (this.groupRenameDraft || '').trim();
        if (!nextName) {
            this.sharedGuardError = '센터/그룹 이름을 입력해 주세요.';
            await this.service.render();
            return;
        }
        if (nextName === group.name) {
            await this.closeSharedGuardDialog();
            return;
        }

        let updated: OwnitGroup | null = null;
        try {
            updated = this.dataRepository.updateGroup(group.id, {
                name: nextName,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            this.sharedGuardError = this.dataErrorMessage(error, '센터/그룹 이름을 저장하지 못했습니다.');
            await this.service.render();
            return;
        }
        if (!updated) {
            this.sharedGuardError = '센터/그룹 이름을 저장하지 못했습니다.';
            await this.service.render();
            return;
        }

        this.applyUpdatedGroup(updated);
        this.memberManagementNotice = '센터/그룹 이름을 변경했습니다.';
        await this.closeSharedGuardDialog();
    }

    private async confirmMemberRemoval() {
        const group = this.selectedSharedGroup;
        const member = this.memberRemovalTarget;
        if (!member || !this.canRemoveMember(member)) {
            this.sharedGuardError = '내보낼 구성원을 찾지 못했거나 권한이 없습니다.';
            await this.service.render();
            return;
        }
        if (member.role === 'owner' && this.isLastActiveOwner(group, member)) {
            this.sharedGuardError = '마지막 owner는 다른 owner를 먼저 지정해야 내보낼 수 있습니다.';
            await this.service.render();
            return;
        }

        const updated = this.updateGroupMembers(group, group.members.map((item) => item.id === member.id ? {
            ...item,
            status: 'removed' as const
        } : item));
        if (!updated) {
            this.sharedGuardError = this.memberManagementError || '구성원을 내보내지 못했습니다.';
            await this.service.render();
            return;
        }

        this.memberManagementError = '';
        this.memberManagementNotice = `${this.memberDisplayName(member)} 구성원을 내보냈습니다.`;
        await this.closeSharedGuardDialog();
    }

    private async confirmGroupLeave() {
        const group = this.selectedSharedGroup;
        const member = this.currentMember;
        if (!group.id || !member) {
            this.sharedGuardError = '탈퇴할 수 있는 참여 상태가 아닙니다.';
            await this.service.render();
            return;
        }

        if (this.leaveGroupRequiresOwnerTransfer) {
            const nextOwner = this.leaveOwnerCandidates.find((candidate) => candidate.id === this.leaveGroupTransferMemberId) || null;
            if (!nextOwner) {
                this.sharedGuardError = '다음 owner를 선택해 주세요.';
                await this.service.render();
                return;
            }

            const updated = this.updateGroupMembers(group, group.members.map((item) => {
                if (item.id === nextOwner.id) return { ...item, role: 'owner' as const };
                if (item.id === member.id) return { ...item, status: 'left' as const };
                return item;
            }));
            if (!updated) {
                this.sharedGuardError = this.memberManagementError || '그룹 탈퇴를 저장하지 못했습니다.';
                await this.service.render();
                return;
            }
            this.initializeUserData();
            this.memberManagementOpen = false;
            this.showAuthToast('그룹에서 탈퇴했습니다.');
            await this.closeSharedGuardDialog();
            return;
        }

        if (this.leaveGroupDeletesGroup) {
            try {
                this.dataRepository.deleteGroup(group.id);
            } catch (error) {
                this.sharedGuardError = this.dataErrorMessage(error, '그룹 탈퇴를 저장하지 못했습니다.');
                await this.service.render();
                return;
            }
            this.initializeUserData();
            this.memberManagementOpen = false;
            this.showAuthToast('그룹을 삭제하고 탈퇴했습니다.');
            await this.closeSharedGuardDialog();
            return;
        }

        const updated = this.updateGroupMembers(group, group.members.map((item) => item.id === member.id ? { ...item, status: 'left' as const } : item));
        if (!updated) {
            this.sharedGuardError = this.memberManagementError || '그룹 탈퇴를 저장하지 못했습니다.';
            await this.service.render();
            return;
        }
        this.initializeUserData();
        this.memberManagementOpen = false;
        this.showAuthToast('그룹에서 탈퇴했습니다.');
        await this.closeSharedGuardDialog();
    }

    private createInviteCode() {
        return `OWNIT-${Math.random().toString(36).slice(2, 8)}`.toUpperCase();
    }

    private createInviteLink(code: string) {
        const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://ownit.local';
        return `${origin}/dashboard?invite=${encodeURIComponent(code)}`;
    }

    private normalizeEmailInput(value: string) {
        return (value || '').trim().toLowerCase();
    }

    private createInviteUserId(value: string) {
        const normalized = (value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        return `invite-${normalized || Date.now().toString(36)}`;
    }

    private normalizeGroupRole(role: GroupRole | string): GroupRole {
        if (role === 'owner' || role === 'admin' || role === 'viewer') return role;
        return 'member';
    }

    private isLastActiveOwner(group: OwnitGroup, member: OwnitGroupMember) {
        if (member.role !== 'owner' || member.status !== 'active') return false;
        return group.members.filter((item) => item.role === 'owner' && item.status === 'active' && item.id !== member.id).length === 0;
    }

    private findMemberByChoice(members: OwnitGroupMember[], choice: string) {
        const normalized = choice.trim().toLowerCase();
        return members.find((member) => {
            const candidates = [member.email, member.name, member.nickname, member.userId].map((value) => (value || '').trim().toLowerCase());
            return candidates.includes(normalized);
        }) || null;
    }

    private syncSharedSelection(preferredGroupId = this.selectedSharedGroupId, preferredCalendarId = this.selectedSharedCalendarId) {
        const groups = this.activeSharedGroups;
        const preferredCalendar = this.sharedCalendars.find((calendar) => calendar.id === preferredCalendarId) || null;
        const group = groups.find((item) => item.id === preferredGroupId)
            || (preferredCalendar ? groups.find((item) => item.id === preferredCalendar.groupId) : null)
            || groups[0]
            || createEmptyGroup(this.user);
        const calendars = group.id ? this.sharedCalendars.filter((calendar) => calendar.groupId === group.id) : [];
        const calendar = preferredCalendarId
            ? (calendars.find((item) => item.id === preferredCalendarId)
                || (preferredCalendar?.groupId === group.id ? preferredCalendar : null)
                || null)
            : null;

        this.group = group;
        this.selectedSharedGroupId = group.id || '';
        this.selectedSharedCalendarId = calendar?.id || '';
    }

    private sortEvents(events: OwnitEvent[]) {
        return [...events].sort((a, b) => {
            if (a.startDate !== b.startDate) return a.startDate.localeCompare(b.startDate);
            if (a.endDate !== b.endDate) return b.endDate.localeCompare(a.endDate);
            const timeA = a.startTime || '00:00';
            const timeB = b.startTime || '00:00';
            if (timeA !== timeB) return timeA.localeCompare(timeB);
            return a.title.localeCompare(b.title);
        });
    }

    private sortTodos(todos: OwnitTodo[]) {
        return [...todos].sort((a, b) => {
            if (a.date !== b.date) return a.date.localeCompare(b.date);
            const orderA = Number.isFinite(Number(a.order)) ? Number(a.order) : 0;
            const orderB = Number.isFinite(Number(b.order)) ? Number(b.order) : 0;
            if (orderA !== orderB) return orderA - orderB;
            if (a.completed !== b.completed) return Number(a.completed) - Number(b.completed);
            return a.title.localeCompare(b.title);
        });
    }

    private nextCalendarOrder(type: OwnitCalendar['type']) {
        const base = type === 'shared' ? 100 : 0;
        const orders = this.calendars
            .filter((calendar) => calendar.type === type)
            .map((calendar) => Number(calendar.order))
            .filter((order) => Number.isFinite(order));
        if (orders.length === 0) return base;
        return Math.max(...orders) + 1;
    }

    private resolveSharedGroupForCalendar(payload: CalendarPayload, existingCalendar: OwnitCalendar | null = null): OwnitGroup | null {
        const requestedGroupId = payload.groupId || existingCalendar?.groupId || this.selectedSharedGroupId;
        const existingGroup = this.groups.find((group) => group.id === requestedGroupId && !group.archivedAt && !group.deletedAt) || null;

        if (existingGroup) {
            if (!this.canManageSharedGroup(existingGroup) && existingCalendar?.groupId !== existingGroup.id) {
                this.calendarFormError = '이 센터에 공유 캘린더를 추가할 권한이 없습니다.';
                return null;
            }

            const nextName = payload.groupName.trim();
            if (nextName && nextName !== existingGroup.name) {
                if (!this.canManageSharedGroup(existingGroup)) {
                    this.calendarFormError = '센터/그룹 이름을 변경할 권한이 없습니다.';
                    return null;
                }
                const updated = this.dataRepository.updateGroup(existingGroup.id, {
                    name: nextName,
                    updatedAt: new Date().toISOString()
                });
                if (updated) {
                    this.groups = this.sortGroups(this.groups.map((group) => group.id === updated.id ? updated : group));
                    this.group = updated;
                    return updated;
                }
            }
            return existingGroup;
        }

        if (!this.canManageSettingsAction) {
            this.calendarFormError = '새 센터/그룹을 만들 권한이 없습니다.';
            return null;
        }

        return this.createSharedGroup(payload.groupName);
    }

    private createSharedGroup(name: string): OwnitGroup {
        const now = new Date().toISOString();
        const groupId = this.createId('group');
        const inviteCode = `OWNIT-${Math.random().toString(36).slice(2, 8)}`.toUpperCase();
        const group: OwnitGroup = {
            id: groupId,
            name: name.trim() || `${this.user.nickname || this.user.name || '내'} 공유 그룹`,
            adminId: this.user.id,
            inviteCode,
            inviteLink: this.createInviteLink(inviteCode),
            members: [{
                id: `member-${this.user.id}-owner`,
                groupId,
                userId: this.user.id,
                name: this.user.nickname || this.user.name || '나',
                nickname: this.user.nickname || this.user.name || '나',
                email: this.user.email || '',
                role: this.user.role === 'admin' ? 'admin' : 'owner',
                status: 'active',
                joinedAt: now
            }],
            archivedAt: null,
            deletedAt: null,
            createdAt: now,
            updatedAt: now
        };

        const saved = this.dataRepository.createGroup(group);
        this.groups = this.sortGroups([...this.groups, saved]);
        this.group = saved;
        this.selectedSharedGroupId = saved.id;
        return saved;
    }

    private todoCompletionPercent(todos: OwnitTodo[]) {
        if (todos.length === 0) return 0;
        const done = todos.filter((todo) => todo.completed).length;
        return Math.round((done / todos.length) * 100);
    }

    private focusDate(date: string) {
        this.selectedDate = date;
        const selected = this.fromDateKey(date);
        const monthChanged = selected.getFullYear() !== this.currentMonth.getFullYear() || selected.getMonth() !== this.currentMonth.getMonth();
        if (monthChanged) {
            this.currentMonth = new Date(selected.getFullYear(), selected.getMonth(), 1);
            this.syncDecorationForCurrentMonth();
        }
        this.ensureReceiptDraftForDate(this.selectedDate);
        this.syncDailyRecordDraftForDate(this.selectedDate);
        this.buildMonth();
    }

    private refreshTodoRecordsFromRepository() {
        const snapshot = this.dataRepository.getSnapshotForUser(this.user.id);
        this.todos = this.sortTodos(snapshot.todos);
        this.todoCategories = this.sortTodoCategories(snapshot.todoCategories);
        this.todoGoals = this.sortTodoGoals(snapshot.todoGoals);
        this.todoRoutines = this.sortRoutines(snapshot.todoRoutines);
        this.todoDailyRecords = snapshot.todoDailyRecords;
        this.todoTimerSessions = snapshot.todoTimerSessions;
        this.todoCompletionRecords = snapshot.todoCompletionRecords;
        this.todoReactions = snapshot.todoReactions;
        this.reconcileTodoGoalPreferences();
    }

    private startTodoTimerTicker() {
        if (this.todoTimerTickTimer !== null) return;
        this.todoTimerTickTimer = window.setInterval(async () => {
            this.todoTimerNow = Date.now();
            await this.service.render();
        }, 1000);
    }

    private stopTodoTimerTicker() {
        if (this.todoTimerTickTimer === null) return;
        window.clearInterval(this.todoTimerTickTimer);
        this.todoTimerTickTimer = null;
    }

    private clearActiveTodoTimer() {
        this.activeTodoTimer = null;
        this.stopTodoTimerTicker();
        this.todoTimerNow = Date.now();
    }

    private syncNotificationPermissionState() {
        if (typeof window === 'undefined' || !('Notification' in window)) {
            this.notificationPermission = 'unsupported';
            return;
        }
        this.notificationPermission = Notification.permission;
    }

    private async ensureNotificationPermissionForReminder(): Promise<boolean> {
        this.syncNotificationPermissionState();
        if (this.notificationPermission === 'granted') {
            this.reminderPermissionMessage = '';
            return true;
        }
        if (this.notificationPermission === 'unsupported') {
            this.reminderPermissionMessage = '이 환경에서는 브라우저 알림을 사용할 수 없어 앱 안 알림으로 표시합니다.';
            return false;
        }
        if (this.notificationPermission === 'denied') {
            this.reminderPermissionMessage = '브라우저 알림이 차단되어 앱 안 알림으로 표시합니다. 브라우저 설정에서 알림을 허용할 수 있습니다.';
            return false;
        }
        if (typeof window !== 'undefined' && window.isSecureContext === false) {
            this.reminderPermissionMessage = 'HTTPS 또는 localhost가 아니어서 브라우저 알림 권한을 요청할 수 없습니다. 앱 안 알림으로 표시합니다.';
            return false;
        }

        try {
            this.notificationPermission = await Notification.requestPermission();
        } catch (error) {
            this.notificationPermission = 'unsupported';
            this.reminderPermissionMessage = '브라우저 알림 권한 요청을 완료하지 못해 앱 안 알림으로 표시합니다.';
            return false;
        }

        if (this.notificationPermission === 'granted') {
            this.reminderPermissionMessage = '';
            return true;
        }

        this.reminderPermissionMessage = this.notificationPermission === 'denied'
            ? '브라우저 알림이 차단되어 앱 안 알림으로 표시합니다. 브라우저 설정에서 알림을 허용할 수 있습니다.'
            : '브라우저 알림 권한 요청을 건너뛰어 앱 안 알림으로 표시합니다.';
        return false;
    }

    private syncReminderRuntimeForCurrentUser() {
        const userId = this.isAuthenticated && !this.needsProfileSetup ? this.user.id : '';
        if (userId !== this.reminderRuntimeUserId) {
            this.stopReminderWatcher();
            this.clearReminderRuntime(false);
            this.reminderRuntimeUserId = userId;
            this.reminderFiredKeys = this.loadReminderFiredKeys(userId);
        }
        if (userId) {
            if (this.reminderCheckTimer === null) this.startReminderWatcher();
            else this.refreshReminderSchedules();
        } else {
            this.stopReminderWatcher();
        }
    }

    private startReminderWatcher() {
        if (typeof window === 'undefined') return;
        if (!this.isAuthenticated || this.needsProfileSetup || !this.user.id) return;
        if (this.reminderCheckTimer !== null) return;
        this.checkDueReminders(false);
        this.scheduleUpcomingReminderNotifications();
        this.reminderCheckTimer = window.setInterval(async () => {
            await this.checkDueReminders();
        }, 30000);
    }

    private stopReminderWatcher() {
        if (this.reminderCheckTimer !== null) {
            window.clearInterval(this.reminderCheckTimer);
            this.reminderCheckTimer = null;
        }
        this.clearScheduledReminderNotifications();
    }

    private async checkDueReminders(render = true) {
        if (!this.isAuthenticated || this.needsProfileSetup || !this.user.id) return;
        const dueNotices = this.collectDueReminderNotices(new Date());
        const freshNotices = dueNotices.filter((notice) => !this.reminderFiredKeys.has(notice.id));
        if (freshNotices.length === 0) return;

        freshNotices.forEach((notice) => this.reminderFiredKeys.add(notice.id));
        this.persistReminderFiredKeys();
        this.reminderNotices = [...freshNotices, ...this.reminderNotices].slice(0, 20);
        this.showReminderToast(freshNotices[0]);
        freshNotices.forEach((notice) => this.sendBrowserReminderNotification(notice));
        if (render) await this.service.render();
    }

    private refreshReminderSchedules() {
        if (!this.isAuthenticated || this.needsProfileSetup || !this.user.id) {
            this.clearScheduledReminderNotifications();
            return;
        }
        this.scheduleUpcomingReminderNotifications();
    }

    private scheduleUpcomingReminderNotifications() {
        if (typeof window === 'undefined') return;
        this.clearScheduledReminderNotifications();
        if (!this.isAuthenticated || this.needsProfileSetup || !this.user.id) return;

        const now = new Date();
        for (const notice of this.collectUpcomingReminderNotices(now)) {
            if (this.reminderFiredKeys.has(notice.id)) continue;
            const delay = Date.parse(notice.scheduledAt) - now.getTime();
            if (!Number.isFinite(delay) || delay < 0 || delay > this.maxReminderTimeoutMs) continue;
            const timer = window.setTimeout(async () => {
                this.reminderScheduleTimers.delete(notice.id);
                await this.checkDueReminders(true);
                this.scheduleUpcomingReminderNotifications();
            }, delay);
            this.reminderScheduleTimers.set(notice.id, timer);
        }
    }

    private clearScheduledReminderNotifications() {
        if (typeof window !== 'undefined') {
            this.reminderScheduleTimers.forEach((timer) => window.clearTimeout(timer));
        }
        this.reminderScheduleTimers.clear();
    }

    private collectDueReminderNotices(now: Date): DueReminderNotice[] {
        const userId = this.user.id;
        const nowMs = now.getTime();
        const earliestMs = nowMs - this.reminderLookbackMs;
        const notices: DueReminderNotice[] = [];

        for (const event of this.events) {
            if (event.completed) continue;
            if (!this.isReminderRecordForCurrentUser(event, null)) continue;
            for (const reminder of event.reminders || []) {
                if (!reminder.enabled) continue;
                notices.push(...this.eventReminderNoticesInWindow(event, reminder, earliestMs, nowMs, userId));
            }
        }

        for (const todo of this.todos) {
            if (todo.completed || !todo.reminderAt) continue;
            if (!this.isReminderRecordForCurrentUser(todo, todo.reminderRule)) continue;
            const scheduledMs = Date.parse(todo.reminderAt);
            if (Number.isNaN(scheduledMs) || scheduledMs > nowMs || scheduledMs < earliestMs) continue;
            notices.push(this.createDueReminderNotice('todo', todo.id, todo.title, todo.memo, todo.reminderAt, todo.date, todo.color, userId));
        }

        const today = this.toDateKey(now);
        for (const routine of this.todoRoutines) {
            if (!this.isReminderRecordForCurrentUser(routine, routine.reminderRule)) continue;
            const reminderAt = this.routineReminderAtForDate(routine, today);
            if (!reminderAt) continue;
            const completion = this.routineCompletionRecord(routine.id, today);
            if (completion?.completed) continue;
            const scheduledMs = Date.parse(reminderAt);
            if (Number.isNaN(scheduledMs) || scheduledMs > nowMs || scheduledMs < earliestMs) continue;
            notices.push(this.createDueReminderNotice('routine', routine.id, routine.title, routine.memo, reminderAt, today, routine.color, userId));
        }

        return notices.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
    }

    private collectUpcomingReminderNotices(now: Date): DueReminderNotice[] {
        const userId = this.user.id;
        const nowMs = now.getTime();
        const latestMs = nowMs + this.reminderScheduleWindowMs;
        const notices: DueReminderNotice[] = [];

        for (const event of this.events) {
            if (event.completed) continue;
            if (!this.isReminderRecordForCurrentUser(event, null)) continue;
            for (const reminder of event.reminders || []) {
                if (!reminder.enabled) continue;
                const eventNotices = this.eventReminderNoticesInWindow(event, reminder, nowMs + 1, latestMs, userId);
                notices.push(...eventNotices);
            }
        }

        for (const todo of this.todos) {
            if (todo.completed || !todo.reminderAt) continue;
            if (!this.isReminderRecordForCurrentUser(todo, todo.reminderRule)) continue;
            const scheduledMs = Date.parse(todo.reminderAt);
            if (Number.isNaN(scheduledMs) || scheduledMs <= nowMs || scheduledMs > latestMs) continue;
            notices.push(this.createDueReminderNotice('todo', todo.id, todo.title, todo.memo, todo.reminderAt, todo.date, todo.color, userId));
        }

        for (const routine of this.todoRoutines) {
            if (!this.isReminderRecordForCurrentUser(routine, routine.reminderRule)) continue;
            if (!routine.reminderRule?.enabled && !(routine.reminders || []).some((reminder) => reminder.enabled)) continue;
            const time = this.routineReminderTime(routine);
            const offsetMinutes = this.routineReminderOffsetMinutes(routine);
            const date = this.nextRoutineReminderDate(routine, this.todayDate, time, offsetMinutes);
            const reminderAt = this.reminderAtFromBase(date, time, offsetMinutes);
            if (!reminderAt) continue;
            const completion = this.routineCompletionRecord(routine.id, date);
            if (completion?.completed) continue;
            const scheduledMs = Date.parse(reminderAt);
            if (Number.isNaN(scheduledMs) || scheduledMs <= nowMs || scheduledMs > latestMs) continue;
            notices.push(this.createDueReminderNotice('routine', routine.id, routine.title, routine.memo, reminderAt, date, routine.color, userId));
        }

        return notices.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
    }

    private eventReminderNoticesInWindow(event: OwnitEvent, reminder: ReminderSetting, earliestMs: number, latestMs: number, userId: string): DueReminderNotice[] {
        const notices: DueReminderNotice[] = [];
        const offsetMs = this.normalizeReminderOffset(reminder.offsetMinutes) * 60000;
        const startDate = event.repeatRule
            ? this.toDateKey(new Date(earliestMs + offsetMs))
            : event.startDate;
        const endDate = event.repeatRule
            ? this.toDateKey(new Date(latestMs + offsetMs))
            : event.startDate;
        let cursor = startDate;
        let guard = 0;

        while (cursor && cursor <= endDate && guard < 400) {
            guard += 1;
            if (this.eventOccursOnDate(event, cursor)) {
                const reminderAt = this.eventReminderAtForDate(event, reminder, cursor);
                const scheduledMs = reminderAt ? Date.parse(reminderAt) : NaN;
                if (!Number.isNaN(scheduledMs) && scheduledMs >= earliestMs && scheduledMs <= latestMs) {
                    notices.push(this.createDueReminderNotice('event', event.id, event.title, event.description, reminderAt, cursor, event.color, userId));
                }
            }
            if (!event.repeatRule) break;
            cursor = this.shiftDateKey(cursor, 1);
        }

        return notices;
    }

    private eventReminderAtForDate(event: OwnitEvent, reminder: ReminderSetting, date: string): string | null {
        const startTime = event.allDay ? '09:00' : (event.startTime || '09:00');
        const startAt = new Date(`${date}T${startTime}:00`);
        if (Number.isNaN(startAt.getTime())) return null;
        return new Date(startAt.getTime() - this.normalizeReminderOffset(reminder.offsetMinutes) * 60000).toISOString();
    }

    private eventReminderAt(event: OwnitEvent, reminder: ReminderSetting): string | null {
        return this.eventReminderAtForDate(event, reminder, event.startDate);
    }

    private eventOccursOnDate(event: OwnitEvent, dateKey: string): boolean {
        if (!event.repeatRule) return dateKey === event.startDate;
        if (!dateKey || dateKey < event.startDate) return false;
        if (event.repeatRule.until && dateKey > event.repeatRule.until) return false;

        const start = this.fromDateKey(event.startDate);
        const date = this.fromDateKey(dateKey);
        const interval = Math.max(1, Number(event.repeatRule.interval) || 1);
        const daysDiff = this.daysBetweenDateKeys(event.startDate, dateKey);
        if (daysDiff < 0) return false;

        if (event.repeatRule.frequency === 'daily') return daysDiff % interval === 0;
        if (event.repeatRule.frequency === 'weekly') {
            const weeksDiff = Math.floor(daysDiff / 7);
            const weekdays = event.repeatRule.byWeekday && event.repeatRule.byWeekday.length > 0
                ? event.repeatRule.byWeekday
                : [start.getDay()];
            return weeksDiff % interval === 0 && weekdays.includes(date.getDay());
        }
        if (event.repeatRule.frequency === 'monthly') {
            const monthsDiff = (date.getFullYear() - start.getFullYear()) * 12 + (date.getMonth() - start.getMonth());
            return monthsDiff >= 0 && monthsDiff % interval === 0 && date.getDate() === start.getDate();
        }
        if (event.repeatRule.frequency === 'yearly') {
            const yearsDiff = date.getFullYear() - start.getFullYear();
            return yearsDiff >= 0 && yearsDiff % interval === 0 && date.getMonth() === start.getMonth() && date.getDate() === start.getDate();
        }
        return dateKey === event.startDate;
    }

    private daysBetweenDateKeys(startDate: string, endDate: string): number {
        const start = this.fromDateKey(startDate);
        const end = this.fromDateKey(endDate);
        return Math.floor((end.getTime() - start.getTime()) / 86400000);
    }

    private createDueReminderNotice(targetType: 'event' | 'todo' | 'routine', targetId: string, title: string, memo: string, scheduledAt: string, date: string, color: string, userId: string): DueReminderNotice {
        return {
            id: `reminder::${userId}::${targetType}::${targetId}::${scheduledAt}`,
            userId,
            targetId,
            targetType,
            title,
            body: memo || (targetType === 'event' ? '일정 시간입니다.' : (targetType === 'routine' ? '루틴 시간입니다.' : '할 일 시간입니다.')),
            scheduledAt,
            date,
            color,
            createdAt: new Date().toISOString()
        };
    }

    private addSharedEventChangeNotice(action: 'created' | 'updated' | 'deleted' | 'completed', event: OwnitEvent, calendar: OwnitCalendar | null = this.calendarById(event.calendarId)) {
        if (!calendar || calendar.type !== 'shared' || !this.user.id) return;
        const labels: Record<'created' | 'updated' | 'deleted' | 'completed', string> = {
            created: '공유 일정이 추가되었습니다.',
            updated: '공유 일정이 변경되었습니다.',
            deleted: '공유 일정이 삭제되었습니다.',
            completed: event.completed ? '공유 일정이 완료 처리되었습니다.' : '공유 일정 완료가 해제되었습니다.'
        };
        const now = new Date().toISOString();
        const notice: DueReminderNotice = {
            id: `shared-change::${this.user.id}::${event.id}::${action}::${now}`,
            userId: this.user.id,
            targetId: event.id,
            targetType: 'shared',
            title: labels[action],
            body: event.title,
            scheduledAt: now,
            date: event.startDate || this.selectedDate,
            color: event.color || calendar.color,
            createdAt: now
        };
        this.reminderNotices = [notice, ...this.reminderNotices].slice(0, 20);
        this.showReminderToast(notice);
    }

    private isReminderRecordForCurrentUser(record: { authorId?: string; assigneeId?: string | null; createdBy?: string; assignedTo?: string | null; userId?: string; ownerId?: string; reminderRule?: ReminderRule | null }, rule: ReminderRule | null | undefined) {
        const userId = this.user.id;
        if (!userId) return false;
        if (rule?.userId && rule.userId !== userId) return false;
        return record.authorId === userId
            || record.assigneeId === userId
            || record.createdBy === userId
            || record.assignedTo === userId
            || record.userId === userId
            || record.ownerId === userId;
    }

    private showReminderToast(notice: DueReminderNotice) {
        this.clearReminderToastTimer();
        this.reminderToast = notice;
        this.reminderToastHideTimer = window.setTimeout(async () => {
            this.reminderToastHideTimer = null;
            this.reminderToast = null;
            await this.service.render();
        }, 7200);
    }

    private sendBrowserReminderNotification(notice: DueReminderNotice) {
        this.syncNotificationPermissionState();
        if (this.notificationPermission !== 'granted') return;
        try {
            new Notification(notice.title, {
                body: `${this.reminderNoticeTypeLabel(notice)} · ${this.reminderDateTimeLabel(notice.scheduledAt)}`,
                tag: notice.id
            });
        } catch (error) {
            this.notificationPermission = 'unsupported';
            this.reminderPermissionMessage = '브라우저 알림을 표시하지 못해 앱 안 알림으로 표시합니다.';
        }
    }

    private clearReminderRuntime(clearUser = true) {
        this.reminderNotices = [];
        this.reminderToast = null;
        this.reminderCenterOpen = false;
        this.clearReminderToastTimer();
        this.clearScheduledReminderNotifications();
        if (clearUser) {
            this.reminderRuntimeUserId = '';
            this.reminderFiredKeys = new Set<string>();
        }
    }

    private clearReminderToastTimer() {
        if (this.reminderToastHideTimer === null) return;
        window.clearTimeout(this.reminderToastHideTimer);
        this.reminderToastHideTimer = null;
    }

    private loadReminderFiredKeys(userId: string) {
        if (!userId) return new Set<string>();
        const parsed = this.dataRepository.loadLocalState<string[]>(this.reminderFiredStorageKey(userId), []);
        return new Set(Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : []);
    }

    private persistReminderFiredKeys() {
        const userId = this.reminderRuntimeUserId || this.user.id;
        if (!userId) return;
        this.dataRepository.saveLocalState(this.reminderFiredStorageKey(userId), Array.from(this.reminderFiredKeys).slice(-200));
    }

    private reminderFiredStorageKey(userId: string) {
        return `${this.reminderFiredStoragePrefix}.${userId}`;
    }

    private secondsBetween(startAt: string, endTime: number): number {
        const start = Date.parse(startAt);
        if (Number.isNaN(start)) return 0;
        return Math.max(0, Math.floor((endTime - start) / 1000));
    }

    private formatTimerDuration(seconds: number): string {
        const safeSeconds = Math.max(0, Math.round(seconds || 0));
        const hours = Math.floor(safeSeconds / 3600);
        const minutes = Math.floor((safeSeconds % 3600) / 60);
        const restSeconds = safeSeconds % 60;
        if (hours > 0) return `${hours}시간 ${minutes}분`;
        if (minutes > 0) return restSeconds > 0 ? `${minutes}분 ${restSeconds}초` : `${minutes}분`;
        return `${restSeconds}초`;
    }

    private formatClockDuration(seconds: number): string {
        const safeSeconds = Math.max(0, Math.round(seconds || 0));
        const hours = Math.floor(safeSeconds / 3600);
        const minutes = Math.floor((safeSeconds % 3600) / 60);
        const restSeconds = safeSeconds % 60;
        if (hours > 0) return `${hours}:${`${minutes}`.padStart(2, '0')}:${`${restSeconds}`.padStart(2, '0')}`;
        return `${`${minutes}`.padStart(2, '0')}:${`${restSeconds}`.padStart(2, '0')}`;
    }

    private createLocalCloudStatus(): CloudRepositoryStatus {
        return {
            enabled: false,
            provider: 'localStorage',
            label: 'localStorage',
            state: 'local',
            message: '클라우드 환경변수가 없어 로컬 저장소로 동작 중입니다.',
            lastSyncedAt: '',
            lastCheckedAt: '',
            lastError: '',
            pendingWrites: false,
            pushedRecords: 0,
            skippedStaleWrites: 0
        };
    }

    private syncCloudStatus(): boolean {
        const previous = JSON.stringify(this.cloudSyncStatus);
        this.cloudSyncStatus = this.dataRepository.getCloudStatus?.() || this.createLocalCloudStatus();
        if (this.cloudSyncStatus.lastError) this.cloudSyncError = this.cloudSyncStatus.lastError;
        return previous !== JSON.stringify(this.cloudSyncStatus);
    }

    private syncNetworkStatus(): boolean {
        const previousOffline = this.networkIsOffline;
        const previousSlow = this.networkConnectionIsSlow;
        const nav = typeof navigator !== 'undefined'
            ? navigator as Navigator & {
                connection?: { effectiveType?: string; saveData?: boolean };
                mozConnection?: { effectiveType?: string; saveData?: boolean };
                webkitConnection?: { effectiveType?: string; saveData?: boolean };
            }
            : null;
        const connection = nav?.connection || nav?.mozConnection || nav?.webkitConnection || null;
        const effectiveType = (connection?.effectiveType || '').toLowerCase();

        this.networkIsOffline = nav ? nav.onLine === false : false;
        this.networkConnectionIsSlow = !this.networkIsOffline
            && (connection?.saveData === true || effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g');

        return previousOffline !== this.networkIsOffline || previousSlow !== this.networkConnectionIsSlow;
    }

    private startCloudStatusWatcher() {
        if (this.cloudStatusPollTimer !== null) return;
        this.cloudStatusPollTimer = window.setInterval(async () => {
            const networkChanged = this.syncNetworkStatus();
            const cloudChanged = this.syncCloudStatus();
            if (networkChanged || cloudChanged) await this.service.render();
        }, 1500);
    }

    private stopCloudStatusWatcher() {
        if (this.cloudStatusPollTimer === null) return;
        window.clearInterval(this.cloudStatusPollTimer);
        this.cloudStatusPollTimer = null;
    }

    private async refreshCloudDataIfStale(minIntervalMs = 60000) {
        const status = this.dataRepository.getCloudStatus?.() || this.createLocalCloudStatus();
        if (!status.enabled || !this.isAuthenticated || this.needsProfileSetup || this.cloudSyncLoading) return;
        const lastCheck = Date.parse(status.lastCheckedAt || status.lastSyncedAt || '');
        if (Number.isFinite(lastCheck) && Date.now() - lastCheck < minIntervalMs) return;
        await this.refreshCloudData(true);
    }

    private dataErrorMessage(error: unknown, fallback: string): string {
        return repositoryErrorMessage(error, fallback);
    }

    private createId(prefix: string) {
        const userId = this.user.id || 'guest';
        const stamp = Date.now().toString(36);
        const random = Math.random().toString(36).slice(2, 8);
        return `${prefix}-${userId}-${stamp}-${random}`;
    }

    private showAuthToast(message: string, duration = 3200) {
        this.clearAuthToastTimers();
        this.authToastMessage = message;
        this.authToastLeaving = false;
        this.authToastHideTimer = window.setTimeout(async () => {
            this.authToastHideTimer = null;
            this.authToastLeaving = true;
            await this.service.render();
            this.authToastClearTimer = window.setTimeout(async () => {
                this.authToastClearTimer = null;
                this.authToastMessage = '';
                this.authToastLeaving = false;
                await this.service.render();
            }, 220);
        }, duration);
    }

    private clearAuthToastTimers() {
        if (this.authToastHideTimer !== null) {
            window.clearTimeout(this.authToastHideTimer);
            this.authToastHideTimer = null;
        }
        if (this.authToastClearTimer !== null) {
            window.clearTimeout(this.authToastClearTimer);
            this.authToastClearTimer = null;
        }
    }

    private clearAuthToast() {
        this.clearAuthToastTimers();
        this.authToastMessage = '';
        this.authToastLeaving = false;
    }

    private async runAuthAction(action: () => void | Promise<void>) {
        this.authLoading = true;
        this.authError = '';
        this.clearAuthToast();
        try {
            await action();
        } catch (error) {
            this.authError = error instanceof Error ? error.message : '인증 처리 중 오류가 발생했습니다.';
        } finally {
            this.authLoading = false;
            await this.service.render();
        }
    }

    private authEmailCandidate(targetMode: AuthMode) {
        const signinIdentifier = targetMode !== 'signin' ? this.signinForm.identifier : '';
        const candidates = [
            targetMode !== 'signup' ? this.signupForm.email : '',
            targetMode !== 'reset' ? this.resetForm.email : '',
            signinIdentifier.includes('@') ? signinIdentifier : '',
            this.user.email
        ];
        return (candidates.find((value) => (value || '').trim()) || '').trim();
    }

    private authLoginIdentifierCandidate(targetMode: AuthMode) {
        const candidates = [
            targetMode !== 'signin' ? this.signinForm.identifier : '',
            targetMode !== 'signup' ? this.signupForm.email : '',
            targetMode !== 'reset' ? this.resetForm.email : '',
            this.user.username,
            this.user.email
        ];
        return (candidates.find((value) => (value || '').trim()) || '').trim();
    }

    private async restoreAuthSession() {
        try {
            const session = await this.authProvider.getSession();
            if (session) {
                await this.applySession(session);
                return;
            }
        } catch (error) {
            this.authError = error instanceof Error ? error.message : '저장된 로그인 상태를 확인하지 못했습니다.';
        }

        this.isAuthenticated = false;
        this.needsProfileSetup = false;
        this.user = { ...createEmptyUser(), profileComplete: false };
        this.initializeUserData();
    }

    private async applySession(session: OwnitAuthSession) {
        this.user = {
            ...session.user,
            defaultFont: normalizeOwnitFontId(session.user.defaultFont)
        };
        this.isAuthenticated = true;
        this.needsProfileSetup = !this.user.profileComplete;
        this.profileForm = {
            name: this.user.name,
            nickname: this.user.nickname,
            profileImage: this.user.profileImage,
            defaultFont: this.user.defaultFont,
            defaultTheme: this.user.defaultTheme || OWNIT_DEFAULT_THEME_ID
        };
        this.initializeUserData();
        await this.refreshCloudData(true);
        if (this.needsProfileSetup) this.authMode = 'profile';
    }

    private initializeUserData(snapshotOverride?: ReturnType<OwnitRepositoryAdapter['loadForUser']>) {
        const snapshot = snapshotOverride || this.dataRepository.loadForUser(this.user);
        this.externalOAuthConnections = this.loadExternalOAuthConnections();
        this.externalSyncPolicies = this.loadExternalSyncPolicies();
        this.externalEventMappings = this.loadExternalEventMappings();
        const externalSnapshot = this.prepareExternalCalendarSnapshot(this.loadExternalCalendarSnapshot());
        this.externalProviderSummaries = externalSnapshot.providers;
        this.calendars = this.sortCalendars([...snapshot.calendars, ...externalSnapshot.calendars]);
        this.groups = this.sortGroups(snapshot.groups);
        this.syncSharedSelection();
        this.events = this.sortEvents([...snapshot.events, ...externalSnapshot.events]);
        this.todos = this.sortTodos(snapshot.todos);
        this.todoCategories = snapshot.todoCategories;
        this.todoGoals = snapshot.todoGoals;
        this.todoRoutines = this.sortRoutines(snapshot.todoRoutines);
        this.todoDailyRecords = snapshot.todoDailyRecords;
        this.todoTimerSessions = snapshot.todoTimerSessions;
        this.todoCompletionRecords = snapshot.todoCompletionRecords;
        this.todoReactions = snapshot.todoReactions;
        this.defaultTodoGoalId = this.loadDefaultTodoGoalId();
        this.recentTodoGoalId = this.loadRecentTodoGoalId();
        this.reconcileTodoGoalPreferences();
        this.followedMateIds = this.loadFollowedMateIds();
        this.todoCalendarDisplayOptions = this.loadTodoCalendarDisplayOptions();
        this.receiptTemplate = this.loadReceiptTemplate();
        this.themeTokens = snapshot.themes;
        this.stickerAssets = this.sortStickerAssets(snapshot.stickerAssets);
        this.decorations = snapshot.decorations;
        this.syncDecorationForCurrentMonth();
        this.selectedRange = null;
        this.resetCalendarRangeDragState();
        this.resetCalendarTapRangeState();
        this.eventForm = this.createEventForm(this.selectedDate);
        this.eventFormOpen = false;
        this.editingEventId = null;
        this.focusedEventId = null;
        this.eventFormError = '';
        this.calendarForm = this.createCalendarForm('personal');
        this.calendarFormOpen = false;
        this.editingCalendarId = null;
        this.calendarFormError = '';
        this.calendarActionCalendarId = null;
        this.memberManagementOpen = false;
        this.memberManagementError = '';
        this.memberManagementNotice = '';
        this.memberInviteForm = this.createMemberInviteDraft();
        this.memberJoinCode = '';
        this.todoForm = this.createTodoForm(this.selectedDate);
        this.todoFormOpen = false;
        this.editingTodoId = null;
        this.todoFormError = '';
        this.todoActionTodoId = null;
        this.todoActionMode = 'menu';
        this.todoActionError = '';
        this.todoActionNotice = '';
        this.todoActionDraft = this.createTodoActionDraft();
        this.clearActiveTodoTimer();
        this.todoTimerNotice = '';
        this.unlockTodoActionScroll();
        this.todoCreateFlowOpen = false;
        this.todoCreateMode = 'todo';
        this.todoCreateContext = this.createTodoCreateContext(this.selectedDate);
        this.todoGoalForm = this.createTodoGoalForm();
        this.todoGoalFormOpen = false;
        this.editingTodoGoalId = null;
        this.todoGoalFormError = '';
        this.todoCategoryDeleteDialogOpen = false;
        this.todoCategoryDeleteTargetId = null;
        this.todoCategoryDeleteMode = 'uncategorized';
        this.todoCategoryDeleteMoveTargetId = '';
        this.todoCategoryDeleteError = '';
        this.routineForm = this.createRoutineForm(this.selectedDate);
        this.routineFormOpen = false;
        this.editingRoutineId = null;
        this.routineFormError = '';
        this.receiptDrafts = {};
        this.ensureReceiptDraftForDate(this.selectedDate);
        this.syncDailyRecordDraftForDate(this.selectedDate);
        this.buildMonth();
        if (!this.canAccessTab(this.activeTab)) this.activeTab = this.fallbackTab();
        this.syncNotificationPermissionState();
        this.syncReminderRuntimeForCurrentUser();
        this.syncCloudStatus();
    }

    private loadExternalCalendarSnapshot() {
        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const rangeStartDate = new Date(year, month, 1 - firstDay.getDay());
        const rangeEndDate = new Date(rangeStartDate.getFullYear(), rangeStartDate.getMonth(), rangeStartDate.getDate() + 41);
        return collectExternalCalendarSnapshot(
            this.user,
            this.activeExternalCalendarProviders(),
            this.toDateKey(rangeStartDate),
            this.toDateKey(rangeEndDate)
        );
    }

    private prepareExternalCalendarSnapshot(snapshot: ExternalCalendarSnapshot): ExternalCalendarSnapshot {
        const calendarById = new Map<string, OwnitCalendar>();
        const calendars = snapshot.calendars.map((calendar) => {
            const policy = this.ensureExternalSyncPolicy(calendar);
            const nextCalendar = this.applyExternalPolicyToCalendar(calendar, policy);
            calendarById.set(nextCalendar.id, nextCalendar);
            return nextCalendar;
        });
        const incomingMappings = snapshot.mappings.map((mapping) => {
            const calendar = calendarById.get(mapping.calendarId) || null;
            const policy = calendar ? this.ensureExternalSyncPolicy(calendar) : null;
            return this.applyExternalPolicyToMapping(mapping, policy);
        });
        const mappings = this.mergeExternalEventMappings(incomingMappings);
        this.externalEventMappings = mappings;
        this.saveExternalSyncPolicies();
        this.saveExternalEventMappings();

        const mappingByEventId = new Map(mappings.map((mapping) => [mapping.eventId, mapping]));
        const events = snapshot.events.map((event) => {
            const calendar = calendarById.get(event.calendarId) || null;
            const policy = calendar ? this.ensureExternalSyncPolicy(calendar) : null;
            const mapping = mappingByEventId.get(event.id) || null;
            const existing = this.events.find((item) => item.id === event.id) || null;
            if (mapping?.syncStatus === 'conflict' && existing) {
                return {
                    ...existing,
                    syncStatus: 'conflict' as const,
                    syncError: mapping.syncError || '외부 일정과 Ownit 일정이 모두 변경되어 사용자 선택을 기다립니다.'
                };
            }
            return this.applyExternalPolicyToEvent(event, policy, mapping);
        });

        return { ...snapshot, calendars, events, mappings };
    }

    private applyExternalPolicyToCalendar(calendar: OwnitCalendar, policy: ExternalCalendarSyncPolicy): OwnitCalendar {
        return {
            ...calendar,
            syncDirection: policy.direction,
            syncDeletionPolicy: policy.deletionPolicy,
            syncStatus: policy.syncStatus,
            syncError: policy.syncError,
            externalWritePermission: policy.writePermission,
            externalWriteScope: policy.writeScope,
            externalWritable: policy.writePermission === 'granted',
            lastSyncedAt: policy.lastSyncedAt || calendar.lastSyncedAt || null,
            externalUpdatedAt: calendar.externalUpdatedAt || policy.lastSyncedAt || calendar.lastSyncedAt || null,
            localUpdatedAt: calendar.localUpdatedAt || calendar.updatedAt
        };
    }

    private applyExternalPolicyToEvent(event: OwnitEvent, policy: ExternalCalendarSyncPolicy | null, mapping: ExternalEventMapping | null): OwnitEvent {
        return {
            ...event,
            syncDirection: policy?.direction || event.syncDirection || 'import_only',
            syncStatus: mapping?.syncStatus || event.syncStatus || 'synced',
            syncError: mapping?.syncError || event.syncError || null,
            lastSyncedAt: mapping?.lastSyncedAt || event.lastSyncedAt || null,
            externalUpdatedAt: mapping?.externalUpdatedAt || event.externalUpdatedAt || null,
            localUpdatedAt: mapping?.localUpdatedAt || event.localUpdatedAt || event.updatedAt
        } as OwnitEvent;
    }

    private applyExternalPolicyToMapping(mapping: ExternalEventMapping, policy: ExternalCalendarSyncPolicy | null): ExternalEventMapping {
        const direction = policy?.direction || mapping.syncDirection || 'import_only';
        const syncStatus = policy?.syncStatus === 'write_scope_required' && direction !== 'import_only'
            ? 'write_scope_required'
            : mapping.syncStatus;
        return {
            ...mapping,
            syncDirection: direction,
            syncStatus,
            syncError: syncStatus === 'write_scope_required'
                ? '외부 캘린더 쓰기 권한을 명시적으로 켠 뒤 내보내기를 진행합니다.'
                : mapping.syncError
        };
    }

    private mergeExternalEventMappings(incomingMappings: ExternalEventMapping[]): ExternalEventMapping[] {
        const byId = new Map(this.externalEventMappings.map((mapping) => [mapping.id, mapping]));
        for (const incoming of incomingMappings) {
            const previous = byId.get(incoming.id);
            byId.set(incoming.id, previous ? this.mergeExternalEventMapping(previous, incoming) : incoming);
        }
        return Array.from(byId.values()).sort((a, b) => {
            if (a.calendarId !== b.calendarId) return a.calendarId.localeCompare(b.calendarId);
            return a.externalEventId.localeCompare(b.externalEventId);
        });
    }

    private mergeExternalEventMapping(previous: ExternalEventMapping, incoming: ExternalEventMapping): ExternalEventMapping {
        const direction = incoming.syncDirection || previous.syncDirection || 'import_only';
        const lastSyncedAt = previous.lastSyncedAt || incoming.lastSyncedAt || null;
        const externalChanged = this.isSyncTimestampAfter(incoming.externalUpdatedAt, lastSyncedAt) && incoming.externalUpdatedAt !== previous.externalUpdatedAt;
        const localChanged = this.isSyncTimestampAfter(previous.localUpdatedAt, lastSyncedAt) && previous.localUpdatedAt !== incoming.localUpdatedAt;
        const base: ExternalEventMapping = {
            ...previous,
            ...incoming,
            syncDirection: direction,
            localUpdatedAt: this.latestSyncTimestamp(previous.localUpdatedAt, incoming.localUpdatedAt),
            updatedAt: new Date().toISOString()
        };

        if (previous.syncStatus === 'conflict' || (direction === 'bidirectional' && externalChanged && localChanged)) {
            return {
                ...base,
                localUpdatedAt: previous.localUpdatedAt || incoming.localUpdatedAt,
                syncStatus: 'conflict',
                syncError: '외부 일정과 Ownit 일정이 마지막 동기화 이후 모두 수정되었습니다.'
            };
        }
        if (direction !== 'import_only' && localChanged) {
            return {
                ...base,
                syncStatus: 'pending_export',
                syncError: null
            };
        }
        if (incoming.externalDeletedAt && direction === 'bidirectional') {
            return {
                ...base,
                syncStatus: 'deleted_external',
                syncError: '외부에서 삭제된 일정입니다. 삭제 전파는 사용자 확인 후 진행합니다.'
            };
        }
        return {
            ...base,
            syncStatus: incoming.syncStatus === 'deleted_external' ? 'deleted_external' : 'synced',
            syncError: incoming.syncError || null
        };
    }

    private isSyncTimestampAfter(value: string | null | undefined, baseline: string | null | undefined): boolean {
        if (!value || !baseline) return false;
        return new Date(value).getTime() > new Date(baseline).getTime();
    }

    private latestSyncTimestamp(a: string | null | undefined, b: string | null | undefined): string | null {
        if (!a) return b || null;
        if (!b) return a;
        return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
    }

    private refreshExternalCalendarData(markSynced = true) {
        const syncedAt = new Date().toISOString();
        const externalSnapshot = this.prepareExternalCalendarSnapshot(this.loadExternalCalendarSnapshot());
        this.externalProviderSummaries = externalSnapshot.providers;
        this.calendars = this.sortCalendars([
            ...this.calendars.filter((calendar) => !this.isExternalCalendar(calendar)),
            ...externalSnapshot.calendars
        ]);
        this.events = this.sortEvents([
            ...this.events.filter((event) => !this.isExternalEvent(event)),
            ...externalSnapshot.events
        ]);
        if (markSynced) this.markExternalOAuthProvidersSynced(externalSnapshot.providers, syncedAt);
    }

    private activeExternalCalendarProviders() {
        const connectedProviders = new Set(
            this.externalOAuthConnections
                .filter((connection) => connection.status === 'connected')
                .map((connection) => connection.provider)
        );
        return this.externalCalendarProviders.filter((provider) => {
            const source = provider.id;
            return isExternalOAuthProviderId(source) && connectedProviders.has(source);
        });
    }

    private markExternalOAuthProvidersSynced(providers: ExternalCalendarProviderSummary[], syncedAt: string): void {
        const syncedProviders = new Set(
            providers
                .map((provider) => provider.source)
                .filter((source): source is ExternalOAuthProviderId => isExternalOAuthProviderId(source))
        );
        if (syncedProviders.size === 0) return;
        let changed = false;
        this.externalOAuthConnections = this.externalOAuthConnections.map((connection) => {
            if (!syncedProviders.has(connection.provider) || connection.status !== 'connected') return connection;
            changed = true;
            return {
                ...connection,
                lastSyncedAt: syncedAt,
                updatedAt: syncedAt,
                lastMessage: `${this.externalOAuthProviderLabel(connection.provider)} 읽기 전용 mock 캘린더를 동기화했습니다.`
            };
        });
        if (changed) this.saveExternalOAuthConnections();
    }

    private async completeMockExternalOAuthConnection(provider: ExternalOAuthProviderId, message: string) {
        const now = new Date().toISOString();
        const current = this.externalOAuthConnectionFor(provider);
        const pending = this.loadExternalOAuthPendingState();
        const scope = pending?.provider === provider ? pending.scope : current.scope;
        const hasWriteScope = this.hasExternalWriteScope(provider, scope);
        this.upsertExternalOAuthConnection(createExternalOAuthConnection(provider, {
            status: 'connected',
            readOnly: true,
            scope,
            writeEnabled: false,
            writeScope: hasWriteScope ? scope : '',
            writeScopeRequestedAt: hasWriteScope ? current.writeScopeRequestedAt || now : current.writeScopeRequestedAt,
            connectedAt: current.connectedAt || now,
            lastSyncedAt: now,
            updatedAt: now,
            lastMessage: hasWriteScope
                ? `${message} 서버 token exchange 검증 전까지 쓰기 권한은 대기 상태입니다.`
                : message
        }, now));
        this.clearExternalOAuthPendingState();
        this.saveExternalOAuthConnections();
        this.refreshExternalCalendarData(false);
        this.buildMonth();
        this.activeTab = 'settings';
        this.externalOAuthNotice = message;
        this.externalOAuthError = '';
        this.service.href('/dashboard/settings');
        await this.service.render();
    }

    private async handleExternalOAuthCallback() {
        let url: URL;
        try {
            url = new URL(window.location.href);
        } catch (error) {
            return;
        }
        const hasOAuthParams = url.searchParams.has('external_oauth')
            || url.searchParams.has('code')
            || url.searchParams.has('error')
            || url.searchParams.has('state');
        if (!hasOAuthParams) return;

        const pending = this.loadExternalOAuthPendingState();
        const providerParam = url.searchParams.get('external_oauth') || url.searchParams.get('provider') || pending?.provider || '';
        if (!isExternalOAuthProviderId(providerParam) || !this.externalOAuthConfig(providerParam)) return;

        const provider = providerParam;
        const state = url.searchParams.get('state') || '';
        const error = url.searchParams.get('error') || '';
        const errorDescription = url.searchParams.get('error_description') || '';
        const now = new Date().toISOString();
        const providerLabel = this.externalOAuthProviderLabel(provider);
        this.activeTab = 'settings';

        if (!pending || pending.provider !== provider || pending.state !== state) {
            this.upsertExternalOAuthConnection(createExternalOAuthConnection(provider, {
                status: 'error',
                updatedAt: now,
                lastMessage: 'OAuth state 검증에 실패했습니다. 연결을 다시 시작해 주세요.'
            }, now));
            this.saveExternalOAuthConnections();
            this.clearExternalOAuthPendingState();
            this.externalOAuthError = 'OAuth callback state가 일치하지 않습니다. 연결을 다시 시도해 주세요.';
            this.externalOAuthNotice = '';
            this.cleanExternalOAuthCallbackUrl(url);
            await this.service.render();
            return;
        }

        if (error) {
            const cancelled = error === 'access_denied';
            if (cancelled && this.hasExternalWriteScope(provider, pending.scope)) this.markExternalProviderWriteAccessDenied(provider, now);
            this.upsertExternalOAuthConnection(createExternalOAuthConnection(provider, {
                status: cancelled ? 'cancelled' : 'error',
                updatedAt: now,
                lastMessage: cancelled ? '사용자가 OAuth 연결을 취소했습니다.' : (errorDescription || error)
            }, now));
            this.saveExternalOAuthConnections();
            this.clearExternalOAuthPendingState();
            this.externalOAuthNotice = cancelled ? `${providerLabel} 연결이 취소되었습니다.` : '';
            this.externalOAuthError = cancelled ? '' : `${providerLabel} OAuth 실패: ${errorDescription || error}`;
            this.cleanExternalOAuthCallbackUrl(url);
            await this.service.render();
            return;
        }

        if (url.searchParams.has('code') || url.searchParams.get('mock') === '1') {
            const hasWriteScope = this.hasExternalWriteScope(provider, pending.scope);
            this.upsertExternalOAuthConnection(createExternalOAuthConnection(provider, {
                status: 'connected',
                readOnly: true,
                scope: pending.scope,
                writeEnabled: false,
                writeScope: hasWriteScope ? pending.scope : '',
                writeScopeRequestedAt: hasWriteScope ? now : null,
                connectedAt: this.externalOAuthConnectionFor(provider).connectedAt || now,
                lastSyncedAt: now,
                updatedAt: now,
                lastMessage: hasWriteScope
                    ? 'OAuth callback에서 쓰기 scope 요청을 확인했습니다. 서버 token exchange 검증 전까지 쓰기 권한은 대기합니다.'
                    : 'OAuth callback을 확인했습니다. 현재 MVP는 code/token을 저장하지 않고 mock 캘린더만 동기화합니다.'
            }, now));
            this.saveExternalOAuthConnections();
            this.clearExternalOAuthPendingState();
            this.refreshExternalCalendarData(false);
            this.buildMonth();
            this.externalOAuthNotice = hasWriteScope
                ? `${providerLabel} 쓰기 scope 요청을 확인했습니다. token exchange는 서버 구현 전까지 대기 상태입니다.`
                : `${providerLabel} OAuth callback을 확인했습니다. token exchange는 서버 구현 전까지 stub 상태입니다.`;
            this.externalOAuthError = '';
            this.cleanExternalOAuthCallbackUrl(url);
            await this.service.render();
        }
    }

    private cleanExternalOAuthCallbackUrl(url: URL): void {
        [
            'external_oauth',
            'provider',
            'code',
            'state',
            'scope',
            'authuser',
            'prompt',
            'hd',
            'error',
            'error_description',
            'mock'
        ].forEach((key) => url.searchParams.delete(key));
        const query = url.searchParams.toString();
        window.history.replaceState({}, document.title, `${url.pathname}${query ? `?${query}` : ''}${url.hash || ''}`);
    }

    private syncRoute() {
        const tab = WizRoute && WizRoute.segment ? WizRoute.segment.tab : null;
        if (this.isKnownTab(tab) && this.canAccessTab(tab)) {
            this.activeTab = tab;
            if (tab !== 'calendar') this.detailPanelOpen = false;
            return;
        }
        this.activeTab = this.fallbackTab();
        if (this.activeTab !== 'calendar') this.detailPanelOpen = false;
    }

    private isKnownTab(tab: any): tab is AppTab {
        return this.knownTabs.includes(tab);
    }

    private fallbackTab(): AppTab {
        return this.knownTabs.find((tab) => this.canAccessTab(tab)) || 'calendar';
    }

    private createWidgetSummary(source: 'live' | 'cache' = 'live'): OwnitWidgetSummary {
        const date = this.todayDate;
        return createOwnitWidgetSummary({
            user: this.user,
            date,
            now: new Date(),
            timezone: this.localTimezone(),
            events: this.visibleEvents,
            todos: this.todosForDay(date),
            calendars: this.calendars,
            dailyRecord: this.todoDailyRecordForDate(date),
            source,
            cacheKey: this.widgetSummaryStorageKey()
        });
    }

    private syncWidgetSummaryCache() {
        this.widgetSummary = this.createWidgetSummary('live');
        if (!this.canShowApp || !this.user.id) {
            this.widgetSummaryCacheLabel = '최근 위젯 캐시 없음';
            return;
        }
        const cache = createOwnitWidgetSummaryCache(this.widgetSummary);
        this.dataRepository.saveLocalState(this.widgetSummaryStorageKey(), cache);
        this.widgetSummaryCacheLabel = `최근 캐시 ${this.reminderDateTimeLabel(cache.cachedAt)}`;
    }

    private widgetSummaryStorageKey() {
        return widgetSummaryCacheKey(this.user.id || 'guest');
    }

    private buildMonth() {
        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const start = new Date(year, month, 1 - firstDay.getDay());
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 41);
        const holidaysByDate = this.holidayLayer.enabled
            ? this.holidayService.groupByDate(this.holidayService.getHolidaysForRange(this.toDateKey(start), this.toDateKey(end)))
            : {};
        const days: CalendarDay[] = [];
        const todayKey = this.toDateKey(new Date());

        for (let index = 0; index < 42; index++) {
            const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
            const dateKey = this.toDateKey(date);
            const weekday = date.getDay();
            const weekendTone = this.weekendToneForWeekday(weekday);
            const holidays = holidaysByDate[dateKey] || [];
            const isPublicHoliday = holidays.some((holiday) => holiday.isPublicHoliday);
            const isCurrentMonth = date.getMonth() === month;
            const dayEvents = this.eventsForDay(dateKey);
            const dayTodos = this.todosForDay(dateKey);
            days.push({
                date: dateKey,
                day: date.getDate(),
                weekday,
                weekendTone,
                holidays,
                holidayLabel: this.summarizeHolidays(holidays),
                isCurrentMonth,
                isToday: dateKey === todayKey,
                isSunday: weekendTone === 'sunday',
                isSaturday: weekendTone === 'saturday',
                isHoliday: holidays.length > 0,
                isPublicHoliday,
                eventCount: dayEvents.length,
                eventOverflowCount: 0,
                todoCount: dayTodos.length,
                todoRecord: null
            });
        }
        this.monthDays = days;
        this.monthWeeks = this.buildCalendarWeeks(days);
        this.syncWidgetSummaryCache();
    }

    private weekendToneForWeekday(weekday: number): WeekendTone {
        if (weekday === 0) return 'sunday';
        if (weekday === 6) return 'saturday';
        return 'weekday';
    }

    private async moveMonth(offset: number) {
        const selected = this.fromDateKey(this.selectedDate);
        const targetMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + offset, 1);
        const targetDay = Math.min(selected.getDate(), this.daysInMonth(targetMonth));
        const nextSelected = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), targetDay);
        this.currentMonth = targetMonth;
        this.selectedDate = this.toDateKey(nextSelected);
        this.selectedRange = null;
        this.resetCalendarRangeDragState();
        this.resetCalendarTapRangeState();
        this.syncDecorationForCurrentMonth();
        this.ensureReceiptDraftForDate(this.selectedDate);
        this.syncDailyRecordDraftForDate(this.selectedDate);
        this.refreshExternalCalendarData();
        this.buildMonth();
        await this.service.render();
    }

    private buildCalendarWeeks(days: CalendarDay[]) {
        const weeks: CalendarWeek[] = [];
        const segmentLayout = this.buildEventSegments(days);
        for (let index = 0; index < days.length; index += 7) {
            const weekDays = days.slice(index, index + 7);
            const weekRowIndex = index / 7;
            weekDays.forEach((day) => {
                day.eventOverflowCount = segmentLayout.hiddenByDate[day.date] || 0;
            });
            weeks.push({
                key: `${weekDays[0].date}-${weekDays[6].date}`,
                days: weekDays,
                segments: segmentLayout.segmentsByWeekRowIndex[weekRowIndex] || [],
                hiddenSegmentCount: segmentLayout.hiddenSegmentCountByWeekRowIndex[weekRowIndex] || 0
            });
        }
        return weeks;
    }

    private buildEventSegments(days: CalendarDay[]): CalendarEventSegmentBuildResult {
        const maxRows = 3;
        const segmentsByWeekRowIndex: Record<number, CalendarEventSegment[]> = {};
        const hiddenSegmentCountByWeekRowIndex: Record<number, number> = {};
        const hiddenByDate: Record<string, number> = {};
        const dateIndexByKey = new Map<string, number>(days.map((day, index): [string, number] => [day.date, index]));
        const gridStart = days[0]?.date || '';
        const gridEnd = days[days.length - 1]?.date || '';
        const lanes: boolean[][] = [];
        if (!gridStart || !gridEnd) {
            return { segmentsByWeekRowIndex, hiddenSegmentCountByWeekRowIndex, hiddenByDate };
        }

        const layoutItems = this.visibleEvents
            .map((event) => this.createMonthEventLayoutItem(event, dateIndexByKey, gridStart, gridEnd))
            .filter((item): item is MonthEventLayoutItem => !!item)
            .sort((a, b) => this.compareMonthEventLayoutItems(a, b));

        for (const item of layoutItems) {
            const laneIndex = this.findAvailableMonthEventLane(lanes, item.startIndex, item.endIndex);
            if (laneIndex >= maxRows) {
                this.addHiddenMonthEventSegmentCounts(item, days, hiddenByDate, hiddenSegmentCountByWeekRowIndex);
                continue;
            }

            this.occupyMonthEventLane(lanes, laneIndex, item.startIndex, item.endIndex, days.length);
            for (const segment of this.createMonthEventWeekSegments(item, days, laneIndex)) {
                if (!segmentsByWeekRowIndex[segment.weekRowIndex]) segmentsByWeekRowIndex[segment.weekRowIndex] = [];
                segmentsByWeekRowIndex[segment.weekRowIndex].push(segment);
            }
        }

        Object.values(segmentsByWeekRowIndex).forEach((segments) => {
            segments.sort((a, b) => {
                if (a.lane !== b.lane) return a.lane - b.lane;
                if (a.startColumn !== b.startColumn) return a.startColumn - b.startColumn;
                if (a.spanColumns !== b.spanColumns) return b.spanColumns - a.spanColumns;
                return a.label.localeCompare(b.label);
            });
        });

        return { segmentsByWeekRowIndex, hiddenSegmentCountByWeekRowIndex, hiddenByDate };
    }

    private createMonthEventLayoutItem(event: OwnitEvent, dateIndexByKey: Map<string, number>, gridStart: string, gridEnd: string): MonthEventLayoutItem | null {
        if (!event.startDate || !event.endDate || event.endDate < event.startDate) return null;
        if (event.startDate > gridEnd || event.endDate < gridStart) return null;

        const visibleStart = event.startDate > gridStart ? event.startDate : gridStart;
        const visibleEnd = event.endDate < gridEnd ? event.endDate : gridEnd;
        const startIndex = dateIndexByKey.get(visibleStart);
        const endIndex = dateIndexByKey.get(visibleEnd);
        if (startIndex === undefined || endIndex === undefined || endIndex < startIndex) return null;
        return { event, visibleStart, visibleEnd, startIndex, endIndex };
    }

    private compareMonthEventLayoutItems(a: MonthEventLayoutItem, b: MonthEventLayoutItem) {
        if (a.visibleStart !== b.visibleStart) return a.visibleStart.localeCompare(b.visibleStart);
        const durationA = a.endIndex - a.startIndex;
        const durationB = b.endIndex - b.startIndex;
        if (durationA !== durationB) return durationB - durationA;
        if (a.event.startDate !== b.event.startDate) return a.event.startDate.localeCompare(b.event.startDate);
        if (a.event.endDate !== b.event.endDate) return b.event.endDate.localeCompare(a.event.endDate);
        const timeA = a.event.startTime || '00:00';
        const timeB = b.event.startTime || '00:00';
        if (timeA !== timeB) return timeA.localeCompare(timeB);
        return a.event.title.localeCompare(b.event.title);
    }

    private findAvailableMonthEventLane(lanes: boolean[][], startIndex: number, endIndex: number) {
        for (let laneIndex = 0; laneIndex < lanes.length; laneIndex++) {
            const lane = lanes[laneIndex];
            let available = true;
            for (let index = startIndex; index <= endIndex; index++) {
                if (lane[index]) {
                    available = false;
                    break;
                }
            }
            if (available) return laneIndex;
        }
        return lanes.length;
    }

    private occupyMonthEventLane(lanes: boolean[][], laneIndex: number, startIndex: number, endIndex: number, dayCount: number) {
        if (!lanes[laneIndex]) lanes[laneIndex] = Array(dayCount).fill(false);
        for (let index = startIndex; index <= endIndex; index++) {
            lanes[laneIndex][index] = true;
        }
    }

    private addHiddenMonthEventSegmentCounts(item: MonthEventLayoutItem, days: CalendarDay[], hiddenByDate: Record<string, number>, hiddenSegmentCountByWeekRowIndex: Record<number, number>) {
        for (let index = item.startIndex; index <= item.endIndex; index++) {
            const date = days[index]?.date;
            if (date) hiddenByDate[date] = (hiddenByDate[date] || 0) + 1;
        }

        const startWeekRowIndex = Math.floor(item.startIndex / 7);
        const endWeekRowIndex = Math.floor(item.endIndex / 7);
        for (let weekRowIndex = startWeekRowIndex; weekRowIndex <= endWeekRowIndex; weekRowIndex++) {
            hiddenSegmentCountByWeekRowIndex[weekRowIndex] = (hiddenSegmentCountByWeekRowIndex[weekRowIndex] || 0) + 1;
        }
    }

    private createMonthEventWeekSegments(item: MonthEventLayoutItem, days: CalendarDay[], laneIndex: number): CalendarEventSegment[] {
        const segments: CalendarEventSegment[] = [];
        const event = item.event;
        const startWeekRowIndex = Math.floor(item.startIndex / 7);
        const endWeekRowIndex = Math.floor(item.endIndex / 7);
        const lane = laneIndex + 1;

        for (let weekRowIndex = startWeekRowIndex; weekRowIndex <= endWeekRowIndex; weekRowIndex++) {
            const weekStartIndex = weekRowIndex * 7;
            const weekEndIndex = weekStartIndex + 6;
            const segmentStartIndex = Math.max(item.startIndex, weekStartIndex);
            const segmentEndIndex = Math.min(item.endIndex, weekEndIndex);
            const segmentStart = days[segmentStartIndex]?.date || item.visibleStart;
            const segmentEnd = days[segmentEndIndex]?.date || item.visibleEnd;
            const startColumn = segmentStartIndex - weekStartIndex + 1;
            const spanColumns = segmentEndIndex - segmentStartIndex + 1;
            const isStart = segmentStart === event.startDate;
            const isEnd = segmentEnd === event.endDate;

            segments.push({
                event,
                label: this.segmentLabel(event),
                focusDate: segmentStart,
                segmentStart,
                segmentEnd,
                weekRowIndex,
                startColumn,
                spanColumns,
                isStart,
                isEnd,
                gridColumn: `${startColumn} / span ${spanColumns}`,
                gridRow: `${lane}`,
                continuesBefore: !isStart,
                continuesAfter: !isEnd,
                isShared: this.calendarById(event.calendarId)?.type === 'shared',
                isExternal: this.isExternalEvent(event),
                isSingleDay: event.startDate === event.endDate,
                lane
            });
        }

        return segments;
    }

    private summarizeHolidays(holidays: OwnitHoliday[]) {
        return holidays.map((holiday) => holiday.name).join(' · ');
    }

    private daysInMonth(date: Date) {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    }

    private calendarRangeForDisplay(): CalendarDateRange | null {
        if (this.isDragging && this.dragStartDate && (this.hoveredDate || this.dragEndDate)) {
            return this.normalizeCalendarDateRange(this.dragStartDate, this.hoveredDate || this.dragEndDate || this.dragStartDate);
        }
        if (this.calendarTapRangeMode && this.calendarTapRangeStartDate) {
            return this.normalizeCalendarDateRange(this.calendarTapRangeStartDate, this.calendarTapRangeEndDate || this.calendarTapRangeStartDate);
        }
        return this.selectedRange;
    }

    private normalizeCalendarDateRange(startDate: string, endDate: string): CalendarDateRange {
        return startDate <= endDate
            ? { startDate, endDate }
            : { startDate: endDate, endDate: startDate };
    }

    private updateCalendarRangeDragEnd(date: string) {
        this.dragEndDate = date;
        this.hoveredDate = date;
        if (this.dragStartDate && date !== this.dragStartDate) this.rangeDragMoved = true;
    }

    private resetCalendarRangeDragState() {
        this.dragStartDate = null;
        this.dragEndDate = null;
        this.hoveredDate = null;
        this.isDragging = false;
        this.rangeDragPointerId = null;
        this.rangeDragMoved = false;
    }

    private resetCalendarTapRangeState() {
        this.calendarTapRangeMode = false;
        this.calendarTapRangeStartDate = null;
        this.calendarTapRangeEndDate = null;
        this.popCalendarTapRangeHistoryState();
    }

    private async cancelCalendarTapRangeMode(source: CalendarRangeCancelSource) {
        if (!this.calendarTapRangeMode && !this.calendarTapRangeHistoryPushed) return;
        this.calendarTapRangeMode = false;
        this.calendarTapRangeStartDate = null;
        this.calendarTapRangeEndDate = null;
        if (source === 'history') {
            this.calendarTapRangeHistoryPushed = false;
        } else {
            this.popCalendarTapRangeHistoryState();
        }
        await this.service.render();
    }

    private pushCalendarTapRangeHistoryState() {
        if (this.calendarTapRangeHistoryPushed || typeof window === 'undefined' || !window.history?.pushState) return;
        window.history.pushState({ ownitCalendarRangeMode: true }, '', window.location.href);
        this.calendarTapRangeHistoryPushed = true;
    }

    private popCalendarTapRangeHistoryState() {
        if (!this.calendarTapRangeHistoryPushed) return;
        this.calendarTapRangeHistoryPushed = false;
        if (typeof window === 'undefined' || !window.history?.back) return;
        window.history.back();
    }

    private calendarDateFromPointerEvent(event: PointerEvent) {
        if (typeof document === 'undefined') return '';
        const directElement = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null;
        const directDay = directElement?.closest?.('[data-calendar-date]') as HTMLElement | null;
        const directDate = directDay?.dataset?.calendarDate || '';
        if (this.isVisibleMonthDate(directDate)) return directDate;

        const elements = document.elementsFromPoint(event.clientX, event.clientY) as HTMLElement[];
        const weekElement = elements
            .map((element) => element.closest?.('[data-calendar-week-index]') as HTMLElement | null)
            .find((element): element is HTMLElement => !!element);
        if (!weekElement) return '';

        const weekIndex = Number(weekElement.dataset.calendarWeekIndex);
        const week = Number.isInteger(weekIndex) ? this.monthWeeks[weekIndex] : null;
        if (!week) return '';

        const rect = weekElement.getBoundingClientRect();
        if (rect.width <= 0 || event.clientX < rect.left || event.clientX > rect.right) return '';
        const column = this.clamp(Math.floor(((event.clientX - rect.left) / rect.width) * 7), 0, 6);
        return week.days[column]?.date || '';
    }

    private isVisibleMonthDate(date: string) {
        return !!date && this.monthDays.some((day) => day.date === date);
    }

    private eventDraftRangeForDate(date: string): CalendarDateRange {
        if (this.selectedRange && this.isDateWithin(date, this.selectedRange.startDate, this.selectedRange.endDate)) {
            return this.selectedRange;
        }
        return { startDate: date, endDate: date };
    }

    private isDateWithin(date: string, start: string, end: string) {
        return date >= start && date <= end;
    }

    private toDateKey(date: Date) {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private fromDateKey(key: string) {
        const [year, month, day] = key.split('-').map((value) => Number(value));
        return new Date(year, month - 1, day);
    }

    private nextDateKey(key: string) {
        return this.shiftDateKey(key, 1);
    }

    private shiftDateKey(key: string, amount: number) {
        const date = this.fromDateKey(key);
        return this.toDateKey(new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount));
    }
}

export default PageDashboardComponent;